import { useState, useEffect, useCallback, useRef, lazy, Suspense, Fragment } from "react";
import { ScrollBox, usePaged, Pager } from "./ScrollList";
import ItemGlyph from "./ItemGlyph";
import DesignConstantsRegister from "./DesignConstantsRegister";
import ReactMarkdown from "react-markdown";
import { fmtIST } from "./time";
import { startLive, stopLive, useLive, touchesItem, ITEM_TABLES } from "./live";
import { setActiveProject, takeOpenProject } from "./activeProject";
import ProjectSetup, { TeamEditor } from "./ProjectSetup";
import { useInputScope, ScopeMark, ScopeNote, HiddenValues } from "./inputScope";
import { ac, useTableAligner } from "./tableAlign";
import RatesPolicy, { TierBadge, LinePricing, LineSourceModal, OverrideModal, BoqCommercialCard } from "./RatesPolicy";

// Same-origin by default (single-service deploy). For a separate local backend,
// set REACT_APP_API_BASE=http://localhost:8020 (the dev server also proxies /api).
const API = process.env.REACT_APP_API_BASE || "";

// The room builder pulls in three.js — several hundred KB the rest of the app has
// no use for — so it is fetched only when someone actually opens the module.
const RoomBuilder = lazy(() => import("./RoomBuilder"));
const EngineeringFlow = lazy(() => import("./EngineeringFlow"));
// The customer's own documents. Already mounted at stage 1 of the engineering
// flow; also wanted here, because this is the screen an engineer is on when the
// design inputs are missing, and sending them to another page to attach the
// drawing they are looking at is how a file never gets attached.
const DocumentRegister = lazy(() => import("./DocumentRegister"));

// ── Auth plumbing ─────────────────────────────────────────────────────────────
// One place attaches the bearer token, so no call site can forget it, and a 401
// anywhere drops the session instead of leaving a half-logged-in screen.
const TOKEN_KEY = "kaizen_token";
let _token = localStorage.getItem(TOKEN_KEY) || null;
let _onUnauthorized = null;          // App registers a forced-logout callback

function setToken(t) {
  _token = t || null;
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

// A response body is not guaranteed to be JSON. Starlette's default 500 handler
// answers with PLAIN TEXT "Internal Server Error", and Safari's Response.json()
// rejects that with "The string did not match the expected pattern." — so a
// server fault surfaced to the user as a baffling string-format error with no
// status code and no clue where it came from. Read the body once as text, parse
// it only if it parses, and always check `ok` before trusting the shape.
async function jsonOrThrow(r, fallback = "Request failed") {
  const text = await r.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* not JSON */ }
  if (!r.ok) {
    const d = data && data.detail;
    const msg = typeof d === "string" ? d
      : Array.isArray(d) ? d.map(e => (e && e.msg) || JSON.stringify(e)).join("; ")
      : (text || "").trim().slice(0, 200) || fallback;
    throw new Error(`${msg} (HTTP ${r.status})`);
  }
  return data;
}

/** How long to keep retrying a read that could not reach the server. */
const RETRY_MS = [400, 900, 1800];

/** A failure worth trying again: the request never got an answer.
 *
 *  502/503/504 come from Caddy when the app is not listening — a deploy, or a
 *  restart. A thrown fetch is a dropped connection or a lost network. Neither
 *  tells us anything about the request itself, so the same request will
 *  probably work in a moment.
 *
 *  A 4xx is not here on purpose. "You cannot edit this item" does not become
 *  true by asking three times.
 */
const _transient = (res) => !res || res.status === 502 || res.status === 503
  || res.status === 504;

async function authFetch(url, opts = {}) {
  const headers = { ...(opts.headers || {}) };
  if (_token) headers.Authorization = `Bearer ${_token}`;
  const method = (opts.method || "GET").toUpperCase();

  // Only reads are retried here.
  //
  // A POST that failed with "Failed to fetch" may still have been received and
  // acted on — the answer was lost, not the request — so retrying it could
  // raise the same item twice. In a master whose whole purpose is to stop
  // duplicates that is the worst possible way to be helpful. Caddy retries
  // non-GETs instead, and only when the connection was REFUSED, which is the
  // one case where the app provably never saw it.
  const retryable = method === "GET" || method === "HEAD";
  let last, thrown;

  for (let attempt = 0; ; attempt++) {
    try {
      thrown = null;
      last = await window.fetch(url, { ...opts, headers });
    } catch (e) {
      thrown = e;
      last = null;
    }
    if (!retryable || attempt >= RETRY_MS.length || !_transient(last)) break;
    await new Promise(r => setTimeout(r, RETRY_MS[attempt]));
  }

  if (!last) {
    // Rethrown with something a reader can act on. "Failed to fetch" is the
    // browser's words for every network fault there is, and on screen it read
    // as the page being broken when the server was simply restarting.
    const e = new Error(
      "Could not reach the server — it may be restarting. "
      + "Nothing was lost; try again in a moment.");
    e.cause = thrown;
    e.offline = true;
    throw e;
  }
  if (last.status === 401 && _token) {
    setToken(null);
    _onUnauthorized && _onUnauthorized();
  }
  return last;
}

// Tabs each role may reach. The backend enforces the same split independently —
// hiding a tab is a convenience, not the security boundary.
// Navigation is driven by CAPABILITIES, not by role. Keying tabs off the role
// name meant TABS_BY_ROLE[user.role] returned undefined the moment a role was
// added — and `[0]` on undefined throws, so a Super Admin could not even load
// the app. A tab now states the capability it needs and the server decides who
// holds it, which is the same table the API enforces.
const NAV_TABS = [
  { name: "Dashboard",         perm: "dashboard.view" },
  { name: "Inquiries",         perm: "project.view" },
  { name: "Projects",          perm: "project.view" },
  // Open to anyone who raises items, not only approvers: a creator needs
  // somewhere to see that their submission was rejected and why. The page
  // scopes itself — an approver sees everything, a creator only their own.
  { name: "Approvals",         perm: "item.create" },
  // One entry, three routes. Creating an item was three sidebar entries —
  // Classify & Create, Import Items, Composite Item — which made the user
  // choose their method before they had started. Nobody arrives thinking "I
  // need a composite item"; they arrive because an order has a line the master
  // does not hold. Whether that is one described item, an assembly of existing
  // SKUs, or forty rows off a sheet is a question the screen can ask.
  { name: "Create Items",      perm: "item.create" },
  { name: "Price List",        perm: "project.view" },
  // KAPL B1 (20-Oct-2026): the rate sources behind every BOQ line, the
  // Commercial Policy Master and its overrides.
  { name: "Rates & Policy",    perm: "rates.enter" },
  { name: "AI Chat",           perm: "item.create" },
  { name: "SOPs",              perm: "sop.manage" },
  { name: "Design Constants",  perm: "constants.manage" },
  { name: "Item Creation Logic", perm: "constants.manage" },
  // KAPL's five stages for preparing a project; the HVAC calculation is its
  // stage 4, not a tab of its own.
  { name: "Engineering Flow",  perm: "project.view" },
  { name: "Room Builder",      perm: "project.view" },
  { name: "Zoho Sync",         perm: "zoho.sync" },
  { name: "Users",             perm: "user.manage" },
  { name: "Archive",           perm: "item.delete" },
];

// FALLBACK ONLY. The Users screen reads the real list from GET /api/users/roles,
// which is generated from the UserRole enum and therefore cannot go stale. This
// array is what it draws if that call fails.
//
// It is five roles and the system has ten. That is not an oversight left here
// by accident — it is the original list, kept deliberately as the offline
// fallback and labelled so nobody edits it thinking they are adding a role.
// Adding a role means adding it to app/services/permissions.py; this file
// never needs to know.
const ROLES = [
  { value: "SUPER_ADMIN",            label: "Super Admin",          kapl: true },
  { value: "ADMIN",                  label: "Admin",                kapl: true },
  { value: "ENGINEERING_APPROVER",   label: "Engg Approver",        kapl: true },
  { value: "SCM",                    label: "SCM Approver",         kapl: true },
  { value: "FINANCE",                label: "Finance Approver",     kapl: true },
  { value: "MASTER_DATA_CONTROLLER", label: "Item Master Approver", kapl: true },
  { value: "VIEWER",                 label: "Viewer",               kapl: true },
  { value: "ENGINEER",               label: "Engineer" },
  { value: "CREATOR",                label: "Creator" },
  { value: "APPROVER_RELEASE",       label: "Approver / Release",
    superseded_by: "MASTER_DATA_CONTROLLER" },
];
const ROLE_HELP = {
  SUPER_ADMIN: "Everything, including deleting records and company-wide commercials.",
  ADMIN: "Approves items and manages users. No delete, no company-wide commercials.",
  ENGINEERING_APPROVER: "Signs Engineering Verification, stage 1 of 4.",
  SCM: "Signs SCM Verification, stage 2 of 4.",
  FINANCE: "Signs Finance Review, stage 3 of 4.",
  MASTER_DATA_CONTROLLER: "Signs the last stage and gives the final approval.",
  VIEWER: "Read-only.",
  ENGINEER: "Projects, BOQ, costing and drawings. Raises items, does not approve them.",
  CREATOR: "Raises items and submits them for approval. Edits only their own.",
  APPROVER_RELEASE: "Superseded by Item Master Approver.",
};

/** Does this user hold a capability? The server sends the resolved list. */
const HAS = (me, perm) => Array.isArray(me?.permissions) && me.permissions.includes(perm);

/** The tabs this user may see, never empty — everyone can read something. */
function tabsFor(me) {
  const t = NAV_TABS.filter(x => HAS(me, x.perm)).map(x => x.name);
  return t.length ? t : ["Dashboard"];
}

// Icons keep the vertical rail scannable at a glance.
const TAB_ICONS = {
  "Dashboard": "▦", "Inquiries": "✉", "Projects": "🏗", "Create Items": "✨",
  "Price List": "₹", "Rates & Policy": "🧾", "AI Chat": "💬", "SOPs": "📄", "Design Constants": "⚙",
  "Zoho Sync": "🔄", "Users": "👤", "Approvals": "✔", "Archive": "🗄",
  "Item Creation Logic": "❓", "Room Builder": "🧱", "Engineering Flow": "🧭",
};


const categoryColors = {
  A01: "#3B82F6", A02: "#F59E0B", A03: "#10B981", A04: "#8B5CF6",
  A05: "#EC4899", A06: "#06B6D4", A07: "#F97316", A08: "#6366F1",
  A09: "#14B8A6", A10: "#EF4444", A11: "#84CC16"
};

// ── Shared UI ─────────────────────────────────────────────────────────────────
function Badge({ text, color = "#3B82F6" }) {
  return <span style={{ background: color + "20", color, border: `1px solid ${color}40`, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{text}</span>;
}
/** A category's share of the master, printed so a small share is not mistaken
 *  for a missing one.
 *
 *  Named `sharePct` and not `pct`: Dashboard already has a local
 *  `const pct = (n, t) => t ? Math.round(n / t * 100) : 0`, which shadowed a
 *  module-scope `pct` at both call sites. Called with one argument it returned
 *  0 for every category, the helper went unreferenced and was tree-shaken, and
 *  the bundle looked as though the deploy had not landed.
 *
 *  "0%" is reserved for a category that genuinely holds nothing. Anything that
 *  holds items but rounds below a tenth of a percent says "<0.1%", because
 *  "0%" next to a count of 6 reads as a broken calculation — which is exactly
 *  how it was reported. The exact figure is on the hover.
 */
function sharePct(v) {
  const n = Number(v) || 0;
  if (n <= 0) return "0%";
  if (n < 0.1) return "<0.1%";
  return `${n.toFixed(1)}%`;
}

function shareTitle(c, total) {
  const n = Number(c.share) || 0;
  return total
    ? `${c.items.toLocaleString()} of ${total.toLocaleString()} items — ${n.toFixed(3)}%`
    : `${c.items.toLocaleString()} items`;
}

/** An area, printed as a room schedule prints one.
 *
 *  Two decimals is a hundredth of a square metre. Rooms stored before the
 *  write-side rounding still carry float noise — 11.623199999999999 — so this
 *  formats rather than trusting what it is given.
 */
function m2(v) {
  if (v === null || v === undefined || v === "") return "\u2014";
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "\u2014";
}

function Card({ children, style = {}, onClick, title }) {
  return <div onClick={onClick} title={title} style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: 20, ...style }}>{children}</div>;
}
/** A box whose contents survive closing the panel it lives in.
 *
 *  An approver typed the reason for approving their own item, the approval was
 *  refused because the §12 stages were still open, they closed the item and
 *  reopened it — and the reason was gone. It was ordinary component state, so
 *  unmounting threw it away. Typing is work; losing it is the screen's fault.
 *
 *  Kept per item and per field, cleared when the thing it belongs to succeeds.
 *  Wrapped in try/catch throughout: a private window or blocked site data makes
 *  every one of these calls throw, and a lost draft must never take the page
 *  down with it.
 */
function useDraft(key, initial = "") {
  const k = `kz.draft.${key}`;
  const read = () => {
    try { return window.localStorage.getItem(k) ?? initial; } catch { return initial; }
  };
  const [v, setV] = useState(read);
  // Re-read when the key changes. The lazy initialiser above runs once, so a
  // component that stays mounted while its item changes would otherwise keep
  // the previous item's text — showing one item's reason on another, which is
  // worse than the empty box this hook exists to prevent.
  useEffect(() => { setV(read()); }, [k]);   // eslint-disable-line react-hooks/exhaustive-deps
  const set = (next) => {
    setV(next);
    try {
      if (next) window.localStorage.setItem(k, next);
      else window.localStorage.removeItem(k);
    } catch { /* no storage: the draft is simply not kept */ }
  };
  const clear = () => {
    setV("");
    try { window.localStorage.removeItem(k); } catch { /* as above */ }
  };
  return [v, set, clear];
}

/** Download a file from the API, with the request authenticated.
 *
 *  `window.open` and a plain <a href> both open a fresh browser request, and a
 *  fresh request carries no Authorization header — the token lives in this
 *  page's JavaScript, not in a cookie. Every such link in the app answered
 *  {"detail":"Not authenticated."} and handed the user a page of JSON instead
 *  of their document. Fetching first and handing the browser a blob is the
 *  whole fix; the alternative, a credential in the URL, lands in the browser
 *  history and the server log.
 */
async function downloadAuthed(url, filename) {
  const r = await authFetch(url);
  if (!r.ok) {
    let msg = `Download failed (HTTP ${r.status}).`;
    try { msg = (await r.json()).detail || msg; } catch { /* not JSON */ }
    throw new Error(msg);
  }
  const u = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = u; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 10000);
}

function Btn({ children, onClick, color = "#3B82F6", disabled = false, outline = false, small = false, full = false }) {
  // whiteSpace: nowrap because a squeezed flex row broke "Save" into "Sa ve"
  // and "Cancel" into "Can cel". A button is as wide as its label needs.
  return <button onClick={onClick} disabled={disabled} style={{ whiteSpace: "nowrap", padding: small ? "5px 12px" : "9px 20px", background: disabled ? "#E5E7EB" : outline ? "transparent" : color, color: disabled ? "#9CA3AF" : outline ? color : "#fff", border: `1.5px solid ${disabled ? "#E5E7EB" : color}`, borderRadius: 8, fontWeight: 600, fontSize: small ? 12 : 13, cursor: disabled ? "not-allowed" : "pointer", transition: "all 0.15s", width: full ? "100%" : "auto" }}>{children}</button>;
}
// ── Zoho sync, inline per item ────────────────────────────────────────────────
// erp_item_id is written back by a push, so it doubles as the
// "already in Zoho" flag — the item list already carries it, no per-row API call.
function useZohoStatus() {
  const [status, setStatus] = useState({ configured: null, missing: [] });
  useEffect(() => {
    let alive = true;
    authFetch(`${API}/api/integrations/zoho/status`)
      .then(r => (r.ok ? r.json() : { configured: false, missing: [] }))
      .then(d => { if (alive) setStatus(d); })
      .catch(() => { if (alive) setStatus({ configured: false, missing: [] }); });
    return () => { alive = false; };
  }, []);
  return status;
}

function ZohoChip({ item, configured, onSynced }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const synced = Boolean(item.erp_item_id);

  // Eligibility is a real sign-off, not the RELEASED state.
  //
  // This read `status !== "RELEASED"`, so A06-002.562 — RELEASED, imported,
  // Approved By blank — showed "Push to Zoho" next to a chip saying it had not
  // been approved here. The server refused with a 409, but offering a button
  // that cannot work is its own defect: the screen said the item was ready
  // while the chip beside it said it was not.
  //
  // is_locked is the server's own answer (RELEASED or APPROVED *and* a named
  // approver), so the button appears exactly where the push would succeed.
  if (!item.is_locked) return null;

  const push = async (e) => {
    e.stopPropagation();                     // don't toggle the row open/closed
    setBusy(true); setErr("");
    try {
      const r = await authFetch(
        `${API}/api/integrations/zoho/push/${encodeURIComponent(item.item_code)}`,
        { method: "POST" }
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Push failed");
      // Only worth saying when CRM is actually switched on. With it off, Books
      // IS the destination and reporting a CRM outcome would imply a step that
      // is not part of the job.
      if (d.crm_error) setErr(`In Zoho Books. CRM did not take it — ${d.crm_error}`);
      onSynced?.(item.item_code, d);
    } catch (e2) {
      setErr(String(e2.message || e2));
    }
    setBusy(false);
  };

  const base = {
    display: "inline-flex", alignItems: "center", gap: 4, borderRadius: 6,
    padding: "2px 8px", fontSize: 11, fontWeight: 600, cursor: "pointer",
    transition: "all 0.15s",
  };

  if (busy) {
    return <span style={{ ...base, background: "#F3F4F6", color: "#6B7280", border: "1px solid #E5E7EB", cursor: "wait" }}>⏳ Pushing…</span>;
  }

  if (synced) {
    return (
      <span
        onClick={push}
        title={`In Zoho Books (id ${item.erp_item_id}) — click to push the latest values`}
        style={{ ...base, background: "#ECFDF5", color: "#047857", border: "1px solid #6EE7B7" }}
      >✓ In Zoho</span>
    );
  }

  if (configured === false) {
    return (
      <span
        title="Zoho credentials are not set in .env — see the Zoho Sync tab"
        style={{ ...base, background: "#F9FAFB", color: "#9CA3AF", border: "1px dashed #E5E7EB", cursor: "not-allowed" }}
      >Zoho not set up</span>
    );
  }

  // A refusal is shown, not hidden in a tooltip.
  //
  // The reason lived only in `title`, so a failed push read "⚠ Retry push" and
  // nothing else — and the reason was worth reading: Zoho was rejecting every
  // item in the catalogue over a custom field that does not exist in the org.
  // Pressing retry could never have worked, and the screen gave no way to know
  // that.
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6,
                   flexWrap: "wrap" }}>
      <span
        onClick={push}
        title={err || "Push this item to Zoho Books + CRM"}
        style={{
          ...base,
          background: err ? "#FEF2F2" : "#EFF6FF",
          color: err ? "#DC2626" : "#1D4ED8",
          border: `1px solid ${err ? "#FCA5A5" : "#BFDBFE"}`,
        }}
      >{err ? "⚠ Retry push" : "↗ Push to Zoho"}</span>
      {err && (
        <span style={{ fontSize: 11, color: "#B91C1C", maxWidth: 420,
                       lineHeight: 1.45 }}>
          {err}
        </span>
      )}
    </span>
  );
}
/** Where an item stands, and the one thing to do about it next.
 *
 *  The workflow was enforced on the server and invisible on screen. Approve and
 *  Reject lived on the Approvals page; Submit and Release had no button at all,
 *  so an item created in Draft could not be moved forward by anyone — the
 *  status changed only where somebody happened to have built a control for it.
 *
 *  Five stages, always all five, so the two that have not happened yet are as
 *  visible as the ones that have. The action offered is the single next step,
 *  gated three ways: the transition must be legal from here, the person must
 *  hold the right, and for approval and release the item must be complete.
 *  Where an action is withheld it says why, because "no button" and "not
 *  allowed" and "not ready" are three different situations and only one of
 *  them is the reader's to fix.
 */
const FLOW_STAGES = [
  { key: "DRAFT", label: "Draft" },
  { key: "PENDING_REVIEW", label: "Review" },
  { key: "APPROVED", label: "Approved" },
  { key: "RELEASED", label: "Released" },
  { key: "ZOHO", label: "In Zoho" },
];

/** How far along this item is: the index of the last stage it has reached. */
function flowReached(item) {
  // Retired: out of the flow altogether, so no stage reads as reached.
  if (String(item.status) === "OBSOLETE") return -1;
  if (item.erp_item_id) return 4;
  const at = FLOW_STAGES.findIndex(s => s.key === String(item.status));
  // A RELEASED item with no approver never travelled the flow — the importer
  // wrote the state. Drawing it at stage 4 would show a journey that did not
  // happen; drawing it at Draft claims it is in Draft, which is also untrue and
  // contradicts the badge beside it. So no stage is marked reached: the whole
  // strip stays grey, which is exactly what "this never went through" looks
  // like, and the note underneath says why.
  if (at >= 2 && !item.is_locked) return -1;
  return at < 0 ? 0 : at;
}

/** The next step from here: what it is, who may take it, and whether it can. */
function nextStep(item, me, q) {
  const st = String(item.status);
  if (st === "REJECTED") {
    return { to: "DRAFT", label: "Reopen", perm: "item.submit", tone: "#6B7280",
             note: "Rejected. Reopening puts it back in Draft to be fixed." };
  }
  // Retired — out of use, so there is no next step in the flow. Bringing it
  // back is a reviewer's decision with a reason (item_service.update_status).
  if (st === "OBSOLETE") {
    if (String(item.item_code || "").toUpperCase().endsWith("-OLD")) return { retired: true, renumbered: true };
    if (!HAS(me, "item.approve")) return { retired: true };
    return { retired: true, to: "DRAFT", label: "Reinstate to Draft", tone: "#B45309", needsNote: true,
             note: "Puts it back to Draft, to be submitted and reviewed again." };
  }
  // A status nobody granted. Offered a way back into the flow rather than
  // simply explained: these items were stuck — too "advanced" to be reviewed,
  // never approved, and barred from Zoho — so saying why without offering a
  // route left the catalogue uncorrectable.
  if ((st === "RELEASED" || st === "APPROVED") && !item.is_locked) {
    return { to: "DRAFT", label: "Send through the workflow", perm: "item.approve",
             tone: "#B45309",
             note: `Reads ${st} but nobody approved it. This puts it back to `
                 + `Draft so it can be submitted, reviewed and released `
                 + `properly.` };
  }
  if (st === "RELEASED") return null;  // the Zoho chip is the action from here
  const map = {
    DRAFT: { to: "PENDING_REVIEW", label: "Submit for review", perm: "item.submit",
             tone: "#1D4ED8", note: "Sends it to the Approvals queue." },
    PENDING_REVIEW: { to: "APPROVED", label: "Approve", perm: "item.approve",
             tone: "#047857", needsComplete: true,
             note: "Approving locks the item and puts your name to it." },
    APPROVED: { to: "RELEASED", label: "Release", perm: "item.release",
             tone: "#7C3AED", needsComplete: true,
             note: "Released items can be pushed to Zoho." },
  };
  const step = map[st];
  if (!step) return null;
  if (!HAS(me, step.perm)) {
    return { blocked: `Waiting on someone with the right to ${step.label.toLowerCase()}.` };
  }
  if (step.needsComplete && q && !q.ready_to_approve) {
    return { blocked: `${q.blocking} thing(s) above must be resolved first.` };
  }
  return step;
}

function ItemFlow({ item, me, q, onMoved, compact }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Approving an item you raised is permitted as a documented override
  // (KAPL-IM-USER-MATRIX-01 §2) and the reason is mandatory. This button is on
  // the item page rather than in the Approvals dialog, and it had no box: the
  // server said a reason was required and there was nowhere to type one.
  //
  // Kept as a draft, because the commonest thing that happens after typing it
  // is a refusal for a different reason — the §12 stages are still open — and
  // closing the item then lost what was typed.
  const [why, setWhy, clearWhy] = useDraft(`approve.${item.item_code}`);
  const reached = flowReached(item);
  const step = nextStep(item, me, q);
  const selfApproval = Boolean(
    item?.requested_by && me && item.requested_by === me.username);

  // Admin / Super Admin: call a signed-off item back into review (KAPL,
  // 03-Oct-2026). Every stage is asked again; the reason is required.
  const [recall, setRecall] = useState(null);
  const canRecall = ["APPROVED", "RELEASED"].includes(String(item.status))
    && ["ADMIN", "SUPER_ADMIN"].includes(String(me?.role || ""));
  const doRecall = async () => {
    if (!recall?.trim()) { setErr("Say why this item is going back for review."); return; }
    setBusy(true); setErr("");
    try {
      const r = await authFetch(`${API}/api/items/${item.item_code}/status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "PENDING_REVIEW", performed_by: me.username, notes: recall.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not recall it for review.");
      setRecall(null);
      onMoved?.(d);
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const move = async (to) => {
    if (step?.needsNote && !why.trim()) {
      setErr("Say why this retired item is needed again — it is recorded against your name.");
      return;
    }
    const needsWhy = selfApproval && to === "APPROVED";
    if (needsWhy && !why.trim()) {
      setErr("You raised this item, so approving it yourself is a documented "
             + "override — say why, and it is recorded against your name.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const r = await authFetch(`${API}/api/items/${item.item_code}/status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: to, performed_by: me.username,
                               override_reason: needsWhy ? why.trim() : undefined,
                               notes: step?.needsNote ? why.trim() : undefined }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not change the status");
      clearWhy();
      onMoved?.(d);
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const dot = (i) => {
    const done = i < reached, here = i === reached;
    return {
      width: here ? 11 : 8, height: here ? 11 : 8, borderRadius: 999,
      background: done ? "#86EFAC" : here ? KZ.blue : "#E5E7EB",
      border: here ? `2px solid ${KZ.blue}` : "none", flexShrink: 0,
    };
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 0,
                    flexWrap: "wrap", rowGap: 6 }}>
        {FLOW_STAGES.map((s, i) => (
          <Fragment key={s.key}>
            {i > 0 && (
              <span style={{ width: compact ? 14 : 26, height: 2, flexShrink: 0,
                             background: i <= reached ? "#86EFAC" : "#E5E7EB" }} />
            )}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={dot(i)} />
              <span style={{ fontSize: compact ? 10.5 : 12,
                             fontWeight: i === reached ? 700 : 500,
                             color: i === reached ? "#111827"
                                  : i < reached ? "#047857" : "#9CA3AF" }}>
                {s.label}
              </span>
            </span>
          </Fragment>
        ))}
      </div>

      {step?.retired && !compact && (
        <div style={{ marginTop: 10, background: "#F3F4F6", border: "1px solid #D1D5DB",
                      borderRadius: 8, padding: "9px 11px", fontSize: 12.5, color: "#374151" }}>
          <b>Retired (obsolete).</b> This item is out of use, so it is not submitted for review.
          {step.renumbered
            ? " Its number was given to another item when its master was replaced — raise a new item if it is needed."
            : step.to ? " If it is still needed, reinstate it below with the reason."
            : " An approver can reinstate it if it is still needed."}
          {step.to && (
            <textarea value={why} onChange={e => setWhy(e.target.value)} rows={2}
              placeholder="Why is this item needed again? e.g. still bought for site X; not covered by A02-008 Rev1"
              style={{ ...ES.input, marginTop: 6, marginBottom: 0, resize: "vertical" }} />
          )}
        </div>
      )}
      {/* The box for the reason, beside the button that needs it. */}
      {step && !compact && !step.blocked && selfApproval
        && step.to === "APPROVED" && (
        <div style={{ marginTop: 10, background: "#FFFBEB",
                      border: "1px solid #FDE68A", borderRadius: 8,
                      padding: "9px 11px" }}>
          <div style={{ fontSize: 12, color: "#92400E", marginBottom: 6 }}>
            You raised this item. Approving it yourself is permitted as a
            <b> documented override</b> — KAPL-IM-USER-MATRIX-01 §2 — so the
            reason is required and is recorded against your name, permanently.
          </div>
          <textarea value={why} onChange={e => setWhy(e.target.value)} rows={2}
            placeholder="e.g. sole Item Master Approver; Engineering, SCM and Finance have signed"
            style={{ ...ES.input, marginBottom: 0, resize: "vertical" }} />
        </div>
      )}
      {step && !compact && (step.to || step.blocked) && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center",
                      gap: 10, flexWrap: "wrap" }}>
          {step.blocked ? (
            <span style={{ fontSize: 12, color: "#9CA3AF" }}>{step.blocked}</span>
          ) : (
            <>
              <Btn small color={step.tone} disabled={busy}
                onClick={() => move(step.to)}>
                {busy ? "Working…" : step.label}
              </Btn>
              <span style={{ fontSize: 12, color: "#9CA3AF" }}>{step.note}</span>
            </>
          )}
        </div>
      )}
      {canRecall && !compact && (
        recall === null ? (
          <button onClick={() => setRecall("")}
            style={{ marginTop: 8, border: "none", background: "none", padding: 0, fontSize: 12,
                     color: "#B45309", cursor: "pointer" }}>Recall for review…</button>
        ) : (
          <div style={{ marginTop: 10, background: "#FFFBEB", border: "1px solid #FDE68A",
                        borderRadius: 8, padding: "9px 11px" }}>
            <div style={{ fontSize: 12, color: "#92400E", marginBottom: 6 }}>
              The item goes back to <b>Pending review</b> and is unlocked, and its earlier sign-offs
              are withdrawn. Correct it (the name, for one), then use <b>Send for sign-off</b> so
              Engineering, SCM and Finance sign it again before it is approved and released. Its
              Zoho record stays linked.
            </div>
            <textarea value={recall} onChange={e => setRecall(e.target.value)} rows={2}
              placeholder="Why is it going back? e.g. wrong radius in the name"
              style={{ ...ES.input, marginBottom: 6, resize: "vertical" }} />
            <div style={{ display: "flex", gap: 8 }}>
              <Btn small color="#B45309" disabled={busy} onClick={doRecall}>{busy ? "Working…" : "Recall for review"}</Btn>
              <Btn small outline color="#6B7280" onClick={() => setRecall(null)}>Cancel</Btn>
            </div>
          </div>
        )
      )}
      {err && <div style={{ marginTop: 8, fontSize: 12.5, color: "#B91C1C" }}>{err}</div>}
      {/* The one blocker you can clear from here.
          SOP-A12 §9 blocks release without a BOM revision, and by then the
          item is approved and therefore locked — so A12-002.001 sat approved,
          un-releasable, with no field anywhere in the product able to give it
          a revision. The way out was the database. It is a box now. */}
      {err && /bom revision/i.test(err) && (
        <BomRevisionFix item={item} me={me} onSaved={onMoved} />
      )}
    </div>
  );
}

/** Give an approved composite the revision §9 needs, without unapproving it. */
function BomRevisionFix({ item, me, onSaved }) {
  const [rev, setRev] = useState(item.bom_revision || "");
  const [ref, setRef] = useState(item.bom_reference || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const save = async () => {
    setBusy(true); setErr(""); setOk(false);
    try {
      const r = await authFetch(`${API}/api/items/${item.item_code}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bom_revision: rev.trim(),
                               bom_reference: ref.trim() || null }),
      });
      await jsonOrThrow(r, "Could not set the revision");
      setOk(true);
      onSaved && onSaved();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  // "From where will this information be fetched?" — the revision is a
  // sequence the engineer owns and the citation is a real document the
  // platform already holds. Loaded on demand, like the attribute help.
  const [help, setHelp] = useState(null);
  const [helpBusy, setHelpBusy] = useState(false);
  const loadHelp = async () => {
    if (help) { setHelp(h => ({ ...h, _open: !h._open })); return; }
    setHelpBusy(true);
    try {
      const r = await authFetch(`${API}/api/items/${item.item_code}/bom-guidance`);
      const j = await r.json();
      setHelp({ ...j, _open: true });
      if (j.suggested_revision && !rev) setRev(j.suggested_revision);
    } catch { /* the box still works without it */ }
    setHelpBusy(false);
  };

  return (
    <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8,
                  background: "#FFFBEB", border: "1px solid #FDE68A" }}>
      <div style={{ fontSize: 12.5, color: "#92400E", marginBottom: 8 }}>
        SOP-A12 §9 wants a revision and what the bill cites. Setting them here
        does not re-open the approval — the bill is unchanged; this records
        which revision it is.
        <button onClick={loadHelp} type="button"
          style={{ border: "1px solid #DBEAFE", background: "#EFF6FF",
                   color: "#1D4ED8", borderRadius: 6, fontSize: 11,
                   fontWeight: 600, padding: "2px 8px", cursor: "pointer",
                   marginLeft: 8, whiteSpace: "nowrap" }}>
          {helpBusy ? "Reading the SOP…"
            : help?._open ? "Hide help" : "Where do I get these?"}
        </button>
      </div>
      {help?._open && (
        <div style={{ marginBottom: 10, padding: "9px 11px", borderRadius: 7,
                      background: "#F8FAFF", border: "1px solid #DBEAFE",
                      fontSize: 12.5, lineHeight: 1.55, color: "#374151" }}>
          {help.available === false && <div>{help.reason}</div>}
          {help.ai?.what_it_means && <div style={{ marginBottom: 6 }}>{help.ai.what_it_means}</div>}
          {help.ai?.revision_why && (
            <div style={{ marginBottom: 6 }}>
              <b>Revision {help.ai.revision_suggestion}</b> — {help.ai.revision_why}
            </div>
          )}
          {help.ai?.reference_guidance && (
            <div style={{ marginBottom: 6 }}><b>The citation:</b> {help.ai.reference_guidance}</div>
          )}
          {help.ai?.sop_says && (
            <div style={{ borderLeft: "3px solid #BFDBFE", paddingLeft: 9,
                          margin: "7px 0", color: "#4B5563", fontStyle: "italic" }}>
              “{help.ai.sop_says}”
              {(help.sop_files || []).length > 0 && (
                <div style={{ fontStyle: "normal", fontSize: 11, color: "#9CA3AF",
                              marginTop: 2 }}>— {help.sop_files.join(", ")}</div>
              )}
            </div>
          )}
          {/* The real sales orders, clickable. The platform can offer the list
              and refuses to pick from it — no order line references this item
              or its parts, and choosing one would invent the traceability
              record §9 exists to create. */}
          {(help.sales_orders || []).length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3,
                            color: "#6B7280", marginBottom: 4 }}>
                SALES ORDERS IN BOOKS — {help.sales_order_count} of them, newest first
              </div>
              <div style={{ maxHeight: 150, overflowY: "auto", display: "grid", gap: 3 }}>
                {help.sales_orders.map(o => (
                  <div key={o.number} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                    <button type="button" onClick={() => setRef(o.number)}
                      style={{ border: "1px solid #C7D2FE", background: "#fff",
                               color: "#1D4ED8", borderRadius: 5, fontSize: 11.5,
                               padding: "1px 7px", cursor: "pointer",
                               fontFamily: "monospace", flexShrink: 0 }}>
                      {o.number}
                    </button>
                    <span style={{ color: "#6B7280", fontSize: 11.5 }}>
                      {o.customer}{o.date ? ` · ${o.date}` : ""}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 10.5, color: "#B45309", marginTop: 5 }}>
                {help.why_not}
              </div>
            </div>
          )}
          {help.ai?.note && (
            <div style={{ marginTop: 7, color: "#B45309" }}>{help.ai.note}</div>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
        <input value={rev} onChange={e => setRev(e.target.value)}
          placeholder="Revision — e.g. R0"
          style={{ ...ES.input, width: 150, marginBottom: 0 }} />
        <input value={ref} onChange={e => setRef(e.target.value)}
          placeholder="Cites — e.g. SO-2291 or DRG-114 R2"
          style={{ ...ES.input, width: 250, marginBottom: 0, flex: "1 1 auto" }} />
        <Btn small disabled={busy || !rev.trim()} onClick={save}>
          {busy ? "Saving…" : "Set revision"}
        </Btn>
      </div>
      {err && <div style={{ fontSize: 12, color: "#B91C1C", marginTop: 6 }}>{err}</div>}
      {ok && <div style={{ fontSize: 12, color: "#047857", marginTop: 6 }}>
        Saved. Try Release again.
      </div>}
    </div>
  );
}


function MarkdownMessage({ content }) {
  return <ReactMarkdown components={{
    h1: ({ node, children, ...p }) => <h1 style={{ fontSize: 15, fontWeight: 700, margin: "12px 0 6px" }} {...p}>{children}</h1>,
    h2: ({ node, children, ...p }) => <h2 style={{ fontSize: 14, fontWeight: 700, margin: "10px 0 4px" }} {...p}>{children}</h2>,
    h3: ({ node, children, ...p }) => <h3 style={{ fontSize: 13, fontWeight: 700, margin: "8px 0 4px" }} {...p}>{children}</h3>,
    strong: ({ node, ...p }) => <strong style={{ fontWeight: 700, color: "#111827" }} {...p} />,
    p: ({ node, ...p }) => <p style={{ margin: "4px 0", fontSize: 13, lineHeight: 1.6, color: "#1F2937" }} {...p} />,
    ul: ({ node, ...p }) => <ul style={{ margin: "6px 0", paddingLeft: 20, listStyleType: "disc" }} {...p} />,
    ol: ({ node, ...p }) => <ol style={{ margin: "6px 0", paddingLeft: 20, listStyleType: "decimal" }} {...p} />,
    li: ({ node, ...p }) => <li style={{ margin: "3px 0", fontSize: 13, lineHeight: 1.5, color: "#1F2937" }} {...p} />,
    code: ({ node, inline, ...p }) => inline
      ? <code style={{ background: "#F3F4F6", border: "1px solid #E5E7EB", borderRadius: 4, padding: "1px 5px", fontSize: 12, fontFamily: "monospace", color: "#1D4ED8" }} {...p} />
      : <pre style={{ background: "#1F2937", borderRadius: 8, padding: "10px 14px", overflowX: "auto", margin: "8px 0" }}><code style={{ fontSize: 12, fontFamily: "monospace", color: "#D1FAE5" }} {...p} /></pre>,
    blockquote: ({ node, ...p }) => <blockquote style={{ borderLeft: "3px solid #3B82F6", paddingLeft: 10, margin: "6px 0", color: "#6B7280", fontStyle: "italic", fontSize: 13 }} {...p} />,
    table: ({ node, ...p }) => <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, margin: "8px 0" }} {...p} />,
    th: ({ node, ...p }) => <th style={{ background: "#F3F4F6", padding: "5px 10px", border: "1px solid #E5E7EB", fontWeight: 700}} {...p} />,
    td: ({ node, ...p }) => <td style={{ padding: "5px 10px", border: "1px solid #E5E7EB" }} {...p} />,
  }}>{content}</ReactMarkdown>;
}


// ── Dashboard ─────────────────────────────────────────────────────────────────
const KZ = { blue: "#1F4E79", green: "#10B981", lightGreen: "#84CC16", orange: "#F97316", yellow: "#F59E0B", charcoal: "#374151", red: "#EF4444" };
const inr0 = (n) => Number(n || 0).toLocaleString("en-IN");

// The little grey image box Books puts at the head of every associated-item
// row. There is no item photography in this master, so it is a placeholder
// glyph rather than a broken <img>.
const THUMB = { width: 40, height: 40, flexShrink: 0, borderRadius: 4,
                background: "#F3F4F6", border: "1px solid #E5E7EB",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#C4C9D1", fontSize: 15 };

const ADDLINK = { border: "none", background: "transparent", color: KZ.blue,
                  cursor: "pointer", font: "inherit", fontSize: 12.5,
                  fontWeight: 600, padding: 0 };

// ── Category / subcategory management ─────────────────────────
// Structural edits to the master, so Super Admin only and additive-by-refusal:
// the server declines to delete anything that still has items (archived ones
// included) rather than cascading, and warns when a code the controlled Q&A
// specification defines is about to disappear from the app.

/** Which issued subcategory's questions a new one uses, and its own labels.
 *
 *  A subcategory is only offered on Create Items once the item-creation
 *  specification has questions for it, and a code added here had none — so
 *  A02-011 existed and could not be chosen. It borrows the questions of an
 *  issued subcategory (a sibling by default), with the attribute labels
 *  editable, and is marked as added in the app rather than issued by KAPL.
 */
const LABEL_KEYS = ["P3", "P4", "P5A", "P5B", "P6", "P7A", "P7B", "P7C"];

function QuestionSource({ categoryCode, suggested, value, onChange }) {
  const [subs, setSubs] = useState(null);
  useEffect(() => {
    authFetch(`${API}/api/qa-spec/subcategories?status=ACTIVE`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setSubs((d && d.subcategories) || []))
      .catch(() => setSubs([]));
  }, []);
  // Default: the suggestion, else the last active subcategory in this category.
  useEffect(() => {
    if (!subs || value.copy_from) return;
    const pick = (suggested && subs.find(x => x.subcategory_code === suggested))
      || [...subs].reverse().find(x => x.category_code === categoryCode);
    if (pick) onChange({ copy_from: pick.subcategory_code, labels: { ...pick.labels } });
  }, [subs]);            // eslint-disable-line react-hooks/exhaustive-deps

  if (!subs) return <div style={{ fontSize: 12, color: "#9CA3AF" }}>Loading questions…</div>;
  if (!subs.length) return <div style={{ fontSize: 12, color: "#B45309" }}>
    No item-creation specification is loaded, so there are no questions to copy.</div>;
  const src = subs.find(x => x.subcategory_code === value.copy_from);
  const setLabel = (k, v) => onChange({ ...value, labels: { ...value.labels, [k]: v } });
  return (
    <div>
      <EngField label="Questions for creating items — copy from">
        <select value={value.copy_from || ""} style={ES.input}
          onChange={e => {
            const x = subs.find(y => y.subcategory_code === e.target.value);
            onChange({ copy_from: e.target.value, labels: { ...(x ? x.labels : {}) } });
          }}>
          <option value="" disabled>Choose a subcategory…</option>
          {subs.map(x => (
            <option key={x.subcategory_code} value={x.subcategory_code}>
              {x.subcategory_code} · {x.subcategory_name}
              {x.category_code === categoryCode ? "" : ` (${x.category_name})`}
            </option>
          ))}
        </select>
      </EngField>
      {src && (
        <div style={{ fontSize: 11.5, color: "#6B7280", margin: "-4px 0 8px" }}>
          The attribute questions below are what the item form will ask. Rename
          any that do not fit — the rest of {src.subcategory_code}'s rules
          (what is required, where each answer is kept) are copied as issued.
        </div>
      )}
      {src && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                      gap: "6px 10px", marginBottom: 8 }}>
          {LABEL_KEYS.filter(k => (src.labels || {})[k]).map(k => (
            <label key={k} style={{ fontSize: 11, color: "#6B7280" }}>
              {k}
              <input value={(value.labels || {})[k] || ""} onChange={e => setLabel(k, e.target.value)}
                style={{ ...ES.input, marginTop: 2, fontSize: 12.5, padding: "6px 8px" }} />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/** Only the labels actually renamed, so the server keeps the rest as issued. */
function changedLabels(copyFrom, labels, subsLabels) {
  const out = {};
  for (const k of LABEL_KEYS) {
    const v = (labels || {})[k];
    if (v && v.trim() && v.trim() !== ((subsLabels || {})[k] || "")) out[k] = v.trim();
  }
  return out;
}

function AddNodeModal({ kind, categoryCode, onClose, onDone }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState({ copy_from: "", labels: {} });
  const [srcLabels, setSrcLabels] = useState({});
  const isCat = kind === "category";

  // The source's own labels, to send only the ones renamed.
  useEffect(() => {
    if (!q.copy_from) return;
    authFetch(`${API}/api/qa-spec/subcategories?status=ACTIVE`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const x = ((d && d.subcategories) || []).find(y => y.subcategory_code === q.copy_from);
        setSrcLabels(x ? x.labels : {});
      }).catch(() => { });
  }, [q.copy_from]);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const url = isCat
        ? `${API}/api/categories/`
        : `${API}/api/categories/${encodeURIComponent(categoryCode)}/subcategories`;
      const body = { code: code.trim(), name: name.trim() };
      if (!isCat && q.copy_from) {
        body.copy_questions_from = q.copy_from;
        body.labels = changedLabels(q.copy_from, q.labels, srcLabels);
      }
      const r = await authFetch(url, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      await jsonOrThrow(r, "Could not add it");
      onDone();
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };

  return (
    <EngModal title={isCat ? "Add category" : `Add subcategory to ${categoryCode}`}
              onClose={onClose} maxWidth={isCat ? 460 : 620}>
      {err && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C",
                            borderRadius: 8, padding: "9px 12px", fontSize: 12.5, marginBottom: 12 }}>{err}</div>}
      <EngField label={isCat ? "Code" : "Subcategory code"}>
        <input value={code} onChange={e => setCode(e.target.value)} style={ES.input}
               placeholder={isCat ? "A13" : "005"} />
      </EngField>
      <EngField label="Name">
        <input value={name} onChange={e => setName(e.target.value)} style={ES.input}
               placeholder={isCat ? "GASKETS & SEALS" : "O-RING"} />
      </EngField>
      {!isCat && (
        <QuestionSource categoryCode={categoryCode} value={q} onChange={setQ} />
      )}
      <div style={{ fontSize: 11.5, color: "#9CA3AF", margin: "4px 0 14px", lineHeight: 1.6 }}>
        {isCat
          ? "Item codes are built from this, so the code cannot be changed afterwards. "
            + "Add a subcategory to it before creating items here."
          : `Items here will be coded ${categoryCode}-${code.trim() || "005"}.001 onwards, `
            + "and it can be chosen on Create Items as soon as it is added. It is marked "
            + "as added in the app, not issued in KAPL's specification workbook."}
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn outline color="#6B7280" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={busy || !code.trim() || !name.trim()
                                      || (!isCat && !q.copy_from)}>
          {busy ? "Adding…" : "Add"}
        </Btn>
      </div>
    </EngModal>
  );
}

/** Give an existing subcategory the questions it needs to be chosen on
 *  Create Items — for ones added before this was part of adding (A02-011). */
function SetupQuestionsModal({ code, name, suggested, onClose, onDone }) {
  const [q, setQ] = useState({ copy_from: "", labels: {} });
  const [srcLabels, setSrcLabels] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!q.copy_from) return;
    authFetch(`${API}/api/qa-spec/subcategories?status=ACTIVE`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const x = ((d && d.subcategories) || []).find(y => y.subcategory_code === q.copy_from);
        setSrcLabels(x ? x.labels : {});
      }).catch(() => { });
  }, [q.copy_from]);
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const r = await authFetch(`${API}/api/qa-spec/subcategories/local`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subcategory_code: code, copy_from: q.copy_from,
                               labels: changedLabels(q.copy_from, q.labels, srcLabels) }),
      });
      await jsonOrThrow(r, "Could not set it up");
      onDone();
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };
  return (
    <EngModal title={`Set up ${code} ${name} for creating items`} onClose={onClose} maxWidth={620}>
      {err && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C",
                            borderRadius: 8, padding: "9px 12px", fontSize: 12.5, marginBottom: 12 }}>{err}</div>}
      <div style={{ fontSize: 12.5, color: "#374151", marginBottom: 10 }}>
        {code} is in the category list but has no item-creation questions, so it
        cannot be chosen on Create Items yet.
      </div>
      <QuestionSource categoryCode={code.split("-")[0]} suggested={suggested}
                      value={q} onChange={setQ} />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
        <Btn outline color="#6B7280" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={busy || !q.copy_from}>
          {busy ? "Setting up…" : "Set up"}
        </Btn>
      </div>
    </EngModal>
  );
}

/** Delete a category or subcategory completely — Super Admin.
 *
 *  KAPL, 03-Oct-2026: "the category and subcategory deletion is not seamlessly
 *  working. It should delete it completely." One window, built from the
 *  server's preview: everything that will go (items active and archived, their
 *  documents, the item-creation questions), anything that blocks it with the
 *  reason, and one confirmation. Items still in use elsewhere (Zoho, POs, BOQ,
 *  layouts, other BOMs) block the delete and are listed — "Delete them too,
 *  with guard". Moving the items to another subcategory first stays possible.
 */
function DeleteNodeButton({ kind, categoryCode, subCode, name, dests, onDone }) {
  const [open, setOpen] = useState(false);
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [blockers, setBlockers] = useState(null);
  const [mode, setMode] = useState("delete");     // delete | move
  const [dest, setDest] = useState("");
  const [ack, setAck] = useState(false);
  const [ackZoho, setAckZoho] = useState(false);
  const label = kind === "category" ? categoryCode : `${categoryCode}-${subCode}`;
  const base = kind === "category"
    ? `${API}/api/categories/${encodeURIComponent(categoryCode)}`
    : `${API}/api/categories/${encodeURIComponent(categoryCode)}/subcategories/${encodeURIComponent(subCode)}`;
  const here = kind === "category" ? cd => cd.cat === categoryCode
                                   : cd => cd.value === `${categoryCode}-${subCode}`;
  const options = (dests || []).filter(o => !here(o));

  const start = async () => {
    setOpen(true); setPv(null); setErr(""); setBlockers(null); setMode("delete");
    setDest(""); setAck(false); setAckZoho(false);
    try {
      const r = await authFetch(`${base}/delete-preview`);
      const d = await jsonOrThrow(r, "Could not read what would be deleted");
      setPv(d); setBlockers(d.blockers?.length ? d.blockers : null);
    } catch (e) { setErr(String(e.message || e)); }
  };

  const go = async () => {
    setBusy(true); setErr("");
    try {
      const qs = new URLSearchParams();
      if (ack) qs.set("acknowledge_spec", "true");
      if (ackZoho) qs.set("acknowledge_zoho", "true");
      if (mode === "move" && dest) qs.set("move_items_to", dest);
      const r = await authFetch(base + (qs.toString() ? `?${qs}` : ""), { method: "DELETE" });
      if (r.status === 409) {
        const j = await r.json().catch(() => ({}));
        const d = j.detail || {};
        if (d.blockers) setBlockers(d.blockers);
        setErr(typeof d === "string" ? d : (d.message || "Cannot delete this."));
        return;
      }
      await jsonOrThrow(r, "Delete failed");
      setOpen(false);
      onDone();
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };

  const needsAck = (pv?.in_specification || []).length > 0;
  const hasItems = (pv?.items || 0) > 0;
  const moving = mode === "move" && hasItems;
  // Held only by Zoho Books — the Super Admin may delete them from the master
  // after saying so; anything used by a BOQ, a plan or a BOM still blocks.
  const zohoOnly = !!blockers && blockers.every(b => b.zoho_only);
  const blocked = !!blockers && !moving && !(zohoOnly && ackZoho);
  const ready = pv && !blocked && (!needsAck || ack) && (!moving || dest);

  return (
    <>
      <button onClick={e => { e.stopPropagation(); start(); }} title={`Delete ${label}`}
              style={{ border: "none", background: "transparent", color: "#9CA3AF",
                       fontSize: 12, cursor: "pointer", padding: "0 4px" }}>✕</button>
      {open && (
        <span onClick={e => e.stopPropagation()}>
          <EngModal title={`Delete ${label} ${name || ""}`} onClose={() => setOpen(false)} maxWidth={620}>
            {!pv && !err && <div style={{ fontSize: 13, color: "#6B7280" }}>Checking what this would remove…</div>}
            {pv && (
              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
                <div>This permanently removes:</div>
                <ul style={{ margin: "6px 0 10px 18px", padding: 0 }}>
                  {kind === "category" && <li>{pv.subcategories.length} subcategor{pv.subcategories.length === 1 ? "y" : "ies"}</li>}
                  <li>{pv.items} item{pv.items === 1 ? "" : "s"} ({pv.items_active} active, {pv.items_archived} archived)
                    {hasItems && mode === "move" ? " — moved instead, see below" : ""}</li>
                  <li>{pv.documents} attached document{pv.documents === 1 ? "" : "s"}</li>
                  <li>the item-creation questions for {pv.subcategories.join(", ") || label}</li>
                </ul>
                <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 10 }}>
                  The audit trail is kept, with one record listing every code deleted.
                </div>
                {hasItems && (
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ display: "block", fontSize: 12.5 }}>
                      <input type="radio" checked={mode === "delete"} onChange={() => setMode("delete")} /> Delete the items too
                    </label>
                    <label style={{ display: "block", fontSize: 12.5 }}>
                      <input type="radio" checked={mode === "move"} onChange={() => setMode("move")} /> Move the items to another subcategory first
                    </label>
                    {mode === "move" && (
                      <select value={dest} onChange={e => setDest(e.target.value)}
                        style={{ ...ES.input, marginTop: 6 }}>
                        <option value="">choose a subcategory…</option>
                        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    )}
                  </div>
                )}
                {blockers && mode === "delete" && (
                  <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8,
                                padding: "8px 11px", marginBottom: 10 }}>
                    <div style={{ fontWeight: 600, color: "#B91C1C", marginBottom: 4 }}>
                      {blockers.length} item{blockers.length === 1 ? " is" : "s are"} still in use
                      {zohoOnly ? " — by Zoho Books only:" : " — nothing will be deleted until they are moved or freed:"}
                    </div>
                    <div style={{ maxHeight: 180, overflowY: "auto", fontSize: 12 }}>
                      {blockers.map(b => (
                        <div key={b.item_code}><b>{b.item_code}</b> {b.name} — {b.reasons.join("; ")}</div>
                      ))}
                    </div>
                  </div>
                )}
                {blockers && mode === "delete" && zohoOnly && (
                  <label style={{ display: "block", fontSize: 12.5, color: "#92400E", marginBottom: 10 }}>
                    <input type="checkbox" checked={ackZoho} onChange={e => setAckZoho(e.target.checked)} />{" "}
                    Delete them from the item master only. Their Zoho Books records, purchase orders and
                    bills stay in Zoho Books; nothing is changed in Books, and the deleted codes are
                    listed in the audit record.
                  </label>
                )}
                {blockers && mode === "delete" && !zohoOnly && (
                  <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 10 }}>
                    Items used by a BOQ element, a layout plan, the luminaire library or another item's BOM
                    have to be moved first — choose "Move the items to another subcategory".
                  </div>
                )}
                {needsAck && (
                  <label style={{ display: "block", fontSize: 12.5, color: "#92400E", marginBottom: 10 }}>
                    <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />{" "}
                    {pv.in_specification.join(", ")} {pv.in_specification.length === 1 ? "is" : "are"} defined
                    in KAPL's loaded item-creation specification. I understand its questions are removed too.
                  </label>
                )}
              </div>
            )}
            {err && <div style={{ color: "#B91C1C", fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn outline color="#6B7280" onClick={() => setOpen(false)}>Cancel</Btn>
              <Btn color="#DC2626" onClick={go} disabled={busy || !ready}>
                {busy ? "Deleting…" : moving ? "Move items & delete" : "Delete completely"}
              </Btn>
            </div>
          </EngModal>
        </span>
      )}
    </>
  );
}
function Dashboard({ me }) {
  const [d, setD] = useState(null);
  const [selCat, setSelCat] = useState(null);
  const [selSub, setSelSub] = useState(null);          // { cat, sub, name }
  const [subData, setSubData] = useState(null);
  const [subSkip, setSubSkip] = useState(0);
  const subPer = 25;
  const [detail, setDetail] = useState(null);
  const [qDraft, setQDraft] = useState("");
  const [q, setQ] = useState("");
  const [sres, setSres] = useState(null);
  // Which of the matched codes Zoho Books had already used for something else.
  const [sretired, setSretired] = useState({});
  const [spage, setSpage] = useState(0);
  const sper = 25;
  const [adding, setAdding] = useState(null);   // {kind, categoryCode}
  // Subcategories with no item-creation questions: in the list, but not
  // offered on Create Items until set up. {"A02-011": {suggested_copy_from}}
  const [unconf, setUnconf] = useState({});
  const [settingUp, setSettingUp] = useState(null);
  const [tick, setTick] = useState(0);
  // Clearing out a run of empty codes one confirmation at a time is the wrong
  // shape of work, so the rows carry a tick box and delete as a batch.
  const [bulkCats, setBulkCats] = useState(() => new Set());   // category codes
  const [bulkSubs, setBulkSubs] = useState(() => new Set());   // "CAT|SUB"
  const [bulkAsk, setBulkAsk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkErr, setBulkErr] = useState("");
  const [bulkSpec, setBulkSpec] = useState(null);   // nodes the specification guards
  const [bulkDest, setBulkDest] = useState("");    // where a batch's items are re-homed
  useEffect(() => { authFetch(`${API}/api/items/dashboard`).then(r => r.json()).then(setD).catch(() => { }); }, [tick]);
  // Counts, the category drill-down and search results follow changes made
  // anywhere — an approval moves an item between the columns counted here.
  useLive([...ITEM_TABLES, "categories", "subcategories", "eng_qa_subcategory"],
          () => setTick(t => t + 1), { delay: 1500 });
  useEffect(() => {
    authFetch(`${API}/api/qa-spec/subcategories/unconfigured`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setUnconf(Object.fromEntries(((d && d.subcategories) || [])
        .map(x => [x.subcategory_code, x]))))
      .catch(() => { });
  }, [tick]);
  // How items are ARRIVING, which the dashboard's own counts cannot show: an
  // imported item and a created one both sit at PENDING_REVIEW, and only
  // `origin` tells them apart. Of 14,668 items, 15 were made in the app.
  const [made, setMade] = useState(null);
  const [madeOpen, setMadeOpen] = useState(false);
  // The window the modal is looking at. A preset until somebody types dates,
  // and then whatever they typed — the chart and the listing follow the same
  // one, because a chart showing a week above a table showing a month is two
  // answers to one question.
  const [madeDays, setMadeDays] = useState(30);
  const [madeFrom, setMadeFrom] = useState("");
  const [madeTo, setMadeTo] = useState("");
  // Which day's items are being read. The chart answers "when did they
  // arrive"; this answers "which ones", and it is a different question asked
  // by clicking a bar. Fetched for that day alone rather than filtered out of
  // the window, so a busy day is not cut off by the window's own cap.
  const [pickDay, setPickDay] = useState(null);
  const [dayItems, setDayItems] = useState(null);
  const [dayPage, setDayPage] = useState(0);
  const DAY_PER_PAGE = 50;
  useEffect(() => { setDayPage(0); }, [pickDay]);
  useEffect(() => {
    if (!pickDay) { setDayItems(null); return; }
    let alive = true;
    setDayItems("loading");
    // A page at a time. 1 September brought 957 items; asking for all of them
    // to render a list nobody scrolls to the end of is a long wait for a long
    // page.
    const p = new URLSearchParams({ start: pickDay, end: pickDay,
                                    limit: String(DAY_PER_PAGE),
                                    offset: String(dayPage * DAY_PER_PAGE) });
    authFetch(`${API}/api/items/creation-stats?${p}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive) setDayItems(d || { recent: [] }); })
      .catch(() => { if (alive) setDayItems({ recent: [] }); });
    return () => { alive = false; };
  }, [pickDay, dayPage]);
  useEffect(() => {
    const p = new URLSearchParams({ limit: "200" });
    if (madeFrom || madeTo) {
      if (madeFrom) p.set("start", madeFrom);
      if (madeTo) p.set("end", madeTo);
    } else {
      p.set("days", String(madeDays));
    }
    authFetch(`${API}/api/items/creation-stats?${p}`)
      .then(r => (r.ok ? r.json() : null)).then(setMade).catch(() => { });
  }, [tick, madeDays, madeFrom, madeTo]);
  useEffect(() => {
    if (!selSub) { setSubData(null); return; }
    authFetch(`${API}/api/items/by-subcategory?category=${selSub.cat}&subcategory=${selSub.sub}&skip=${subSkip}&limit=${subPer}`)
      .then(r => r.json()).then(setSubData).catch(() => setSubData({ items: [], total: 0 }));
  }, [selSub, subSkip, tick]);
  // Separate boxes for the attributes people search by (KAPL, 03-Oct-2026),
  // beside the free-text box. Each narrows the same search.
  const [fDraft, setFDraft] = useState({ category: "", subcategory: "", material: "", size: "", make: "", status: "" });
  const [f, setF] = useState(fDraft);
  useEffect(() => { const t = setTimeout(() => { setQ(qDraft.trim()); setF(fDraft); setSpage(0); }, 350); return () => clearTimeout(t); }, [qDraft, fDraft]);
  const filtering = Object.values(f).some(v => String(v).trim());
  // Words match item names first; this widens them to attributes and
  // descriptions on request.
  const [broad, setBroad] = useState(false);
  useEffect(() => { setBroad(false); }, [q]);
  useEffect(() => {
    if (!q && !filtering) { setSres(null); return; }
    const p = new URLSearchParams({ skip: String(spage * sper), limit: String(sper) });
    if (q) p.set("q", q);
    if (broad) p.set("broad", "true");
    Object.entries(f).forEach(([k, v]) => { if (String(v).trim()) p.set(k, String(v).trim()); });
    authFetch(`${API}/api/items/list?${p}`).then(r => r.json()).then(setSres).catch(() => setSres({ items: [], total: 0 }));
  }, [q, f, filtering, broad, spage, tick]);

  // A found item can still have a history worth knowing: 266 codes were taken
  // from a Zoho record that had to be renamed to release the number. The row
  // showed nothing, so the collision was invisible exactly where somebody is
  // deciding what the code means.
  useEffect(() => {
    const codes = (sres?.items || []).map(i => i.item_code).filter(Boolean);
    if (!codes.length) { setSretired({}); return; }
    let alive = true;
    authFetch(`${API}/api/items/retired-codes`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ item_codes: codes }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive) setSretired(d?.retired || {}); })
      .catch(() => { });
    return () => { alive = false; };
  }, [sres]);

  // A deleted row leaves the screen on the answer from its own request rather
  // than on the refetch behind it, so the effect is visible at the moment the
  // work is done. The refetch still runs and is what the numbers settle on.
  const dropNodes = (catCodes, subKeys) => setD(prev => {
    if (!prev) return prev;
    const by = prev.by_category
      .filter(c => !catCodes.has(c.code))
      .map(c => ({ ...c, subcategories: (c.subcategories || []).filter(sc => !subKeys.has(`${c.code}|${sc.code}`)) }));
    // active_categories counts the ones holding items, not the rows.
    return { ...prev, by_category: by,
             active_categories: by.filter(c => c.items > 0).length,
             total_categories: by.length,
             subcategories: by.reduce((n, c) => n + (c.subcategories || []).length, 0) };
  });

  const clearBulk = () => { setBulkCats(new Set()); setBulkSubs(new Set()); setBulkAsk(false); setBulkErr(""); setBulkSpec(null); setBulkDest(""); };
  const bulkCount = bulkCats.size + bulkSubs.size;

  // Every subcategory in the master is a possible home. The list is built from
  // the same payload the chart draws, so it can never offer a code that is not
  // there.
  const allDests = (d?.by_category || []).flatMap(c => (c.subcategories || []).map(sc => ({
    value: `${c.code}-${sc.code}`, cat: c.code,
    label: `${c.code}-${sc.code} · ${sc.name}`,
  })));
  // What the batch would strand: items under anything ticked. A subcategory of
  // a ticked category is already counted by its category, so it is not added
  // twice.
  const bulkItems = (d?.by_category || []).reduce((n, c) => {
    if (bulkCats.has(c.code)) return n + c.items;
    return n + (c.subcategories || []).reduce(
      (m, sc) => m + (bulkSubs.has(`${c.code}|${sc.code}`) ? sc.items : 0), 0);
  }, 0);
  const bulkDests = allDests.filter(o =>
    !bulkCats.has(o.cat) && !bulkSubs.has(o.value.replace("-", "|")));

  // Subcategories go first: deleting a category takes its subcategories with
  // it, and a selection can hold both.
  const runBulk = async (ackNodes) => {
    setBulkBusy(true); setBulkErr("");
    const nodes = ackNodes || [
      ...[...bulkSubs].map(k => ({ cat: k.split("|")[0], sub: k.split("|")[1] })),
      ...[...bulkCats].map(c => ({ cat: c, sub: null })),
    ];
    const ack = !!ackNodes;
    const okCats = new Set(), okSubs = new Set(), spec = [], failed = [];
    for (const n of nodes) {
      const label = n.sub ? `${n.cat}-${n.sub}` : n.cat;
      const base = n.sub
        ? `${API}/api/categories/${encodeURIComponent(n.cat)}/subcategories/${encodeURIComponent(n.sub)}`
        : `${API}/api/categories/${encodeURIComponent(n.cat)}`;
      try {
        const qs = new URLSearchParams();
        if (ack) qs.set("acknowledge_spec", "true");
        if (bulkDest) qs.set("move_items_to", bulkDest);
        const r = await authFetch(base + (qs.toString() ? `?${qs}` : ""), { method: "DELETE" });
        if (r.status === 409) {
          const j = await r.json().catch(() => ({}));
          const msg = typeof j.detail === "string" ? j.detail : "Cannot delete this.";
          if (/specification/i.test(msg)) spec.push({ ...n, label });
          else failed.push(`${label}: ${msg}`);
          continue;
        }
        await jsonOrThrow(r, "Delete failed");
        if (n.sub) okSubs.add(`${n.cat}|${n.sub}`); else okCats.add(n.cat);
      } catch (e) { failed.push(`${label}: ${String(e.message || e)}`); }
    }
    dropNodes(okCats, okSubs);
    setBulkCats(prev => new Set([...prev].filter(c => !okCats.has(c))));
    setBulkSubs(prev => new Set([...prev].filter(k => !okSubs.has(k))));
    if (selCat && okCats.has(selCat)) { setSelCat(null); setSelSub(null); }
    if (selSub && (okCats.has(selSub.cat) || okSubs.has(`${selSub.cat}|${selSub.sub}`))) setSelSub(null);
    setBulkSpec(spec.length ? spec : null);
    setBulkErr(failed.join(" · "));
    if (!spec.length && !failed.length) setBulkAsk(false);
    setBulkBusy(false);
    setTick(t => t + 1);
  };

  if (!d) return <div style={{ color: "#9CA3AF", padding: 20 }}>Loading dashboard…</div>;
  const openItem = (code, catName, subName) => setDetail({ code, catName, subName });

  const pct = (n, t) => (t ? Math.round((n / t) * 100) : 0);
  const st = d.by_status || {};
  const obsolete = st.OBSOLETE || 0;
  const pending = (st.SUBMITTED || 0) + (st.PENDING_REVIEW || 0) + (st.UNDER_REVIEW || 0);

  const kpis = [
    { label: "Total Active Items", value: inr0(d.total_items), color: KZ.blue, icon: "📦" },
    { label: "Categories", value: d.active_categories, color: KZ.green, icon: "🗂" },
    { label: "Subcategories", value: d.subcategories, color: KZ.lightGreen, icon: "📑" },
    { label: "HSN / SAC Complete", value: pct(d.hsn_complete, d.total_items) + "%", sub: `${inr0(d.hsn_complete)} of ${inr0(d.total_items)}`, color: KZ.green, icon: "✅" },
    // Only items naming a company count. The import copied the Make/Vendor
    // classification into the vendor list, so thousands carry "KAPL APPROVED
    // MAKE" — a class of make, not a supplier. Counting those read as 96%
    // coverage for a catalogue barely half of which names anyone an order
    // could be sent to. The rest is named underneath rather than hidden: those
    // items are not missing an answer, they are carrying the wrong kind.
    { label: "Vendor Coverage", value: pct(d.with_vendor, d.total_items) + "%",
      sub: `${inr0(d.with_vendor)} name a supplier`
           + (d.class_of_make_only ? ` · ${inr0(d.class_of_make_only)} name a class of make` : ""),
      color: KZ.orange, icon: "🏷" },
    { label: "Duplicates Blocked", value: d.duplicates, color: KZ.charcoal, icon: "🛡" },
    // Created here, not loaded here. The two are different achievements and a
    // combined total flatters the smaller one out of existence.
    ...(made ? [{
      label: "Created in the app",
      value: inr0(made.totals?.IN_APP || 0),
      sub: `${inr0(made.window_totals?.IN_APP || 0)} in the last ${made.window_days} days`
           + ` · ${inr0(made.totals?.MASTER_IMPORT || 0)} imported`,
      color: KZ.purple || "#7C3AED", icon: "✨", onClick: () => setMadeOpen(true),
    }] : []),
  ];

  // An empty category is hidden from the chart — it would be a zero-width bar.
  // Whoever can delete one has to be able to see it, so for them nothing is
  // filtered out.
  const canManageCats = HAS(me, "category.manage");
  const cats = d.by_category.filter(c => c.items > 0 || canManageCats);
  const maxCat = Math.max(...cats.map(c => c.items), 1);
  const sel = selCat ? d.by_category.find(c => c.code === selCat) : null;
  const subs = sel ? [...sel.subcategories].sort((a, b) => b.items - a.items) : [];
  const maxSub = Math.max(...subs.map(s => s.items), 1);

  const dq = [
    ["Missing HSN / SAC", d.hsn_missing, d.hsn_missing === 0],
    ["Duplicate part numbers", d.duplicates, d.duplicates === 0],
    ["Items without a vendor", d.without_vendor, d.without_vendor === 0],
    ["Obsolete items", obsolete, true],
    ["Pending approval", pending, true],
    ["Purchase price captured", d.priced, d.priced > 0],
  ];
  const statusColors = { RELEASED: KZ.green, DRAFT: "#9CA3AF", PENDING_REVIEW: KZ.yellow, APPROVED: KZ.blue, REJECTED: KZ.red, OBSOLETE: KZ.charcoal };
  const statusEntries = Object.entries(st);
  const stMax = Math.max(...statusEntries.map(([, v]) => v), 1);

  return (
    <div>
      <div><span style={{ fontSize: 22, fontWeight: 800, color: KZ.blue }}>Dashboard</span>
        <span style={{ fontSize: 13, color: "#9CA3AF", marginLeft: 8 }}>Item Master — live coverage & data quality</span></div>
      <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 16 }}>All figures computed live from the item database.</div>

      <input placeholder="🔍  Search items — SKU, name or any word…" value={qDraft} onChange={e => setQDraft(e.target.value)}
        style={{ width: "100%", padding: "11px 14px", border: "1px solid #E5E7EB", borderRadius: 10, fontSize: 14, fontFamily: "inherit", marginBottom: 8, boxSizing: "border-box" }} />
      {(() => {
        const box = { padding: "8px 10px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "inherit", width: "100%", boxSizing: "border-box", background: "#fff" };
        const lab = { fontSize: 10.5, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.3, display: "block", marginBottom: 3 };
        const setFd = (k, v) => setFDraft(x => ({ ...x, [k]: v, ...(k === "category" ? { subcategory: "" } : {}) }));
        const catRow = d.by_category.find(c => c.code === fDraft.category);
        return (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginBottom: 16 }}>
            <label><span style={lab}>Category</span>
              <select value={fDraft.category} onChange={e => setFd("category", e.target.value)} style={box}>
                <option value="">All</option>
                {d.by_category.filter(c => c.items > 0).map(c => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
              </select></label>
            <label><span style={lab}>Subcategory</span>
              <select value={fDraft.subcategory} onChange={e => setFd("subcategory", e.target.value)} style={box} disabled={!catRow}>
                <option value="">{catRow ? "All" : "Pick a category first"}</option>
                {(catRow?.subcategories || []).filter(x => x.items > 0).map(x => <option key={x.code} value={x.code}>{catRow.code}-{x.code} · {x.name}</option>)}
              </select></label>
            <label><span style={lab}>Material</span>
              <input value={fDraft.material} onChange={e => setFd("material", e.target.value)} placeholder="e.g. SS304, aluminium" style={box} /></label>
            <label><span style={lab}>Size / dimension</span>
              <input value={fDraft.size} onChange={e => setFd("size", e.target.value)} placeholder="e.g. M10, R50, 4C x 2.5" style={box} /></label>
            <label><span style={lab}>Make / brand</span>
              <input value={fDraft.make} onChange={e => setFd("make", e.target.value)} placeholder="e.g. Daikin" style={box} /></label>
            <label><span style={lab}>Status</span>
              <select value={fDraft.status} onChange={e => setFd("status", e.target.value)} style={box}>
                <option value="">Any</option>
                {["DRAFT", "PENDING_REVIEW", "APPROVED", "RELEASED", "REJECTED", "OBSOLETE"].map(x => <option key={x} value={x}>{x.replace("_", " ")}</option>)}
              </select></label>
          </div>
        );
      })()}

      {(q || filtering) && (
        <Card style={{ marginBottom: 18, padding: 0 }}>
          <div style={{ display: "flex", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid #F3F4F6", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: KZ.blue }}>Search results</div>
            <div style={{ fontSize: 12, color: "#9CA3AF" }}>{sres ? `${inr0(sres.total)} match(es)` + (q ? ` for “${q}”` : "")
              + Object.entries(f).filter(([, v]) => String(v).trim()).map(([k, v]) => ` · ${k} ${v}`).join("") : "searching…"}</div>
            {sres?.matched_on === "name" && !broad && (
              <button onClick={() => { setBroad(true); setSpage(0); }}
                style={{ border: "none", background: "none", color: "#2563EB", fontSize: 12, cursor: "pointer", padding: 0 }}>
                Matched in item names · also search descriptions</button>)}
            {(broad || sres?.matched_on === "anywhere") && q && (
              <span style={{ fontSize: 12, color: "#B45309" }}>Including attributes and descriptions</span>)}
            <div style={{ flex: 1 }} />
            <button onClick={() => { const e = { category: "", subcategory: "", material: "", size: "", make: "", status: "" }; setQDraft(""); setQ(""); setFDraft(e); setF(e); }} style={{ border: "none", background: "#F3F4F6", color: "#374151", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>✕ Clear</button>
          </div>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}><tr>{["SKU", "Category", "Nomenclature", "Make", "HSN", "Status"].map(h => <th key={h} style={{ padding: "8px 14px", fontSize: 11, color: "#6B7280", textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>{h}</th>)}</tr></thead>
              <tbody>
                {sres && sres.items.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 14, fontSize: 13 }}>
                    <div style={{ color: "#9CA3AF" }}>No items match.</div>
                  </td></tr>
                )}
                {sres && sres.items.map(it => (
                  <tr key={it.item_code} onClick={() => openItem(it.item_code, it.category_name, it.subcategory_name)} style={{ cursor: "pointer" }} title="Open item detail">
                    <td style={{ padding: "7px 14px", fontWeight: 600, color: KZ.blue, fontSize: 13, borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>
                      {it.item_code}
                      {sretired[it.item_code] && (
                        <div style={{ fontSize: 10, color: "#B45309", fontWeight: 600, marginTop: 2 }}
                          title={`Zoho Books used this code for "${sretired[it.item_code].name}". That record was renamed ${sretired[it.item_code].sku} to release the number for this item.`}>
                          was {sretired[it.item_code].sku} in Zoho
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "7px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>{it.category_code} {it.category_name}</td>
                    <td style={{ padding: "7px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{it.short_name}</td>
                    <td style={{ padding: "7px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{it.make || "—"}</td>
                    <td style={{ padding: "7px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{it.hsn_code || "—"}</td>
                    <td style={{ padding: "7px 14px", borderBottom: "1px solid #F3F4F6" }}><Badge text={it.status} color={statusColors[it.status] || KZ.charcoal} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sres && sres.total > sper && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderTop: "1px solid #F3F4F6" }}>
              <span style={{ fontSize: 12, color: "#6B7280" }}>{spage * sper + 1}–{spage * sper + sres.items.length} of {inr0(sres.total)}</span>
              <div style={{ flex: 1 }} />
              <Btn small outline disabled={spage <= 0} onClick={() => setSpage(p => Math.max(0, p - 1))}>‹ Prev</Btn>
              <span style={{ fontSize: 12, color: "#374151" }}>Page {spage + 1} / {Math.max(1, Math.ceil(sres.total / sper))}</span>
              <Btn small outline disabled={(spage + 1) * sper >= sres.total} onClick={() => setSpage(p => p + 1)}>Next ›</Btn>
            </div>
          )}
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginBottom: 22 }}>
        {kpis.map(c => (
          <Card key={c.label} onClick={c.onClick}
            style={{ borderTop: `3px solid ${c.color}`,
                     cursor: c.onClick ? "pointer" : "default" }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 18 }}>{c.icon}</div>
            </div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2, textTransform: "uppercase", letterSpacing: 0.3 }}>{c.label}</div>
            {c.sub && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{c.sub}</div>}
          </Card>
        ))}
      </div>

      {madeOpen && !detail && made && (
        <div onClick={() => { setMadeOpen(false); setPickDay(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.55)",
                   display: "flex", alignItems: "center", justifyContent: "center",
                   zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 14, padding: 22, width: "100%",
                     maxWidth: 860, maxHeight: "84vh", overflowY: "auto",
                     boxShadow: "0 20px 50px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: "#111827", margin: 0 }}>
                Items created in the app
              </h3>
              <button onClick={() => { setMadeOpen(false); setPickDay(null); }}
                style={{ border: "none", background: "transparent", color: "#6B7280",
                         fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
            <p style={{ fontSize: 12.5, color: "#6B7280", margin: "6px 0 14px", maxWidth: "76ch" }}>
              An imported item and one somebody made both sit at PENDING_REVIEW, so the status
              chart cannot tell them apart — only the origin can. Of {inr0(d.total_items)} items,{" "}
              <b>{inr0(made.totals?.IN_APP || 0)}</b> were created here.
            </p>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
                          marginBottom: 14, paddingBottom: 12,
                          borderBottom: "1px solid #F3F4F6" }}>
              {[[7, "7 days"], [30, "30 days"], [90, "90 days"], [3650, "All time"]].map(([d, label]) => (
                <button key={d} onClick={() => { setMadeFrom(""); setMadeTo(""); setMadeDays(d); }}
                  style={{ padding: "5px 11px", borderRadius: 7, fontSize: 11.5, fontWeight: 600,
                           cursor: "pointer",
                           border: `1px solid ${!madeFrom && !madeTo && madeDays === d ? "#7C3AED" : "#E5E7EB"}`,
                           background: !madeFrom && !madeTo && madeDays === d ? "#F5F3FF" : "#fff",
                           color: !madeFrom && !madeTo && madeDays === d ? "#7C3AED" : "#6B7280" }}>
                  {label}</button>
              ))}
              <span style={{ fontSize: 11.5, color: "#9CA3AF", marginLeft: 4 }}>or</span>
              <input type="date" value={madeFrom} onChange={e => setMadeFrom(e.target.value)}
                style={{ padding: "4px 8px", fontSize: 11.5, border: "1px solid #E5E7EB",
                         borderRadius: 7, fontFamily: "inherit" }} />
              <span style={{ fontSize: 11.5, color: "#9CA3AF" }}>to</span>
              <input type="date" value={madeTo} onChange={e => setMadeTo(e.target.value)}
                style={{ padding: "4px 8px", fontSize: 11.5, border: "1px solid #E5E7EB",
                         borderRadius: 7, fontFamily: "inherit" }} />
              {(madeFrom || madeTo) && (
                <button onClick={() => { setMadeFrom(""); setMadeTo(""); }}
                  style={{ border: "none", background: "transparent", color: "#6B7280",
                           fontSize: 11.5, cursor: "pointer" }}>clear</button>
              )}
            </div>

            <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginBottom: 16 }}>
              {Object.entries(made.totals || {}).map(([k, v]) => (
                <div key={k}>
                  <div style={{ fontSize: 20, fontWeight: 800,
                                color: k === "IN_APP" ? "#7C3AED" : "#9CA3AF" }}>{inr0(v)}</div>
                  <div style={{ fontSize: 10.5, color: "#9CA3AF", textTransform: "uppercase",
                                letterSpacing: 0.3 }}>{k.replace(/_/g, " ")}</div>
                  <div style={{ fontSize: 10, color: "#D1D5DB" }}>
                    {inr0(made.window_totals?.[k] || 0)} in range</div>
                </div>
              ))}
            </div>

            {made.by_day?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF",
                              textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 6 }}>
                  {made.range_given
                    ? `${made.since.slice(0, 10)} to ${made.until || "today"}`
                    : `Last ${made.window_days} days`}
                </div>
                {made.by_day.slice().reverse().map(e => {
                  // Width is the day's share of the BIGGEST day, not its own
                  // internal split. Scaling each bar to its own total made a
                  // one-item day and a 6,488-item day the same length, which
                  // reads as magnitude while encoding nothing of the sort.
                  const mx = Math.max(...made.by_day.map(x => x.total), 1);
                  const w = (e.total / mx) * 100;
                  return (
                  <div key={e.date}
                    onClick={() => setPickDay(pickDay === e.date ? null : e.date)}
                    title={`Show the ${e.total} item${e.total === 1 ? "" : "s"} created on ${e.date}`}
                    style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12,
                             padding: "3px 6px", cursor: "pointer", borderRadius: 5,
                             background: pickDay === e.date ? "#F5F3FF" : "transparent" }}>
                    <span style={{ width: 86, color: "#6B7280" }}>{e.date}</span>
                    <span style={{ flex: 1, background: "#F9FAFB", borderRadius: 3, height: 10,
                                   overflow: "hidden", display: "flex" }}>
                      <span style={{ width: `${Math.max(w, e.total ? 0.6 : 0)}%`, height: "100%",
                                     display: "flex", minWidth: e.total ? 2 : 0 }}>
                        {["IN_APP", "SHEET_IMPORT", "MASTER_IMPORT"].map(k => (
                          e[k] ? <span key={k} title={`${k}: ${e[k]}`}
                            style={{ width: `${(e[k] / e.total) * 100}%`, height: "100%",
                                     background: k === "IN_APP" ? "#7C3AED"
                                               : k === "SHEET_IMPORT" ? "#60A5FA" : "#D1D5DB" }} /> : null
                        ))}
                      </span>
                    </span>
                    <span style={{ width: 56, textAlign: "right", fontVariantNumeric: "tabular-nums",
                                   color: "#374151" }}>{inr0(e.total)}</span>
                    <span style={{ width: 46, textAlign: "right", fontWeight: 700,
                                   color: e.IN_APP ? "#7C3AED" : "#D1D5DB",
                                   fontVariantNumeric: "tabular-nums" }}>
                      {e.IN_APP ? `+${e.IN_APP}` : "—"}</span>
                  </div>
                  ); })}
                <div style={{ fontSize: 10.5, color: "#9CA3AF", marginTop: 4 }}>
                  Click a day to see what was created on it. Bar length is the day against the
                  busiest day, so the import dwarfs everything — which is the true shape of it.
                  Purple is made here, blue an uploaded sheet, grey the master workbook import.
                </div>
              </div>
            )}

            {pickDay && (
              <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 12 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#7C3AED",
                                 textTransform: "uppercase", letterSpacing: 0.3 }}>
                    Created on {pickDay}
                  </span>
                  <span style={{ fontSize: 11, color: "#D1D5DB" }}>
                    {dayItems === "loading" ? "loading…" : (() => {
                      const tot = dayItems?.total_in_range || 0;
                      const from = (dayItems?.offset || 0) + 1;
                      const to = (dayItems?.offset || 0) + (dayItems?.recent || []).length;
                      return tot > (dayItems?.recent || []).length
                        ? `${inr0(from)}–${inr0(to)} of ${inr0(tot)} · click one to open it`
                        : `${inr0(tot)} item${tot === 1 ? "" : "s"} · click one to open it`;
                    })()}
                  </span>
                  <div style={{ flex: 1 }} />
                  <button onClick={() => setPickDay(null)}
                    style={{ border: "none", background: "transparent", color: "#6B7280",
                             fontSize: 11, cursor: "pointer" }}>close</button>
                </div>

                {dayItems !== "loading" && !(dayItems?.recent || []).length && (
                  <div style={{ fontSize: 12.5, color: "#9CA3AF", padding: "8px 0" }}>
                    Nothing was created on this day.
                  </div>
                )}

                {dayItems !== "loading" && (dayItems?.recent || []).length > 0 && (
                  // The list scrolls in its own box. Rendering fifty rows into
                  // the modal pushed the chart off the top, so reading a day
                  // meant losing the thing you clicked to get there.
                  <div style={{ maxHeight: "38vh", overflowY: "auto",
                                border: "1px solid #F3F4F6", borderRadius: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5,
                                  tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: 130 }} /><col />
                      <col style={{ width: 96 }} /><col style={{ width: 60 }} />
                    </colgroup>
                    <tbody>
                      {(dayItems?.recent || []).map(r => (
                        // The same detail a search hit opens, not a second one
                        // written for this list. The creation modal hides while
                        // it is up and returns when it closes.
                        <tr key={r.item_code} onClick={() => openItem(r.item_code)}
                            title={`Open ${r.item_code}`}
                            style={{ borderBottom: "1px solid #F3F4F6", cursor: "pointer" }}
                            onMouseEnter={e => (e.currentTarget.style.background = "#F9FAFB")}
                            onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                          <td style={{ padding: "5px 8px", fontFamily: "monospace", fontWeight: 700,
                                       color: "#1F4E79", whiteSpace: "nowrap", overflow: "hidden",
                                       textOverflow: "ellipsis" }} title={r.item_code}>{r.item_code}</td>
                          <td style={{ padding: "5px 8px", color: "#374151", overflow: "hidden",
                                       textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              title={r.short_name}>{r.short_name}</td>
                          <td style={{ padding: "5px 8px", fontSize: 10.5, whiteSpace: "nowrap",
                                       color: r.origin === "IN_APP" ? "#7C3AED" : "#9CA3AF" }}>
                            {r.origin.replace(/_/g, " ")}</td>
                          <td style={{ padding: "5px 8px", fontSize: 11, color: "#9CA3AF",
                                       whiteSpace: "nowrap" }}>
                            {(r.created_at || "").slice(11, 16)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}

                {dayItems !== "loading" && (dayItems?.total_in_range || 0) > DAY_PER_PAGE && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                    <button onClick={() => setDayPage(p2 => Math.max(0, p2 - 1))}
                      disabled={dayPage === 0}
                      style={{ padding: "4px 11px", borderRadius: 7, fontSize: 11.5,
                               fontWeight: 600, border: "1px solid #E5E7EB",
                               background: "#fff",
                               color: dayPage === 0 ? "#D1D5DB" : "#6B7280",
                               cursor: dayPage === 0 ? "not-allowed" : "pointer" }}>
                      ← Previous</button>
                    <span style={{ fontSize: 11.5, color: "#9CA3AF" }}>
                      Page {dayPage + 1} of{" "}
                      {Math.ceil((dayItems?.total_in_range || 1) / DAY_PER_PAGE)}
                    </span>
                    <button onClick={() => setDayPage(p2 => p2 + 1)}
                      disabled={(dayItems?.offset || 0) + (dayItems?.recent || []).length
                                >= (dayItems?.total_in_range || 0)}
                      style={{ padding: "4px 11px", borderRadius: 7, fontSize: 11.5,
                               fontWeight: 600, border: "1px solid #E5E7EB",
                               background: "#fff", color: "#6B7280", cursor: "pointer" }}>
                      Next →</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16 }} className="kz-registers">
        <Card>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: KZ.blue }}>Active Items by Category</div>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8 }}>— click a bar to drill into subcategories</div>
            <div style={{ flex: 1 }} />
            {canManageCats && (
              <button onClick={() => setAdding({ kind: "category" })}
                style={{ border: "1px solid #E5E7EB", background: "#fff", borderRadius: 6,
                         fontSize: 11.5, fontWeight: 600, color: KZ.blue, cursor: "pointer",
                         padding: "3px 9px", marginRight: 8 }}>+ Category</button>
            )}
            {selCat && <button onClick={() => { setSelCat(null); setSelSub(null); }} style={{ border: "none", background: "#F3F4F6", color: "#374151", borderRadius: 6, padding: "3px 10px", fontSize: 12, cursor: "pointer" }}>✕ Show all</button>}
          </div>

          {canManageCats && bulkCount > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                          background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 7,
                          padding: "6px 10px", marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#991B1B" }}>
                {bulkCats.size > 0 && `${bulkCats.size} categor${bulkCats.size === 1 ? "y" : "ies"}`}
                {bulkCats.size > 0 && bulkSubs.size > 0 && " · "}
                {bulkSubs.size > 0 && `${bulkSubs.size} subcategor${bulkSubs.size === 1 ? "y" : "ies"}`}
                {" selected"}
              </span>
              {bulkItems > 0 && (
                <span style={{ fontSize: 11.5, color: "#7C2D12" }}>
                  holding {bulkItems.toLocaleString("en-IN")} item{bulkItems === 1 ? "" : "s"}
                </span>
              )}
              <div style={{ flex: 1 }} />
              {bulkErr && <span style={{ fontSize: 11, color: "#B91C1C", maxWidth: 320 }}>{bulkErr}</span>}
              {bulkSpec
                ? <>
                    <span style={{ fontSize: 11, color: "#B91C1C", maxWidth: 300 }}>
                      {bulkSpec.map(n => n.label).join(", ")} {bulkSpec.length === 1 ? "is" : "are"} in the
                      controlled item-creation specification.
                    </span>
                    <button onClick={() => runBulk(bulkSpec)} disabled={bulkBusy}
                      style={{ border: "none", background: "#EF4444", color: "#fff", borderRadius: 5,
                               fontSize: 11, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}>
                      {bulkBusy ? "…" : "Delete anyway"}</button>
                  </>
                : bulkAsk
                  ? <>
                      {bulkItems > 0
                        ? <>
                            <span style={{ fontSize: 11, color: "#B91C1C" }}>Move the items to</span>
                            <select value={bulkDest} onChange={e => setBulkDest(e.target.value)}
                              style={{ fontSize: 11, padding: "2px 5px", border: "1px solid #FCA5A5",
                                       borderRadius: 4, maxWidth: 230, fontFamily: "inherit" }}>
                              <option value="">choose a subcategory…</option>
                              {bulkDests.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </>
                        : <span style={{ fontSize: 11, color: "#B91C1C" }}>Delete {bulkCount}?</span>}
                      <button onClick={() => runBulk(null)} disabled={bulkBusy || (bulkItems > 0 && !bulkDest)}
                        style={{ border: "none", background: (bulkItems > 0 && !bulkDest) ? "#FCA5A5" : "#EF4444",
                                 color: "#fff", borderRadius: 5, fontSize: 11, fontWeight: 600,
                                 padding: "3px 10px", cursor: (bulkItems > 0 && !bulkDest) ? "not-allowed" : "pointer" }}>
                        {bulkBusy ? "Deleting…" : bulkItems > 0 ? "Move & delete" : "Delete"}</button>
                    </>
                  : <button onClick={() => { setBulkAsk(true); setBulkErr(""); }}
                      style={{ border: "1px solid #FCA5A5", background: "#fff", color: "#B91C1C", borderRadius: 5,
                               fontSize: 11.5, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}>
                      Delete selected</button>}
              <button onClick={clearBulk}
                style={{ border: "none", background: "transparent", color: "#6B7280",
                         fontSize: 11, cursor: "pointer" }}>Clear</button>
              {bulkItems > 0 && bulkAsk && (
                <div style={{ flexBasis: "100%", fontSize: 10.5, color: "#7C2D12", lineHeight: 1.5 }}>
                  Item codes are issued once and never reissued, so a moved item keeps
                  the code it was born with — an A07 part still reads A07 after the move.
                  Nothing is deleted but the empty codes themselves, and every move is
                  written to the audit log.
                </div>
              )}
            </div>
          )}
          {cats.map(c => (
            <div key={c.code} onClick={() => { setSelCat(selCat === c.code ? null : c.code); setSelSub(null); }}
              style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7, cursor: "pointer", opacity: selCat && selCat !== c.code ? 0.45 : 1 }}>
              {canManageCats && (
                <input type="checkbox" checked={bulkCats.has(c.code)}
                  onClick={e => e.stopPropagation()}
                  onChange={e => { const on = e.target.checked; setBulkErr(""); setBulkSpec(null);
                    setBulkCats(prev => { const n = new Set(prev); if (on) n.add(c.code); else n.delete(c.code); return n; }); }}
                  title={c.items > 0 ? `Select ${c.code} — its ${c.items} items will need a new home` : `Select ${c.code}`}
                  style={{ width: 13, height: 13, margin: 0, flex: "0 0 auto", accentColor: KZ.blue, cursor: "pointer" }} />
              )}
              <div style={{ width: 34, fontSize: 12, fontWeight: 700, color: categoryColors[c.code] || KZ.charcoal }}>{c.code}</div>
              <div style={{ width: 118, fontSize: 12, color: "#374151", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
              <div style={{ flex: 1, background: "#F3F4F6", borderRadius: 4, height: 16 }}>
                <div style={{ width: `${(c.items / maxCat) * 100}%`, background: categoryColors[c.code] || KZ.blue, height: "100%", borderRadius: 4 }} />
              </div>
              <div title={shareTitle(c, d.total_items)}
                style={{ width: 82, fontSize: 12, color: "#6B7280", textAlign: "right" }}>{c.items} · {sharePct(c.share)}</div>
              {canManageCats && (
                <DeleteNodeButton kind="category" categoryCode={c.code} name={c.name}
                  dests={allDests}
                  onDone={() => { dropNodes(new Set([c.code]), new Set()); setSelCat(null); setSelSub(null); setTick(t => t + 1); }} />
              )}
            </div>
          ))}
          {sel && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #E5E7EB" }}>
              {canManageCats && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
                  <button onClick={() => setAdding({ kind: "subcategory", categoryCode: sel.code })}
                    style={{ border: "1px solid #E5E7EB", background: "#fff", borderRadius: 6,
                             fontSize: 11.5, fontWeight: 600, color: KZ.blue, cursor: "pointer",
                             padding: "3px 9px" }}>+ Subcategory</button>
                </div>
              )}
              <div style={{ fontWeight: 700, fontSize: 13, color: KZ.blue, marginBottom: 10 }}>{sel.code} {sel.name} — subcategories</div>
              {subs.map(s => (
                <div key={s.code} onClick={() => { if (s.items) { setSelSub({ cat: sel.code, sub: s.code, name: s.name }); setSubSkip(0); } }}
                  style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: s.items ? "pointer" : "default", opacity: s.items ? 1 : 0.5, background: selSub && selSub.sub === s.code && selSub.cat === sel.code ? "#F0FDF4" : "transparent", borderRadius: 4 }}>
                  {canManageCats && (
                    <input type="checkbox" checked={bulkSubs.has(`${sel.code}|${s.code}`)}
                      onClick={e => e.stopPropagation()}
                      onChange={e => { const on = e.target.checked; const k = `${sel.code}|${s.code}`; setBulkErr(""); setBulkSpec(null);
                        setBulkSubs(prev => { const n = new Set(prev); if (on) n.add(k); else n.delete(k); return n; }); }}
                      title={s.items > 0 ? `Select ${sel.code}-${s.code} — its ${s.items} items will need a new home` : `Select ${sel.code}-${s.code}`}
                      style={{ width: 13, height: 13, margin: 0, flex: "0 0 auto", accentColor: KZ.blue, cursor: "pointer" }} />
                  )}
                  <div style={{ width: 56, fontSize: 11, fontWeight: 600, color: "#6B7280" }}>{s.code}</div>
                  <div style={{ width: 150, fontSize: 12, color: "#374151", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                  <div style={{ flex: 1, background: "#F3F4F6", borderRadius: 4, height: 12 }}>
                    <div style={{ width: `${(s.items / maxSub) * 100}%`, background: KZ.lightGreen, height: "100%", borderRadius: 4 }} />
                  </div>
                  <div style={{ width: 44, fontSize: 12, color: "#6B7280", textAlign: "right" }}>{s.items}{s.items ? " ›" : ""}</div>
                  {unconf[`${sel.code}-${s.code}`] && sel.code !== "A12" && (canManageCats ? (
                    <button onClick={e => { e.stopPropagation();
                        setSettingUp({ code: `${sel.code}-${s.code}`, name: s.name,
                                       suggested: unconf[`${sel.code}-${s.code}`].suggested_copy_from }); }}
                      title="Not offered on Create Items until it has questions"
                      style={{ border: "1px solid #FCD34D", background: "#FFFBEB", color: "#92400E",
                               borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "2px 7px",
                               cursor: "pointer", whiteSpace: "nowrap" }}>Set up questions</button>
                  ) : (
                    <span title="Not offered on Create Items until an administrator sets up its questions"
                      style={{ fontSize: 11, color: "#B45309", whiteSpace: "nowrap" }}>not in Create Items</span>
                  ))}
                  {canManageCats && (
                    <DeleteNodeButton kind="subcategory" categoryCode={sel.code} subCode={s.code}
                      name={s.name} dests={allDests}
                      onDone={() => { dropNodes(new Set(), new Set([`${sel.code}|${s.code}`])); setSelSub(null); setTick(t => t + 1); }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <div>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: KZ.blue, marginBottom: 12 }}>Item Status</div>
            {statusEntries.map(([s, v]) => (
              <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                <div style={{ width: 110, fontSize: 12, color: "#374151" }}>{s.replace(/_/g, " ")}</div>
                <div style={{ flex: 1, background: "#F3F4F6", borderRadius: 4, height: 12 }}>
                  <div style={{ width: `${(v / stMax) * 100}%`, background: statusColors[s] || KZ.charcoal, height: "100%", borderRadius: 4 }} />
                </div>
                <div style={{ width: 48, fontSize: 12, color: "#6B7280", textAlign: "right" }}>{inr0(v)}</div>
              </div>
            ))}
          </Card>
          <Card>
            <div style={{ fontWeight: 700, fontSize: 15, color: KZ.blue, marginBottom: 12 }}>Master Data Quality</div>
            {dq.map(([label, val, ok]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #F3F4F6", fontSize: 13 }}>
                <div style={{ flex: 1, color: "#374151" }}>{label}</div>
                <div style={{ fontWeight: 700, color: "#374151", marginRight: 10 }}>{inr0(val)}</div>
                <Badge text={ok ? "OK" : "REVIEW"} color={ok ? KZ.green : KZ.orange} />
              </div>
            ))}
          </Card>
        </div>
      </div>

      {selSub && (
        <Card style={{ marginTop: 16, padding: 0 }}>
          <div style={{ display: "flex", alignItems: "center", padding: "12px 14px", borderBottom: "1px solid #F3F4F6", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: KZ.blue }}>{selSub.cat}-{selSub.sub} · {selSub.name}</div>
            <div style={{ fontSize: 12, color: "#9CA3AF" }}>{subData ? `${inr0(subData.total)} item(s)` : "loading…"}</div>
            <div style={{ flex: 1 }} />
            <button onClick={() => setSelSub(null)} style={{ border: "none", background: "#F3F4F6", color: "#374151", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>✕ Close list</button>
          </div>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}><tr>{["SKU", "Nomenclature", "Make", "HSN/SAC", "Status"].map(h => <th key={h} style={{ padding: "8px 14px", fontSize: 11, color: "#6B7280", textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>{h}</th>)}</tr></thead>
              <tbody>
                {subData && subData.items.length === 0 && <tr><td colSpan={5} style={{ padding: 14, color: "#9CA3AF", fontSize: 13 }}>No items.</td></tr>}
                {subData && subData.items.map(it => (
                  <tr key={it.item_code} onClick={() => openItem(it.item_code, (d.by_category.find(c => c.code === selSub.cat) || {}).name, selSub.name)} style={{ cursor: "pointer" }} title="Open item detail">
                    <td style={{ padding: "7px 14px", fontWeight: 600, color: KZ.blue, fontSize: 13, borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>{it.item_code}</td>
                    <td style={{ padding: "7px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{it.short_name}</td>
                    <td style={{ padding: "7px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{it.make || "—"}</td>
                    <td style={{ padding: "7px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{it.hsn_code || "—"}</td>
                    <td style={{ padding: "7px 14px", borderBottom: "1px solid #F3F4F6" }}><Badge text={it.status} color={statusColors[it.status] || KZ.charcoal} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {subData && subData.total > subPer && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderTop: "1px solid #F3F4F6" }}>
              <span style={{ fontSize: 12, color: "#6B7280" }}>{subSkip + 1}–{subSkip + subData.items.length} of {inr0(subData.total)}</span>
              <div style={{ flex: 1 }} />
              <Btn small outline disabled={subSkip <= 0} onClick={() => setSubSkip(Math.max(0, subSkip - subPer))}>‹ Prev</Btn>
              <span style={{ fontSize: 12, color: "#374151" }}>Page {Math.floor(subSkip / subPer) + 1} / {Math.max(1, Math.ceil(subData.total / subPer))}</span>
              <Btn small outline disabled={subSkip + subPer >= subData.total} onClick={() => setSubSkip(subSkip + subPer)}>Next ›</Btn>
            </div>
          )}
        </Card>
      )}

      <Card style={{ marginTop: 16, padding: 0 }}>
        <div style={{ padding: "12px 14px", fontWeight: 700, fontSize: 15, color: KZ.blue }}>Category Breakdown <span style={{ fontSize: 12, fontWeight: 400, color: "#9CA3AF" }}>— click a row to drill down</span></div>
        <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Code", "Category", "Items", "Share", "HSN Complete", "Subcats"].map(h => <th key={h} className={ac(h)} style={{ padding: "8px 14px", fontSize: 11, color: "#6B7280", textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>{h}</th>)}</tr></thead>
            <tbody>
              {d.by_category.map(c => (
                <tr key={c.code} onClick={() => { if (c.items) { setSelCat(selCat === c.code ? null : c.code); setSelSub(null); } }} style={{ cursor: c.items ? "pointer" : "default", opacity: c.items ? 1 : 0.5, background: selCat === c.code ? "#F8FAFC" : "transparent" }}>
                  <td style={{ padding: "8px 14px", fontWeight: 700, color: categoryColors[c.code] || KZ.charcoal, borderBottom: "1px solid #F3F4F6" }}>{c.code}</td>
                  <td style={{ padding: "8px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{c.name}</td>
                  <td style={{ padding: "8px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{c.items}</td>
                  <td title={shareTitle(c, d.total_items)}
                    style={{ padding: "8px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{sharePct(c.share)}</td>
                  <td style={{ padding: "8px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6", color: c.hsn_complete === c.items ? KZ.green : KZ.orange }}>{c.hsn_complete}/{c.items}</td>
                  <td style={{ padding: "8px 14px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{c.subcategories.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {detail && <ItemDetail item={detail} me={me} onBack={() => setDetail(null)} />}
      {settingUp && (
        <SetupQuestionsModal code={settingUp.code} name={settingUp.name}
          suggested={settingUp.suggested} onClose={() => setSettingUp(null)}
          onDone={() => { setSettingUp(null); setTick(t => t + 1); }} />
      )}
      {adding && (
        <AddNodeModal kind={adding.kind} categoryCode={adding.categoryCode}
          onClose={() => setAdding(null)}
          onDone={() => { setAdding(null); setTick(t => t + 1); }} />
      )}
    </div>
  );
}

// ── Item Detail page (Section 5) — 8 tabs + data-quality score ────────────────
/** What is missing from this item, and the means to fix it in the same place.
 *
 *  The API could always edit an item; nothing on screen ever called it, so a
 *  field the approval gate refuses on had no route to being corrected. This is
 *  that route, and it is driven by the readiness check rather than being a
 *  form of every column: the fields it offers are the ones actually reported
 *  as missing, plus the handful that decide what the item is.
 *
 *  A locked item is not editable here. Approval is a statement someone put
 *  their name to about specific content, and changing it afterwards would
 *  leave the sign-off describing something that no longer exists — the server
 *  refuses too, and only a Super Admin gets past it, at the cost of the
 *  approval transferring to them.
 */
/** What may be edited on an item, and what each field is called.
 *
 *  Fetched rather than hardcoded: the labels are per subcategory — p3 is
 *  "Standard" on a bolt and the form must neither print "p3" nor invent a
 *  name — and which fields are editable is a rule the API enforces.
 */
function useItemFields(code) {
  const [spec, setSpec] = useState(null);
  const load = useCallback(() => {
    let alive = true;
    authFetch(`${API}/api/items/${code}/fields`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive) setSpec(d); }).catch(() => { });
    return () => { alive = false; };
  }, [code]);
  useEffect(() => load(), [load]);
  // Reloadable, and it must be reloaded after every save.
  //
  // The classification values and the vendor list on the form are built from
  // this, not from the item — so a spec fetched once went stale the moment
  // anything was saved. Reopening the editor showed the values from before the
  // edit, and saving again wrote those stale values back over the new ones.
  // From the chair that is indistinguishable from the save being ignored,
  // which is exactly what it was reported as.
  return [spec, load];
}

/** The starting state of the form, from the item and what the server allows. */
function blankEdit(item, spec) {
  const f = {};
  (spec?.fields || []).forEach(x => {
    const v = item?.[x.key];
    f[x.key] = v === null || v === undefined ? "" : String(v);
  });
  const cls = {};
  (spec?.attributes || []).forEach(a => {
    cls[a.key] = a.value === null || a.value === undefined ? "" : String(a.value);
  });
  return { ...f, __note: "", __loaded: Boolean(spec), __cls: cls,
           __sup: (spec?.suppliers || []).map(v => ({
             name: v.name || "", brand: v.brand || "",
             price: (v.last_prices || [])[0] ?? "" })) };
}

/** What the API is sent. Kept beside the form so both screens agree.
 *
 *  Only what changed. The form loads every field the item has and used to
 *  submit every one of them, so SCM filling in a UOM sent the HSN, the
 *  description and the classification back with it — untouched, but sent — and
 *  the save refused the lot: "Scm may not change classification…, hsn_code
 *  (Finance)". A PATCH should carry the change, not the screen.
 */
function editBody(form, spec, me, baseline) {
  const { __cls, __sup, __note, __loaded, ...plain } = form;
  const locked = (spec && spec.not_editable) || {};
  Object.keys(plain).forEach(k => {
    // Never send what the server already said this account may not change.
    if (k in locked) { delete plain[k]; return; }
    // Nor what the user did not touch.
    if (baseline && k in baseline
        && String(plain[k] ?? "") === String(baseline[k] ?? "")) delete plain[k];
  });
  const body = { ...plain, updated_by: me?.username, note: __note || null };
  // A number or nothing. An empty box means "not stated", which is not zero —
  // a lead time of 0 days is a claim about the item.
  ["min_order_qty", "lead_time_days"].forEach(k => {
    if (k in body) {
      const t = String(body[k]).trim();
      if (t === "") delete body[k]; else body[k] = Number(t);
    }
  });
  const cls = {};
  Object.entries(__cls || {}).forEach(([k, v]) => {
    if (String(v).trim() !== "") cls[k] = String(v).trim();
  });
  // The attributes are one field for ownership, so they travel together or not
  // at all — and not at all when this account does not own them.
  const clsTouched = baseline
    ? Object.entries(__cls || {}).some(
        ([k, v]) => String(v ?? "") !== String((baseline.__cls || {})[k] ?? ""))
    : true;
  if (Object.keys(cls).length && !("classification" in locked) && clsTouched) {
    body.classification = cls;
  }
  // Only when the form actually rendered the vendor list. `__sup` is built
  // from the field list, so a form opened before that arrived has an empty one
  // — and sending it would read as "this item now has no vendors" and delete
  // the ones it has. An absent key means "unchanged"; an empty list is a claim.
  // A row is kept if it holds anything. After the 27-Sep-2026 repair most
  // imported items carry the make as brand with the vendor left for SCM, and
  // saving on "has a name" deleted that row — brand, price and all.
  const sup = (__sup || []).filter(x => String(x.name || "").trim()
    || String(x.brand || "").trim() || String(x.price ?? "").trim());
  if (spec && !("suppliers" in locked)
      && (sup.length || (spec.suppliers || []).length)) {
    body.suppliers = sup.map(x => ({
      name: String(x.name || "").trim(),
      brand: String(x.brand || "").trim() || null,
      last_prices: String(x.price).trim() === "" ? [] : [Number(x.price)],
    }));
  }
  return body;
}

/** Every editable field of an item. One component, so the Approvals queue and
 *  the item page cannot offer different subsets of the same item.
 *
 *  They did: this screen had seven fields and every attribute, while the queue
 *  had five and none — and the queue's "Note (recorded in the audit trail)"
 *  was wired to `remarks`, a field on the item carrying the price basis, GST
 *  and UOM. It loaded blank, so saving replaced all of it.
 */
/** A dropdown of the values this kind of item actually uses, that can still
 *  take a value nobody has used yet.
 *
 *  A datalist was here before. It holds the same options but shows nothing
 *  until you type, so on screen it is indistinguishable from a blank box —
 *  which is what it looked like on an item whose subcategory has twelve
 *  approved makes to choose from.
 *
 *  Two groups, because they are two different claims. What this subcategory
 *  already uses is the answer nearly every time. What the rest of the
 *  catalogue uses is worth offering and worth marking as a different thing —
 *  A01 FASTENER has no unit recorded on any of its 300 items, and a list that
 *  quietly led with SQM would be inviting a wall panel's unit onto a bolt.
 *
 *  "Other" is always last and always available. KAPL must be able to enter a
 *  value the catalogue has never seen; a vocabulary that cannot grow stops the
 *  next genuine item.
 */
/** Drag state for a reorderable list of rows.
 *
 *  HTML5 drag-and-drop rather than a pointer-event library: it is three
 *  handlers, it works with the keyboard fallback below, and it does not add a
 *  dependency to move six rows around.
 *
 *  The list is reordered on dragover — as the row passes under the cursor, not
 *  on drop — so what you see while dragging IS the result. A preview that only
 *  resolves on release makes you drop and check.
 */
function useRowDrag(items, setItems) {
  const from = useRef(null);

  const move = (a, b) => {
    if (a === b || a == null || b == null) return;
    setItems(prev => {
      const next = [...prev];
      const [row] = next.splice(a, 1);
      next.splice(b, 0, row);
      return next;
    });
    from.current = b;
  };

  // Split deliberately: only the grip is draggable, the whole row is the drop
  // target. Marking the row draggable instead takes mouse text-selection away
  // from the quantity and note inputs inside it — the row starts a drag where
  // the user is trying to select "2 per door leaf" and retype it.
  return {
    row: (i) => ({
      onDragOver: (e) => { e.preventDefault(); move(from.current, i); },
      onDrop: (e) => { e.preventDefault(); from.current = null; },
    }),
    grip: (i) => ({
      draggable: true,
      tabIndex: 0,
      role: "button",
      "aria-label": `Reorder line ${i + 1}. Alt with up or down arrow.`,
      onDragStart: (e) => {
        from.current = i;
        e.dataTransfer.effectAllowed = "move";
        // Firefox starts no drag at all unless data is set on the transfer.
        try { e.dataTransfer.setData("text/plain", String(i)); } catch { }
      },
      onDragEnd: () => { from.current = null; },
      // The keyboard route to the same thing. A control that answers only to a
      // mouse is not a control everybody has.
      onKeyDown: (e) => {
        if (!e.altKey) return;
        if (e.key === "ArrowUp" && i > 0) { e.preventDefault(); move(i, i - 1); }
        if (e.key === "ArrowDown" && i < items.length - 1) { e.preventDefault(); move(i, i + 1); }
      },
    }),
  };
}

/** The grip you actually pull. */
function DragHandle(props) {
  return (
    <span {...props} title="Drag to reorder — or Alt+↑ / Alt+↓"
      style={{ cursor: "grab", color: "#C7CBD1", fontSize: 15, lineHeight: 1,
               userSelect: "none", padding: "3px 1px", alignSelf: "center" }}>⠿</span>
  );
}

/** "What do I put here?" for one attribute.
 *
 *  KAPL: "if the creator is new he might not be having knowledge on how to
 *  choose the values ... the SOPs are also provided there, those should be
 *  used for knowledgebase."
 *
 *  Fetched on demand, never on load. Ten fields fetching a model answer the
 *  moment the form opens would cost ten calls for a form that is mostly filled
 *  from the description already, and would make the page wait on all of them.
 */
function AttributeHelp({ stage, subcategory, typed, onPick }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    if (d) { setOpen(o => !o); return; }
    setOpen(true); setBusy(true); setErr("");
    try {
      const r = await authFetch(
        `${API}/api/items/attribute-guidance?stage=${encodeURIComponent(stage)}`
        + `&subcategory_code=${encodeURIComponent(subcategory)}`
        + (typed ? `&typed=${encodeURIComponent(typed)}` : ""));
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Could not fetch guidance");
      setD(j);
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };

  const ai = d?.ai;
  return (
    <>
      <button onClick={load} type="button"
        style={{ border: "1px solid #DBEAFE", background: "#EFF6FF", color: "#1D4ED8",
                 borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "2px 8px",
                 cursor: "pointer", marginLeft: 8, whiteSpace: "nowrap" }}>
        {open ? "Hide help" : "What do I put here?"}
      </button>
      {open && (
        <div style={{ marginTop: 7, border: "1px solid #DBEAFE", background: "#F8FAFF",
                      borderRadius: 8, padding: "10px 12px", fontSize: 12.5,
                      lineHeight: 1.55, color: "#374151" }}>
          {busy && <div style={{ color: "#6B7280" }}>Reading the SOP…</div>}
          {err && <div style={{ color: "#B45309" }}>{err}</div>}
          {d && d.available === false && (
            <div style={{ color: "#6B7280" }}>{d.reason}</div>
          )}
          {ai?.what_it_means && (
            <div style={{ marginBottom: 6 }}>{ai.what_it_means}</div>
          )}
          {ai?.how_to_answer && (
            <div style={{ marginBottom: 6 }}><b>How to answer:</b> {ai.how_to_answer}</div>
          )}
          {/* The SOP is quoted, and the file named, so the reader can go and
              check rather than take the paraphrase on trust. */}
          {ai?.sop_says && (
            <div style={{ borderLeft: "3px solid #BFDBFE", paddingLeft: 9,
                          margin: "7px 0", color: "#4B5563", fontStyle: "italic" }}>
              “{ai.sop_says}”
              {(d.sop_files || []).length > 0 && (
                <div style={{ fontStyle: "normal", fontSize: 11, color: "#9CA3AF",
                              marginTop: 2 }}>— {d.sop_files.join(", ")}</div>
              )}
            </div>
          )}
          {(ai?.suggestions || []).length > 0 && (
            <div style={{ marginTop: 7 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3,
                            color: "#6B7280", marginBottom: 4 }}>
                VALUES YOU CAN USE
              </div>
              <div style={{ display: "grid", gap: 4 }}>
                {ai.suggestions.map(v => (
                  <div key={v.value} style={{ display: "flex", gap: 8,
                                              alignItems: "flex-start" }}>
                    <button type="button" onClick={() => onPick(v.value)}
                      style={{ border: "1px solid #C7D2FE", background: "#fff",
                               color: "#1D4ED8", borderRadius: 5, fontSize: 11.5,
                               padding: "2px 7px", cursor: "pointer",
                               whiteSpace: "nowrap", flexShrink: 0 }}>
                      {v.value}
                    </button>
                    <span style={{ color: "#6B7280", fontSize: 11.5 }}>
                      {v.why}
                      {/* Where it came from. A value that is only in the SOP is
                          a different kind of answer from one 30 items already
                          carry, and the reader should be able to tell. */}
                      <span style={{ color: "#9CA3AF" }}>
                        {v.evidence === "sop" ? " · from the SOP"
                          : " · already in use"}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {ai?.note && (
            <div style={{ marginTop: 7, color: "#B45309" }}>{ai.note}</div>
          )}
          {d && !ai && !busy && d.available !== false && (
            <div style={{ color: "#6B7280" }}>
              {d.note || "No guidance is available for this field."}
              {d.sop_extract && (
                <div style={{ marginTop: 6, fontStyle: "italic", color: "#4B5563" }}>
                  {d.sop_extract.slice(0, 400)}…
                </div>
              )}
            </div>
          )}
          {/* Said plainly rather than left to be inferred: what is on screen
              is this company's SOP and this catalogue's own values, and
              anything the model offered that was in neither was discarded
              before it got here. */}
          {ai && (
            <div style={{ marginTop: 8, fontSize: 10.5, color: "#9CA3AF" }}>
              From KAPL's SOP for this category and the values already recorded.
              No value is suggested that is not in one of those.
              {ai.suggestions_dropped > 0 &&
                ` ${ai.suggestions_dropped} unsourced suggestion(s) were discarded.`}
            </div>
          )}
        </div>
      )}
    </>
  );
}

/** One editable cell of the room schedule.
 *
 *  KAPL: "for each such field the editable option should be there." The
 *  schedule showed area, class, ACH, temperature, RH, pressure and occupancy
 *  and let you change none of them — the only way in was a modal behind a row
 *  click, which is the wrong shape for correcting one number in one room.
 *
 *  Click the value, type, Enter. Escape abandons it. A field is saved only if
 *  it actually changed, so tabbing through the row writes nothing.
 */
function RoomCell({ room, field, value, unit, onSaved, width, cls = "num",
                   format, canEdit = true }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const start = (e) => {
    e.stopPropagation();          // the row opens the URS modal; a cell must not
    // A cell nobody may change must not open. It did, and the reader found out
    // by typing a number and being handed "Your role does not allow this:
    // project.edit. (HTTP 403)" — the server talking to itself, after the work
    // was already done. Refusing at the click is the same rule, said in time.
    if (!canEdit) return;
    setV(value === null || value === undefined ? "" : String(value));
    setErr("");
    setEditing(true);
  };

  const commit = async () => {
    const was = value === null || value === undefined ? "" : String(value);
    if (String(v) === was) { setEditing(false); return; }
    setBusy(true); setErr("");
    try {
      // ACH lives on the room's design record and everything else on the room.
      // Two endpoints, one cell — the split is a fact about the schema and
      // should not be a fact the user has to know.
      const isDesign = field === "ach";
      const url = isDesign
        ? `${API}/api/eng/rooms/${room.id}/design`
        : `${API}/api/eng/rooms/${room.id}`;
      const num = v === "" ? null : (field === "cleanroom_class" ? v : Number(v));
      let body;
      if (isDesign) {
        // PUT replaces the design row, so everything already on it is resent.
        const d = room.design || {};
        body = { ...d, ach: num };
        delete body.id; delete body.room_id; delete body.updated_at;
      } else {
        body = { [field]: num };
      }
      const r = await authFetch(url, {
        method: isDesign ? "PUT" : "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      await jsonOrThrow(r, "Could not save");
      setEditing(false);
      onSaved();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  if (editing) {
    return (
      <td className={cls} style={{ ...ES.td, padding: "3px 6px" }}
          onClick={e => e.stopPropagation()}>
        <input autoFocus disabled={busy}
          type={field === "cleanroom_class" ? "text" : "number"} step="any"
          value={v}
          onChange={e => setV(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") { setEditing(false); setErr(""); }
          }}
          onBlur={commit}
          // No textAlign here: index.css aligns an input the way its column
          // does, so the value does not jump sideways the moment you click it.
          style={{ ...ES.input, width: width || 74, padding: "3px 6px", fontSize: 12.5, marginBottom: 0 }} />
        {err && (
          <div style={{ fontSize: 10.5, color: "#B45309", maxWidth: 210,
                        whiteSpace: "normal", lineHeight: 1.3, marginTop: 2 }}>
            {err}
          </div>
        )}
      </td>
    );
  }
  return (
    <td className={cls}
        style={{ ...ES.td, cursor: canEdit ? "text" : "default",
                 color: canEdit ? undefined : "#6B7280" }}
        onClick={start}
        title={canEdit ? "Click to edit"
                       : "Read-only for your role — Engineering and the project "
                         + "owner set these"}>
      {/* Formatted for reading, raw for editing. Rooms stored before the
          write-side rounding carry float noise — an area of
          11.623199999999999 — and showing that in a schedule is how a real
          measurement comes to look like a mistake. The box still opens on the
          stored number, because that is what gets saved back. */}
      {value === null || value === undefined || value === ""
        ? <span style={{ color: "#D1D5DB" }}>—</span>
        : <>{format ? format(value) : value}
            {unit ? <span style={{ color: "#9CA3AF" }}> {unit}</span> : null}</>}
    </td>
  );
}

/** "What do I put here?" for any field, anywhere.
 *
 *  KAPL: "this kind of assistance should be provided at all the stages or
 *  locations so that anyone can get information handy while filling it."
 *
 *  One component against one endpoint. There were two bespoke panels before
 *  this — the guided-creation question help and the BOM revision box — both
 *  doing the same three things, both written separately. A third would have
 *  made that a habit. Adding help to a new box is now one tag.
 *
 *  Fetched on demand. A form with ten helped fields must not make ten calls
 *  when it opens.
 */
function FieldHelp({ field, subcategory, itemCode, onPick, label }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    if (d) { setOpen(o => !o); return; }
    setOpen(true); setBusy(true); setErr("");
    try {
      const q = new URLSearchParams({ field });
      if (subcategory) q.set("subcategory_code", subcategory);
      if (itemCode) q.set("item_code", itemCode);
      // The AI endpoint returns the deterministic panel too, under `base`, so
      // one call carries both and the registers are never waiting on a model.
      const r = await authFetch(`${API}/api/items/field-help/ai?${q}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "No help available");
      setD({ ...(j.base || j), ai2: j.ai || null });
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  return (
    <>
      <button type="button" onClick={load} title="What do I put here?"
        style={{ border: "1px solid #DBEAFE", background: "#EFF6FF",
                 color: "#1D4ED8", borderRadius: 999, width: 17, height: 17,
                 fontSize: 11, fontWeight: 700, lineHeight: 1, padding: 0,
                 cursor: "pointer", marginLeft: 6, verticalAlign: "middle" }}>
        ?
      </button>
      {open && (
        <div style={{ marginTop: 6, padding: "9px 11px", borderRadius: 8,
                      background: "#F8FAFF", border: "1px solid #DBEAFE",
                      fontSize: 12.5, lineHeight: 1.55, color: "#374151",
                      whiteSpace: "normal" }}>
          {busy && <div style={{ color: "#6B7280" }}>Reading…</div>}
          {err && <div style={{ color: "#B45309" }}>{err}</div>}
          {d && d.available === false && (
            <div style={{ color: "#6B7280" }}>{d.reason}</div>
          )}
          {d && d.available && (
            <>
              <div style={{ fontWeight: 700, color: "#111827", marginBottom: 3 }}>
                {label || d.label}
              </div>
              <div style={{ marginBottom: 6 }}>{d.what}</div>
              <div style={{ marginBottom: 6, color: "#4B5563" }}>
                <b>Who decides:</b> {d.ai2?.who_signs || d.authority}
              </div>

              {/* Read and organised, rather than the top-scoring paragraph
                  pasted in and cut off where it happened to run out. Each
                  point carries the source it came from; a point citing
                  anything that was not supplied is dropped server-side. */}
              {(d.ai2?.rules || []).length > 0 && (
                <div style={{ margin: "8px 0" }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3,
                                color: "#6B7280", marginBottom: 4 }}>
                    WHAT THE RULES REQUIRE
                  </div>
                  {d.ai2.rules.map((r, i) => (
                    <div key={i} style={{ display: "flex", gap: 6, marginBottom: 5 }}>
                      <span style={{ color: "#1F4E79", flex: "0 0 auto" }}>•</span>
                      <span>
                        {r.point}
                        <span style={{ color: "#9CA3AF", fontSize: 11 }}>
                          {" "}— {r.source}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {(d.ai2?.watch_out || []).length > 0 && (
                <div style={{ margin: "8px 0", background: "#FFFBEB",
                              border: "1px solid #FDE68A", borderRadius: 6,
                              padding: "6px 9px" }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3,
                                color: "#92400E", marginBottom: 4 }}>
                    WATCH OUT
                  </div>
                  {d.ai2.watch_out.map((w, i) => (
                    <div key={i} style={{ color: "#92400E", marginBottom: 3 }}>{w}</div>
                  ))}
                </div>
              )}
              {d.ai2 && !d.ai2.available && d.ai2.reason && (
                <div style={{ fontSize: 11.5, color: "#9CA3AF", margin: "6px 0" }}>
                  {d.ai2.reason}
                </div>
              )}
              {/* Quoted with the file named, so it can be checked rather than
                  taken on trust. */}
              {d.sop_extract && (
                <div style={{ borderLeft: "3px solid #BFDBFE", paddingLeft: 9,
                              margin: "7px 0", color: "#4B5563",
                              fontStyle: "italic" }}>
                  {d.sop_extract.slice(0, 320)}
                  {d.sop_extract.length > 320 ? "…" : ""}
                  {(d.sop_files || []).length > 0 && (
                    <div style={{ fontStyle: "normal", fontSize: 11,
                                  color: "#9CA3AF", marginTop: 2 }}>
                      — {d.sop_files.join(", ")}
                    </div>
                  )}
                </div>
              )}
              {/* An absent quote and an unread document look the same on
                  screen, so the panel says which it is. */}
              {!d.sop_extract && d.sop_note && (
                <div style={{ fontSize: 11.5, color: "#9CA3AF", margin: "7px 0" }}>
                  {d.sop_note}
                </div>
              )}
              {d.values.length > 0 ? (
                <div style={{ marginTop: 7 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3,
                                color: "#6B7280", marginBottom: 4 }}>
                    VALUES IN USE
                  </div>
                  <div style={{ maxHeight: 170, overflowY: "auto",
                                display: "grid", gap: 3 }}>
                    {d.values.map((v, i) => (
                      <div key={`${v.value}-${i}`}
                           style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                        {onPick ? (
                          <button type="button" onClick={() => { onPick(v.value); setOpen(false); }}
                            style={{ border: "1px solid #C7D2FE", background: "#fff",
                                     color: "#1D4ED8", borderRadius: 5, fontSize: 11.5,
                                     padding: "1px 7px", cursor: "pointer",
                                     flexShrink: 0, maxWidth: 230, overflow: "hidden",
                                     textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {v.value}
                          </button>
                        ) : (
                          <span style={{ fontWeight: 600, flexShrink: 0 }}>{v.value}</span>
                        )}
                        <span style={{ color: "#6B7280", fontSize: 11.5 }}>
                          {v.note || (v.items ? `${v.items} item${v.items === 1 ? "" : "s"}` : "")}
                          {v.supplied_this_category ? " · has supplied this category" : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                  {d.values_basis && (
                    <div style={{ fontSize: 10.5, color: "#9CA3AF", marginTop: 5 }}>
                      {d.values_basis}
                    </div>
                  )}
                </div>
              ) : (
                /* No lookup, and it says so rather than dressing a sentence
                   up as an answer. */
                <div style={{ fontSize: 11.5, color: "#B45309", marginTop: 6 }}>
                  There is nothing to look up for this one — it is measured or
                  decided, not selected.
                </div>
              )}
              <div style={{ fontSize: 10.5, color: "#9CA3AF", marginTop: 7 }}>
                {d.note}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

/** Above this many options a scrolling list stops being a way to find things. */
const SEARCH_AT = 12;

/** A list you can type into.
 *
 *  The vendor field offers all 199 of KAPL's Books vendors, the Books category
 *  field 103 and the purchase account field 95. A native <select> holds every
 *  one of them and is still the wrong control at that length: it opens on "A
 *  CREATIVE SPOT", has no way to search, and reaching "Voltas" means scrolling
 *  past a hundred names. Complete and unusable are not the same thing.
 *
 *  Declared here rather than inside ValuePicker on purpose. A component
 *  defined in another component's body is a new type on every render, so React
 *  unmounts and remounts it — which is exactly the "I can only type one letter"
 *  fault reported on the description box, and it would come straight back here
 *  where every keystroke re-renders the parent.
 */
function ComboPicker({ value, options, onChange, style, placeholder, onOther,
                      closedNote, freeText, groups }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const box = useRef(null);
  //: Which way the list opens, and how tall it may be. Measured rather than
  //: assumed: the Supplier / Vendor question is the LAST field on the guided
  //: form, so a list that always dropped downwards ran off the bottom of the
  //: page and could not be scrolled to. It now opens upwards when there is
  //: more room above, and never asks for more height than there is.
  const [drop, setDrop] = useState({ up: false, max: 240 });

  const place = useCallback(() => {
    const el = box.current;
    if (!el || typeof window === "undefined") return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const up = below < 180 && above > below;
    setDrop({ up, max: Math.max(120, Math.min(280, up ? above : below)) });
  }, []);

  // Closing on a click elsewhere rather than on blur: blur fires before the
  // click on an option registers, which would close the list and swallow the
  // choice.
  useEffect(() => {
    if (!open) return;
    place();
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    // Re-measured while open, because scrolling the page moves the field out
    // from under the list it opened.
    document.addEventListener("mousedown", away);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", away);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  const needle = q.trim().toLowerCase();
  // Matched from the FIRST letter, not from anywhere in the value.
  //
  // KAPL, 14-Sep-2026: "if I type any letter it should consider searching it
  // from the first letter, not that the alphabet is included anywhere in the
  // list item value." Typing "a" was returning every vendor with an "a" in it
  // — which is nearly all 199 — so the list barely narrowed and the first
  // keystroke looked like it had done nothing.
  const hits = !needle ? options : options.filter(
    o => String(o.value || "").trim().toLowerCase().startsWith(needle));
  // What the old rule would have found and this one does not. Counted, not
  // listed: a name that IS in the catalogue must not read as absent just
  // because the search is stricter, and "Bombay Air Products" is a real answer
  // to "air" even though it does not start with it.
  const elsewhere = !needle ? 0 : options.filter(o => {
    const v = String(o.value || "").trim().toLowerCase();
    return !v.startsWith(needle) && v.includes(needle);
  }).length;
  // Alphabetical, because this is a list you READ down.
  //
  // It was most-used-first, which is the right order for a list of five and
  // the wrong one for 199 vendors: with no alphabetical run there is nowhere
  // for the eye to land, and a name you half-remember cannot be found by
  // looking. The frequency signal is not lost — every row still carries the
  // number of items using it, which is what separated a typo used once from a
  // standard used four hundred times.
  //
  // localeCompare with numeric so "SQM 10" sorts after "SQM 9", and so that
  // case does not split the list into an upper-case half and a lower-case one.
  const abc = (a, b) =>
    String(a.value || "").localeCompare(String(b.value || ""),
                                        undefined, { numeric: true,
                                                     sensitivity: "base" });
  // Grouped lists keep their groups — "For this item" above "Other items in
  // this subcategory" — each alphabetical, so which list a value came from is
  // still visible once the dropdown became searchable.
  // An option may also name its own group — "All vendors in KAPL's Zoho
  // Books" below this item's and this subcategory's vendors — which comes last.
  const g = groups || (options.some(o => o.group) ? { key: "here" } : null);
  const rank = o => (o.group ? 2 : o[g.key] ? 0 : 1);
  const ranked = g
    ? [0, 1, 2].flatMap(r => hits.filter(o => rank(o) === r).sort(abc))
    : [...hits].sort(abc);
  const shown = ranked.slice(0, 60);
  const nearCount = g ? shown.filter(o => rank(o) === 0).length : 0;
  const header = (i) => {
    if (!g) return null;
    const o = shown[i], prev = shown[i - 1];
    if (prev && rank(prev) === rank(o) && prev.group === o.group) return null;
    const label = o.group || (rank(o) === 0 ? g.near
      : (nearCount ? g.far : (g.farOnly || g.far)));
    return label ? (
      <div key={`h${i}`} style={{ padding: "6px 11px 3px", fontSize: 10.5,
                                  fontWeight: 600, color: "#6B7280",
                                  textTransform: "uppercase", letterSpacing: .3,
                                  background: "#F9FAFB" }}>{label}</div>
    ) : null;
  };

  const take = (v) => { onChange(v); setQ(""); setOpen(false); };

  const key = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi(h => Math.min(h + 1, shown.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && open && shown[hi]) { e.preventDefault(); take(shown[hi].value); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={box} style={{ position: "relative", marginBottom: style?.marginBottom ?? 9 }}>
      <input
        style={{ ...style, marginBottom: 0, width: "100%", boxSizing: "border-box" }}
        value={freeText ? (open ? q : (value || "")) : (open ? q : (value || ""))}
        placeholder={value ? value : (placeholder || `Type to search ${options.length}…`)}
        onFocus={() => { setOpen(true); setQ(freeText ? (value || "") : ""); setHi(0); }}
        onChange={e => {
          setQ(e.target.value); setOpen(true); setHi(0);
          // In freeText mode the typed text IS the answer, committed as it is
          // typed. The list only assists. These questions name controlled
          // registers KAPL have never issued, so the values offered are what
          // the catalogue already contains — a real aid against typing DAIKIN,
          // Daikin and DIAKIN into three items, and not a register that may
          // refuse a genuine new value.
          if (freeText) onChange(e.target.value);
        }}
        onKeyDown={key} />
      {value && !open && (
        <button onClick={() => onChange("")} title="Clear"
          style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                   border: 0, background: "transparent", color: "#9CA3AF",
                   cursor: "pointer", fontSize: 15, lineHeight: 1, padding: "0 4px" }}>×</button>
      )}
      {open && (
        <div style={{ position: "absolute", zIndex: 40, left: 0, right: 0,
                      ...(drop.up ? { bottom: "100%", marginBottom: 3 }
                                  : { top: "100%", marginTop: 3 }),
                      background: "#fff", border: "1px solid #E5E7EB",
                      borderRadius: 8, boxShadow: drop.up
                        ? "0 -8px 24px rgba(0,0,0,.10)"
                        : "0 8px 24px rgba(0,0,0,.10)",
                      maxHeight: drop.max, overflowY: "auto" }}>
          {shown.length === 0 && (
            <div style={{ padding: "9px 11px", fontSize: 12.5, color: "#9CA3AF" }}>
              {freeText
                ? `No item in the catalogue starts with “${q}” — yours would be the first.`
                : `Nothing starts with “${q}”.`}
            </div>
          )}
          {shown.map((o, i) => [header(i), (
            <div key={o.value} onMouseEnter={() => setHi(i)}
              onMouseDown={e => { e.preventDefault(); take(o.value); }}
              style={{ padding: "7px 11px", fontSize: 13, cursor: "pointer",
                       display: "flex", justifyContent: "space-between", gap: 10,
                       background: i === hi ? "#F3F4F6" : "#fff" }}>
              <span>{o.value}</span>
              {o.items > 0 && (
                <span style={{ color: "#9CA3AF", fontSize: 11, whiteSpace: "nowrap" }}>
                  {o.items} {o.unit || "item"}{o.items === 1 ? "" : "s"}
                </span>
              )}
            </div>
          )])}
          {/* The count is the honest part: it says how many were NOT drawn, so
              a name missing from the list reads as "narrow the search" rather
              than "this vendor does not exist". */}
          {ranked.length > shown.length && (
            <div style={{ padding: "6px 11px", fontSize: 11, color: "#9CA3AF",
                          borderTop: "1px solid #F3F4F6" }}>
              {ranked.length - shown.length} more — keep typing to narrow.
            </div>
          )}
          {elsewhere > 0 && (
            <div style={{ padding: "6px 11px", fontSize: 11, color: "#9CA3AF",
                          borderTop: "1px solid #F3F4F6" }}>
              {elsewhere} other {elsewhere === 1 ? "entry has" : "entries have"}
              {" "}“{q}” later in the name, not at the start.
            </div>
          )}
          {/* Offered only where a value off the list is a legal answer. A
              vendor or a unit the catalogue has not seen yet is legitimate and
              must be enterable. An account name is not: Books rejects one that
              is not in the chart of accounts, so a free-text box there would
              only produce a push that fails later. */}
          {onOther ? (
            <div onMouseDown={e => { e.preventDefault(); setOpen(false); onOther(); }}
              style={{ padding: "7px 11px", fontSize: 12.5, color: "#2563EB",
                       cursor: "pointer", borderTop: "1px solid #F3F4F6" }}>
              Other — type a value not in this list…
            </div>
          ) : closedNote ? (
            <div style={{ padding: "6px 11px", fontSize: 11, color: "#9CA3AF",
                          borderTop: "1px solid #F3F4F6" }}>{closedNote}</div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** An HSN / SAC box that says what each code IS.
 *
 *  KAPL, 27-Sep-2026: the HSN list was bare numbers, so a creator could not
 *  tell 85258030 from 85258090. Search by number, or by words — "cctv camera",
 *  "split air conditioner" — against the GST HSN/SAC directory, each result
 *  with its chapter › heading › description, the way Zoho Books' HSN finder
 *  shows it. This item's own codes (KAPL's candidates for the subcategory,
 *  codes its items and Zoho already use) come first and say why.
 */
function HsnPicker({ value, onChange, itemCode, subcategoryCode, compact, style, disabled }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState(null);
  const [cur, setCur] = useState(null);
  const box = useRef(null);

  // What the current value means, shown under the box.
  useEffect(() => {
    const c = String(value || "").replace(/\D/g, "");
    if (c.length < 2) { setCur(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      authFetch(`${API}/api/items/hsn/describe?code=${encodeURIComponent(c)}`)
        .then(r => (r.ok ? r.json() : null)).then(d => { if (alive) setCur(d); })
        .catch(() => { });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      const p = new URLSearchParams({ limit: "30" });
      if (q.trim()) p.set("q", q.trim());
      if (itemCode) p.set("item_code", itemCode);
      if (subcategoryCode) p.set("subcategory_code", subcategoryCode);
      authFetch(`${API}/api/items/hsn/search?${p}`)
        .then(r => (r.ok ? r.json() : null)).then(d => { if (alive) setRes(d); })
        .catch(() => { });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open, itemCode, subcategoryCode]);

  useEffect(() => {
    if (!open) return undefined;
    const off = e => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open]);

  const rows = (res && res.results) || [];
  return (
    <div ref={box} style={{ position: "relative", ...(compact ? { display: "inline-block",
                                                                   minWidth: 220 } : {}) }}>
      <input disabled={disabled}
        value={open ? q : (value || "")}
        placeholder={open ? "Type an HSN number or words — e.g. 8415, cctv camera" : "HSN / SAC — click to search"}
        onFocus={() => { setQ(""); setOpen(true); }}
        onChange={e => setQ(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Escape") setOpen(false);
          // A code typed in full is taken as it is, directory or not.
          if (e.key === "Enter" && /^[\d\s]{4,}$/.test(q.trim())) {
            onChange(q.replace(/\D/g, "")); setOpen(false);
          }
        }}
        style={{ ...(style || {}), width: "100%", boxSizing: "border-box" }} />
      {!open && cur && (cur.path_text || cur.description) && (
        <div style={{ fontSize: 11, color: "#6B7280", margin: compact ? "2px 0 0" : "-5px 0 9px",
                      lineHeight: 1.45 }}>
          {cur.type} {cur.code} — {cur.path_text || cur.description}
          {!cur.in_directory && " (not an exact code in the GST directory)"}
        </div>
      )}
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 50,
                      width: compact ? 560 : "100%", maxWidth: "90vw", maxHeight: 360,
                      overflowY: "auto", background: "#fff", border: "1px solid #CBD5E1",
                      borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", marginTop: 2 }}>
          {!res && <div style={{ padding: 10, fontSize: 12.5, color: "#9CA3AF" }}>Searching…</div>}
          {res && rows.length === 0 && (
            <div style={{ padding: 10, fontSize: 12.5, color: "#9CA3AF" }}>
              {q.trim() ? `Nothing matches “${q}”. Try other words, or the chapter number (e.g. 84).`
                        : "Type an HSN number or words to search the GST directory."}
            </div>
          )}
          {rows.map(r => (
            <div key={r.code}
              onMouseDown={e => { e.preventDefault(); onChange(r.code); setOpen(false); }}
              style={{ padding: "7px 11px", borderBottom: "1px solid #F3F4F6", cursor: "pointer",
                       background: r.for_this_item ? "#F0F9FF" : "#fff" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; }}
              onMouseLeave={e => { e.currentTarget.style.background = r.for_this_item ? "#F0F9FF" : "#fff"; }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                <b style={{ fontVariantNumeric: "tabular-nums", fontSize: 13 }}>{r.code}</b>
                <span style={{ fontSize: 10.5, color: "#6B7280" }}>{r.type}</span>
                {r.gst_rate ? <span style={{ fontSize: 10.5, color: "#047857" }}>GST {r.gst_rate}% in Zoho</span> : null}
                {r.for_this_item && (
                  <span style={{ fontSize: 10.5, color: "#0369A1", fontWeight: 600 }}>
                    for this item · {r.why.join(" · ")}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: "#374151", lineHeight: 1.45, marginTop: 2 }}>
                {r.path.map((p, i) => (
                  <span key={p.code} style={{ color: i === r.path.length - 1 ? "#111827" : "#6B7280" }}>
                    {i > 0 && " › "}{p.description.charAt(0) + p.description.slice(1).toLowerCase()}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {res && (
            <div style={{ padding: "6px 11px", fontSize: 10.5, color: "#9CA3AF" }}>
              From the {res.source}. Finance confirms the code; this only helps find it.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ValuePicker({ value, options, onChange, style, placeholder, groups,
                      emptyLabel, emptyNote }) {
  const opts = options || [];
  const inList = opts.some(o => (o.value || "") === (value || ""));
  // Free text stays open once opened, so a half-typed value is not swallowed
  // by a re-render deciding it now matches something.
  //
  // An empty list opens as free text too. Manufacturer is blank on all 13,882
  // items in KAPL's Books org and brand on all but two — checked against both
  // the list and the detail endpoint, the key is there and the value is "" —
  // so those two dropdowns have nothing to offer and never will until someone
  // fills them. Shown as an empty select they read as a control that failed to
  // load. `emptyNote` says which it is.
  const [free, setFree] = useState((Boolean(value) && !inList) || opts.length === 0);

  // Grouping is optional. The edit panel splits "used in this subcategory"
  // from "used elsewhere"; the composite form has no subcategory to split on,
  // so its lists are flat and every option sits in one run.
  const g = groups || null;
  const near = g ? opts.filter(o => o[g.key]) : [];
  const far = g ? opts.filter(o => !o[g.key]) : opts;
  // The option text is the value and nothing else.
  //
  // The count used to be appended, which put "VOLTAS · 300" in the closed
  // control — and a box that reads "VOLTAS · 300" looks like it is holding
  // that string, whatever is actually stored. The count is still worth
  // knowing, so it moves to a line underneath where it cannot be mistaken for
  // part of the value. The ordering already carries most of the same signal:
  // the values used most sit at the top.
  const chosen = opts.find(o => (o.value || "") === (value || ""));

  if (free) {
    return (
      <div style={{ display: "flex", gap: 6, marginBottom: style?.marginBottom ?? 9 }}>
        <input style={{ ...style, marginBottom: 0, flex: 1 }} value={value ?? ""}
          placeholder={placeholder || ""} autoFocus
          onChange={e => onChange(e.target.value)} />
        {opts.length === 0 && emptyNote && (
          <span style={{ fontSize: 11, color: "#9CA3AF", alignSelf: "center",
                         whiteSpace: "nowrap" }}>{emptyNote}</span>
        )}
        {opts.length > 0 && (
          <button onClick={() => setFree(false)} title="Choose from the list instead"
            style={{ border: "1px solid #E5E7EB", borderRadius: 7, background: "#fff",
                     color: "#6B7280", fontSize: 11.5, padding: "0 9px",
                     cursor: "pointer", whiteSpace: "nowrap" }}>List</button>
        )}
      </div>
    );
  }

  // Every list is searchable. KAPL, 27-Sep-2026: "it should search or display
  // the values when I start to type." Short and grouped lists used to fall
  // back to a native <select>, which cannot be typed into — and the edit
  // form's lists are all grouped, so none of them could be searched.
  if (opts.length > 0) {
    return (
      <>
        <ComboPicker value={value} options={opts} onChange={onChange} style={style}
          placeholder={placeholder || (emptyLabel ? String(emptyLabel) : undefined)}
          groups={g} onOther={() => setFree(true)} />
        {chosen?.note && (
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: -5, marginBottom: 9 }}>
            {chosen.note}
          </div>
        )}
      </>
    );
  }

  const select = (
    <select style={{ ...style, cursor: "pointer",
                     marginBottom: chosen?.items ? 5 : (style?.marginBottom ?? 9) }}
      value={value ?? ""}
      onChange={e => {
        if (e.target.value === "__other__") { setFree(true); return; }
        onChange(e.target.value);
      }}>
      <option value="">{emptyLabel || "— not set —"}</option>
      {g && near.length > 0 && (
        <optgroup label={g.near}>
          {near.map(o => <option key={o.value} value={o.value}>{o.value}</option>)}
        </optgroup>
      )}
      {far.length > 0 && (g
        ? <optgroup label={near.length ? g.far : g.farOnly || g.far}>
            {far.map(o => <option key={o.value} value={o.value}>{o.value}</option>)}
          </optgroup>
        : far.map(o => <option key={o.value} value={o.value}>{o.value}</option>))}
      <option value="__other__">Other — type a value…</option>
    </select>
  );

  return (
    <>
      {select}
      {/* Where the chosen value came from. The server says it where it can
          ("on 3 purchase orders for this item in Zoho"); otherwise the count. */}
      {chosen?.note ? (
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: -5, marginBottom: 9 }}>
          {chosen.note}
        </div>
      ) : chosen?.items > 0 && (
        <div style={{ fontSize: 11, color: "#9CA3AF",
                      marginTop: -5, marginBottom: 9 }}>
          on {chosen.items} item{chosen.items === 1 ? "" : "s"}{" "}
          {!g ? "in the catalogue"
              : chosen[g.key] ? "in this subcategory" : "elsewhere in the catalogue"}
        </div>
      )}
    </>
  );
}

function ItemEditFields({ item, spec, form, setForm, mode = "core" }) {
  const fld = { width: "100%", boxSizing: "border-box", padding: "7px 10px",
                fontSize: 13, border: "1px solid #E5E7EB", borderRadius: 7,
                marginBottom: 9, fontFamily: "inherit" };
  const lbl = { fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3,
                textTransform: "uppercase", color: "#9CA3AF", marginBottom: 3,
                display: "block" };
  const set = (k, v) => setForm({ ...form, [k]: v });

  if (!spec) return <div style={{ fontSize: 13, color: "#9CA3AF" }}>Loading fields…</div>;

  /** A field this account may not change, and the sentence that says why.
   *
   *  The server has always known: each stage owns its own fields, and the rule
   *  was checked on SAVE. So an SCM approver was shown the classification, the
   *  description and the HSN, filled them in, pressed Save and was told "Scm
   *  may not change classification… hsn_code (Finance)" — after the work. The
   *  answer was a list of notes at the foot of the form that disabled nothing.
   *
   *  Shown, not hidden: seeing the HSN is part of reviewing the item. Only the
   *  ability to change it belongs to someone else.
   */
  const lockedWhy = (key) => (spec.not_editable || {})[key] || null;
  const fldLocked = { ...fld, background: "#F3F4F6", color: "#6B7280",
                      borderStyle: "dashed", cursor: "not-allowed" };

  // KAPL, 27-Sep-2026: Edit shows the item's final fields — name, HSN, UOM,
  // vendors — and not its classification attributes, which are edited on the
  // Classification tab. One form, two views, so the two cannot drift apart.
  const core = mode !== "attributes";
  return (
    <>
      {core && (spec.fields || []).map(f => (
        <div key={f.key}>
          <label style={lbl}>
            {f.label}
            {lockedWhy(f.key) && (
              <span style={{ marginLeft: 6, fontWeight: 600, letterSpacing: 0,
                             textTransform: "none", color: "#B45309" }}>
                · not yours
              </span>
            )}
          </label>
          {f.type === "textarea" ? (
            <textarea style={{ ...(lockedWhy(f.key) ? fldLocked : fld), resize: "vertical" }}
              rows={3} disabled={!!lockedWhy(f.key)} title={lockedWhy(f.key) || undefined}
              value={form[f.key] ?? ""} onChange={e => set(f.key, e.target.value)} />
          ) : lockedWhy(f.key) ? (
            // A picker has no disabled state of its own, so a locked one is
            // rendered as the plain value it holds.
            <>
              <input style={fldLocked} disabled title={lockedWhy(f.key)}
                value={form[f.key] ?? ""} readOnly />
              <div style={{ fontSize: 11, color: "#B45309", marginBottom: 9 }}>
                {lockedWhy(f.key)}
              </div>
            </>
          ) : f.key === "hsn_code" ? (
            <HsnPicker value={form[f.key]} onChange={v => set(f.key, v)}
              itemCode={item?.item_code} style={{ ...fld, marginBottom: 2 }} />
          ) : f.options?.length ? (
            <>
              <ValuePicker value={form[f.key]} options={f.options}
                onChange={v => set(f.key, v)} placeholder={f.placeholder}
                style={{ ...fld, marginBottom: f.hint ? 2 : 9 }}
                groups={f.groups || { key: "here",
                          near: "Used in this subcategory",
                          far: "Used elsewhere in the catalogue",
                          farOnly: "Nothing recorded here yet — used elsewhere" }} />
              {f.hint && (
                <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 9 }}>
                  {f.hint}
                </div>
              )}
            </>
          ) : (
            <>
              <input style={{ ...fld, marginBottom: f.hint ? 2 : 9 }}
                type={f.type === "number" ? "number" : "text"}
                placeholder={f.placeholder || ""} value={form[f.key] ?? ""}
                onChange={e => set(f.key, e.target.value)} />
              {f.hint && (
                <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 9 }}>
                  {f.hint}
                </div>
              )}
            </>
          )}
        </div>
      ))}

      {!core && (spec.attributes || []).length > 0 && (
        <>
          <div style={{ ...lbl, marginTop: 6, marginBottom: 7, color: "#6B7280" }}>
            Classification — {spec.attributes.length} attribute(s)
            {lockedWhy("classification") && (
              <span style={{ color: "#B45309", fontWeight: 600, letterSpacing: 0,
                             textTransform: "none" }}>
                {" "}· not yours to change
              </span>
            )}
            {spec.schema_source === "fallback"
              ? " · this subcategory declares none, so these are the five the rest of the catalogue uses"
              : spec.schema_source === "item"
                ? " · as recorded on this item"
                : " from the item master"}
          </div>
          <div style={{ display: "grid",
                        gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))",
                        gap: "0 10px" }}>
            {spec.attributes.map(a => (
              <div key={a.key}>
                <label style={lbl}>
                  {a.label}{a.required && <span style={{ color: "#DC2626" }}> *</span>}
                  {!a.labelled && (
                    <span title="This attribute has no label in the item master."
                      style={{ color: "#B45309", fontWeight: 500 }}> · unlabelled</span>
                  )}
                </label>
                {/* Values already recorded are offered, not enforced: a
                    vocabulary that cannot grow stops the next genuine item.
                    The item master's own list first, then anything else the
                    subcategory is using that the workbook does not name —
                    those are two different claims and are labelled as such. */}
                {/* Every attribute is part of `classification`, which is one
                    field as far as ownership goes: SCM was refused the whole of
                    it on save. Locked together, for the same reason. */}
                {lockedWhy("classification") ? (
                  <input style={fldLocked} disabled readOnly
                    title={lockedWhy("classification")}
                    value={(form.__cls || {})[a.key] ?? ""} />
                ) : a.options?.length ? (
                  <ValuePicker value={(form.__cls || {})[a.key]} options={a.options}
                    onChange={v => setForm({ ...form,
                      __cls: { ...(form.__cls || {}), [a.key]: v } })}
                    style={fld}
                    groups={{ key: "declared",
                              near: "From the item master",
                              far: "Also in use in this subcategory",
                              farOnly: "In use in this subcategory" }} />
                ) : (
                  <input style={fld} value={(form.__cls || {})[a.key] ?? ""}
                    onChange={e => setForm({ ...form,
                      __cls: { ...(form.__cls || {}), [a.key]: e.target.value } })} />
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {core && (<>
      <div style={{ ...lbl, marginTop: 6, marginBottom: 7, color: "#6B7280" }}>Vendors</div>
      {/* Column headings, because the fields stopped labelling themselves.
          Vendor, brand and price were text boxes whose placeholders named
          them; a <select> has no placeholder, so the brand column became an
          unlabelled dropdown reading "— not set —" with nothing to say what
          was not set. */}
      {(form.__sup || []).length > 0 && (
        <div style={{ display: "grid",
              gridTemplateColumns: "2fr 1.4fr 1fr auto", gap: 8,
              marginBottom: 4 }}>
          {["Vendor", "Brand", "Price (INR)", ""].map((h, i) => (
            <div key={i} style={{ ...lbl, marginBottom: 0, fontSize: 10 }}>{h}</div>
          ))}
        </div>
      )}
      {(form.__sup || []).map((v, i) => (
        <div key={i} style={{ display: "grid",
              gridTemplateColumns: "2fr 1.4fr 1fr auto", gap: 8, alignItems: "start" }}>
          {["name", "brand", "price"].map(k => {
            const setK = val => { const n = [...form.__sup];
              n[i] = { ...v, [k]: val }; setForm({ ...form, __sup: n }); };
            // Vendors and brands come from the catalogue, so they can be
            // picked rather than remembered — "DAIKIN", "Daikin" and "DIAKIN"
            // are otherwise three vendors. Brand has almost no data yet, so it
            // stays a plain box until it does; an empty dropdown is a worse
            // control than a text field, not a better one.
            const opts = k === "price" ? null : (spec.supplier_options || {})[k];
            // Wrapped, one cell each. The picker renders a control plus a note
            // beneath it, and unwrapped those became two children of this grid
            // — which put the vendor's item count in the Brand column and slid
            // every field one place to the right.
            return (
              <div key={k}>
                {opts?.length ? (
                  <ValuePicker value={v[k]} options={opts} onChange={setK}
                    style={fld}
                    emptyLabel={k === "name" ? "— no vendor —" : "— no brand —"}
                    groups={spec.supplier_groups || { key: "here",
                              near: "Used in this subcategory",
                              far: "Used elsewhere in the catalogue",
                              farOnly: "Nothing recorded here yet — used elsewhere" }} />
                ) : (
                  <input style={fld} type={k === "price" ? "number" : "text"}
                    placeholder={k === "name" ? "Vendor name"
                                              : k[0].toUpperCase() + k.slice(1)}
                    value={v[k]} onChange={e => setK(e.target.value)} />
                )}
              </div>
            );
          })}
          <button title="Remove this vendor"
            onClick={() => setForm({ ...form, __sup: form.__sup.filter((_, j) => j !== i) })}
            style={{ ...fld, width: 34, cursor: "pointer", color: "#9CA3AF",
                     background: "#fff", padding: "7px 0" }}>×</button>
        </div>
      ))}
      <button onClick={() => setForm({ ...form,
          __sup: [...(form.__sup || []), { name: "", brand: "", price: "" }] })}
        style={{ border: "1px dashed #D1D5DB", background: "#fff", borderRadius: 7,
                 padding: "5px 12px", fontSize: 12, color: "#6B7280",
                 cursor: "pointer", marginBottom: 11, fontFamily: "inherit" }}>
        + Add vendor
      </button>
      </>)}

      <label style={lbl}>Why this was changed (recorded in the audit trail)</label>
      {/* Its own field now. It was wired to `remarks` — a fact about the item,
          not a note about the edit — and wiped it on every save. */}
      <input style={fld} value={form.__note ?? ""}
        placeholder="optional, but it is what the next person reads"
        onChange={e => set("__note", e.target.value)} />

      {/* Only the structural ones. A field owned by another stage now says so
          on the field itself, which is where somebody is about to type. */}
      {spec.not_editable && (
        <div style={{ fontSize: 11.5, color: "#9CA3AF", marginBottom: 11, lineHeight: 1.55 }}>
          {Object.entries(spec.not_editable)
            .filter(([, why]) => !String(why).includes("owns this field"))
            .map(([k, why]) => (
              <div key={k}><b style={{ color: "#6B7280" }}>{k}</b> — {why}</div>
            ))}
        </div>
      )}
    </>
  );
}


/** The Classification tab: the attributes, and where they are edited.
 *
 *  They moved here from Edit on the Overview (KAPL, 27-Sep-2026: Edit shows
 *  the final fields). Removing them outright would have left a wrong or
 *  placeholder attribute with nowhere to be corrected — the readiness check
 *  still flags them, and Engineering still owns them.
 */
function ClassificationPanel({ item, me, onSaved, children }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [spec, reloadSpec] = useItemFields(item.item_code);
  const locked = Boolean(item.is_locked);
  const mayEdit = HAS(me, "item.edit_any") || HAS(me, "item.edit_own");
  const canEditNow = mayEdit && (!locked || HAS(me, "item.edit_locked"));

  useEffect(() => {
    if (edit && spec && !form.__loaded) setForm({ ...blankEdit(item, spec), __loaded: true });
  }, [edit, spec]);        // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = editBody(form, spec, me, blankEdit(item, spec));
      // This view edits attributes only. The vendor rows are rebuilt from the
      // form on every save, so they must not ride along from a screen that
      // never showed them.
      delete body.suppliers;
      const r = await authFetch(`${API}/api/items/${item.item_code}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const saved = await jsonOrThrow(r, "Could not save");
      setMsg(zohoSyncNote(saved)); setEdit(false); reloadSpec(); onSaved && onSaved();
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: "#9CA3AF", flex: 1 }}>
          Classification attributes (fields vary by subcategory).
        </div>
        {msg && !edit && <span style={{ fontSize: 12, color: "#047857" }}>{msg}</span>}
        {canEditNow && !edit && (
          <Btn small outline color={KZ.blue} disabled={!spec}
            onClick={() => { setForm(blankEdit(item, spec)); setErr(""); setMsg(""); setEdit(true); }}>
            Edit
          </Btn>
        )}
      </div>
      {err && <div style={{ color: "#B91C1C", fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
      {edit ? (
        <>
          <ItemEditFields item={item} spec={spec} form={form} setForm={setForm} mode="attributes" />
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={save} disabled={busy} color={KZ.blue}>{busy ? "Saving…" : "Save"}</Btn>
            <Btn onClick={() => setEdit(false)} outline color="#6B7280">Cancel</Btn>
          </div>
        </>
      ) : typeof children === "function" ? children(spec) : children}
    </div>
  );
}


function ItemQuality({ item, me, q, reload, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  // The server's own test, not a guess from the status. Reading APPROVED or
  // RELEASED as "locked" declared 6,364 imported items signed off — so this
  // panel warned "This item is signed off" on an item whose Approved By was
  // blank, and demanded Super Admin rights the server would not have asked for.
  const locked = Boolean(item.is_locked);
  // The names are the server's, verbatim. Written with dots first time round
  // ("item.edit.any"), which HAS() simply never matched — so the Edit button
  // was absent for every user, including Super Admin, and nothing errored.
  const mayEdit = HAS(me, "item.edit_any") || HAS(me, "item.edit_own");
  const mayEditLocked = HAS(me, "item.edit_locked");
  const canEditNow = mayEdit && (!locked || mayEditLocked);

  const [spec, reloadSpec] = useItemFields(item.item_code);
  // Zoho Books may hold a different item under this very code.
  const [clash, setClash] = useState(null);
  useEffect(() => {
    authFetch(`${API}/api/items/${item.item_code}/zoho-conflict`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setClash(d && d.conflict ? d : null)).catch(() => { });
  }, [item.item_code]);

  // A conflict that has already been resolved leaves no trace in the check
  // above — the Books record was renamed, so the codes no longer collide and
  // nothing is reported. But the history is exactly what somebody reconciling
  // against an old purchase order needs, so it is said here.
  const [wasInZoho, setWasInZoho] = useState(null);
  useEffect(() => {
    let alive = true;
    authFetch(`${API}/api/items/retired-codes`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ item_codes: [item.item_code] }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive) setWasInZoho((d?.retired || {})[item.item_code] || null); })
      .catch(() => { });
    return () => { alive = false; };
  }, [item.item_code]);

  const open = () => {
    setForm(blankEdit(item, spec));
    setErr(""); setMsg(""); setEdit(true);
  };

  // If the field list lands after the form was opened, fill it in. Without
  // this the boxes stay empty while the labels appear, and saving would write
  // those blanks over the item.
  useEffect(() => {
    if (edit && spec && !form.__loaded) {
      setForm({ ...blankEdit(item, spec), __loaded: true });
    }
  }, [edit, spec]);        // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = editBody(form, spec, me, blankEdit(item, spec));
      const r = await authFetch(`${API}/api/items/${item.item_code}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not save.");
      setMsg(zohoSyncNote(d)); setEdit(false);
      reloadSpec();                       // or the next edit reverts this one
      onSaved ? onSaved(d) : reload();
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  };

  if (!q) return null;
  const tone = q.issues === 0 ? "ok" : q.blocking === 0 ? "warn" : "bad";
  const band = { ok: "#047857", warn: "#B45309", bad: "#B91C1C" }[tone];
  const bandBg = { ok: "#ECFDF5", warn: "#FFFBEB", bad: "#FEF2F2" }[tone];
  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {/* Named for the eight fields it reads. It used to be "Data quality
            100% · complete", which reads as a verdict on the item — and was
            shown beside checklists with nine fields still empty. */}
        <span style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
          Core master data
        </span>
        <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 12,
                       fontWeight: 700, color: band, background: bandBg }}
          title={q.note}>
          {q.checks - q.issues} of {q.checks} fields
        </span>
        {!q.signed_off && (
          <span title={q.provenance}
            style={{ padding: "2px 9px", borderRadius: 999, fontSize: 11,
                     fontWeight: 700, color: "#B45309", background: "#FFFBEB" }}>
            no approver on record
          </span>
        )}
        {canEditNow && !edit && (
          <button onClick={open} disabled={!spec}
            title={spec ? "Edit this item"
                        : "Loading the item's field list…"}
            style={{ marginLeft: "auto", padding: "5px 14px", fontSize: 12.5,
                     fontWeight: 700, borderRadius: 7, cursor: "pointer",
                     border: `1.5px solid ${KZ.blue}`, background: "#fff",
                     color: KZ.blue }}>
            Edit
          </button>
        )}
        {locked && !mayEditLocked && (
          <span style={{ marginLeft: "auto", fontSize: 11.5, color: "#9CA3AF" }}>
            Signed off — only a Super Admin may change it
          </span>
        )}
      </div>

      {/* Said on the item, because it decides what can be done with it: this
          one cannot go to Books under its own code, and deactivating the Zoho
          record would not release it. */}
      {clash && (
        <div style={{ marginTop: 10, background: "#FEF2F2",
                      border: "1px solid #FECACA", borderRadius: 8,
                      padding: "9px 12px", fontSize: 12.5, lineHeight: 1.55 }}>
          <div style={{ fontWeight: 700, color: "#B91C1C", marginBottom: 3 }}>
            Zoho Books uses this code for a different item
          </div>
          <div style={{ color: "#374151" }}>
            <b>here</b> {clash.ours}
          </div>
          <div style={{ color: "#374151" }}>
            <b>Zoho</b> {clash.theirs}
            {clash.zoho_status && (
              <span style={{ color: "#9CA3AF" }}> · {clash.zoho_status} in Books</span>
            )}
          </div>
          <div style={{ color: "#B45309", marginTop: 4 }}>{clash.note}</div>
        </div>
      )}
      {wasInZoho && !clash && (
        <div style={{ marginTop: 10, background: "#FFFBEB",
                      border: "1px solid #FDE68A", borderRadius: 8,
                      padding: "9px 12px", fontSize: 12.5, lineHeight: 1.55 }}>
          <div style={{ fontWeight: 700, color: "#92400E", marginBottom: 3 }}>
            This code was used for something else in Zoho Books
          </div>
          <div style={{ color: "#374151" }}>
            <b>{wasInZoho.name}</b>
          </div>
          <div style={{ color: "#78350F", marginTop: 3 }}>
            That record was renamed <code>{wasInZoho.sku}</code> to release the
            number for this item. It keeps its purchase history, and a purchase
            order raised against it before the change now shows the suffixed code.
          </div>
        </div>
      )}
      <div style={{ fontSize: 12.5, color: "#6B7280", marginTop: 6 }}>{q.note}</div>
      {!q.signed_off && (
        <div style={{ fontSize: 11.5, color: "#B45309", marginTop: 4 }}>
          {q.provenance}
        </div>
      )}

      {q.findings.length > 0 && (
        <div style={{ marginTop: 10, border: "1px solid #F3F4F6", borderRadius: 8,
                      overflow: "hidden" }}>
          {q.findings.map((f, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "auto 1fr",
                  gap: 10, padding: "7px 11px", borderBottom: "1px solid #F9FAFB",
                  fontSize: 12.5, alignItems: "baseline" }}>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px",
                             borderRadius: 4, whiteSpace: "nowrap",
                             color: f.level === "BLOCK" ? "#B91C1C" : "#B45309",
                             background: f.level === "BLOCK" ? "#FEF2F2" : "#FFFBEB" }}>
                {f.level === "BLOCK" ? "BLOCKS" : "NOTE"}
              </span>
              <span style={{ color: "#374151" }}>
                <b>{f.label}</b> — {f.message}
              </span>
            </div>
          ))}
        </div>
      )}

      {err && <div style={{ marginTop: 9, fontSize: 12.5, color: "#B91C1C" }}>{err}</div>}
      {msg && <div style={{ marginTop: 9, fontSize: 12.5, color: "#047857" }}>{msg}</div>}

      {edit && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #F3F4F6" }}>
          {locked && (
            <div style={{ fontSize: 11.5, color: "#B45309", background: "#FFFBEB",
                          border: "1px solid #FDE68A", borderRadius: 7,
                          padding: "7px 11px", marginBottom: 10, lineHeight: 1.5 }}>
              This item is signed off. Saving keeps it approved and moves the
              approval to your name — the name on an approval should be whoever
              vouches for the content as it now stands.
            </div>
          )}
          <ItemEditFields item={item} spec={spec} form={form} setForm={setForm} />

          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={save} disabled={busy} color={KZ.blue}>
              {busy ? "Saving…" : "Save"}
            </Btn>
            <Btn onClick={() => setEdit(false)} outline color="#6B7280">Cancel</Btn>
          </div>
        </div>
      )}
    </Card>
  );
}


/** Catalogues, datasheets, drawings, quotations and certificates on an item.
 *
 *  This tab said "no attachments" from the day it was written, so an engineer
 *  holding a supplier datasheet had nowhere to put it and the file lived in
 *  somebody's mail instead.
 *
 *  The kind is chosen, not guessed. A PDF can be a datasheet or a quotation,
 *  and only the person attaching it knows which — a quotation is commercially
 *  sensitive in a way a datasheet is not, and a list that cannot tell them
 *  apart is a list nobody can hand to a supplier.
 */
function Attachments({ item, me }) {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("datasheet");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Removing an attachment unlinks the bytes. One stray click should not be
  // able to do that, so the × asks first and the second click confirms.
  const [confirming, setConfirming] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    authFetch(`${API}/api/items/${item.item_code}/attachments`)
      .then(r => (r.ok ? r.json() : null)).then(setData).catch(() => { });
  }, [item.item_code]);
  useEffect(() => { load(); }, [load]);
  useLive(touchesItem(item.item_code, ["item_documents"]), load);

  const locked = Boolean(item.is_locked);
  const mayEdit = HAS(me, "item.edit_any") || HAS(me, "item.edit_own");
  const mayAttach = mayEdit && (!locked || HAS(me, "item.edit_locked"));

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      if (title.trim()) fd.append("title", title.trim());
      const r = await authFetch(`${API}/api/items/${item.item_code}/attachments`,
        { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not attach the file.");
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const remove = async (doc) => {
    setBusy(true); setErr(""); setConfirming(null);
    try {
      const r = await authFetch(`${API}/api/items/attachments/${doc.id}`,
        { method: "DELETE" });
      if (!r.ok) throw new Error((await r.json()).detail || "Could not remove it.");
      load();
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const size = (n) => (n == null ? "—"
    : n < 1024 ? `${n} B`
    : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB`
    : `${(n / 1024 / 1024).toFixed(1)} MB`);

  const TONE = {
    catalogue: "#1D4ED8", datasheet: "#047857", drawing: "#7C3AED",
    quotation: "#B45309", certificate: "#BE185D", other: "#6B7280",
  };

  if (!data) return <div style={{ color: "#9CA3AF", fontSize: 13 }}>Loading…</div>;
  const rows = data.attachments || [];

  return (
    <div>
      {mayAttach ? (
        <Card style={{ marginBottom: 14, background: "#F9FAFB" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select value={kind} onChange={e => setKind(e.target.value)}
              style={{ ...ES.input, width: "auto", minWidth: 140, padding: "6px 10px",
                       fontSize: 12.5 }}>
              {(data.kinds || []).map(k => (
                <option key={k} value={k}>{k[0].toUpperCase() + k.slice(1)}</option>
              ))}
            </select>
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="What is it? (optional)"
              style={{ ...ES.input, width: "auto", flex: 1, minWidth: 180,
                       padding: "6px 10px", fontSize: 12.5 }} />
            <input ref={fileRef} type="file" disabled={busy}
              onChange={e => upload(e.target.files?.[0])}
              style={{ fontSize: 12 }} />
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>
            {(data.accepted || []).join("  ")} · up to{" "}
            {Math.round((data.max_bytes || 0) / 1024 / 1024)} MB
            {busy && <b style={{ color: KZ.blue }}> · attaching…</b>}
          </div>
        </Card>
      ) : (
        <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12 }}>
          {locked
            ? "This item is approved and locked — only a Super Admin can attach to it."
            : "You do not have rights to attach files to this item."}
        </div>
      )}

      {err && <div style={{ color: "#B91C1C", fontSize: 12.5, marginBottom: 10 }}>{err}</div>}

      {rows.length === 0 ? (
        <div style={{ color: "#9CA3AF", fontSize: 13, padding: "8px 0" }}>
          Nothing attached yet.
        </div>
      ) : (
        // Catalogues, datasheets and certificates accumulate on a long-lived
        // item, and this sits inside a modal that cannot grow forever.
        <ScrollBox max="44vh"
                   style={{ border: "1px solid #F3F4F6", borderRadius: 8 }}>
          {rows.map(d => (
            <div key={d.id} style={{ display: "grid",
                  gridTemplateColumns: "94px 1fr auto auto", gap: 12,
                  alignItems: "center", padding: "9px 12px",
                  borderBottom: "1px solid #F9FAFB", fontSize: 13 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: .3,
                             textTransform: "uppercase", color: TONE[d.kind] || "#6B7280" }}>
                {d.kind}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#111827", wordBreak: "break-word" }}>
                  {d.title || d.filename}
                </div>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                  {d.title ? `${d.filename} · ` : ""}{size(d.size_bytes)}
                  {d.uploaded_by ? ` · ${d.uploaded_by}` : ""}
                  {d.uploaded_at ? ` · ${fmtIST(d.uploaded_at, "date")}` : ""}
                </div>
                {/* A row whose file is gone says so rather than serving an
                    empty download, which reads as a corrupt document. */}
                {d.missing && (
                  <div style={{ fontSize: 11, color: "#B91C1C", fontWeight: 600 }}>
                    the file is not on disk
                  </div>
                )}
              </div>
              {d.missing ? <span style={{ fontSize: 12, color: "#9CA3AF" }}>—</span> : (
                /* A button, not a link with the click intercepted.
                   The href was still there under the handler, so a
                   middle-click or "open in new tab" went straight to the URL
                   with no token and answered "Not authenticated." A control
                   that only works when clicked the expected way is a trap. */
                <button onClick={() => downloadAuthed(
                          `${API}/api/items/attachments/${d.id}/download`, d.filename)
                          .catch(e => setErr(String(e.message || e)))}
                   style={{ fontSize: 12.5, color: KZ.blue, fontWeight: 600,
                            cursor: "pointer", background: "transparent",
                            border: "none", padding: 0, fontFamily: "inherit" }}>
                  Download
                </button>
              )}
              {mayAttach ? (
                confirming === d.id ? (
                  <span style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                    <button onClick={() => remove(d)} disabled={busy}
                      style={{ border: "none", background: "transparent",
                               color: "#B91C1C", fontWeight: 700,
                               cursor: "pointer", fontSize: 12 }}>Delete</button>
                    <button onClick={() => setConfirming(null)}
                      style={{ border: "none", background: "transparent",
                               color: "#9CA3AF", cursor: "pointer",
                               fontSize: 12 }}>Keep</button>
                  </span>
                ) : (
                  <button onClick={() => setConfirming(d.id)} disabled={busy}
                    title="Detach and delete this file"
                    style={{ border: "none", background: "transparent",
                             color: "#9CA3AF", cursor: "pointer",
                             fontSize: 15 }}>×</button>
                )
              ) : <span />}
            </div>
          ))}
        </ScrollBox>
      )}
    </div>
  );
}


/** Where an item stands in KAPL-IM-SOP-03's workflow, and what it needs next.
 *
 *  §20 requires this to be "always visible in item detail": the current state
 *  AND the next required stage. Neither was shown anywhere — an item's page
 *  said PENDING_REVIEW and nothing about who, if anyone, was being waited on.
 *
 *  The eight SOP statuses are derived on the server rather than stored, so the
 *  vocabulary is visible without renumbering 14,700 live items that BOQs,
 *  purchase orders and Zoho already point at.
 */
function SopBanner({ code }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    authFetch(`${API}/api/items/by-code/${encodeURIComponent(code)}/routing`)
      .then(r => r.ok ? r.json() : null).then(setD).catch(() => { });
  }, [code]);
  useEffect(() => { load(); }, [load]);
  // A stage signing elsewhere flips its row here without a refresh.
  useLive(touchesItem(code), load);
  if (!d || !d.sop) return null;
  const s = d.sop;
  const tone = s.code === "RELEASED" || s.code === "ZOHO_SYNCED"
    ? { bg: "#ECFDF5", br: "#A7F3D0", fg: "#065F46" }
    : s.code === "REVIEW_CORRECTION"
      ? { bg: "#FEF2F2", br: "#FCA5A5", fg: "#B91C1C" }
      : s.code === "REVIEW_SUBMITTED"
        ? { bg: "#FFFBEB", br: "#FCD34D", fg: "#92400E" }
        : { bg: "#F3F4F6", br: "#E5E7EB", fg: "#4B5563" };
  const stateTone = { DONE: "#047857", OPEN: "#B45309", RETURNED: "#B91C1C" };
  return (
    <div style={{ background: tone.bg, border: `1px solid ${tone.br}`, borderRadius: 10,
                  padding: "11px 14px", marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, fontWeight: 700,
                       letterSpacing: .5, color: tone.fg }}>{s.code}</span>
        <span style={{ fontSize: 12.5, color: "#4B5563" }}>{s.meaning}</span>
        <div style={{ flex: 1 }} />
        {/* What the item may be USED for — the operative column of the SOP's
            status table, and what §18 turns into a control. */}
        {s.permitted_use && (
          <span style={{ fontSize: 11.5, fontWeight: 600,
                         color: s.controlled_use_ok ? "#047857" : "#B45309" }}>
            {s.permitted_use}
          </span>
        )}
        <span style={{ fontSize: 11, color: "#9CA3AF" }}>{s.reference}</span>
      </div>
      {s.next_label && (
        <div style={{ fontSize: 12.5, color: tone.fg, marginTop: 5 }}>
          <b>Next:</b> {s.next_label}
        </div>
      )}
      {/* §20 approval panel — Engineering → SCM → Finance, with the named
          approver, when, and what they said. */}
      {d.assignments?.length > 0 && (
        <div style={{ marginTop: 9, borderTop: `1px solid ${tone.br}`, paddingTop: 8 }}>
          {s.sequence.filter(x => x.state !== "NOT_STARTED").map(x => {
            // The row behind the state shown — not simply the newest, which can
            // be a superseded or withdrawn request.
            const a = [...d.assignments].reverse().find(y => y.stage === x.stage
                                                             && y.status === x.state);
            return (
              <div key={x.stage} style={{ display: "flex", gap: 8, fontSize: 12.5,
                                          padding: "2px 0", flexWrap: "wrap" }}>
                <span style={{ minWidth: 148, fontWeight: 600 }}>{x.label}</span>
                <span style={{ color: stateTone[x.state] || "#9CA3AF", fontWeight: 600,
                               minWidth: 78 }}>
                  {x.state === "NOT_REQUESTED" ? "NOT YET ASKED" : x.state}
                </span>
                <span style={{ color: "#6B7280" }}>
                  {/* Signer from the server: a request row, or the §12 signature
                      where no request recorded it. */}
                  {x.state === "DONE" && (x.by || a?.closed_by)
                    ? `${x.by || a.closed_by} · ${fmtIST(x.at || a?.closed_at)}`
                    : x.state === "RETURNED" && a?.closed_by
                      ? `${a.closed_by} — ${a.closed_note || "no comment"}`
                      : x.state === "OPEN" && a
                        ? `asked for ${(a.reason || []).map(z => z.label).join(", ") || "review"}`
                        : "—"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ItemDetail({ item, onBack, me }) {
  const [it, setIt] = useState(null);
  const [audit, setAudit] = useState([]);
  const [zoho, setZoho] = useState(null);
  const [comp, setComp] = useState(null);
  const [sales, setSales] = useState(null);
  const [tab, setTab] = useState("Overview");
  // Keyed on the item, so opening a different composite starts at its page 1
  // rather than at page 3 of the one before it.
  const compPage = usePaged(comp?.components, 25, item.code);
  const salesPage = usePaged(sales?.orders, 15, item.code);
  const auditPage = usePaged(audit, 30, item.code);
  const zohoStatus = useZohoStatus();
  const isAdmin = HAS(me, "item.edit_any");
  // The item and its history — what a status change elsewhere alters.
  const loadItem = useCallback(() => {
    authFetch(`${API}/api/items/${encodeURIComponent(item.code)}`).then(r => r.json()).then(setIt).catch(() => { });
    authFetch(`${API}/api/items/${encodeURIComponent(item.code)}/audit`).then(r => r.json()).then(d => setAudit(Array.isArray(d) ? d : [])).catch(() => { });
  }, [item.code]);
  useEffect(() => {
    loadItem();
    authFetch(`${API}/api/items/${encodeURIComponent(item.code)}/zoho`).then(r => r.json()).then(setZoho).catch(() => { });
    // A composite's parts. The endpoint has existed since composites were
    // built and nothing ever called it, so the screen showed the one-line
    // "Assembly of 2 item(s): A; B" rolled into the long description and no
    // way to reach either part. 404 is the ordinary answer for a plain item.
    authFetch(`${API}/api/items/${encodeURIComponent(item.code)}/composition`)
      .then(r => r.ok ? r.json() : null).then(setComp).catch(() => { });
    authFetch(`${API}/api/items/${encodeURIComponent(item.code)}/sales`)
      .then(r => r.ok ? r.json() : null).then(setSales).catch(() => { });
  }, [item.code, loadItem]);
  // Data quality is the server's reading, not a second one computed here.
  //
  // The version this replaces counted `status === "APPROVED"` as a completeness
  // check, so an item scored higher for having been approved — and since these
  // statuses were written by the importer rather than earned in the workflow,
  // the number meant to judge whether an item DESERVES approval was partly just
  // reporting that someone had already granted it. It also read `!!hsn_code`,
  // which counts "8415 (DRAFT)" as an HSN.
  //
  // One number, one method, and the same one the approval gate enforces — so
  // what the screen shows and what the server will allow cannot drift apart.
  const [q, setQ] = useState(null);
  const loadQ = useCallback(() => {
    authFetch(`${API}/api/items/${encodeURIComponent(item.code)}/readiness`)
      .then(r => (r.ok ? r.json() : null)).then(setQ).catch(() => { });
  }, [item.code]);
  useEffect(() => { loadQ(); }, [loadQ]);
  useLive(touchesItem(item.code), () => { loadItem(); loadQ(); });

  if (!it) return <EngModal title={item.code} onClose={onBack} maxWidth={900}><div style={{ color: "#9CA3AF", padding: 20 }}>Loading item…</div></EngModal>;

  const catCode = item.code.split("-")[0];
  const subCode = (item.code.split("-")[1] || "").split(".")[0];
  const cls = it.classification || {};
  const sup = it.suppliers || [];
  const sc = { RELEASED: KZ.green, DRAFT: "#9CA3AF", PENDING_REVIEW: "#F59E0B", APPROVED: KZ.blue, REJECTED: "#EF4444", OBSOLETE: "#374151" };
  const dt = (v) => fmtIST(v, "date");
  const guess = (obj, keys) => { for (const k of Object.keys(obj)) { if (keys.some(g => k.toLowerCase().includes(g))) return obj[k]; } return null; };
  const F = (label, val) => (<div style={{ marginBottom: 12 }}><div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div><div style={{ fontSize: 14, color: "#374151", marginTop: 2, wordBreak: "break-word" }}>{(val === 0 || val) ? val : "—"}</div></div>);
  const gapNote = (t) => <div style={{ color: "#9CA3AF", fontSize: 13, padding: "8px 0" }}>{t}</div>;

  const score = q ? q.score : null;
  const scoreColor = score === null ? "#9CA3AF"
    : score >= 80 ? KZ.green : score >= 50 ? KZ.orange : "#EF4444";

  const grid = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 20px" };
  const TABS = ["Overview", "Classification", "Manufacturer", "Pricing & Purchases", "Attachments", "Audit"];

  return (
    <EngModal
      title={it.item_code}
      onClose={onBack}
      maxWidth={900}
      // A signed-off item is not deleted. It may be on an issued BOQ or already
      // in Zoho, and both need to keep resolving it — so the offer is withdrawn
      // for APPROVED and RELEASED, where the controlled route is OBSOLETE
      // through the workflow. The server refuses either way; this stops the
      // button being there to press.
      actions={HAS(me, "item.delete")
                && !["APPROVED", "RELEASED"].includes(String(it.status)) ? (
        <DeleteButton
          what={`item ${it.item_code}`}
          onDelete={() => authFetch(`${API}/api/items/${it.item_code}`, { method: "DELETE" })}
          onDone={onBack} />
      ) : HAS(me, "item.delete") ? (
        <span style={{ fontSize: 11.5, color: "#9CA3AF" }}
          title="A signed-off item is withdrawn by moving it to OBSOLETE in the workflow, not by deleting it.">
          {String(it.status)} — withdraw via the workflow
        </span>
      ) : null}
    >
      {/* §20 — "always visible in item detail", so it sits above everything
          else rather than in a tab somebody has to find. */}
      <SopBanner code={item.code} />
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>{it.short_name}</div>
              <TrustChip trust={it.trust} />
              {/* Nothing on this screen said an item was composed of others.
                  It reads as an ordinary line until you notice the long
                  description, and by then you have already priced it as one. */}
              {comp && (comp.components || []).length > 0 && (
                <span title={`${comp.kind_label} of ${comp.components.length} item(s) from the master`}
                      style={{ background: "#EEF2FF", color: "#4338CA",
                               border: "1px solid #C7D2FE", borderRadius: 999,
                               padding: "2px 10px", fontSize: 11, fontWeight: 700,
                               whiteSpace: "nowrap" }}>
                  ⧉ {comp.kind_label || "Composite"} · {comp.components.length} parts
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>{catCode} {item.catName || ""} · {catCode}-{subCode} {item.subName || ""}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <Badge text={it.status} color={sc[it.status] || "#374151"} />
            {isAdmin && (
              <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                <ZohoChip item={it} configured={zohoStatus.configured} onSynced={(code, result) => setIt(prev => ({ ...prev, erp_item_id: result.zoho_item_id, crm_product_id: result.crm_product_id }))} />
              </div>
            )}
            <div style={{ marginTop: 8, fontSize: 11, color: "#6B7280", textTransform: "uppercase" }}
              title="HSN, description, classification, name, vendor, price, make, UOM. Not the stage checklists or the signatures.">
              Core master data
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: scoreColor }}>
              {score === null ? "—" : `${score}%`}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #F3F4F6" }}>
          <ItemFlow item={it} me={me} q={q}
            onMoved={u => { setIt(prev => ({ ...prev, ...u })); loadQ(); }} />
        </div>
      </Card>

      <div className="kz-scroll-x" style={{ display: "flex", gap: 2, overflowX: "auto", borderBottom: "1px solid #E5E7EB", marginBottom: 16 }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: "8px 14px", border: "none", borderBottom: tab === t ? `2px solid ${KZ.blue}` : "2px solid transparent", background: "transparent", color: tab === t ? KZ.blue : "#6B7280", fontWeight: tab === t ? 700 : 400, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>{t}</button>
        ))}
      </div>

      <ItemQuality item={it} me={me} q={q} reload={loadQ}
        onSaved={u => { setIt(prev => ({ ...prev, ...u })); loadQ(); }} />

      <Card>
        {tab === "Overview" && (
          <div className="kz-registers" style={grid}>
            {F("SKU", it.item_code)}
            {F("Category", `${catCode} ${item.catName || ""}`)}
            {F("Subcategory", `${catCode}-${subCode} ${item.subName || ""}`)}
            {F("Nomenclature", it.short_name)}
            {/* The screen hunted for classification["item_type"], and not one
                of 7,211 items has that key — KAPL do not define one. The
                P-labels are per subcategory: for a screw P4 is "Screw Type"
                and holds "Machine Screw". So the field read blank on every
                item while the answer sat one key away. */}
            {/* Zoho Books marks Unit mandatory, and this screen showed no unit
                at all — so an item Books would refuse outright looked complete
                here. */}
            {F("Unit (UOM)", it.uom)}
            {/* Populated from the Make/Vendor classification for 13,522 items.
                Worth its own row now that it holds something — it was NULL on
                every item when this grid was written. */}
            {F("Make", it.make)}
            {F("Status", it.status)}
            {F("Created", `${dt(it.created_at)} by ${it.requested_by || "—"}`)}
            {F("Last updated", dt(it.updated_at))}
            {F("Approved by", it.approved_by)}
            {F("ERP / CRM id", [it.erp_item_id, it.crm_product_id].filter(Boolean).join(" / "))}
            <div style={{ gridColumn: "1 / -1" }}>{F("Long description", it.full_description)}</div>
          </div>
        )}
        {/* ── What a composite is actually made of ─────────────────────────
            Laid out as Books lays out its own Associated Items panel, which is
            the screen KAPL compare this against: the part's name, its code
            beneath, what is known of it in Books, and the quantity on the
            right. The parts were previously only prose inside the long
            description — "Assembly of 2 item(s): 4 x LOUVERS …" — which cannot
            be scanned and cannot be totalled. */}
        {tab === "Overview" && comp && (comp.components || []).length > 0 && (
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #F3F4F6" }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 10 }}>
              Associated Items
            </div>
            <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12,
                            padding: "8px 14px", background: "#F9FAFB",
                            borderBottom: "1px solid #E5E7EB",
                            fontSize: 11, fontWeight: 600, color: "#6B7280",
                            letterSpacing: 0.4 }}>
                <div style={{ flex: 1 }}>ITEM DETAILS</div>
                <div style={{ width: 90, textAlign: "right" }}>QUANTITY</div>
              </div>
              {/* An eleven-part composite pushed everything below it off the
                  screen, the sales history included. Bounded, and paged once a
                  composite has more parts than a box this size can hold. */}
              <ScrollBox max="44vh">
              {compPage.slice.map((c, i) => (
                <div key={c.id} style={{ display: "flex", alignItems: "flex-start", gap: 12,
                                         padding: "12px 14px",
                                         borderTop: i === 0 ? "none" : "1px solid #F3F4F6" }}>
                  {/* Books shows a product photograph here and we have none,
                      so it showed the same grey ▤ against a HEPA filter, a
                      copper pipe and a cable tray. Drawn from what the item is
                      instead, in its category's colour. */}
                  <div style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 4,
                                background: "#F8FAFC", border: "1px solid #E5E7EB",
                                display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ItemGlyph code={c.item_code} name={c.description} size={26}
                      color={categoryColors[String(c.item_code).slice(0, 3)] || "#94A3B8"}
                      title={c.description} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: KZ.blue, fontWeight: 600, fontSize: 13,
                                  lineHeight: 1.35, wordBreak: "break-word" }}>
                      {c.description}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 3 }}>
                      [{c.item_code}]{c.unit || c.books_unit ? ` · ${c.unit || c.books_unit}` : ""}
                      {c.status ? ` · ${c.status}` : ""}
                      {c.is_composite ? " · composite" : ""}
                    </div>
                    {/* Books carries the stock, and only for a component it
                        actually holds. An unmatched part shows why it has no
                        figure rather than an empty line, because a blank reads
                        as "none in stock" and that is the opposite of true. */}
                    <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 4, lineHeight: 1.6 }}>
                      {!c.in_books ? (
                        <span style={{ color: "#B45309" }}>Not in Books — no stock or price held</span>
                      ) : (
                        <>
                          <div>Stock on hand: {c.stock_on_hand === null ? "not tracked"
                            : Number(c.stock_on_hand).toLocaleString("en-IN")}</div>
                          <div>
                            Selling: {c.selling_price === null ? "—" : `₹${inr0(c.selling_price)}`}
                            {"  ·  "}
                            Cost: {c.cost_source === "last_purchase_order"
                              ? `₹${inr0(c.last_purchase_rate)}`
                              : c.cost_price ? `₹${inr0(c.cost_price)}` : "—"}
                          </div>
                          {/* Where that cost came from, whenever it is not the
                              item card. KAPL's Books item holds 0 for these and
                              the price is on the purchase order that brought
                              them in — saying which PO, and when, is the
                              difference between a cost and a number. */}
                          {c.cost_source === "last_purchase_order" && (
                            <div style={{ color: "#B45309" }}>
                              cost from {c.last_purchase_po}
                              {c.last_purchase_date ? ` · ${c.last_purchase_date}` : ""}
                              {c.last_purchase_vendor ? ` · ${c.last_purchase_vendor}` : ""}
                              {" — the Books item card holds none"}
                            </div>
                          )}
                          {c.cost_source === "none" && c.in_books && (
                            <div style={{ color: "#B45309" }}>
                              no cost in Books and never purchased
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{ width: 90, textAlign: "right", fontWeight: 600,
                                fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                    {c.qty}
                  </div>
                </div>
              ))}
              </ScrollBox>
            </div>
            <Pager p={compPage} noun="part" />

            {/* What the parts come to. KAPL: "need to show purchase cost of
                composite item (sum of associated item purchase cost)".
                Extended by quantity, from Books, beside the selling total —
                the two together are what says whether the parent is priced
                above what it is made of. */}
            <div style={{ display: "flex", gap: 26, flexWrap: "wrap",
                          marginTop: 10, padding: "10px 14px",
                          background: "#F8FAFC", border: "1px solid #E2E8F0",
                          borderRadius: 8 }}>
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6B7280",
                              letterSpacing: 0.4 }}>PURCHASE COST OF PARTS</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827",
                              fontVariantNumeric: "tabular-nums" }}>
                  ₹{inr0(comp.purchase_total_with_po ?? comp.books_purchase_total ?? 0)}
                </div>
                {/* The Books item card is the basis; the last purchase order
                    fills in only where the card holds nothing. Said here
                    because the two are different claims and this total is then
                    a mixture of both. */}
                {comp.components_costed_from_po > 0 && (
                  <div style={{ fontSize: 10.5, color: "#B45309", marginTop: 2 }}>
                    incl. {comp.components_costed_from_po} part
                    {comp.components_costed_from_po === 1 ? "" : "s"} priced from
                    the last purchase order
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6B7280",
                              letterSpacing: 0.4 }}>SELLING VALUE OF PARTS</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827",
                              fontVariantNumeric: "tabular-nums" }}>
                  ₹{inr0(comp.books_selling_total || 0)}
                </div>
              </div>
              {/* The vendor-quote roll-up, kept and labelled rather than
                  dropped: it is a different claim from the Books cost and the
                  two disagree by a lot when the master has no quote. */}
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6B7280",
                              letterSpacing: 0.4 }}>FROM VENDOR QUOTES</div>
                <div style={{ fontSize: 15, fontWeight: 700,
                              color: comp.cost_is_partial ? "#B45309" : "#111827",
                              fontVariantNumeric: "tabular-nums" }}>
                  ₹{inr0(comp.rolled_up_cost || 0)}
                </div>
              </div>
            </div>

            {(comp.components_without_any_cost > 0 || comp.unpriced_components > 0) && (
              <div style={{ fontSize: 11.5, color: "#B45309", marginTop: 6 }}>
                {/* Only the parts with no cost ANYWHERE make the total read
                    low now. A part whose item card is blank but which has been
                    bought is priced, not missing, and counting it here said
                    the total was short when it was not. */}
                {comp.components_without_any_cost > 0 && (
                  <>{comp.components_without_any_cost} part(s) have no cost in Books and
                    have never been purchased, so the cost of parts reads low. </>
                )}
                {comp.unpriced_components > 0 && (
                  <>{comp.unpriced_components} have no vendor quote in the master.</>
                )}
              </div>
            )}

            <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 6 }}>
              {comp.kind_label} of {comp.components.length} item(s) from the master · per 1 {comp.unit || "unit"}
            </div>
          </div>
        )}
        {/* ── Sales Order Summary ──────────────────────────────────────────
            The panel Books shows under an item, and the one KAPL marked "need
            this sales History". Read from the sales-order mirror; an item that
            has never been on a sales order says so, and an unsynced mirror
            says THAT, because a flat line at zero means the opposite thing in
            each case. */}
        {tab === "Overview" && sales && (
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #F3F4F6" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10,
                          flexWrap: "wrap", marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
                Sales Order Summary (In INR)
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ fontSize: 12, color: "#6B7280" }}>Last 12 months</div>
            </div>

            {sales.mirror_empty ? (
              <div style={{ fontSize: 12.5, color: "#B45309",
                            background: "#FFFBEB", border: "1px solid #FDE68A",
                            borderRadius: 8, padding: "10px 12px" }}>
                No sales orders have been synced from Books yet, so this is not a
                record of nothing sold — it is a record of nothing read.
                Zoho Sync → Sync Books fills it.
              </div>
            ) : (
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "stretch" }}>
                <div style={{ flex: "1 1 360px", minWidth: 300 }}>
                  {(() => {
                    const ms = sales.months || [];
                    // Ordered AND billed. The graph drew sales orders only, so
                    // an item billed on an invoice that no sales order carries
                    // — four of KAPL's eight invoices have such lines — showed
                    // a Billed total beside an empty graph.
                    // And purchased (27-Sep-2026): A02-001.002 was bought for
                    // ₹19,000 and never sold, and the chart was blank.
                    const peak = Math.max(1, ...ms.map(m => Math.max(m.amount || 0, m.billed || 0, m.purchased || 0)));
                    const bar = (v, color) => (
                      <div style={{ flex: 1, height: `${Math.max(v ? 3 : 0, (v / peak) * 100)}%`,
                                    background: v ? color : "transparent",
                                    borderRadius: "3px 3px 0 0" }} />
                    );
                    return (
                      <div>
                        <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#6B7280",
                                      marginBottom: 6 }}>
                          <span><span style={{ display: "inline-block", width: 9, height: 9,
                            background: KZ.blue, borderRadius: 2, marginRight: 4 }} />Ordered</span>
                          <span><span style={{ display: "inline-block", width: 9, height: 9,
                            background: "#10B981", borderRadius: 2, marginRight: 4 }} />Billed</span>
                          <span><span style={{ display: "inline-block", width: 9, height: 9,
                            background: "#F59E0B", borderRadius: 2, marginRight: 4 }} />Purchased</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 4,
                                      height: 130, borderBottom: "1px solid #E5E7EB",
                                      paddingBottom: 2 }}>
                          {ms.map(m => (
                            <div key={m.month}
                                 title={`${m.month} — ordered ₹${inr0(m.amount)} (${m.orders} order(s)) · billed ₹${inr0(m.billed || 0)} · purchased ₹${inr0(m.purchased || 0)}`}
                                 style={{ flex: 1, display: "flex", alignItems: "flex-end",
                                          gap: 1, height: "100%" }}>
                              {bar(m.amount || 0, KZ.blue)}
                              {bar(m.billed || 0, "#10B981")}
                              {bar(m.purchased || 0, "#F59E0B")}
                            </div>
                          ))}
                        </div>
                        <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                          {ms.map(m => (
                            <div key={m.month} style={{ flex: 1, textAlign: "center",
                                                        fontSize: 9.5, color: "#9CA3AF" }}>
                              {m.month.slice(5)}
                            </div>
                          ))}
                        </div>
                        {/* Why there is nothing, which is not always the same
                            reason. "in the N lines synced so far" read as an
                            unfinished sync; every one of KAPL's 90 sales orders
                            and 8 invoices is mirrored. An item that was never
                            pushed to Zoho cannot appear on a document there,
                            and that has a different remedy — push it. */}
                        {sales.order_count === 0 && sales.invoice_count === 0 && (
                          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 8 }}>
                            {sales.in_zoho === false
                              ? "This item has never been pushed to Zoho, so it cannot appear on a sales order or an invoice there."
                              : sales.purchase_lines > 0
                                ? `Not sold yet — no Zoho sales order or invoice carries this item. Bought for ₹${inr0(sales.purchased_amount)} (${sales.purchase_lines} purchase line(s)), shown in amber; details under Pricing & Purchases.`
                                : `Not on any sales order or invoice — ${(sales.sales_lines_synced || 0).toLocaleString("en-IN")} order lines and ${(sales.invoice_lines_synced || 0).toLocaleString("en-IN")} invoice lines are mirrored.`}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div style={{ width: 200, flexShrink: 0 }}>
                  <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>Total Sales</div>
                  <div style={{ border: "1px solid #BFDBFE", background: "#EFF6FF",
                                borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontSize: 10.5, color: "#1D4ED8", fontWeight: 700,
                                  letterSpacing: 0.4 }}>ORDERED</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#111827",
                                  fontVariantNumeric: "tabular-nums", marginTop: 2 }}>
                      ₹{inr0(sales.total_amount)}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 4 }}>
                      {sales.order_count} order{sales.order_count === 1 ? "" : "s"}
                      {sales.total_qty ? ` · ${sales.total_qty} sold` : ""}
                    </div>
                  </div>
                  {/* Billed is a different number from ordered and KAPL have
                      both. Four of their eight invoices carry lines that are on
                      no sales order at all, so reading revenue from orders
                      alone could not see them. */}
                  <div style={{ border: "1px solid #A7F3D0", background: "#ECFDF5",
                                borderRadius: 8, padding: "10px 12px", marginTop: 8 }}>
                    <div style={{ fontSize: 10.5, color: "#047857", fontWeight: 700,
                                  letterSpacing: 0.4 }}>BILLED</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#111827",
                                  fontVariantNumeric: "tabular-nums", marginTop: 2 }}>
                      ₹{inr0(sales.billed_amount || 0)}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 4 }}>
                      {sales.invoice_count || 0} invoice{sales.invoice_count === 1 ? "" : "s"}
                      {sales.billed_qty ? ` · ${sales.billed_qty} billed` : ""}
                    </div>
                    {sales.invoices_excluded > 0 && (
                      <div style={{ fontSize: 10.5, color: "#B45309", marginTop: 4 }}>
                        {sales.invoices_excluded} draft or void invoice
                        {sales.invoices_excluded === 1 ? "" : "s"} not counted
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {(sales.orders || []).length > 0 && (
              <div className="kz-scroll-x" style={{ overflowX: "auto", marginTop: 14 }}>
                <table style={{ width: "100%", borderCollapse: "collapse",
                                fontSize: 12.5, minWidth: 560 }}>
                  <thead>
                    <tr style={{ color: "#6B7280", fontSize: 11 }}>
                      {["SALES ORDER", "DATE", "CUSTOMER", "STATUS", "QTY", "AMOUNT"].map((h, i) => (
                        <th key={h} style={{ padding: "6px 8px", fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {salesPage.slice.map(o => (
                      <tr key={o.so_number + String(o.date)} style={{ borderTop: "1px solid #F3F4F6" }}>
                        <td style={{ padding: "8px", fontFamily: "monospace", color: KZ.blue }}>
                          {o.so_number || "—"}</td>
                        <td style={{ padding: "8px" }}>{o.date ? dt(o.date) : "—"}</td>
                        <td style={{ padding: "8px" }}>{o.customer || "—"}</td>
                        <td style={{ padding: "8px" }}>{o.status || "—"}</td>
                        <td style={{ padding: "8px",
                                     fontVariantNumeric: "tabular-nums" }}>{o.qty}</td>
                        <td style={{ padding: "8px", fontWeight: 600,
                                     fontVariantNumeric: "tabular-nums" }}>₹{inr0(o.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pager p={salesPage} noun="order" />
              </div>
            )}

            {/* Invoices, listed separately rather than merged into the orders
                table. An order and an invoice are different documents and a
                single list of both, with a column that sometimes says
                KASO2627206 and sometimes KATI/2627/011, hides which is which. */}
            {(sales.invoices || []).length > 0 && (
              <div className="kz-scroll-x" style={{ overflowX: "auto", marginTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280",
                              letterSpacing: 0.4, marginBottom: 4 }}>INVOICES</div>
                <table style={{ width: "100%", borderCollapse: "collapse",
                                fontSize: 12.5, minWidth: 560 }}>
                  <thead>
                    <tr style={{ color: "#6B7280", fontSize: 11 }}>
                      {["INVOICE", "DATE", "CUSTOMER", "STATUS", "QTY", "AMOUNT"].map((h, i) => (
                        <th key={h} style={{ padding: "6px 8px", fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(sales.invoices || []).map(v => (
                      <tr key={v.invoice_number + String(v.date)}
                          style={{ borderTop: "1px solid #F3F4F6",
                                   opacity: v.counts_as_revenue ? 1 : 0.55 }}>
                        <td style={{ padding: "8px", fontFamily: "monospace", color: KZ.blue }}>
                          {v.invoice_number || "—"}</td>
                        <td style={{ padding: "8px" }}>{v.date ? dt(v.date) : "—"}</td>
                        <td style={{ padding: "8px" }}>{v.customer || "—"}</td>
                        <td style={{ padding: "8px" }}>
                          {v.status || "—"}
                          {/* A void invoice is not revenue and a draft is not
                              revenue yet; both are shown so that "never billed"
                              and "billed then cancelled" do not look alike. */}
                          {!v.counts_as_revenue && (
                            <span style={{ color: "#B45309", fontSize: 10.5, marginLeft: 5 }}>
                              not counted
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "8px",
                                     fontVariantNumeric: "tabular-nums" }}>{v.qty}</td>
                        <td style={{ padding: "8px", fontWeight: 600,
                                     fontVariantNumeric: "tabular-nums" }}>₹{inr0(v.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "Classification" && (
          <ClassificationPanel item={it} me={me}
            onSaved={() => { loadItem(); loadQ(); }}>
            {/* Read through the same field list as Edit, so the labels are the
                subcategory's ("Material / Service", not "p1") and an item made
                in Create Items shows its own answers only — not p1–p5 laid
                over them by the old template form (27-Sep-2026). */}
            {spec => Object.keys(cls).length === 0 ? gapNote("No classification captured.") : (
              <div className="kz-registers" style={grid}>
                {(spec?.attributes?.length
                  ? spec.attributes.filter(a => a.value !== null && a.value !== undefined
                                                && String(a.value).trim() !== "")
                      .map(a => [a.label, a.value])
                  : Object.entries(cls).map(([k, v]) => [k.replace(/_/g, " "), v]))
                  .map(([k, v]) => F(k, typeof v === "object" ? JSON.stringify(v) : String(v)))}
              </div>
            )}
          </ClassificationPanel>
        )}
        {tab === "Manufacturer" && (
          <div className="kz-registers" style={grid}>
            {F("Make / Brand", guess(cls, ["make", "brand"]) || (sup[0] && (sup[0].brand || sup[0].name)) || (zoho && zoho.price && zoho.price.brand))}
            {F("Manufacturer", guess(cls, ["manufacturer"]))}
            {F("Model number", guess(cls, ["model"]))}
            {F("Manufacturer part no.", guess(cls, ["part_no", "part number", "mpn"]))}
            {F("Series / Variant", guess(cls, ["series", "variant"]))}
            {F("Country of origin", guess(cls, ["country", "origin"]))}
            <div style={{ gridColumn: "1 / -1" }}>{gapNote("Datasheet ref, warranty, superseded model and replacement SKU need the item_models table (Section 8).")}</div>
          </div>
        )}
        {tab === "Pricing & Purchases" && (
          // `found` means Books holds a record under THIS code. An item whose
          // code was taken from a retired record has none — and that retired
          // record's purchases are the thing somebody most needs explained
          // here, so the panel is shown for it too rather than bailing out
          // with "nothing to show" over a screen full of history.
          <div>
            {/* The prices entered on the item itself. An item raised in the
                app carries its vendor and rate from the day it is created
                (A02-011.001: MSOFT SERV at ₹2,815), and this tab showed only
                "No Zoho Books record" — as if nothing was known about its
                cost. Books history, where there is any, follows. */}
            {sup.some(v => v && v.name) && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
                  Vendor prices recorded on this item
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead><tr style={{ color: "#6B7280", fontSize: 11 }}>
                    {["Vendor", "Brand", "Price (₹)"].map(h => (
                      <th key={h} className={ac(h)} style={{ padding: "5px 8px", borderBottom: "1px solid #E5E7EB" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {sup.filter(v => v && v.name).map((v, i) => {
                      const p = (v.last_prices || [])[0];
                      return (
                        <tr key={i}>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #F3F4F6" }}>{v.name}</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #F3F4F6" }}>{v.brand || "—"}</td>
                          <td style={{ padding: "5px 8px", borderBottom: "1px solid #F3F4F6" }}>
                            {p === undefined || p === null || p === "" ? "—" : inr0(p)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!zoho || (!zoho.found && !zoho.previous_holder)
              ? gapNote(sup.some(v => v && v.name)
                  ? "No Zoho Books record for this SKU yet, so no purchase history or GST from Books. It appears here once the item is pushed to Zoho and bought against."
                  : "No Zoho Books record for this SKU — no rate, GST or purchase history to show. Run a Zoho Books sync (Zoho Sync tab), or the item is not in Books.")
              : <ZohoItemBody data={zoho} masterName={it.short_name} />}
          </div>
        )}
        {tab === "Attachments" && <Attachments item={it} me={me} />}
        {tab === "Audit" && (
          audit.length === 0 ? gapNote("No audit entries recorded for this item.") : (
            // An item that has been through the workflow a few times has a
            // long trail, and it is the newest entries anybody opens this for.
            <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
              <ScrollBox max="52vh">
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["When", "Action", "By", "Change", "Notes"].map(h => <th key={h} className={ac(h)} style={{ padding: "8px 12px", fontSize: 11, color: "#6B7280", textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>{h}</th>)}</tr></thead>
                <tbody>
                  {auditPage.slice.map(a => (
                    <tr key={a.id}>
                      <td style={{ padding: "8px 12px", fontSize: 12, borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>{fmtIST(a.timestamp)}</td>
                      <td style={{ padding: "8px 12px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}><Badge text={a.action} color="#6366F1" /></td>
                      <td style={{ padding: "8px 12px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{a.performed_by}</td>
                      <td style={{ padding: "8px 12px", fontSize: 12, borderBottom: "1px solid #F3F4F6", color: "#6B7280" }}>{(a.old_value || a.new_value) ? `${JSON.stringify(a.old_value || {}).slice(0, 40)} → ${JSON.stringify(a.new_value || {}).slice(0, 40)}` : "—"}</td>
                      <td style={{ padding: "8px 12px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>{a.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </ScrollBox>
              <Pager p={auditPage} noun="entry" />
            </div>
          )
        )}
      </Card>
    </EngModal>
  );
}



/** Composing a new item out of items the master already holds.
 *
 *  Laid out as Zoho Books' own New Composite Item form, section for section,
 *  and every dropdown filled from KAPL's Books organisation rather than from a
 *  list typed here — their chart of accounts, their tax rates, their product
 *  categories, their vendors. A form offering a different set is a form that
 *  produces items Books will not take.
 *
 *  One field behaves differently and it is the point of this master. In Zoho
 *  you type a SKU; here the code is derived from the category, the subcategory
 *  and the next serial. It is shown, not typed. All 267 code collisions this
 *  system had to clean up came from a code somebody chose.
 */
/* ── small building blocks for the composite form ─────────────────────────
 *
 * At module scope, and they must stay here. These were declared inside
 * CompositeItem, which gave each of them a NEW function identity on every
 * render. React compares element types by identity, so `<Fld>` after a
 * keystroke was a different component from `<Fld>` before it: the old subtree
 * was unmounted and a fresh one mounted, taking the focused <input> with it.
 * The form accepted one character at a time and then dropped focus, and every
 * field on the screen was rebuilt on every keypress.
 *
 * They close over nothing, so there is no reason for them to live inside.
 * `T`, `Sel` and `Chk` do close over the form state and stay where they are —
 * they are called, not used as element types, so the elements they return are
 * plain <input>/<select>/<label> and their identity never changes.
 */
const L = ({ children, req }) => (
  <label style={{ fontSize: 12.5, color: req ? "#B91C1C" : "#4B5563",
                  fontWeight: req ? 600 : 500, display: "block",
                  marginBottom: 4 }}>
    {children}{req && " *"}
  </label>
);
const Row = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))",
                gap: "0 26px" }}>{children}</div>
);
const Fld = ({ label, req, children, hint }) => (
  <div style={{ marginBottom: 13 }}>
    <L req={req}>{label}</L>
    {children}
    {hint && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 3 }}>{hint}</div>}
  </div>
);
const Head = ({ children, note }) => (
  <div style={{ margin: "6px 0 12px" }}>
    <div style={{ fontSize: 15, fontWeight: 700, color: "#1F4E79" }}>{children}</div>
    {note && <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{note}</div>}
  </div>
);

function CompositeItem({ me }) {
  const [opts, setOpts] = useState(null);
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({
    category_code: "", subcategory_code: "", short_name: "", alias_name: "",
    kind: "ASSEMBLY", uom: "", hsn_code: "", tax_preference: "taxable",
    bom_revision: "", bom_reference: "",
    is_returnable: true, is_sellable: true, is_purchasable: true,
    selling_price: "", cost_price: "", mrp: "",
    sales_account_name: "Sales", purchase_account_name: "Purchase Account",
    sales_description: "", purchase_description: "", preferred_vendor: "",
    intra_state_tax: "", inter_state_tax: "",
    length_cm: "", width_cm: "", height_cm: "", weight_kg: "",
    manufacturer: "", brand: "", upc: "", mpn: "", ean: "", isbn: "",
    zoho_category: "", cf_category: "", cf_delivery_date: "", cf_percentage: "",
    track_bin_location: false, inventory_account_name: "Finished Goods",
    inventory_valuation_method: "fifo", reorder_point: "", remarks: "",
  });
  const [parts, setParts] = useState([]);
  // What the 18% default put in the tax fields. A prefill that nobody has
  // looked at satisfies Finance's "is there a default tax rate" gate exactly
  // as a considered choice does, and the gate then checks nothing. Keeping
  // what was prefilled lets the submit say which of the two this was.
  const prefilled = useRef({});
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  // A category or subcategory added elsewhere joins the lists here.
  useLive(["categories", "subcategories"], () => {
    authFetch(`${API}/api/categories/`).then(r => r.json())
      .then(d => setCats(Array.isArray(d) ? d : d.categories || [])).catch(() => { });
  });
  useEffect(() => {
    authFetch(`${API}/api/categories/`).then(r => r.json())
      .then(d => {
        const rows = Array.isArray(d) ? d : d.categories || [];
        setCats(rows);
        // A composite is classified A12 COMPOSITE ITEM — KAPL's own note on
        // this screen, and the A12 sheets of the updated workbook. Selected
        // rather than forced: the subcategory below is still a real choice
        // (Product / SO Item / Fabricated Outdoor), and an engineer who has a
        // reason to file one elsewhere can still say so.
        if (rows.some(c => c.code === "A12")) {
          setF(prev => prev.category_code ? prev : { ...prev, category_code: "A12" });
        }
      }).catch(() => { });
    authFetch(`${API}/api/items/composite/options`).then(r => r.json())
      .then(d => {
        setOpts(d);
        // KAPL: "default tax — it should only display 'GST' 18% for intra
        // state, and for interstate 'IGST' 18%." Set from their own Books
        // rates rather than typed here, and only when the field is untouched,
        // so a deliberate 5% is never overwritten.
        //
        // The 18% intra-state rate is a tax GROUP in Books and Zoho splits it
        // into CGST and SGST itself — which is why CGST and SGST are not
        // offered separately. KAPL: "later it can be separated as CGST & SGST
        // in BOQ etc."
        const pick = (re) => (d.taxes || []).find(
          t => re.test(String(t.name).trim()) && Number(t.percentage) === 18);
        const intra = pick(/^GST/i), inter = pick(/^IGST/i);
        prefilled.current = {
          intra_state_tax: intra ? `${intra.name} (${intra.percentage} %)` : "",
          inter_state_tax: inter ? `${inter.name} (${inter.percentage} %)` : "",
        };
        setF(prev => ({
          ...prev,
          intra_state_tax: prev.intra_state_tax
            || (intra ? `${intra.name} (${intra.percentage} %)` : ""),
          inter_state_tax: prev.inter_state_tax
            || (inter ? `${inter.name} (${inter.percentage} %)` : ""),
        }));
      })
      .catch(() => setOpts({ zoho_reachable: false }));
  }, []);

  // Books keeps at least one empty picker row at the foot of the table, and
  // "Add New Row" adds another. Each row is identified rather than indexed so
  // that removing one does not move the focus to a different row's search.
  // Drag a part to a different place in the bill. The order is the engineer's
  // — the housing before the fasteners that hold it — and it is now stored, so
  // dragging is not a rearrangement that vanishes on save.
  const dragRow = useRowDrag(parts, setParts);

  const [rows, setRows] = useState([1]);
  const [active, setActive] = useState(1);
  const [svc, setSvc] = useState(() => new Set());
  const nextRow = useRef(2);
  const addRow = () => {
    const id = nextRow.current++;
    setRows(r => [...r, id]); setActive(id); setQ(""); setHits([]);
  };
  const addServiceRow = () => {
    const id = nextRow.current++;
    setRows(r => [...r, id]); setSvc(v => new Set(v).add(id));
    setActive(id); setQ(""); setHits([]);
  };
  const closeRow = (id) => {
    setRows(r => (r.length > 1 ? r.filter(x => x !== id) : r));
    setSvc(v => { const n = new Set(v); n.delete(id); return n; });
    setQ(""); setHits([]);
  };

  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(() => {
      // "Add Services" is a service line in Books. Here the master already
      // separates them: A10 SERVICE and A11 JOB WORK. So the row searches the
      // same master and keeps only those, rather than inventing a second
      // catalogue of services that nothing else in the system knows about.
      authFetch(`${API}/api/items/search?q=${encodeURIComponent(q)}&limit=12`)
        .then(r => r.json()).then(d => {
          const all = d.items || [];
          setHits(svc.has(active)
            ? all.filter(x => /^A1[01]-/.test(String(x.item_code || "")))
            : all);
        })
        .catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, active, svc]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const cat = cats.find(c => c.code === f.category_code);
  const subs = cat?.subcategories || [];
  const sub = subs.find(x => x.code === f.subcategory_code);

  // The code, shown as it will be issued. Zoho has a box here; this has an
  // answer, because the two dropdowns above already decided it.
  const derivedSku = cat && sub
    ? `${cat.code}-${sub.code}.${String(sub.next_serial || 1).padStart(3, "0")}`
    : null;

  const total = parts.reduce((a, p) => a + (Number(p.qty) || 0) * (Number(p.rate) || 0), 0);
  const sellTotal = parts.reduce((a, p) => a + (Number(p.qty) || 0) * (Number(p.sell) || 0), 0);

  const addPart = (it) => {
    if (parts.some(p => p.item.id === it.id)) return;
    const v = (it.suppliers || []).find(s => s && (s.last_prices?.length || s.price));
    const rate = v ? Number(v.last_prices?.slice(-1)[0] ?? v.price ?? 0) : 0;
    // Books holds the selling price, the master does not, and the search that
    // fills this list reads the master. Left for the person composing the item
    // rather than defaulted to the cost, which would silently price the parts
    // at zero margin and look like a figure somebody had checked.
    setParts(p => [...p, { item: it, qty: 1, rate, sell: "", note: "" }]);
    setQ(""); setHits([]);
  };

  const create = async () => {
    setBusy(true); setErr("");
    const zoho = {};
    for (const k of ["alias_name", "tax_preference", "is_returnable", "is_sellable",
      "is_purchasable", "selling_price", "cost_price", "mrp",
      "sales_account_name", "purchase_account_name", "sales_description",
      "purchase_description", "preferred_vendor", "intra_state_tax",
      "inter_state_tax", "length_cm", "width_cm", "height_cm", "weight_kg",
      "manufacturer", "brand", "upc", "mpn", "ean", "isbn", "zoho_category",
      "cf_category", "cf_delivery_date", "cf_percentage", "track_bin_location",
      "inventory_account_name", "inventory_valuation_method", "reorder_point"]) {
      if (f[k] !== "" && f[k] !== null && f[k] !== undefined) zoho[k] = f[k];
    }
    // Untouched on both counts = nobody chose these, the default did.
    if (f.intra_state_tax === prefilled.current.intra_state_tax &&
        f.inter_state_tax === prefilled.current.inter_state_tax &&
        (f.intra_state_tax || f.inter_state_tax)) {
      zoho.tax_rate_source = "KAPL_DEFAULT_18";
    }
    try {
      const r = await authFetch(`${API}/api/items/composite`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category_code: f.category_code, subcategory_code: f.subcategory_code,
          short_name: f.short_name, kind: f.kind, uom: f.uom,
          hsn_code: f.hsn_code, make: f.manufacturer, remarks: f.remarks,
          bom_revision: f.bom_revision || null,
          bom_reference: f.bom_reference || null,
          zoho_fields: zoho,
          components: parts.map(p => ({ component_id: p.item.id,
                                        qty: Number(p.qty) || 0,
                                        note: p.note || null })),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "It could not be composed.");
      setDone(d); setParts([]);
      setF(p => ({ ...p, short_name: "", alias_name: "", hsn_code: "" }));
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const ready = f.category_code && f.subcategory_code && f.short_name.trim()
    && f.uom && parts.length > 0 && parts.every(p => Number(p.qty) > 0);

  const T = (k, ph, extra) => (
    <input style={{ ...ES.input, marginBottom: 0, ...(extra || {}) }} value={f[k]}
      placeholder={ph || ""} onChange={e => set(k, e.target.value)} />
  );
  // One helper, two controls. Most of these lists are three to seven entries
  // and a native select is right for them; the purchase account field is 95
  // and the Books category field 103, where a select is complete and unusable.
  // The switch is on length rather than on the field, so a list that grows
  // past the line gets a search box without anyone remembering to add one.
  const Sel = (k, list, blank) => {
    const rows = (list || []).map(o => ({
      value: String(o.key ?? o.value ?? o.name ?? o),
      label: String(o.label ?? o.name ?? o.value ?? o),
    }));
    if (rows.length > SEARCH_AT) {
      return (
        <ComboPicker value={f[k]} options={rows} onChange={v => set(k, v)}
          style={{ ...ES.input, marginBottom: 0 }}
          placeholder={`Type to search ${rows.length}…`}
          closedNote="From KAPL's Books org — Books will not accept another." />
      );
    }
    return (
      <select style={{ ...ES.input, marginBottom: 0 }} value={f[k]}
        onChange={e => set(k, e.target.value)}>
        {blank !== false && <option value="">{blank || "— select —"}</option>}
        {rows.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  };
  const Chk = (k, label, note) => (
    <label style={{ display: "flex", gap: 8, alignItems: "flex-start",
                    fontSize: 13, cursor: "pointer", marginBottom: 10 }}>
      <input type="checkbox" checked={!!f[k]} style={{ marginTop: 3 }}
        onChange={e => set(k, e.target.checked)} />
      <span><b style={{ fontWeight: 600 }}>{label}</b>
        {note && <span style={{ display: "block", color: "#6B7280",
                                fontSize: 12 }}>{note}</span>}</span>
    </label>
  );

  if (!opts) return <div style={{ color: "#9CA3AF", fontSize: 13 }}>Loading the
    options from Zoho…</div>;

  const acc = opts.accounts || { sales: [], purchase: [], inventory: [] };

  return (
    <div style={{ maxWidth: 1080 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 3 }}>
        New Composite Item
      </h2>
      <p style={{ color: "#6B7280", fontSize: 13, marginTop: 0, marginBottom: 14,
                  maxWidth: "80ch" }}>
        For a line on a customer's order the master has never held, where its
        parts are already here. Every dropdown below is filled from KAPL's own
        Zoho Books organisation, so what is composed here reaches Books complete.
      </p>

      {opts.options_source === "snapshot" && (
        <Card style={{ marginBottom: 14, background: "#EFF6FF",
                       border: "1px solid #BFDBFE" }}>
          <div style={{ fontSize: 13, color: "#1E40AF" }}>
            Zoho could not be reached just now, so the accounts, taxes,
            categories and vendors below are the last set Books gave us
            {opts.snapshot_saved_at
              ? `, taken ${fmtIST(opts.snapshot_saved_at * 1000)}`
              : ""}. A chart of accounts does not change between one day and the
            next, so composing an item now is safe — but a vendor or category
            added since will not be offered yet.
          </div>
        </Card>
      )}

      {opts.options_source === "unavailable" && (
        <Card style={{ marginBottom: 14, background: "#FFFBEB",
                       border: "1px solid #FDE68A" }}>
          <div style={{ fontSize: 13, color: "#92400E" }}>
            Zoho could not be reached and no stored copy has been taken yet, so
            the accounts, taxes, categories and vendors below are empty. Units,
            makes and brands still come from the local mirror. An item composed
            now will need those fields set in Books by hand.
          </div>
        </Card>
      )}

      {done && (
        <Card style={{ marginBottom: 14, background: "#F0FDF4",
                       border: "1px solid #86EFAC" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#166534" }}>
            {done.item_code} composed from {done.component_count} item(s).
          </div>
          <div style={{ fontSize: 12.5, color: "#166534", marginTop: 5 }}>
            A draft, owned by you, in the Approvals queue — composing an item
            does not approve it.
            {done.cost_is_partial &&
              ` The parts roll up to ${inr(done.rolled_up_cost)}, but ${done.unpriced_components} of them have no price, so that total reads low.`}
          </div>
        </Card>
      )}

      {/* ── Identity ─────────────────────────────────────────────────── */}
      <Card style={{ marginBottom: 12 }}>
        <Row>
          <Fld label="Name" req>{T("short_name", "What the customer is buying")}</Fld>
          <Fld label="Alias Name">{T("alias_name")}</Fld>
        </Row>

        <Fld label="Item Type" req>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {(opts.combo_types || []).map(k => {
              const key = k.key === "assembly" ? "ASSEMBLY" : "KIT";
              const on = f.kind === key;
              return (
                <button key={k.key} onClick={() => set("kind", key)}
                  style={{ flex: "1 1 280px", textAlign: "left", cursor: "pointer",
                           padding: "10px 13px", borderRadius: 8, fontFamily: "inherit",
                           border: `1.5px solid ${on ? KZ.blue : "#E5E7EB"}`,
                           background: on ? "#EFF6FF" : "#fff" }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700,
                                color: on ? KZ.blue : "#374151" }}>{k.label}</div>
                  <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                    {k.meaning}</div>
                </button>
              );
            })}
          </div>
        </Fld>

        <Row>
          <Fld label="Category (item master)" req
               hint="A composite is classified A12 COMPOSITE ITEM. Choose the
                     subcategory beside it — Product, SO Item or Fabricated
                     Outdoor.">
            <select style={{ ...ES.input, marginBottom: 0 }} value={f.category_code}
              onChange={e => { set("category_code", e.target.value);
                               set("subcategory_code", ""); }}>
              <option value="">— select —</option>
              {cats.map(c => <option key={c.code} value={c.code}>{c.code} {c.name}</option>)}
            </select>
          </Fld>
          <Fld label="Subcategory (item master)" req>
            <select style={{ ...ES.input, marginBottom: 0 }} value={f.subcategory_code}
              disabled={!cat} onChange={e => {
                const sc = e.target.value;
                // SOP-A12 §6 and §7 P2: the subcategory carries a default
                // composite mode — Product and Fabricated Outdoor are built
                // and stocked, so Assembly; an SO package is sold as one
                // grouping without consuming anything, so Kit. The reviewer
                // confirms it; this stops the wrong one being the default.
                const dflt = { "001": "ASSEMBLY", "002": "KIT", "003": "ASSEMBLY" }[sc];
                setF(prev => ({ ...prev, subcategory_code: sc,
                                ...(f.category_code === "A12" && dflt ? { kind: dflt } : {}) }));
              }}>
              <option value="">{cat ? "— select —" : "choose a category first"}</option>
              {subs.map(sc => <option key={sc.code} value={sc.code}>{sc.code} {sc.name}</option>)}
            </select>
          </Fld>
        </Row>

        {/* Zoho has a box here. This has an answer. */}
        <Fld label="SKU" req
             hint={`Derived from the category and subcategory above, like every
                  other code in the master. There is nothing to type.`}>
          <input readOnly value={derivedSku || "choose a category and subcategory"}
            style={{ ...ES.input, marginBottom: 0, background: "#F3F4F6",
                     color: derivedSku ? "#111827" : "#9CA3AF",
                     fontFamily: derivedSku ? "monospace" : "inherit",
                     fontWeight: derivedSku ? 700 : 400 }} />
        </Fld>

        <Row>
          <Fld label="Unit" req>
            <ValuePicker value={f.uom} options={opts.units || []}
              onChange={v => set("uom", v)} style={{ ...ES.input, marginBottom: 0 }}
              emptyLabel="— select a unit —" placeholder="Type a new unit" />
          </Fld>
          {/* Zoho's own item category was removed at KAPL's request
              (11-Sep-2026). A composite is classified by the master's
              category and subcategory above; carrying a second, unrelated
              category from Books invited two answers to one question. */}
        </Row>

        {Chk("is_returnable", "Returnable Item")}

        <Row>
          <Fld label="HSN Code">
            {/* Searchable by number or words against the GST HSN/SAC
                directory, this subcategory's codes first (HsnPicker). */}
            <HsnPicker value={f.hsn_code} onChange={v => set("hsn_code", v)}
              subcategoryCode={f.category_code && f.subcategory_code
                ? `${f.category_code}-${f.subcategory_code}` : undefined}
              style={{ ...ES.input, marginBottom: 0 }} />
          </Fld>
          <Fld label="Tax Preference" req>
            {Sel("tax_preference", opts.tax_preferences || [], false)}
          </Fld>
        </Row>
      </Card>

      {/* ── Associate Items ──────────────────────────────────────────────
          Laid out as Books lays out its own New Composite Item form, which is
          the screen KAPL compare this against: the picker is a ROW inside the
          table rather than a search box above it, the row adders sit in the
          footer beside the totals, and each price column is totalled under
          itself. */}
      <Card style={{ marginBottom: 12 }}>
        <Head note="Picked from the item master. A composite whose parts are free
                    text is a description, not a bill of materials. Drag ⠿ to
                    set the order the lines should read in — it is stored with
                    the bill.">
          Associate Items *
        </Head>

        {/* SOP-A12 §9 and App.A 10: a BOM must carry a revision and cite the
            drawing, SO, BOQ or specification it came from. Without them the
            bill cannot be approved against anything, and two parents with the
            same children and different revisions cannot be told apart later.
            They also fill the §8.2 description template. */}
        <Row>
          {/* These two are where a creator most needs telling. "R0" and
              "KASO2627002" are not guessable: the first is a sequence nobody
              explains, and the second is a real document out of ninety in
              Books that nothing offered. The help was only on the release
              banner, which a creator filling this form has not reached. */}
          <Fld label={<>BOM Revision<FieldHelp field="bom_revision"
                 onPick={v => set("bom_revision", v)} /></>}
               hint="SOP-A12 §9 — the controlled revision this bill is at, e.g. R0.">
            {T("bom_revision", "R0")}
          </Fld>
          <Fld label={<>Drawing / SO / BOQ reference<FieldHelp field="bom_reference"
                 onPick={v => set("bom_reference", v)} /></>}
               hint="SOP-A12 §9 — what this BOM cites. Not compulsory on a bill
                     that has not changed; required once it has, so the change
                     has a number and a date.">
            {T("bom_reference", "e.g. KASO2627002 or DRG-114 R2")}
          </Fld>
        </Row>

        <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse",
                          tableLayout: "fixed", minWidth: 680 }}>
            <colgroup>
              <col style={{ width: "auto" }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 128 }} />
              <col style={{ width: 128 }} />
              <col style={{ width: 34 }} />
            </colgroup>
            <thead><tr>{["Item Details", "Quantity", "Selling Price", "Cost Price", ""]
              .map(h => <th key={h} style={ES.th}>{h}</th>)}</tr></thead>
            <tbody>
              {parts.map((p, i) => (
                <tr key={p.item.id} {...dragRow.row(i)}>
                  <td style={{ ...ES.td, whiteSpace: "normal" }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <DragHandle {...dragRow.grip(i)} />
                      {/* The item's own mark rather than the grey ▤ this
                          carried. Seven parts down a bill all showed the same
                          tile, which told a reader nothing about what they
                          were looking at. */}
                      <div style={{ ...THUMB, color: categoryColors[
                            String(p.item.item_code).slice(0, 3)] || "#94A3B8" }}>
                        <ItemGlyph code={p.item.item_code} name={p.item.short_name}
                          size={22} title={p.item.short_name} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ wordBreak: "break-word", color: KZ.blue, fontWeight: 600 }}>
                          {p.item.short_name}
                        </div>
                        <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 2 }}>
                          [{p.item.item_code}]{p.item.uom ? ` · ${p.item.uom}` : ""}
                        </div>
                        <input placeholder="note — e.g. two per side" value={p.note}
                          style={{ ...ES.input, marginBottom: 0, marginTop: 5, fontSize: 12,
                                   display: "block" }}
                          onChange={e => setParts(a => a.map((x, j) =>
                            j === i ? { ...x, note: e.target.value } : x))} />
                      </div>
                    </div>
                  </td>
                  <td style={ES.td}>
                    <input type="number" min="0" step="any" value={p.qty}
                      style={{ ...ES.input, marginBottom: 0, textAlign: "right" }}
                      onChange={e => setParts(a => a.map((x, j) =>
                        j === i ? { ...x, qty: e.target.value } : x))} />
                  </td>
                  <td style={ES.td}>
                    <input type="number" min="0" step="any" value={p.sell} placeholder="0.00"
                      style={{ ...ES.input, marginBottom: 0, textAlign: "right" }}
                      onChange={e => setParts(a => a.map((x, j) =>
                        j === i ? { ...x, sell: e.target.value } : x))} />
                  </td>
                  <td style={ES.td}>
                    <input type="number" min="0" step="any" value={p.rate} placeholder="0.00"
                      style={{ ...ES.input, marginBottom: 0, textAlign: "right" }}
                      onChange={e => setParts(a => a.map((x, j) =>
                        j === i ? { ...x, rate: e.target.value } : x))} />
                    {!p.rate && <div style={{ fontSize: 10.5, color: "#B45309",
                                              marginTop: 2, lineHeight: 1.35 }}>
                      no price in the master</div>}
                  </td>
                  <td style={ES.td}>
                    <button title="Remove this row"
                      onClick={() => setParts(a => a.filter((_, j) => j !== i))}
                      style={{ border: "none", background: "transparent",
                               color: "#9CA3AF", cursor: "pointer", fontSize: 15 }}>×</button>
                  </td>
                </tr>
              ))}

              {/* The empty picker row. Books keeps one at the foot of the
                  table at all times, so selecting a part never means leaving
                  the table to find a search box somewhere above it. */}
              {rows.map((rid, ri) => (
                <tr key={`pick-${rid}`}>
                  <td style={{ ...ES.td, whiteSpace: "normal" }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <div style={THUMB}>▤</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <input autoFocus={ri === rows.length - 1 && q !== ""}
                          style={{ ...ES.input, marginBottom: 0 }}
                          value={active === rid ? q : ""}
                          onFocus={() => setActive(rid)}
                          onChange={e => { setActive(rid); setQ(e.target.value); }}
                          placeholder={svc.has(rid)
                            ? "Click to select a service — searches A10 / A11"
                            : "Click to select an item"} />
                        {active === rid && hits.length > 0 && (
                          <div style={{ border: "1px solid #E5E7EB", borderRadius: 8,
                                        marginTop: 6, maxHeight: 220, overflowY: "auto" }}>
                            {hits.map(h => (
                              <button key={h.id} onClick={() => { addPart(h); closeRow(rid); }}
                                style={{ width: "100%", textAlign: "left", padding: "7px 11px",
                                         border: "none", borderBottom: "1px solid #F9FAFB",
                                         background: "transparent", cursor: "pointer",
                                         font: "inherit", fontSize: 13 }}>
                                <span style={{ fontFamily: "monospace", color: KZ.blue,
                                               marginRight: 9 }}>{h.item_code}</span>{h.short_name}
                                <span style={{ color: "#9CA3AF", marginLeft: 7, fontSize: 11.5 }}>
                                  {h.uom || ""}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={ES.td}>
                    <input readOnly value="1" style={{ ...ES.input, marginBottom: 0,
                      textAlign: "right", background: "#F9FAFB", color: "#9CA3AF" }} />
                  </td>
                  <td style={ES.td}>
                    <input readOnly value="0.00" style={{ ...ES.input, marginBottom: 0,
                      textAlign: "right", background: "#F9FAFB", color: "#9CA3AF" }} />
                  </td>
                  <td style={ES.td}>
                    <input readOnly value="0.00" style={{ ...ES.input, marginBottom: 0,
                      textAlign: "right", background: "#F9FAFB", color: "#9CA3AF" }} />
                  </td>
                  <td style={ES.td}>
                    {rows.length > 1 && (
                      <button title="Remove this row" onClick={() => closeRow(rid)}
                        style={{ border: "none", background: "transparent",
                                 color: "#9CA3AF", cursor: "pointer", fontSize: 15 }}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>

            {/* Row adders and totals on one line, as Books has them. */}
            <tfoot>
              <tr>
                <td style={{ ...ES.td, borderTop: "1px solid #E5E7EB" }}>
                  <button onClick={addRow} style={ADDLINK}>⊕ Add New Row</button>
                  <button onClick={addServiceRow} style={{ ...ADDLINK, marginLeft: 18 }}>
                    ⊕ Add Services</button>
                </td>
                <td style={{ ...ES.td, borderTop: "1px solid #E5E7EB",
                             fontWeight: 700, color: "#6B7280", whiteSpace: "nowrap" }}>
                  Total (₹) :</td>
                <td style={{ ...ES.td, borderTop: "1px solid #E5E7EB",
                             fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                  {sellTotal.toFixed(2)}</td>
                <td style={{ ...ES.td, borderTop: "1px solid #E5E7EB",
                             fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                  {total.toFixed(2)}</td>
                <td style={{ ...ES.td, borderTop: "1px solid #E5E7EB" }} />
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* ── Sales / Purchase ─────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(330px,1fr))",
                    gap: 12, marginBottom: 12 }}>
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Head>Sales Information</Head>
            <div style={{ flex: 1 }} />
            <label style={{ fontSize: 12.5, cursor: "pointer" }}>
              <input type="checkbox" checked={f.is_sellable}
                onChange={e => set("is_sellable", e.target.checked)} /> Sellable
            </label>
          </div>
          <Fld label="Selling Price (INR)" req>
            <div style={{ display: "flex", gap: 7 }}>
              {T("selling_price", "", { flex: 1 })}
              <Btn small outline onClick={() => set("selling_price", String(sellTotal))}>
                Copy from total</Btn>
            </div>
          </Fld>
          <Fld label="MRP">{T("mrp")}</Fld>
          <Fld label="Account" req>
            {Sel("sales_account_name", acc.sales.map(a => a.name), false)}
          </Fld>
          <Fld label="Description">
            <textarea style={{ ...ES.input, marginBottom: 0, minHeight: 62,
                               resize: "vertical" }} value={f.sales_description}
              onChange={e => set("sales_description", e.target.value)} />
          </Fld>
        </Card>

        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Head>Purchase Information</Head>
            <div style={{ flex: 1 }} />
            <label style={{ fontSize: 12.5, cursor: "pointer" }}>
              <input type="checkbox" checked={f.is_purchasable}
                onChange={e => set("is_purchasable", e.target.checked)} /> Purchasable
            </label>
          </div>
          <Fld label="Cost Price (INR)" req>
            <div style={{ display: "flex", gap: 7 }}>
              {T("cost_price", "", { flex: 1 })}
              <Btn small outline onClick={() => set("cost_price", String(total))}>
                Copy from total</Btn>
            </div>
          </Fld>
          <Fld label="Account" req>
            {Sel("purchase_account_name", acc.purchase.map(a => a.name), false)}
          </Fld>
          <Fld label="Description">
            <textarea style={{ ...ES.input, marginBottom: 0, minHeight: 62,
                               resize: "vertical" }} value={f.purchase_description}
              onChange={e => set("purchase_description", e.target.value)} />
          </Fld>
          <Fld label="Preferred Vendor">
            <ValuePicker value={f.preferred_vendor}
              options={(opts.vendors || []).map(v => ({ value: v }))}
              onChange={v => set("preferred_vendor", v)}
              style={{ ...ES.input, marginBottom: 0 }}
              emptyLabel="— select a vendor —" placeholder="Type a new vendor" />
          </Fld>
        </Card>
      </div>

      {/* ── Default tax rates ────────────────────────────────────────── */}
      <Card style={{ marginBottom: 12 }}>
        <Head note="Applied when this item is billed. A GST rate is the
                    intra-state one and splits into CGST and SGST on the
                    invoice; IGST is the inter-state one.">Default Tax Rates</Head>
        <Row>
          {/* IGST is the inter-state tax. Matching only names that START with
              it put five of KAPL's own rates on the wrong side: theirs are
              named "SALES IGST 18%" and "Sales IGST18", so an inter-state rate
              was being offered as the intra-state one.

              Excluding IGST was necessary and not sufficient. It left KAPL's
              CGST and SGST rates on the intra-state list — the two HALVES of
              a GST rate, which Books does not accept as an item's default
              tax and which would have understated the tax by half if one were
              picked. The intra-state list is now the GST rates themselves,
              which is what Books shows on its own composite screen. */}
          <Fld label={<>Intra State Tax Rate<FieldHelp field="intra_state_tax" subcategory={f.category_code ? `${f.category_code}-${f.subcategory_code}` : null} /></>}>
            {Sel("intra_state_tax",
                 (opts.taxes || []).filter(t => /^GST/i.test(String(t.name).trim()))
                   .map(t => `${t.name} (${t.percentage} %)`))}
          </Fld>
          <Fld label={<>Inter State Tax Rate<FieldHelp field="inter_state_tax" subcategory={f.category_code ? `${f.category_code}-${f.subcategory_code}` : null} /></>}>
            {/* ^IGST rather than "contains IGST": their org also holds
                "SALES IGST 18%" and "Sales IGST18", which are the same rate
                under three names and made the list read as though there were
                a choice to make between them. */}
            {Sel("inter_state_tax",
                 (opts.taxes || []).filter(t => /^IGST/i.test(String(t.name).trim()))
                   .map(t => `${t.name} (${t.percentage} %)`))}
          </Fld>
        </Row>
      </Card>

      {/* ── Physical properties and trade identifiers ────────────────── */}
      <Card style={{ marginBottom: 12 }}>
        <Head note="What the item physically is, and the codes the trade knows it
                    by. All optional.">Dimensions &amp; Identifiers</Head>
        <Row>
          <Fld label="Dimensions (Length × Width × Height)">
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {T("length_cm", "L", { width: 84 })}<span style={{ color: "#9CA3AF" }}>×</span>
              {T("width_cm", "W", { width: 84 })}<span style={{ color: "#9CA3AF" }}>×</span>
              {T("height_cm", "H", { width: 84 })}
              <span style={{ fontSize: 12.5, color: "#6B7280" }}>cm</span>
            </div>
          </Fld>
          <Fld label="Weight">
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {T("weight_kg", "", { width: 120 })}
              <span style={{ fontSize: 12.5, color: "#6B7280" }}>kg</span>
            </div>
          </Fld>
        </Row>
        <Row>
          <Fld label="Manufacturer">
            <ValuePicker value={f.manufacturer} options={opts.manufacturers || []}
              onChange={v => set("manufacturer", v)}
              style={{ ...ES.input, marginBottom: 0 }}
              emptyLabel="— select a manufacturer —"
              placeholder="Type a new manufacturer"
              emptyNote="not filled on any Books item" />
          </Fld>
          <Fld label="Brand">
            <ValuePicker value={f.brand} options={opts.brands || []}
              onChange={v => set("brand", v)}
              style={{ ...ES.input, marginBottom: 0 }}
              emptyLabel="— select a brand —" placeholder="Type a new brand"
              emptyNote="not filled on any Books item" />
          </Fld>
        </Row>
        <Row>
          <Fld label="UPC">{T("upc")}</Fld>
          <Fld label="MPN">{T("mpn", "manufacturer part / model number")}</Fld>
        </Row>
        <Row>
          <Fld label="EAN">{T("ean")}</Fld>
          <Fld label="ISBN">{T("isbn")}</Fld>
        </Row>
      </Card>

      {/* ── KAPL's own custom fields on the Books item form ──────────── */}
      <Card style={{ marginBottom: 12 }}>
        <Head note="Fields KAPL added to their own Books item form. They are not
                    Zoho's, and what two of the three mean has not been settled
                    yet.">KAPL Custom Fields</Head>
        <Row>
          <Fld label="Delivery Date"
               hint="KAPL's own field (cf_delivery_date). What it means on an item
                     rather than an order has not been settled.">
            <input type="date" style={{ ...ES.input, marginBottom: 0 }}
              value={f.cf_delivery_date}
              onChange={e => set("cf_delivery_date", e.target.value)} />
          </Fld>
          {/* cf_category removed at KAPL's request (11-Sep-2026), with the
              Zoho one. It was the third category on one form and the only
              required one, so a composite could not be saved without
              answering a question nobody had defined. */}
        </Row>
        <Fld label="Percentage"
             hint="KAPL's own field (cf_percentage). Margin, discount or
                   completion — not settled.">
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {T("cf_percentage", "", { width: 140 })}
            <span style={{ fontSize: 12.5, color: "#6B7280" }}>%</span>
          </div>
        </Fld>
      </Card>

      {/* ── Additional information ───────────────────────────────────── */}
      <Card style={{ marginBottom: 14 }}>
        <Head>Additional Information</Head>
        {Chk("track_bin_location", "Track Bin location for this item",
             "Enable this option if you want to track the bin locations for this item while creating transactions")}
        <Row>
          <Fld label="Inventory Account" req>
            {Sel("inventory_account_name", acc.inventory.map(a => a.name), false)}
          </Fld>
          <Fld label="Inventory Valuation Method" req>
            {Sel("inventory_valuation_method", opts.valuation_methods || [], false)}
          </Fld>
        </Row>
        <Fld label="Reorder Point">{T("reorder_point", "", { width: 180 })}</Fld>
        <Fld label="Remarks (item master)">
          <textarea style={{ ...ES.input, marginBottom: 0, minHeight: 58,
                             resize: "vertical" }} value={f.remarks}
            onChange={e => set("remarks", e.target.value)} />
        </Fld>
      </Card>

      {err && <div style={{ color: "#B91C1C", fontSize: 13, marginBottom: 10 }}>{err}</div>}

      <div style={{ display: "flex", gap: 10, alignItems: "center",
                    flexWrap: "wrap", paddingBottom: 30 }}>
        <Btn onClick={create} disabled={!ready || busy} color={KZ.blue}>
          {busy ? "Saving…" : "Save"}
        </Btn>
        <Btn outline color="#6B7280" onClick={() => { setParts([]); setDone(null);
          setF(p => ({ ...p, short_name: "", alias_name: "", hsn_code: "" })); }}>
          Cancel
        </Btn>
        <span style={{ fontSize: 12, color: "#9CA3AF" }}>
          It will be a draft and go through the normal approval flow.
        </span>
      </div>
    </div>
  );
}


/** Importing items from a spreadsheet.
 *
 *  KAPL's point, and it decided the shape of this screen: a free-form upload
 *  only moves the guessing from the uploader to the parser. So the template is
 *  published first, from this screen, generated from the live database — the
 *  category list and the values already in use are only worth having if they
 *  are current.
 *
 *  Nothing is written on upload. The sheet is read and reported in full — every
 *  row, every problem — and a second, deliberate click imports. Nobody should
 *  discover on row 400 what row 1 could have told them.
 */
function ImportItems({ me }) {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [show, setShow] = useState("all");
  const fileRef = useRef(null);

  const template = async () => {
    setBusy("template"); setErr("");
    try {
      const r = await authFetch(`${API}/api/items/import/template`);
      if (!r.ok) throw new Error("The template could not be built.");
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = "Kaizen item import template.xlsx"; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(""); }
  };

  const send = async (path, f, setter) => {
    setBusy(path); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await authFetch(`${API}/api/items/import/${path}`,
        { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "The sheet could not be read.");
      setter(d);
      return d;
    } catch (e) { setErr(String(e.message || e)); return null; }
    finally { setBusy(""); }
  };

  const pick = async (f) => {
    if (!f) return;
    setFile(f); setReport(null); setDone(null);
    await send("preview", f, setReport);
  };

  const commit = async () => {
    const d = await send("commit", file, setDone);
    if (d) setReport(d);
  };

  const rows = report?.rows || [];
  const shown = show === "all" ? rows
    : show === "problems" ? rows.filter(r => !r.ok)
      : rows.filter(r => r.ok);

  const Stat = ({ n, label, tone }) => (
    <div style={{ flex: 1, minWidth: 110, padding: "10px 14px",
                  background: "#fff", border: "1px solid #F3F4F6",
                  borderRadius: 8 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: tone }}>{n}</div>
      <div style={{ fontSize: 11, color: "#9CA3AF", textTransform: "uppercase",
                    letterSpacing: .4 }}>{label}</div>
    </div>
  );

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 3 }}>
        Import Items
      </h2>
      <p style={{ color: "#6B7280", fontSize: 13, marginTop: 0, marginBottom: 18 }}>
        Fill in the template, upload it, check what it read, then import.
        Everything arrives as a draft and goes through the normal approval flow.
      </p>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap",
                      alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827",
                          marginBottom: 4 }}>1 · Start from the template</div>
            <div style={{ fontSize: 12.5, color: "#6B7280", marginBottom: 10 }}>
              It carries the columns this screen expects, every category and
              subcategory code, and the values already in use — so a new sheet
              matches what is in the master rather than inventing a second
              spelling of it.
            </div>
            <Btn onClick={template} disabled={busy === "template"} color={KZ.blue}>
              {busy === "template" ? "Building…" : "Download the template"}
            </Btn>
          </div>
          <div style={{ width: 1, alignSelf: "stretch", background: "#F3F4F6" }} />
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827",
                          marginBottom: 4 }}>2 · Upload the filled-in sheet</div>
            <div style={{ fontSize: 12.5, color: "#6B7280", marginBottom: 10 }}>
              Nothing is written yet. You will see every row it read and every
              problem it found before anything is imported.
            </div>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm"
              disabled={Boolean(busy)}
              onChange={e => pick(e.target.files?.[0])}
              style={{ fontSize: 12.5 }} />
            {busy === "preview" && (
              <div style={{ fontSize: 12, color: KZ.blue, marginTop: 6 }}>
                Reading the sheet…
              </div>
            )}
          </div>
        </div>
      </Card>

      {err && (
        <Card style={{ marginBottom: 16, background: "#FEF2F2",
                       border: "1px solid #FCA5A5" }}>
          <div style={{ color: "#B91C1C", fontSize: 13 }}>{err}</div>
        </Card>
      )}

      {done && (
        <Card style={{ marginBottom: 16, background: "#F0FDF4",
                       border: "1px solid #86EFAC" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#166534" }}>
            {done.message}
          </div>
          {done.created > 0 && (
            <div style={{ fontSize: 12.5, color: "#166534", marginTop: 6 }}>
              They are drafts, owned by you, in the Approvals queue under your
              name. Nothing has been approved.
              <div style={{ marginTop: 6, fontFamily: "monospace", fontSize: 11.5,
                            wordBreak: "break-all" }}>
                {(done.created_codes || []).join("  ")}
              </div>
            </div>
          )}
        </Card>
      )}

      {report && (
        <Card>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap",
                        marginBottom: 14 }}>
            <Stat n={report.read} label="rows read" tone="#111827" />
            <Stat n={report.ready} label="ready to import" tone="#047857" />
            <Stat n={report.rejected} label="held back" tone="#B91C1C" />
            <Stat n={report.warnings} label="worth a look" tone="#B45309" />
          </div>

          {!done && report.ready > 0 && (
            <div style={{ marginBottom: 14 }}>
              <Btn onClick={commit} disabled={Boolean(busy)} color="#047857">
                {busy === "commit" ? "Importing…"
                  : `Import ${report.ready} item${report.ready === 1 ? "" : "s"} as drafts`}
              </Btn>
              {report.rejected > 0 && (
                <span style={{ fontSize: 12.5, color: "#6B7280", marginLeft: 10 }}>
                  The {report.rejected} row{report.rejected === 1 ? "" : "s"} with
                  problems will be left out. Fix the sheet and upload it again to
                  bring {report.rejected === 1 ? "it" : "them"} in.
                </span>
              )}
            </div>
          )}
          {!done && report.ready === 0 && (
            <div style={{ fontSize: 13, color: "#B91C1C", marginBottom: 14 }}>
              No row is clean, so there is nothing to import. Every problem is
              listed below.
            </div>
          )}

          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {[["all", `All ${report.read}`],
              ["problems", `Problems ${report.rejected}`],
              ["ready", `Ready ${report.ready}`]].map(([k, l]) => (
              <button key={k} onClick={() => setShow(k)}
                style={{ padding: "5px 11px", fontSize: 12, borderRadius: 8,
                         fontWeight: 600, cursor: "pointer",
                         background: show === k ? "#111827" : "#fff",
                         color: show === k ? "#fff" : "#6B7280",
                         border: "1px solid #E5E7EB" }}>{l}</button>
            ))}
          </div>

          <div style={{ overflowX: "auto" }}>
            {/* A price list runs to thousands of rows; bounded so the filters above it stay reachable. */}
<ScrollBox max="56vh">
<table style={{ width: "100%", borderCollapse: "collapse",
                            fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: "#F9FAFB" }}>
                  {["Row", "Item code", "Name", "Make", "HSN", "What it says"]
                    .map(h => (
                    <th key={h} className={ac(h)} style={{ padding: "7px 9px",
                          fontSize: 10.5, textTransform: "uppercase",
                          letterSpacing: .4, color: "#9CA3AF",
                          borderBottom: "1px solid #E5E7EB" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map(r => (
                  <tr key={r.row_no} style={{ borderBottom: "1px solid #F9FAFB",
                        background: r.ok ? "transparent" : "#FEF2F2" }}>
                    <td style={{ padding: "7px 9px", color: "#9CA3AF" }}>{r.row_no}</td>
                    <td style={{ padding: "7px 9px", fontFamily: "monospace",
                                 whiteSpace: "nowrap" }}>
                      {r.item_code || "—"}
                      {/* An assigned number is not a number the uploader chose,
                          and they should not find that out after importing. */}
                      {r.assigned && (
                        <div style={{ fontSize: 10, color: "#9CA3AF" }}>assigned</div>
                      )}
                    </td>
                    <td style={{ padding: "7px 9px" }}>
                      {r.short_name || <span style={{ color: "#9CA3AF" }}>
                        built from P1–P5</span>}
                    </td>
                    <td style={{ padding: "7px 9px", color: "#6B7280" }}>{r.make}</td>
                    <td style={{ padding: "7px 9px", color: "#6B7280" }}>
                      {r.hsn || "—"}
                    </td>
                    <td style={{ padding: "7px 9px" }}>
                      {r.problems.map((p, i) => (
                        <div key={i} style={{ color: "#B91C1C" }}>{p}</div>
                      ))}
                      {r.warnings.map((w, i) => (
                        <div key={i} style={{ color: "#B45309" }}>{w}</div>
                      ))}
                      {!r.problems.length && !r.warnings.length && (
                        <span style={{ color: "#047857" }}>Ready.</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
</ScrollBox>
          </div>
        </Card>
      )}
    </div>
  );
}


// ── Classify & Create ─────────────────────────────────────────────────────────
// ── Guided Q&A item creation ──────────────────────────────────
// The flow the controlled specification describes, for someone who does not
// know what an attribute is. They describe the item; the AI proposes where it
// belongs and fills in what the description already says; the app asks only for
// what is genuinely missing, in KAPL's own wording, using the label that
// subcategory uses. Every question here comes from the loaded document — none
// of this file decides what to ask.

const QA_STATE = {
  PREFILL: { label: "Found", bg: "#ECFDF5", fg: "#047857", bd: "#A7F3D0" },
  CONFIRM: { label: "Check this", bg: "#FFFBEB", fg: "#B45309", bd: "#FDE68A" },
  ASK: { label: "Needed", bg: "#F3F4F6", fg: "#6B7280", bd: "#E5E7EB" },
};

function GuidedQA({ me }) {
  const [step, setStep] = useState(1);
  // Goods or service, answered before anything is described.
  //
  // KAPL: "for each item (excluding composite items) it should be identified as
  // Goods or Service at very beginning, and based on whether its Good or
  // Service the related sections like Inventory should be displayed
  // accordingly." It is not decoration — it decides which subcategories may be
  // proposed at all, so a service cannot be routed into a goods category.
  const [kind, setKind] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [cands, setCands] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [sub, setSub] = useState(null);
  const [stages, setStages] = useState([]);
  const [vals, setVals] = useState({});
  const [edited, setEdited] = useState({});
  const [built, setBuilt] = useState(null);
  const [ack, setAck] = useState(false);
  // What has already been answered to each question, to offer while typing.
  // Suggestions, never a restriction — see /api/items/suggest.
  const [sugg, setSugg] = useState({});
  const [done, setDone] = useState(null);
  const [picker, setPicker] = useState(false);
  // Search across every category and subcategory — code or name.
  const [subQ, setSubQ] = useState("");
  const [allSubs, setAllSubs] = useState([]);
  // Deviating from the issued specification. Super admin only — the capability
  // is not granted to ADMIN, so this whole control is absent for everyone else.
  const canOverride = HAS(me, "qa_spec.override");
  const [reqEdit, setReqEdit] = useState(null);   // the stage being changed
  const [reqReason, setReqReason] = useState("");
  const [reqBusy, setReqBusy] = useState(false);

  const post = async (path, body) => {
    const r = await authFetch(`${API}/api/guided/${path}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      let m = `Request failed (${r.status})`;
      try { const j = await r.json(); m = typeof j.detail === "string" ? j.detail : (j.detail?.message || m); } catch (_) { }
      throw new Error(m);
    }
    return r.json();
  };

  const reset = () => {
    setStep(1); setKind(""); setDesc(""); setCands([]); setChosen(null); setSub(null);
    setStages([]); setVals({}); setEdited({}); setBuilt(null); setAck(false);
    setDone(null); setErr(""); setPicker(false);
  };

  const doRoute = async () => {
    setErr(""); setBusy("Reading your description…");
    try {
      const d = await post("route", { description: desc.trim(), kind });
      setCands(d.candidates); setStep(2);
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(""); }
  };

  /** What the catalogue already holds for each question on this script.
   *
   *  Fetched once per subcategory rather than on every keystroke: the values
   *  do not change while somebody fills a form in, and a request per character
   *  would make a suggestion list cost more than the typing it saves.
   */
  const loadSuggestions = useCallback(async (answers, code) => {
    const wanted = (answers || []).filter(
      a => a.option_source && a.option_source !== "N/A");
    const out = {};
    await Promise.all(wanted.map(async a => {
      try {
        const r = await authFetch(
          `${API}/api/items/suggest?stage=${encodeURIComponent(a.stage)}`
          + `&subcategory_code=${encodeURIComponent(code)}&limit=60`);
        if (!r.ok) return;
        const d = await r.json();
        out[a.stage] = d;          // empty too: its `basis` says why
      } catch { /* a missing suggestion is not a reason to block the form */ }
    }));
    setSugg(out);
  }, []);

  const doExtract = async (code) => {
    setErr(""); setBusy("Filling in what you already told us…");
    try {
      const d = await post("extract", { description: desc.trim(), subcategory_code: code });
      setSub(d.subcategory); setStages(d.answers); setChosen(code);
      const v = {}; d.answers.forEach(a => { if (a.value) v[a.stage] = a.value; });
      setVals(v); setEdited({}); setStep(3); setPicker(false);
      loadSuggestions(d.answers, code);
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(""); }
  };

  /** Make one question optional here, or required again.
   *
   *  This does not edit the controlled specification — the server writes a
   *  separate deviation row carrying the reason and who set it, and the imported
   *  question keeps saying what KAPL issued. Re-extracting afterwards is the
   *  simplest way to pick up the new blocking state without a second source of
   *  truth in this component.
   */
  const setRequired = async (a, want) => {
    setReqBusy(true); setErr("");
    try {
      const body = { required: want, reason: reqReason.trim() || "—" };
      const r = await authFetch(`${API}/api/qa-spec/script/${a.script_id}/required`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not change it");
      setStages(prev => prev.map(x => x.stage === a.stage ? {
        ...x, required: want, blocking: want === "MANDATORY",
        overridden: want !== x.required_controlled,
        override: want === x.required_controlled ? null
          : { reason: body.reason, by: me?.username, from: x.required_controlled, to: want },
      } : x));
      setReqEdit(null); setReqReason("");
    } catch (e) { setErr(String(e.message || e)); } finally { setReqBusy(false); }
  };

  const doPreview = async () => {
    setErr(""); setBusy("Checking for duplicates…");
    try {
      const d = await post("preview", { subcategory_code: chosen, answers: vals });
      setBuilt(d); setAck(false); setStep(4);
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(""); }
  };

  const doCreate = async () => {
    setErr(""); setBusy("Creating…");
    try {
      const qa = {};
      stages.forEach(a => {
        const val = vals[a.stage];
        if (!val) return;
        qa[a.stage] = {
          value: val, label: a.label,
          source: edited[a.stage] ? "user" : (a.value ? "ai" : "user"),
          confidence: edited[a.stage] ? null : a.confidence,
        };
      });
      const d = await post("create", {
        subcategory_code: chosen, description: desc.trim(), answers: vals,
        qa_answers: qa, acknowledge_duplicates: ack,
      });
      setDone(d); setStep(5);
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(""); }
  };

  // Fetched every time the picker opens, not once per page: a subcategory
  // added while this screen was open did not appear until a full reload.
  const openPicker = async () => {
    setPicker(true);
    await fetchAllSubs();
  };
  const fetchAllSubs = async () => {
    try {
      // Every code, not only the selectable ones. KAPL's 16_RESERVED_BLOCKS
      // says to "show active alternatives" when a reserved route is attempted,
      // and a code that is absent from the list cannot be attempted at all —
      // so it reads as missing rather than as reserved, which is exactly how
      // A09-006 looked like a bug for weeks.
      const r = await authFetch(`${API}/api/qa-spec/subcategories`);
      const d = await r.json();
      setAllSubs(d.subcategories || []);
    } catch (_) { /* the picker is a fallback; the candidates still work */ }
  };
  // A subcategory set up on another screen appears in an open picker.
  useLive(["categories", "subcategories", "eng_qa_subcategory"], () => { if (picker) fetchAllSubs(); });

  const stepHead = (n, label) => (
    <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12 }}>
      <span style={{
        width: 22, height: 22, borderRadius: 999, background: step >= n ? KZ.blue : "#E5E7EB",
        color: step >= n ? "#fff" : "#9CA3AF", fontSize: 12, fontWeight: 700,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>{n}</span>
      <div style={{ fontWeight: 700, fontSize: 14 }}>{label}</div>
    </div>
  );

  const need = stages.filter(a => !((vals[a.stage] || "").trim()) && (a.required || "").toUpperCase() === "MANDATORY");
  const toCheck = stages.filter(a => a.action === "CONFIRM" && !edited[a.stage] && (vals[a.stage] || "").trim());

  if (step === 5 && done) {
    return (
      <Card>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#047857", marginBottom: 6 }}>
          {done.item_code} created
        </div>
        <div style={{ fontSize: 14, color: "#111827", marginBottom: 10 }}>{done.short_name}</div>
        <div style={{ fontSize: 12.5, color: "#6B7280", lineHeight: 1.7 }}>
          Saved as {done.status} and sent for approval. It is not a released item and cannot be
          pushed to Zoho until an approver releases it.
          {done.spec_version && <> Created against specification v{done.spec_version}.</>}
        </div>
        {/* Where the HSN came from. KAPL's master supplies it for the 37
            subcategories they mark FIXED CANDIDATE; for the 41 they mark
            CONDITIONAL it is deliberately left blank, and the reason is their
            own instruction rather than a gap in this screen. */}
        {done.hsn_note && (
          <div style={{ fontSize: 12.5, marginTop: 8, padding: "8px 11px",
                        borderRadius: 7, lineHeight: 1.6,
                        background: done.hsn_code ? "#F0FDF4" : "#FFFBEB",
                        border: `1px solid ${done.hsn_code ? "#BBF7D0" : "#FDE68A"}`,
                        color: done.hsn_code ? "#166534" : "#92400E" }}>
            {done.hsn_code
              ? <><b>HSN {done.hsn_code}</b> — {done.hsn_note}</>
              : <>{done.hsn_note}</>}
          </div>
        )}
        <div style={{ marginTop: 14 }}>
          <button onClick={reset} style={{
            padding: "9px 16px", borderRadius: 8, border: "none", background: KZ.blue,
            color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>Create another</button>
        </div>
      </Card>
    );
  }

  return (
    <>
      {err && (
        <div style={{
          background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C",
          borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14,
        }}>{err}</div>
      )}

      <Card style={{ marginBottom: 14 }}>
        {stepHead(1, "Describe what you need")}

        {/* Asked before the description, as Zoho's own New Item form asks it.
            It narrows what may be proposed: a service is routed among A10
            SERVICE and A11 JOB WORK, and goods among everything else. */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {[["goods", "Goods", "A physical item. Carries stock, an inventory account and an HSN."],
            ["service", "Service", "Work rather than a thing. No stock, no inventory account, and a SAC."]]
            .map(([k, label, meaning]) => (
            <button key={k} onClick={() => setKind(k)}
              style={{ flex: "1 1 230px", textAlign: "left", cursor: "pointer",
                       padding: "10px 13px", borderRadius: 9,
                       border: `1.5px solid ${kind === k ? KZ.blue : "#E5E7EB"}`,
                       background: kind === k ? "#EFF6FF" : "#fff" }}>
              <div style={{ fontSize: 13, fontWeight: 700,
                            color: kind === k ? KZ.blue : "#111827" }}>{label}</div>
              <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 2 }}>{meaning}</div>
            </button>
          ))}
        </div>

        <textarea
          value={desc} onChange={e => setDesc(e.target.value)}
          placeholder="e.g. Need hex bolt MS grade 8.8, M10 x 60 mm, zinc plated, ISO 4014, make TVS"
          rows={3}
          style={{
            width: "100%", padding: "10px 12px", border: "1px solid #E5E7EB", borderRadius: 8,
            fontSize: 14, fontFamily: "inherit", resize: "vertical",
          }} />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
          {(() => {
            const ready = kind && desc.trim().length >= 3;
            return (
              <button onClick={doRoute} disabled={!ready || !!busy}
                style={{
                  padding: "9px 16px", borderRadius: 8, border: "none",
                  background: !ready ? "#E5E7EB" : KZ.blue,
                  color: !ready ? "#9CA3AF" : "#fff",
                  fontSize: 13, fontWeight: 600, cursor: !ready ? "default" : "pointer",
                }}>{!kind ? "Choose goods or service first"
                          : step > 1 ? "Start again with this" : "Continue"}</button>
            );
          })()}
          {busy && <span style={{ fontSize: 12.5, color: "#6B7280" }}>{busy}</span>}
          <span style={{ fontSize: 12, color: "#9CA3AF" }}>
            Say it however you like. Anything you leave out will simply be asked for.
          </span>
        </div>
      </Card>

      {step >= 2 && (
        <Card style={{ marginBottom: 14 }}>
          {stepHead(2, "Confirm where it belongs")}
          {!picker ? (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {cands.map(c => {
                  const on = chosen === c.subcategory_code;
                  return (
                    <div key={c.subcategory_code} onClick={() => doExtract(c.subcategory_code)}
                      style={{
                        border: `1px solid ${on ? KZ.blue : "#E5E7EB"}`,
                        background: on ? "#EAF0F6" : "#fff",
                        borderRadius: 8, padding: "10px 13px", cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                      }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                          {c.subcategory_code} · {c.subcategory_name}
                        </div>
                        <div style={{ fontSize: 12, color: "#6B7280" }}>
                          {c.category_code} {c.category_name}{c.reason ? ` — ${c.reason}` : ""}
                        </div>
                      </div>
                      <Badge text={`${Math.round(c.confidence * 100)}% sure`}
                        color={c.confidence >= 0.9 ? "#10B981" : c.confidence >= 0.7 ? "#F59E0B" : "#9CA3AF"} />
                    </div>
                  );
                })}
              </div>
              <button onClick={openPicker} style={{
                marginTop: 10, border: "none", background: "transparent", color: "#3B82F6",
                fontSize: 12.5, cursor: "pointer", padding: 0,
              }}>None of these — search by category or subcategory</button>
            </>
          ) : (
            <>
              {/* KAPL, 27-Sep-2026: search by category or subcategory. A
                  flat dropdown of every code could only be scrolled. */}
              <input autoFocus value={subQ} onChange={e => setSubQ(e.target.value)}
                placeholder="Search by category or subcategory — code or name, e.g. A09, filter, sub product"
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 11px", fontSize: 13,
                         border: "1px solid #CBD5E1", borderRadius: 8 }} />
              {(() => {
                const words = subQ.trim().toLowerCase().split(/\s+/).filter(Boolean);
                const hit = x => {
                  const hay = `${x.subcategory_code} ${x.subcategory_name} ${x.category_code} ${x.category_name}`.toLowerCase();
                  return words.every(w => hay.includes(w));
                };
                const list = allSubs.filter(hit);
                const byCat = [];
                list.forEach(x => {
                  const last = byCat[byCat.length - 1];
                  if (last && last.code === x.category_code) last.items.push(x);
                  else byCat.push({ code: x.category_code, name: x.category_name, items: [x] });
                });
                return (
                  <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #E5E7EB",
                                borderRadius: 8, marginTop: 6 }}>
                    {!allSubs.length && (
                      <div style={{ padding: 12, fontSize: 12.5, color: "#9CA3AF" }}>Loading…</div>
                    )}
                    {allSubs.length > 0 && !list.length && (
                      <div style={{ padding: 12, fontSize: 12.5, color: "#9CA3AF" }}>
                        No category or subcategory matches “{subQ}”.
                      </div>
                    )}
                    {byCat.map(c => (
                      <div key={c.code}>
                        <div style={{ position: "sticky", top: 0, background: "#F8FAFC",
                                      padding: "6px 12px", fontSize: 11, fontWeight: 700,
                                      color: "#475569", letterSpacing: 0.3,
                                      borderBottom: "1px solid #EEF2F7" }}>
                          {c.code} · {c.name}
                        </div>
                        {c.items.map(x => {
                          const off = x.selectable === false;
                          return (
                            <div key={x.subcategory_code}
                              onClick={() => { if (!off) doExtract(x.subcategory_code); }}
                              title={off ? (x.why_not || "Not available") : undefined}
                              style={{ padding: "7px 12px 7px 20px", fontSize: 13,
                                       cursor: off ? "not-allowed" : "pointer",
                                       color: off ? "#9CA3AF" : "#111827",
                                       borderBottom: "1px solid #F3F4F6",
                                       display: "flex", gap: 8, alignItems: "baseline" }}
                              onMouseEnter={e => { if (!off) e.currentTarget.style.background = "#EFF6FF"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                              <b style={{ fontWeight: 600, minWidth: 64 }}>{x.subcategory_code}</b>
                              <span style={{ flex: 1 }}>{x.subcategory_name}</span>
                              {off && <span style={{ fontSize: 11, maxWidth: "55%", textAlign: "right" }}>
                                {x.route === "assemble" ? "composite — use Assemble it"
                                  : x.status === "RESERVED"
                                    ? "reserved by KAPL — raise a Master Data Change Request"
                                    : "set up its questions on the Dashboard first"}</span>}
                              {!off && x.added_in_app && <span style={{ fontSize: 11, color: "#6B7280" }}>
                                added in the app</span>}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                );
              })()}
              <button onClick={() => setPicker(false)} style={{
                marginTop: 10, border: "none", background: "transparent", color: "#3B82F6",
                fontSize: 12.5, cursor: "pointer", padding: 0,
              }}>Back to suggestions</button>
            </>
          )}
        </Card>
      )}

      {step >= 3 && sub && (
        <Card style={{ marginBottom: 14 }}>
          {stepHead(3, `Answer for ${sub.name}`)}
          <div style={{ fontSize: 12.5, color: "#6B7280", marginBottom: 14 }}>
            {stages.length - need.length - toCheck.length} of {stages.length} filled in from your
            description{need.length ? `, ${need.length} still needed` : ""}
            {toCheck.length ? `, ${toCheck.length} to check` : ""}.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {stages.map(a => {
              const st = QA_STATE[edited[a.stage] ? "PREFILL" : a.action] || QA_STATE.ASK;
              const val = vals[a.stage] || "";
              const missing = !val.trim() && (a.required || "").toUpperCase() === "MANDATORY";
              return (
                <div key={a.stage} style={{
                  border: `1px solid ${missing ? "#FCA5A5" : st.bd}`, borderRadius: 8,
                  padding: "11px 13px", background: missing ? "#FEF2F2" : st.bg,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: KZ.blue }}>{a.stage}</span>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{a.label}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4,
                      color: st.fg, background: "#fff", border: `1px solid ${st.bd}`,
                      borderRadius: 999, padding: "2px 8px",
                    }}>{edited[a.stage] ? "Yours" : st.label}</span>
                    {(a.required || "").toUpperCase() === "MANDATORY" && (
                      <span style={{ fontSize: 10, color: "#B91C1C", fontWeight: 700 }}>REQUIRED</span>
                    )}
                    {a.overridden && (
                      <span title={a.override?.reason || ""} style={{
                        fontSize: 9.5, fontWeight: 700, letterSpacing: .3,
                        color: "#6D28D9", background: "#F5F3FF",
                        border: "1px solid #DDD6FE", borderRadius: 999,
                        padding: "2px 7px", textTransform: "uppercase",
                      }}>
                        set here · doc says {(a.required_controlled || "").toLowerCase()}
                      </span>
                    )}
                    {canOverride && a.script_id && (
                      <button
                        onClick={() => { setReqEdit(reqEdit === a.stage ? null : a.stage); setReqReason(a.override?.reason || ""); }}
                        style={{
                          marginLeft: "auto", fontSize: 10, fontWeight: 600,
                          color: "#6B7280", background: "#fff", cursor: "pointer",
                          border: "1px solid #E5E7EB", borderRadius: 6, padding: "2px 8px",
                        }}>
                        {reqEdit === a.stage ? "Close" : "Change"}
                      </button>
                    )}
                  </div>

                  {canOverride && reqEdit === a.stage && (
                    <div style={{
                      background: "#FAFAFA", border: "1px solid #E5E7EB",
                      borderRadius: 7, padding: "10px 12px", marginBottom: 8,
                    }}>
                      <div style={{ fontSize: 11.5, color: "#4B5563", marginBottom: 7, lineHeight: 1.55 }}>
                        {a.required_controlled === "MANDATORY"
                          ? <>The specification makes this <strong>mandatory</strong>. Making it optional here is a recorded deviation — it does not change the document.</>
                          : <>The specification makes this <strong>{(a.required_controlled || "optional").toLowerCase()}</strong>. You can require it here.</>}
                        {" "}
                        <strong>This is not only for this item.</strong> It applies
                        to every item in {sub?.code || "this subcategory"} from now
                        on, until the deviation is cleared — which is why the reason
                        is kept and shown against the question.
                      </div>
                      <input
                        value={reqReason}
                        onChange={e => setReqReason(e.target.value)}
                        placeholder="Why — this is kept on the record"
                        style={{
                          width: "100%", padding: "6px 9px", fontSize: 12,
                          border: "1px solid #E5E7EB", borderRadius: 6, marginBottom: 8,
                        }} />
                      <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                        <button disabled={reqBusy || !reqReason.trim()}
                          onClick={() => setRequired(a, "OPTIONAL")}
                          style={{
                            fontSize: 11.5, fontWeight: 600, padding: "5px 11px",
                            borderRadius: 6, border: "1px solid #E5E7EB",
                            background: (a.required === "OPTIONAL") ? "#EEF2FF" : "#fff",
                            color: reqBusy || !reqReason.trim() ? "#9CA3AF" : "#374151",
                            cursor: reqBusy || !reqReason.trim() ? "default" : "pointer",
                          }}>Optional here</button>
                        <button disabled={reqBusy || !reqReason.trim()}
                          onClick={() => setRequired(a, "MANDATORY")}
                          style={{
                            fontSize: 11.5, fontWeight: 600, padding: "5px 11px",
                            borderRadius: 6, border: "1px solid #E5E7EB",
                            background: (a.required === "MANDATORY") ? "#EEF2FF" : "#fff",
                            color: reqBusy || !reqReason.trim() ? "#9CA3AF" : "#374151",
                            cursor: reqBusy || !reqReason.trim() ? "default" : "pointer",
                          }}>Required here</button>
                        {!reqReason.trim() && (
                          <span style={{ fontSize: 10.5, color: "#9CA3AF", alignSelf: "center" }}>
                            A reason is needed.
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 12.5, color: "#374151", marginBottom: 7 }}>
                    {edited[a.stage] ? a.ask_question : a.question}
                    {/* Two helps, and they answer different questions: this
                        one is about the QUESTION KAPL wrote for this
                        subcategory; FieldHelp below is about the FIELD it
                        lands in. A creator filling P3 wants the first; one
                        filling an HSN wants the second. */}
                    <AttributeHelp
                      stage={a.stage}
                      subcategory={chosen}
                      typed={val}
                      onPick={v => {
                        setVals({ ...vals, [a.stage]: v });
                        setEdited({ ...edited, [a.stage]: true });
                      }} />
                  </div>
                  {/* A picker rather than a <datalist>.
                      The values in use were already fetched and were being
                      handed to a datalist, which shows nothing until you type,
                      gives no hint it exists, and filters by substring — so the
                      box read as an empty field with an invented-looking "e.g."
                      and typing one letter offered every value containing it.
                      The list is visible on focus, carries the count against
                      each value, and matches from the first letter.

                      Free text still wins. These questions name controlled
                      registers — MATERIAL_MASTER, ATTRIBUTE_VALUE_MASTER — that
                      KAPL have never issued to us, so what is offered is what
                      the catalogue already holds. It must suggest and never
                      restrict, or a genuine new value has nowhere to go. */}
                  {(sugg[a.stage]?.values || []).length > 0 ? (
                    <ComboPicker
                      value={val}
                      options={sugg[a.stage].values}
                      groups={sugg[a.stage].values.some(o => o.here === false)
                        ? { key: "here", near: "Answered in Create Items / Zoho",
                            far: "On imported items in this subcategory",
                            farOnly: "On imported items in this subcategory" }
                        : null}
                      freeText
                      onChange={v => {
                        setVals({ ...vals, [a.stage]: v });
                        setEdited({ ...edited, [a.stage]: true });
                      }}
                      placeholder={a.sample ? `e.g. ${a.sample}` : ""}
                      style={{
                        width: "100%", padding: "8px 11px", border: "1px solid #E5E7EB",
                        borderRadius: 6, fontSize: 13, background: "#fff",
                      }} />
                  ) : (
                    <input
                      value={val}
                      onChange={e => {
                        setVals({ ...vals, [a.stage]: e.target.value });
                        setEdited({ ...edited, [a.stage]: true });
                      }}
                      placeholder={a.sample ? `e.g. ${a.sample}` : ""}
                      style={{
                        width: "100%", padding: "8px 11px", border: "1px solid #E5E7EB",
                        borderRadius: 6, fontSize: 13, background: "#fff",
                      }} />
                  )}
                  {(a.option_source && a.option_source !== "N/A") && (
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
                      Controlled list: {a.option_source}{a.validation_rule ? ` — ${a.validation_rule}` : ""}
                      {(sugg[a.stage]?.values || []).length > 0 ? (
                        <span style={{ color: "#6B7280" }}>
                          {" · "}{sugg[a.stage].basis
                            || `${sugg[a.stage].values.length} value(s) already in use`}
                          {" "}— suggestions, not the approved register
                        </span>
                      ) : (
                        /* Nothing to offer, and the reason matters: KAPL have
                           not issued this register and no item in the master
                           has answered this question yet. An empty box under a
                           register's name otherwise reads as a list that failed
                           to load. */
                        <span style={{ color: "#B45309" }}>
                          {" · "}{sugg[a.stage]?.basis
                            || "not issued to us, and no item has answered this yet — type the value"}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
            <button onClick={doPreview} disabled={!!busy || need.length > 0}
              style={{
                padding: "9px 16px", borderRadius: 8, border: "none",
                background: need.length ? "#E5E7EB" : KZ.blue,
                color: need.length ? "#9CA3AF" : "#fff", fontSize: 13, fontWeight: 600,
                cursor: need.length ? "default" : "pointer",
              }}>Review</button>
            {need.length > 0 && (
              <span style={{ fontSize: 12.5, color: "#B91C1C" }}>
                Still needed: {need.map(a => a.label).join(", ")}
              </span>
            )}
            {busy && <span style={{ fontSize: 12.5, color: "#6B7280" }}>{busy}</span>}
          </div>
        </Card>
      )}

      {step >= 4 && built && (
        <Card>
          {stepHead(4, "Review and create")}
          <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>Name</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginTop: 2 }}>{built.short_name}</div>
            <div style={{ fontSize: 12.5, color: "#6B7280", marginTop: 8 }}>{built.full_description}</div>
          </div>

          {built.duplicates?.exact && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "11px 13px", marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#B91C1C" }}>
                This already exists as {built.duplicates.exact.item_code}
              </div>
              <div style={{ fontSize: 12.5, color: "#7F1D1D", marginTop: 3 }}>
                {built.duplicates.exact.short_name} — every identifying value matches, so a new item cannot be created.
              </div>
            </div>
          )}

          {!built.duplicates?.exact && built.duplicates?.near?.length > 0 && (
            <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "11px 13px", marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#B45309", marginBottom: 6 }}>
                {built.duplicates.near.length} similar item{built.duplicates.near.length > 1 ? "s" : ""} already in the master
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 8 }}>
                {built.duplicates.near.map(n => (
                  <div key={n.item_code} style={{ fontSize: 12.5, color: "#78350F" }}>
                    <b>{n.item_code}</b> — {n.short_name}
                  </div>
                ))}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#78350F", cursor: "pointer" }}>
                <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />
                I have checked these and this is a different item
              </label>
            </div>
          )}

          <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", marginBottom: 6 }}>
            Stored as
          </div>
          <div className="kz-tablewrap" style={{ overflowX: "auto", marginBottom: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <tbody>
                {stages.filter(a => (vals[a.stage] || "").trim()).map(a => (
                  <tr key={a.stage}>
                    <td style={{ padding: "5px 8px", color: "#6B7280", whiteSpace: "nowrap" }}>{a.label}</td>
                    <td style={{ padding: "5px 8px", fontWeight: 600 }}>{vals[a.stage]}</td>
                    <td style={{ padding: "5px 8px", color: "#9CA3AF", fontFamily: "ui-monospace, monospace", fontSize: 11 }}>
                      {a.attribute_key || (a.store_path || "").split(".").pop()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button
              onClick={doCreate}
              disabled={!!busy || !!built.duplicates?.exact || (built.duplicates?.near?.length > 0 && !ack)}
              style={{
                padding: "10px 18px", borderRadius: 8, border: "none",
                background: (built.duplicates?.exact || (built.duplicates?.near?.length > 0 && !ack)) ? "#E5E7EB" : "#047857",
                color: (built.duplicates?.exact || (built.duplicates?.near?.length > 0 && !ack)) ? "#9CA3AF" : "#fff",
                fontSize: 13.5, fontWeight: 600,
                cursor: (built.duplicates?.exact || (built.duplicates?.near?.length > 0 && !ack)) ? "default" : "pointer",
              }}>Create item</button>
            <button onClick={() => setStep(3)} style={{
              border: "none", background: "transparent", color: "#3B82F6",
              fontSize: 12.5, cursor: "pointer",
            }}>Back to answers</button>
            {busy && <span style={{ fontSize: 12.5, color: "#6B7280" }}>{busy}</span>}
          </div>
          <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 10 }}>
            Creates a DRAFT for approval. Nothing reaches Zoho until an approver releases it.
          </div>
        </Card>
      )}
    </>
  );
}


// ── Create Items ──────────────────────────────────────────────────────────
//
// Three ways to end up with a new item, behind one entry. They were three
// sidebar entries, which asked the user to route themselves before they had
// started: nobody arrives thinking "I need a composite item", they arrive
// because a customer's order has a line the master does not hold.
//
// Describing it is first because it is the common case. Assembling is for when
// the parts are already coded, and a sheet is for when there are forty of them.
// All three issue a code the same way and land in the same approval queue —
// the difference is only where the description comes from.
const CREATE_ROUTES = [
  ["describe", "✨ Describe it",
   "One item, from a plain-English description"],
  ["assemble", "⧉ Assemble it",
   "A composite item, from SKUs already in the master"],
  ["sheet", "⬆ From a sheet",
   "Many items at once, from the item-master template"],
];

function CreateItems({ me }) {
  const [route, setRoute] = useState("describe");
  const here = CREATE_ROUTES.find(r => r[0] === route) || CREATE_ROUTES[0];
  return (
    <div>
      <div style={{ display: "flex", background: "#F3F4F6", borderRadius: 10,
                    padding: 3, marginBottom: 4, width: "fit-content",
                    flexWrap: "wrap" }}>
        {CREATE_ROUTES.map(([k, label, hint]) => (
          <button key={k} onClick={() => setRoute(k)} title={hint}
            style={{ padding: "7px 14px", borderRadius: 8, border: "none",
                     fontSize: 12, fontWeight: 600, cursor: "pointer",
                     background: route === k ? "#fff" : "transparent",
                     color: route === k ? "#1D4ED8" : "#6B7280",
                     boxShadow: route === k ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
            {label}
          </button>
        ))}
      </div>
      {/* What this route is for, so the choice is legible without hovering. */}
      <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 16 }}>{here[2]}</div>

      {route === "describe" && <ClassifyCreate me={me} />}
      {route === "assemble" && <CompositeItem me={me} />}
      {route === "sheet" && <ImportItems me={me} />}
    </div>
  );
}


// ── Classify & Create: one flow, not four ─────────────────────────────────
//
// This screen carried a mode switch — Guided Q&A, Find / Guided, Describe
// Freely, Step-by-Step — four routes to the same destination. Three of them
// predate the controlled specification and classified by their own rules,
// which on the screen that mints item codes is a governance problem before it
// is a clutter one: four ways to create an item is four ways for two of them
// to disagree.
//
// Guided Q&A is the flow KAPL-INV-AI-LOGIC-03 describes. It asks in the
// document's own wording, prefills whatever the description already answers,
// and checks for duplicates before it issues a code. It is what the other
// three were each approximating, so they are gone and it is the screen.
//
// Removed with them: the reuse-and-refine modal, which offered to open an
// existing item and edit its description rather than create a duplicate.
// Guided Q&A refuses an exact duplicate and names it, but does not yet offer
// to open it — that is a real capability lost here, not a tidy-up.
function ClassifyCreate({ me }) {
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 3 }}>
          Classify &amp; Create Item
        </h2>
        <p style={{ fontSize: 13, color: "#6B7280" }}>
          Describe what you need in plain English. The AI proposes where it belongs and
          fills in what your description already says, then asks only for what is
          genuinely missing — in KAPL&rsquo;s own wording.
        </p>
      </div>
      <GuidedQA me={me} />
    </div>
  );
}


// ── AI Chat ───────────────────────────────────────────────────────────────────
function AIChat() {
  const [messages, setMessages] = useState([{ role: "assistant", text: "Hello! I'm your Kaizen Airtreat inventory assistant. Ask me about classification rules, nomenclature formats, HSN codes, or any SOP guidelines.", sources: [] }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const q = input.trim(); setInput("");
    setMessages(m => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const r = await authFetch(`${API}/api/ai/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ question: q, context_item_code: null }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Chat failed");
      setMessages(m => [...m, { role: "assistant", text: d.answer, sources: d.sources || [], from_sop: d.from_sop }]);
    } catch (e) { setMessages(m => [...m, { role: "assistant", text: `Error: ${e.message}`, sources: [] }]); }
    setLoading(false);
  };

  const suggestions = [
    "What subcategory does a 4-pole MCB belong to?",
    "How do I name a cleanroom wall panel?",
    "Which HSN code applies to GI perforated cable trays?",
    "What are the 8 classification fields for fasteners?",
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "75vh" }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 16 }}>AI Assistant</h2>
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16, marginBottom: 12, padding: "4px 2px" }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", gap: 8 }}>
            {m.role === "assistant" && <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#EFF6FF", border: "1px solid #BFDBFE", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0, marginTop: 2 }}>⚡</div>}
            <div style={{ maxWidth: "75%", padding: m.role === "user" ? "10px 14px" : "12px 16px", borderRadius: m.role === "user" ? "18px 18px 4px 18px" : "4px 18px 18px 18px", background: m.role === "user" ? "#3B82F6" : "#fff", border: m.role === "user" ? "none" : "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", color: m.role === "user" ? "#fff" : "#111827" }}>
              {m.role === "user" ? <span style={{ fontSize: 13, lineHeight: 1.5 }}>{m.text}</span> : <MarkdownMessage content={m.text} />}
              {m.sources?.length > 0 && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #F3F4F6", display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "#9CA3AF" }}>📄 Sources:</span>
                  {m.sources.map((s, j) => <span key={j} style={{ fontSize: 11, background: "#F3F4F6", border: "1px solid #E5E7EB", borderRadius: 4, padding: "1px 6px", color: "#6B7280" }}>{s}</span>)}
                </div>
              )}
            </div>
            {m.role === "user" && <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#3B82F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#fff", fontWeight: 700, flexShrink: 0, marginTop: 2 }}>U</div>}
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start", gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#EFF6FF", border: "1px solid #BFDBFE", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>⚡</div>
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "4px 18px 18px 18px", padding: "12px 16px", display: "flex", gap: 4, alignItems: "center" }}>
              {[0,1,2].map(d => <div key={d} style={{ width: 7, height: 7, borderRadius: "50%", background: "#9CA3AF", animation: "bounce 1.2s infinite", animationDelay: `${d*0.2}s` }} />)}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      {messages.length === 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          {suggestions.map(s => <button key={s} onClick={() => setInput(s)} style={{ padding: "5px 12px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 16, fontSize: 12, color: "#1D4ED8", cursor: "pointer" }}>{s}</button>)}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()} placeholder="Ask about SOPs, classification rules, nomenclature, HSN codes..."
          style={{ flex: 1, padding: "11px 16px", border: "1px solid #D1D5DB", borderRadius: 24, fontSize: 13, outline: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }} />
        <button onClick={send} disabled={loading || !input.trim()}
          style={{ width: 42, height: 42, borderRadius: "50%", border: "none", background: loading || !input.trim() ? "#E5E7EB" : "#3B82F6", color: "#fff", fontSize: 18, cursor: loading || !input.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>➤</button>
      </div>
      <style>{`@keyframes bounce { 0%,80%,100% { transform:scale(0.8);opacity:0.5; } 40% { transform:scale(1.2);opacity:1; } }`}</style>
    </div>
  );
}

// ── SOPs ──────────────────────────────────────────────────────────────────────
// ── Editing an uploaded SOP ───────────────────────────────────
// What the AI answers from is the text extracted out of the .docx, not the file
// itself, and extraction is imperfect — tables flatten, headings lose their
// style, the odd character comes through wrong. So the text is editable, by a
// Super Admin only, and saving re-indexes this document immediately.
//
// The uploaded .docx is never overwritten. It is the document as issued; the
// edit corrects what the system reads from it.

function SOPEditModal({ sop, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [cat, setCat] = useState("");
  const [version, setVersion] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    authFetch(`${API}/api/sop/${sop.id}`)
      .then(r => jsonOrThrow(r, "Could not open this SOP"))
      .then(x => {
        setD(x); setText(x.editable_text || "");
        setCat(x.category_code || ""); setVersion(x.version || "");
      })
      .catch(e => setErr(String(e.message || e)));
  }, [sop.id]);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const r = await authFetch(`${API}/api/sop/${sop.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content_text: text, category_code: cat, version }),
      });
      const out = await jsonOrThrow(r, "Save failed");
      onSaved(out.message || "Saved.");
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };

  const dirty = d && (text !== (d.editable_text || "")
    || cat !== (d.category_code || "") || version !== (d.version || ""));

  return (
    <EngModal title={sop.filename} onClose={onClose} maxWidth={880}>
      {err && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C",
                            borderRadius: 8, padding: "9px 12px", fontSize: 12.5, marginBottom: 12 }}>{err}</div>}
      {!d && !err && <div style={{ color: "#9CA3AF", fontSize: 13 }}>Loading…</div>}
      {d && (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ flex: "1 1 150px" }}>
              <EngField label="Category (blank = global)">
                <input value={cat} onChange={e => setCat(e.target.value)}
                       placeholder="A01" style={ES.input} />
              </EngField>
            </div>
            <div style={{ flex: "1 1 150px" }}>
              <EngField label="Version">
                <input value={version} onChange={e => setVersion(e.target.value)}
                       placeholder="v2.0" style={ES.input} />
              </EngField>
            </div>
          </div>

          <div style={{ fontSize: 11.5, color: "#6B7280", marginBottom: 6, lineHeight: 1.7 }}>
            One paragraph per line. Start a line with <code>{d.heading_mark}</code> to make it a
            section heading — headings are what make a passage findable, and the .docx styling
            does not survive an edit. Currently indexed as <b>{d.chunk_count}</b> chunks.
            {d.edited_by && <> Last corrected by <b>{d.edited_by}</b>
              {d.edited_at ? ` on ${fmtIST(d.edited_at, "date")}` : ""}.</>}
          </div>

          <textarea value={text} onChange={e => setText(e.target.value)} rows={18}
            spellCheck={false}
            style={{ width: "100%", padding: "10px 12px", border: "1px solid #E5E7EB",
                     borderRadius: 8, fontSize: 12.5, fontFamily: "ui-monospace, monospace",
                     lineHeight: 1.6, resize: "vertical" }} />

          <div style={{ fontSize: 11, color: "#9CA3AF", margin: "8px 0 14px" }}>
            The uploaded file is not changed — it stays as issued. Saving re-indexes this
            document, so the AI answers from the corrected text on the next question.
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
            {dirty && <span style={{ fontSize: 11.5, color: "#B45309", marginRight: "auto" }}>
              Unsaved changes</span>}
            <Btn outline color="#6B7280" onClick={onClose}>Cancel</Btn>
            <Btn onClick={save} disabled={busy || !dirty}>
              {busy ? "Saving…" : "Save & re-index"}
            </Btn>
          </div>
        </>
      )}
    </EngModal>
  );
}

function SOPs({ me }) {
  const [sops, setSops] = useState([]);
  const [editing, setEditing] = useState(null);
  const canEdit = HAS(me, "sop.edit");
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("");
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const [deleting, setDeleting] = useState(null);
  const loadSops = async () => { try { const r = await authFetch(`${API}/api/sop/`); const d = await r.json(); setSops(Array.isArray(d) ? d : []); } catch { setSops([]); } };
  useEffect(() => { loadSops(); }, []);
  useLive(["sop_documents"], () => loadSops());
  const upload = async () => {
    if (!file) return; setUploading(true); setMsg("");
    const form = new FormData(); form.append("file", file); if (category) form.append("category_code", category); form.append("uploaded_by", "user");
    try { const r = await authFetch(`${API}/api/sop/upload`, { method: "POST", body: form }); const d = await jsonOrThrow(r, "Upload failed"); setMsg(`✅ ${d.message}`); setFile(null); setCategory(""); await loadSops(); }
    catch (e) { setMsg(`❌ ${e.message}`); } setUploading(false);
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.filename}"?\n\nIts ${s.chunk_count || 0} chunks will be removed from the AI knowledge base and the file will be deleted. This cannot be undone.`)) return;
    setDeleting(s.id); setMsg("");
    try { const r = await authFetch(`${API}/api/sop/${s.id}`, { method: "DELETE" }); const d = await jsonOrThrow(r, "Delete failed"); setMsg(`✅ ${d.message}`); await loadSops(); }
    catch (e) { setMsg(`❌ ${e.message}`); } setDeleting(null);
  };
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>SOP Documents</h2>
        <p style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>
          Company standards the AI follows when classifying items and answering questions.
        </p>
      </div>

      {/* Upload beside the library — both stack on narrow screens. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20, alignItems: "start" }}>
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 12 }}>Upload New SOP</h3>
          <div style={{ border: "2px dashed #D1D5DB", borderRadius: 8, padding: 20, textAlign: "center", marginBottom: 12 }}>
            <input type="file" accept=".docx" onChange={e => setFile(e.target.files[0])} style={{ display: "none" }} id="sop-file" />
            <label htmlFor="sop-file" style={{ cursor: "pointer" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
              <p style={{ fontSize: 13, color: file ? "#166534" : "#9CA3AF" }}>{file ? file.name : "Click to select .docx file"}</p>
            </label>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Category Code (optional)</label>
            <input value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. A01, A02, A07"
              style={{ width: "100%", padding: "8px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
            <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>Leave blank if the SOP applies company-wide.</p>
          </div>
          {msg && <div style={{ fontSize: 12, color: msg.startsWith("✅") ? "#166534" : "#DC2626", marginBottom: 8 }}>{msg}</div>}
          <Btn onClick={upload} disabled={!file || uploading} full>{uploading ? "Uploading…" : "Upload & Index"}</Btn>
          <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 8, textAlign: "center" }}>
            Uploaded SOPs are chunked and added to the AI knowledge base automatically. Re-uploading the same filename replaces the old version.
          </p>
        </Card>

        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#374151" }}>Loaded SOPs</h3>
            <span style={{ fontSize: 12, color: "#9CA3AF" }}>{sops.length} document{sops.length === 1 ? "" : "s"}</span>
          </div>
          {sops.length === 0 ? <p style={{ color: "#9CA3AF", fontSize: 13 }}>No SOPs uploaded yet.</p> : (
            <div className="kz-scroll-y" style={{ display: "grid", gap: 8, maxHeight: "62vh", overflowY: "auto", paddingRight: 6 }}>
              {sops.map(s => (
                <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 12px", background: "#F9FAFB", borderRadius: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📄 {s.filename}</span>
                      {s.category_code && <Badge text={s.category_code} color={categoryColors[s.category_code] || "#6B7280"} />}
                    </div>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
                      {s.chunk_count > 0 ? `${s.chunk_count} chunks indexed` : "Not indexed"} · {fmtIST(s.uploaded_at, "date")}
                      {s.version ? ` · ${s.version}` : ""}
                      {s.edited_by ? ` · text corrected by ${s.edited_by}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {canEdit && (
                    <button onClick={() => setEditing(s)}
                      title="Correct the extracted text the AI answers from"
                      style={{ padding: "5px 10px", fontSize: 12, fontWeight: 600, color: KZ.blue,
                               background: "#EAF0F6", border: "1px solid #DBE5EF", borderRadius: 6,
                               cursor: "pointer" }}>Edit</button>
                  )}
                  <button onClick={() => remove(s)} disabled={deleting === s.id} title="Delete SOP and remove it from the AI knowledge base"
                    style={{ flexShrink: 0, padding: "5px 10px", fontSize: 12, fontWeight: 600, color: "#DC2626", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, cursor: deleting === s.id ? "default" : "pointer", opacity: deleting === s.id ? 0.6 : 1 }}>
                    {deleting === s.id ? "Deleting…" : "🗑 Delete"}
                  </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      {editing && (
        <SOPEditModal sop={editing} onClose={() => setEditing(null)}
          onSaved={m => { setEditing(null); setMsg(`✅ ${m}`); loadSops(); }} />
      )}
    </div>
  );
}

// ── Zoho Sync — bulk operations ───────────────────────────────────────────────
// Single-item push lives inline on each row (see ZohoChip); this tab covers the
// things that don't belong on a row: the Books price sync, bulk push, and ERP
// exports. Nothing here imports items from Zoho (KAPL 19-Oct-2026).
function ZohoSync() {
  const zohoStatus = useZohoStatus();
  const [busy, setBusy] = useState("");           // "" | "bulk" | "books"
  const [result, setResult] = useState(null);
  const [books, setBooks] = useState(null);
  const loadBooks = useCallback(() => {
    authFetch(`${API}/api/eng/zoho/books-summary`).then(r => r.json()).then(setBooks).catch(() => { });
  }, []);
  useEffect(() => { loadBooks(); }, [result, loadBooks]);
  // The nightly sync, or one started from another tab, shows here as it lands.
  useLive(["eng_zoho_items", "eng_zoho_purchase_lines"], loadBooks, { delay: 1500 });

  const call = async (kind, url, body) => {
    setBusy(kind); setResult(null);
    try {
      const r = await authFetch(`${API}${url}`, {
        method: "POST",
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Request failed");
      setResult({ ok: true, kind, data: d });
    } catch (e) {
      // "Failed to fetch" is what a browser says when the connection never
      // completed, and on its own it names nothing a person can act on. The
      // usual causes here are the server restarting mid-deploy or the network
      // dropping a long sync, so say that rather than passing the raw message
      // through.
      const raw = String(e.message || e);
      setResult({ ok: false, error:
        /failed to fetch|networkerror|load failed/i.test(raw)
          ? "The server did not answer. It may be restarting after a deploy, or "
            + "the connection dropped during a long sync. Wait a moment and try "
            + "again — nothing has been half-written, and a sync that did start "
            + "will have finished on the server."
          : raw });
    }
    setBusy("");
  };

  const configured = zohoStatus.configured;
  const disabled = configured !== true || Boolean(busy);

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 6 }}>Zoho Integration</h2>
      <p style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>
        Push individual items straight from the <b>Items</b> list — each row has a Zoho chip.
        This tab is for bulk actions.
      </p>

      {configured === false && (
        <Card style={{ marginBottom: 20, background: "#FFFBEB", border: "1px solid #FDE68A" }}>
          <div style={{ fontSize: 13, color: "#92400E", fontWeight: 600, marginBottom: 4 }}>⚠ Zoho is not configured</div>
          <div style={{ fontSize: 12, color: "#92400E" }}>
            Set {(zohoStatus.missing || []).join(", ") || "the Zoho credentials"} in <code>.env</code>, then restart the backend.
            Push chips stay disabled until then.
          </div>
        </Card>
      )}

      {result && (
        <Card style={{ marginBottom: 20, background: result.ok ? "#F0FDF4" : "#FEF2F2", border: `1px solid ${result.ok ? "#86EFAC" : "#FCA5A5"}` }}>
          {!result.ok ? (
            <div style={{ fontSize: 13, color: "#DC2626" }}>❌ {result.error}</div>
          ) : result.kind === "books" ? (
            <div style={{ fontSize: 13, color: "#166534" }}>
              ✅ Zoho Books sync — prices refreshed for <b>{(result.data.matched_to_master || 0).toLocaleString()}</b> master items,{" "}
              <b>{result.data.purchase_lines_new}</b> new purchase lines added. Prices & purchase history now show on each item's detail page.
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#166534" }}>
              ✅ Bulk push finished — <b>{result.data.synced}</b> synced, <b>{result.data.failed}</b> failed.
              {result.data.errors?.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#DC2626" }}>{result.data.errors.slice(0, 10).join(" · ")}</div>
              )}
            </div>
          )}
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
        <Card style={{ borderColor: "#0EA5E9" }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 4 }}>Sync prices & purchase history</h3>
          <p style={{ fontSize: 12, color: "#6B7280", marginBottom: 10, lineHeight: 1.6 }}>
            Reads item <b>prices</b> and <b>purchase-order history</b> from Zoho Books (read-only) for the items in
            this master, matched by SKU. Items created in Zoho are never imported — the master holds only
            items created here.
          </p>
          {books && books.items > 0 && (
            <div style={{ fontSize: 12, color: "#374151", background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}>
              <b>{books.matched_to_master?.toLocaleString()}</b> items priced · <b>{books.purchase_lines?.toLocaleString()}</b> purchase lines · <b>{books.vendors}</b> vendors
              <div style={{ color: "#6B7280", marginTop: 3 }}>Last sync: {books.last_sync || "—"} · 🗓 auto-syncs nightly</div>
            </div>
          )}
          <Btn onClick={() => call("books", "/api/eng/zoho/sync-books")} disabled={disabled} color="#0EA5E9">
            {busy === "books" ? "Syncing Books… (a minute)" : "Sync now"}
          </Btn>
        </Card>

        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 4 }}>Bulk push</h3>
          <p style={{ fontSize: 12, color: "#6B7280", marginBottom: 12, lineHeight: 1.6 }}>
            Pushes RELEASED items to Zoho Books + CRM in one pass. Already-synced items
            are updated in place rather than duplicated. Capped at 1,000 items per run.
          </p>
          <Btn onClick={() => call("bulk", "/api/integrations/zoho/sync", { direction: "push" })} disabled={disabled} color="#7C3AED">
            {busy === "bulk" ? "Pushing…" : "Push all RELEASED"}
          </Btn>
        </Card>

        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 4 }}>Export for other ERPs</h3>
          <p style={{ fontSize: 12, color: "#6B7280", marginBottom: 12, lineHeight: 1.6 }}>
            Download the master in a format another system can import. No Zoho credentials needed.
          </p>
          {/* Fetched, not opened. These three used window.open, which sends no
              Authorization header, so every one of them answered 401 and showed
              a page reading {"detail":"Not authenticated."}. */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[["tally-xml", "Tally XML", "Kaizen item master.xml"],
              ["sap-json", "SAP IDoc", "Kaizen item master (SAP).json"],
              ["json", "JSON", "Kaizen item master.json"]].map(([path, label, file]) => (
              <Btn key={path} small outline
                onClick={() => downloadAuthed(
                  `${API}/api/integrations/export/${path}`, file)
                  .catch(e => window.alert(String(e.message || e)))}>
                {label}
              </Btn>
            ))}
          </div>
        </Card>

        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 12 }}>Setup Checklist</h3>
          {[
            ["Create Zoho API Client at api-console.zoho.in", configured === true],
            ["Generate OAuth Refresh Token with ZohoBooks.items scopes", configured === true],
            ["Add credentials to .env file", configured === true],
            ["Restart uvicorn server after updating .env", configured === true],
            ["Push one RELEASED item from the Items tab", false],
          ].map(([step, done]) => (
            <div key={step} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6 }}>
              <span style={{ color: done ? "#10B981" : "#D1D5DB", fontSize: 16 }}>●</span>
              <span style={{ fontSize: 13, color: "#374151" }}>{step}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
// ── Login ─────────────────────────────────────────────────────────────────────
// ── Password reset ────────────────────────────────────────────────────────────

function ForgotPassword({ onBack }) {
  const [ident, setIdent] = useState("");
  const [msg, setMsg] = useState("");
  const [avail, setAvail] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    // Say up front whether the server can actually send mail, rather than
    // offering a link that quietly goes nowhere.
    authFetch(`${API}/api/auth/reset-availability`).then(r => r.json()).then(setAvail).catch(() => { });
  }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try {
      const r = await authFetch(`${API}/api/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username_or_email: ident.trim() }),
      });
      const d = await jsonOrThrow(r, "Could not send the link");
      setMsg(d.message);
    } catch (e2) { setMsg(String(e2.message || e2)); }
    setBusy(false);
  };
  return (
    <Card>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 6 }}>Reset your password</h2>
      {avail && !avail.configured && (
        <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E",
                      borderRadius: 8, padding: "8px 10px", fontSize: 12, marginBottom: 12 }}>
          {avail.hint}
        </div>
      )}
      <form onSubmit={submit}>
        <EngField label="Username or email">
          <input value={ident} onChange={e => setIdent(e.target.value)} style={ES.input} autoFocus />
        </EngField>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <Btn full disabled={busy || !ident.trim()}>{busy ? "Sending…" : "Send reset link"}</Btn>
        </div>
      </form>
      {msg && <div style={{ fontSize: 12, color: "#374151", marginTop: 10 }}>{msg}</div>}
      <button onClick={onBack} style={{ border: "none", background: "transparent", color: "#3B82F6",
        fontSize: 12, cursor: "pointer", padding: "10px 0 0" }}>← Back to sign in</button>
    </Card>
  );
}

function ResetPassword({ token, onDone }) {
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [err, setErr] = useState(""); const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw !== pw2) { setErr("The two passwords do not match."); return; }
    setBusy(true);
    try {
      const r = await authFetch(`${API}/api/auth/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: pw }),
      });
      await jsonOrThrow(r, "Could not reset the password");
      setOk(true);
    } catch (e2) { setErr(String(e2.message || e2)); }
    setBusy(false);
  };
  if (ok) return (
    <Card>
      <div style={{ fontSize: 14, color: "#047857", fontWeight: 600, marginBottom: 10 }}>
        ✓ Password changed.
      </div>
      <Btn full onClick={onDone}>Sign in</Btn>
    </Card>
  );
  return (
    <Card>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 10 }}>Choose a new password</h2>
      <form onSubmit={submit}>
        <EngField label="New password">
          <input type="password" value={pw} onChange={e => setPw(e.target.value)} style={ES.input} autoFocus />
        </EngField>
        <EngField label="Confirm new password">
          <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} style={ES.input} />
        </EngField>
        {err && <div style={{ color: "#EF4444", fontSize: 12, margin: "8px 0" }}>{err}</div>}
        <Btn full disabled={busy || !pw}>{busy ? "Saving…" : "Set password"}</Btn>
      </form>
      <button onClick={onDone} style={{ border: "none", background: "transparent", color: "#3B82F6",
        fontSize: 12, cursor: "pointer", padding: "10px 0 0" }}>← Back to sign in</button>
    </Card>
  );
}

function Login({ onLoggedIn }) {
  const [forgot, setForgot] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await authFetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Login failed");
      setToken(d.access_token);
      onLoggedIn(d.user);
    } catch (e2) { setErr(String(e2.message || e2)); }
    setBusy(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F8FAFC", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <img src="/kaizen-logo.png" alt="Kaizen Airtreat Pvt. Ltd."
            style={{ width: 190, height: "auto", display: "block", margin: "0 auto 10px" }} />
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>Item Master System</div>
        </div>
        {forgot ? <ForgotPassword onBack={() => setForgot(false)} /> : (
        <Card>
          <form onSubmit={submit}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Sign in</h2>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Username</label>
            <input value={username} onChange={e => setUsername(e.target.value)} autoFocus autoComplete="username"
              style={{ width: "100%", padding: "9px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, boxSizing: "border-box", marginBottom: 12 }} />
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password"
              style={{ width: "100%", padding: "9px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, boxSizing: "border-box", marginBottom: 14 }} />
            {err && <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#DC2626", borderRadius: 8, padding: "8px 10px", fontSize: 12, marginBottom: 12 }}>{err}</div>}
            <Btn onClick={submit} disabled={busy || !username.trim() || !password} full>{busy ? "Signing in…" : "Sign in"}</Btn>
            <button type="button" onClick={() => setForgot(true)}
              style={{ border: "none", background: "transparent", color: "#3B82F6",
                       fontSize: 12, cursor: "pointer", padding: "12px 0 0", width: "100%" }}>
              Forgot your password?
            </button>
          </form>
        </Card>
        )}
        <p style={{ textAlign: "center", fontSize: 11, color: "#9CA3AF", marginTop: 14 }}>
          Contact an administrator if you need an account.
        </p>
      </div>
    </div>
  );
}

// ── Forced / voluntary password change ───────────────────────────────────────
function ChangePassword({ forced, onDone, onCancel }) {
  const [cur, setCur] = useState(""); const [next, setNext] = useState(""); const [again, setAgain] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (next !== again) { setErr("The two new passwords do not match."); return; }
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    setBusy(true); setErr("");
    try {
      const r = await authFetch(`${API}/api/auth/change-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: cur, new_password: next }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not change password");
      onDone();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  const body = (
    <Card style={{ maxWidth: 400, width: "100%" }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
        {forced ? "Set a new password" : "Change password"}
      </h2>
      <p style={{ fontSize: 12, color: "#6B7280", marginBottom: 14 }}>
        {forced ? "Your password was set by an administrator. Choose your own before continuing."
                : "Choose a new password of at least 8 characters."}
      </p>
      {[["Current password", cur, setCur], ["New password", next, setNext], ["Confirm new password", again, setAgain]].map(([label, val, set]) => (
        <div key={label} style={{ marginBottom: 10 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>{label}</label>
          <input type="password" value={val} onChange={e => set(e.target.value)}
            style={{ width: "100%", padding: "9px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }} />
        </div>
      ))}
      {err && <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#DC2626", borderRadius: 8, padding: "8px 10px", fontSize: 12, margin: "8px 0" }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <Btn onClick={submit} disabled={busy || !cur || !next || !again} full>{busy ? "Saving…" : "Update password"}</Btn>
        {!forced && <Btn onClick={onCancel} outline>Cancel</Btn>}
      </div>
    </Card>
  );

  if (!forced) return body;
  return (
    <div style={{ minHeight: "100vh", background: "#F8FAFC", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {body}
    </div>
  );
}

// ── Users (admin only) ───────────────────────────────────────────────────────
function Users({ me }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ username: "", full_name: "", password: "", role: "CREATOR" });

  // The roles come from the server, which is the only place that knows them.
  // KAPL-IM-USER-MATRIX-01 added five review roles to the enum and to the
  // permission table, and this screen went on offering the original five from
  // a hardcoded array — so the roles existed, the API accepted them, and there
  // was no way to give anybody one. ROLES below is now only the fallback for a
  // failed fetch, and it is labelled as such.
  const [roles, setRoles] = useState(null);
  useEffect(() => {
    authFetch(`${API}/api/users/roles`).then(r => r.ok ? r.json() : null)
      .then(d => setRoles(d && Array.isArray(d.roles) ? d.roles : null))
      .catch(() => setRoles(null));
  }, []);
  const ALL = roles || ROLES.map(r => ({ ...r, description: ROLE_HELP[r.value],
                                         assignable: true }));
  // Who this admin may hand out. The server decides — this only draws it.
  const offer = ALL.filter(r => r.assignable
    && (r.value !== "SUPER_ADMIN" || HAS(me, "item.delete")));
  const helpFor = (v) => (ALL.find(r => r.value === v) || {}).description || "";
  const labelFor = (v) => (ALL.find(r => r.value === v) || {}).label || v;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/api/users/`);
      const d = await r.json();
      setUsers(Array.isArray(d) ? d : []);
    } catch { setUsers([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useLive(["users"], load);

  const act = async (fn) => {
    setMsg(null);
    try {
      const { r, d } = await fn();
      if (!r.ok) throw new Error((d && d.detail) || "Request failed");
      setMsg({ ok: true, text: "Done." });
      await load();
      return true;
    } catch (e) { setMsg({ ok: false, text: String(e.message || e) }); return false; }
  };

  const create = () => act(async () => {
    const r = await authFetch(`${API}/api/users/`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...form, username: form.username.trim() }),
    });
    const d = await r.json();
    if (r.ok) setForm({ username: "", full_name: "", password: "", role: "CREATOR" });
    return { r, d };
  });

  const patch = (username, body) => act(async () => {
    const r = await authFetch(`${API}/api/users/${encodeURIComponent(username)}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return { r, d: await r.json() };
  });

  const remove = (username) => {
    if (!window.confirm(`Delete user "${username}"? Items they created keep their name in the history.`)) return;
    act(async () => {
      const r = await authFetch(`${API}/api/users/${encodeURIComponent(username)}`, { method: "DELETE" });
      return { r, d: await r.json() };
    });
  };

  const resetPw = (username) => {
    const pw = window.prompt(`New temporary password for "${username}" (min 8 characters).\nThey will be asked to change it at next sign-in.`);
    if (!pw) return;
    if (pw.length < 8) { setMsg({ ok: false, text: "Password must be at least 8 characters." }); return; }
    patch(username, { password: pw });
  };

  // The four §12 reviewers share a family of colours so a glance down the
  // account list separates "signs off on something" from "raises and edits".
  const roleColor = r => ({ SUPER_ADMIN: "#DC2626", ADMIN: "#7C3AED",
                            ENGINEER: "#0891B2", CREATOR: "#0EA5E9",
                            VIEWER: "#6B7280",
                            ENGINEERING_APPROVER: "#047857",
                            SCM: "#B45309", FINANCE: "#0F766E",
                            MASTER_DATA_CONTROLLER: "#4338CA",
                            APPROVER_RELEASE: "#BE185D" }[r] || "#6B7280");

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>Users</h2>
        <p style={{ fontSize: 13, color: "#6B7280", marginTop: 4, maxWidth: 900, lineHeight: 1.6 }}>
          The seven roles KAPL name, in their order. Nobody releases their own item —
          a creator submits, the four approvers sign in turn, and only then is the item
          locked and able to reach Zoho. Each approver sees and edits only their own
          section.
        </p>
        {[["KAPL's seven", true], ["Also in this system", false]].map(([heading, want]) => {
          const group = ALL.filter(r => Boolean(r.kapl) === want);
          if (!group.length) return null;
          return (
            <div key={heading} style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4,
                            textTransform: "uppercase", color: "#9CA3AF",
                            marginBottom: 4 }}>
                {heading}
                {!want && (
                  <span style={{ fontWeight: 500, letterSpacing: 0, textTransform: "none",
                                 marginLeft: 6 }}>
                    — not on KAPL's list; kept because accounts hold them
                  </span>
                )}
              </div>
              <div style={{ display: "grid", gap: 4 }}>
                {group.map(r => (
                  <div key={r.value} style={{ fontSize: 12, color: "#6B7280" }}>
                    <b style={{ color: roleColor(r.value),
                                textDecoration: r.superseded_by ? "line-through" : "none",
                                opacity: r.superseded_by ? 0.6 : 1 }}>{r.label}</b>
                    {r.superseded_by && (
                      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3,
                                     color: "#9CA3AF", marginLeft: 6 }}>SUPERSEDED</span>
                    )}
                    {r.signs_stage && (
                      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3,
                                     color: "#9CA3AF", marginLeft: 6 }}>
                        STAGE {["ENGINEERING", "SCM", "FINANCE", "CHECKER"]
                          .indexOf(r.signs_stage) + 1} OF 4
                      </span>
                    )}
                    {" — "}{r.description}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {msg && (
        <Card style={{ marginBottom: 16, background: msg.ok ? "#F0FDF4" : "#FEF2F2", border: `1px solid ${msg.ok ? "#86EFAC" : "#FCA5A5"}` }}>
          <div style={{ fontSize: 13, color: msg.ok ? "#166534" : "#DC2626" }}>{msg.ok ? "✅" : "❌"} {msg.text}</div>
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20, alignItems: "start" }}>
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 12 }}>Add a user</h3>
          {[["Username", "username", "e.g. ravi.kumar"], ["Full name", "full_name", "e.g. Ravi Kumar"], ["Temporary password", "password", "min 8 characters"]].map(([label, key, ph]) => (
            <div key={key} style={{ marginBottom: 10 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>{label}</label>
              <input type={key === "password" ? "text" : "text"} value={form[key]} placeholder={ph}
                onChange={e => setForm({ ...form, [key]: e.target.value })}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
            </div>
          ))}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Role</label>
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
              style={{ width: "100%", padding: "8px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 13, boxSizing: "border-box", background: "#fff" }}>
              {/* Same list as the per-account selector, from one ROLES array.
                  SUPER_ADMIN is only offered to someone who already holds it,
                  so an Admin cannot create an account above their own level. */}
              {/* Grouped so the seven KAPL asked for are not mixed in with
                  the three this system carries in addition. */}
              {[["KAPL roles", true], ["Also available", false]].map(([g, want]) => {
                const rows = offer.filter(r => Boolean(r.kapl) === want);
                return rows.length ? (
                  <optgroup key={g} label={g}>
                    {rows.map(r => (
                      <option key={r.value} value={r.value}>
                        {r.label}{r.signs_stage ? " — signs a stage" : ""}
                      </option>
                    ))}
                  </optgroup>
                ) : null;
              })}
            </select>
            <p style={{ fontSize: 11, color: "#6B7280", marginTop: 6, lineHeight: 1.5 }}>
              {helpFor(form.role)}
            </p>
          </div>
          <Btn onClick={create} full
            disabled={!form.username.trim() || form.password.length < 8}>Create user</Btn>
          <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 8 }}>
            They will be asked to set their own password at first sign-in.
          </p>
        </Card>

        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#374151" }}>Accounts</h3>
            <span style={{ fontSize: 12, color: "#9CA3AF" }}>{users.length} user{users.length === 1 ? "" : "s"}</span>
          </div>
          {loading ? <p style={{ color: "#9CA3AF", fontSize: 13 }}>Loading…</p> : (
            <div className="kz-scroll-y" style={{ display: "grid", gap: 8, maxHeight: "68vh", overflowY: "auto", paddingRight: 6 }}>
              {users.map(u => (
                <div key={u.id} style={{ padding: "10px 12px", background: "#F9FAFB", borderRadius: 8, opacity: u.is_active ? 1 : 0.55 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{u.username}</span>
                    <Badge text={u.role_label || u.role} color={roleColor(u.role)} />
                    {!u.is_active && <Badge text="DISABLED" color="#9CA3AF" />}
                    {u.must_change_password && <Badge text="MUST RESET PW" color="#F59E0B" />}
                    {u.username === me.username && <span style={{ fontSize: 11, color: "#9CA3AF" }}>(you)</span>}
                  </div>
                  {u.full_name && <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>{u.full_name}</div>}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {/* A two-way toggle cannot express five roles. Only a
                        Super Admin may hand out SUPER_ADMIN, so an Admin cannot
                        promote themselves past their own ceiling. */}
                    {/* The account's own role is always an option, even when
                        this admin could not grant it. It was not, and the six
                        accounts already holding a review role rendered with a
                        value matching no option — so the box showed the first
                        role in the list and said a Finance reviewer was a Super
                        Admin. A control that misreports what it is holding is
                        worse than one that refuses to change it. */}
                    <select value={u.role} onChange={e => patch(u.username, { role: e.target.value })}
                      title={helpFor(u.role)}
                      style={{ ...ES.input, width: "auto", minWidth: 190, padding: "4px 8px", fontSize: 12 }}>
                      {(offer.some(r => r.value === u.role)
                        ? offer
                        : [{ value: u.role, label: labelFor(u.role) }, ...offer]
                       ).map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <Btn small outline onClick={() => patch(u.username, { is_active: !u.is_active })}>
                      {u.is_active ? "Disable" : "Enable"}
                    </Btn>
                    <Btn small outline onClick={() => resetPw(u.username)}>Reset password</Btn>
                    {u.username !== me.username && (
                      <Btn small outline color="#DC2626" onClick={() => remove(u.username)}>Delete</Btn>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
// Engineering & Estimation Platform — Module 4.1 UI (Inquiry & Project Master)
// Talks to /api/eng/*. Shares the app's authFetch / Card / Btn / Badge.
// ══════════════════════════════════════════════════════════════════════════════

const ENG_CATEGORIES = ["PHARMA", "HOSPITAL", "LABORATORY", "FOOD", "ELECTRONICS", "AUTOMOTIVE", "COMFORT_HVAC", "INDUSTRIAL_VENTILATION", "OTHER"];
const ENG_EXEC = ["CLEANROOM_ONLY", "HVAC_ONLY", "CLEANROOM_HVAC", "TURNKEY_EPC", "MODIFICATION", "EXPANSION", "REPLACEMENT", "RENOVATION"];
const ENG_STATUSES = ["NEW", "QUALIFIED", "IN_ESTIMATION", "PROPOSAL_SENT", "WON", "LOST", "ON_HOLD", "CANCELLED"];
const ENG_GATES = ["GATE0_INQUIRY", "GATE1_DESIGN_INPUT", "GATE2_CONCEPT_TENDER", "GATE3_PROPOSAL_APPROVED", "GATE4_ORDER_BASELINE", "GATE5_DETAILED_AFC", "GATE6_EXECUTION", "GATE7_ASBUILT"];
const INQ_STATUS_COLORS = { NEW: "#6B7280", QUALIFIED: "#3B82F6", IN_ESTIMATION: "#F59E0B", PROPOSAL_SENT: "#8B5CF6", WON: "#10B981", LOST: "#EF4444", ON_HOLD: "#9CA3AF", CANCELLED: "#9CA3AF" };
const nice = (s) => (s || "").replace(/_/g, " ");
const gateLabel = (g) => nice(g).replace(/^GATE\d\s*/, "");

const ES = {
  input: { width: "100%", padding: "7px 10px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" },
  //: A small input or select that sits inline in a row of text.
  //:
  //: Not `ES.input` with a height on top of it. That is what the inline "fill
  //: this" controls did — 26px of border-box height against ES.input's 7px of
  //: vertical padding and a 1px border leaves ten pixels for eleven-pixel
  //: text, so the value in the box was clipped and the dropdown read as empty.
  //: Height comes from the padding here, which cannot contradict it.
  inputSm: { width: "100%", padding: "3px 8px", border: "1px solid #E5E7EB",
             borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
             fontFamily: "inherit", boxSizing: "border-box" },
  // background is not decoration: these headers are sticky inside .kz-content,
  // so a transparent one would let rows scroll through it.
  // Alignment is NOT set here: tableAlign.js gives a column's heading and its
  // values one class (num right, txt left, ctr centred) and index.css applies
  // it. An inline textAlign here would beat that and split them again.
  th: { padding: "8px 10px", fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap", background: "#fff" },
  td: { padding: "8px 10px", fontSize: 13, borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" },
};

function EngModal({ title, onClose, children, maxWidth = 620, actions = null }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} className="kz-modal" style={{ width: "100%", maxWidth, marginTop: 16 }}>
        <Card>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#1F4E79" }}>{title}</div>
            <div style={{ flex: 1 }} />
            {actions}
            <button onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 22, cursor: "pointer", color: "#9CA3AF", lineHeight: 1, marginLeft: 10 }}>×</button>
          </div>
          {children}
        </Card>
      </div>
    </div>
  );
}

function EngField({ label, children, half }) {
  return (
    <div style={{ marginBottom: 10, flex: half ? "1 1 46%" : "1 1 100%", minWidth: half ? 150 : 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function StatTile({ label, value, color = "#1F4E79" }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "12px 16px", minWidth: 110 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
    </div>
  );
}

// ── New-inquiry modal (with inline new-customer) ──────────────────────────────
// De-duplicated customer picker — loads all folded companies once, filters live.
function CustomerPicker({ onPick }) {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(false);
  useEffect(() => {
    authFetch(`${API}/api/eng/customers/grouped?limit=2000`).then(r => r.json())
      .then(d => setAll(d.items || [])).catch(() => setAll([]));
  }, []);
  const ql = q.trim().toLowerCase();
  const results = (ql ? all.filter(c => (c.company_name || "").toLowerCase().includes(ql)) : all);
  const choose = (c) => { setPicked(true); setQ(c.company_name); setOpen(false); onPick(c.id); };
  return (
    <div style={{ position: "relative" }}>
      <input value={q} placeholder={`Search ${all.length || ""} customers…`} onFocus={() => setOpen(true)}
        onChange={e => { setQ(e.target.value); setPicked(false); setOpen(true); onPick(""); }}
        style={{ ...ES.input, width: "100%" }} />
      {!picked && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{results.length} companies (duplicates folded){ql ? " matching" : ""}</div>}
      {open && results.length > 0 && (
        <div className="kz-scroll-y" style={{ position: "absolute", zIndex: 20, left: 0, right: 0, background: "#fff", border: "1px solid #D1D5DB", borderRadius: 8, marginTop: 2, maxHeight: 260, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
          {results.slice(0, 300).map(c => (
            <div key={c.id} onClick={() => choose(c)} style={{ padding: "7px 10px", cursor: "pointer", borderBottom: "1px solid #F3F4F6", fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"} onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              <span style={{ fontWeight: 600 }}>{c.company_name}</span>
              {c.city && <span style={{ color: "#9CA3AF" }}> · {c.city}</span>}
              {c.variant_count > 1 && <span style={{ color: "#8B5CF6", fontSize: 11 }}> · {c.variant_count} records</span>}
            </div>
          ))}
          {results.length > 300 && <div style={{ padding: "6px 10px", fontSize: 11, color: "#9CA3AF" }}>+{results.length - 300} more — keep typing to narrow</div>}
        </div>
      )}
    </div>
  );
}

function NewInquiryModal({ customers, onClose, onCreated }) {
  const [newCust, setNewCust] = useState(customers.filter(c => !c.is_consultant).length === 0);
  const [f, setF] = useState({
    customer_id: "", consultant_id: "", company_name: "", contact_person: "", email: "", city: "",
    site_name: "", site_city: "", salesperson: "", responsible_engineer: "",
    received_date: "", submission_deadline: "",
    project_category: "PHARMA", execution_type: "CLEANROOM_HVAC", priority: "MEDIUM", scope_summary: "",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const up = (k, v) => setF(s => ({ ...s, [k]: v }));
  const cust = customers.filter(c => !c.is_consultant);
  const cons = customers.filter(c => c.is_consultant);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      let customer_id = f.customer_id;
      if (newCust) {
        if (!f.company_name.trim()) throw new Error("Customer name is required.");
        const r = await authFetch(`${API}/api/eng/customers`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ company_name: f.company_name, contact_person: f.contact_person || null, email: f.email || null, city: f.city || null }) });
        const d = await jsonOrThrow(r, "Customer create failed");
        customer_id = d.id;
      }
      if (!customer_id) throw new Error("Select or add a customer.");
      const payload = {
        customer_id: Number(customer_id),
        consultant_id: f.consultant_id ? Number(f.consultant_id) : null,
        site_name: f.site_name || null, site_city: f.site_city || null,
        salesperson: f.salesperson || null, responsible_engineer: f.responsible_engineer || null,
        received_date: f.received_date || null, submission_deadline: f.submission_deadline || null,
        project_category: f.project_category, execution_type: f.execution_type,
        priority: f.priority, scope_summary: f.scope_summary || null,
      };
      const r2 = await authFetch(`${API}/api/eng/inquiries`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const d2 = await r2.json(); if (!r2.ok) throw new Error(d2.detail || "Inquiry create failed");
      onCreated(d2);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  return (
    <EngModal title="New inquiry" onClose={onClose}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0 14px" }}>
        <EngField label="Customer">
          {!newCust ? (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}><CustomerPicker onPick={id => up("customer_id", id)} /></div>
              <Btn small outline onClick={() => setNewCust(true)}>+ New</Btn>
            </div>
          ) : (
            <div style={{ border: "1px dashed #D1D5DB", borderRadius: 8, padding: 10 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0 10px" }}>
                <EngField label="Company name" half><input value={f.company_name} onChange={e => up("company_name", e.target.value)} style={ES.input} /></EngField>
                <EngField label="Contact person" half><input value={f.contact_person} onChange={e => up("contact_person", e.target.value)} style={ES.input} /></EngField>
                <EngField label="Email" half><input value={f.email} onChange={e => up("email", e.target.value)} style={ES.input} /></EngField>
                <EngField label="City" half><input value={f.city} onChange={e => up("city", e.target.value)} style={ES.input} /></EngField>
              </div>
              {cust.length > 0 && <button onClick={() => setNewCust(false)} style={{ border: "none", background: "transparent", color: "#3B82F6", fontSize: 12, cursor: "pointer", padding: 0 }}>↩ pick existing instead</button>}
            </div>
          )}
        </EngField>
        <EngField label="Consultant (optional)" half>
          <select value={f.consultant_id} onChange={e => up("consultant_id", e.target.value)} style={ES.input}>
            <option value="">— none —</option>
            {cons.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </EngField>
        <EngField label="Priority" half>
          <select value={f.priority} onChange={e => up("priority", e.target.value)} style={ES.input}>
            {["LOW", "MEDIUM", "HIGH"].map(p => <option key={p}>{p}</option>)}
          </select>
        </EngField>
        <EngField label="Site name" half><input value={f.site_name} onChange={e => up("site_name", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Site city" half><input value={f.site_city} onChange={e => up("site_city", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Project category" half>
          <select value={f.project_category} onChange={e => up("project_category", e.target.value)} style={ES.input}>
            {ENG_CATEGORIES.map(c => <option key={c} value={c}>{nice(c)}</option>)}
          </select>
        </EngField>
        <EngField label="Execution type" half>
          <select value={f.execution_type} onChange={e => up("execution_type", e.target.value)} style={ES.input}>
            {ENG_EXEC.map(c => <option key={c} value={c}>{nice(c)}</option>)}
          </select>
        </EngField>
        <EngField label="Salesperson" half><input value={f.salesperson} onChange={e => up("salesperson", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Responsible engineer" half><input value={f.responsible_engineer} onChange={e => up("responsible_engineer", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Received date" half><input type="date" value={f.received_date} onChange={e => up("received_date", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Submission deadline" half><input type="date" value={f.submission_deadline} onChange={e => up("submission_deadline", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Scope summary"><textarea value={f.scope_summary} onChange={e => up("scope_summary", e.target.value)} rows={2} style={{ ...ES.input, resize: "vertical" }} /></EngField>
      </div>
      {err && <div style={{ color: "#EF4444", fontSize: 13, margin: "4px 0 10px" }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
        <Btn outline color="#6B7280" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={busy}>{busy ? "Saving…" : "Create inquiry"}</Btn>
      </div>
    </EngModal>
  );
}

// ── Edit inquiry modal ────────────────────────────────────────────────────────
function EditInquiryModal({ inq, onClose, onSaved }) {
  const [f, setF] = useState({
    site_name: inq.site_name || "", site_city: inq.site_city || "",
    salesperson: inq.salesperson || "", responsible_engineer: inq.responsible_engineer || "",
    received_date: inq.received_date ? inq.received_date.slice(0, 10) : "",
    submission_deadline: inq.submission_deadline ? inq.submission_deadline.slice(0, 10) : "",
    project_category: inq.project_category, execution_type: inq.execution_type,
    priority: inq.priority || "MEDIUM", scope_summary: inq.scope_summary || "", remarks: inq.remarks || "",
  });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const up = (k, v) => setF(s => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = { ...f, received_date: f.received_date || null, submission_deadline: f.submission_deadline || null };
      const r = await authFetch(`${API}/api/eng/inquiries/${inq.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      await jsonOrThrow(r, "Update failed");   // throws with the real reason if it failed
      onSaved();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };
  return (
    <EngModal title={`Edit ${inq.inquiry_no}`} onClose={onClose}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0 14px" }}>
        <EngField label="Site name" half><input value={f.site_name} onChange={e => up("site_name", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Site city" half><input value={f.site_city} onChange={e => up("site_city", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Project category" half><select value={f.project_category} onChange={e => up("project_category", e.target.value)} style={ES.input}>{ENG_CATEGORIES.map(c => <option key={c} value={c}>{nice(c)}</option>)}</select></EngField>
        <EngField label="Execution type" half><select value={f.execution_type} onChange={e => up("execution_type", e.target.value)} style={ES.input}>{ENG_EXEC.map(c => <option key={c} value={c}>{nice(c)}</option>)}</select></EngField>
        <EngField label="Salesperson" half><input value={f.salesperson} onChange={e => up("salesperson", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Responsible engineer" half><input value={f.responsible_engineer} onChange={e => up("responsible_engineer", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Received date" half><input type="date" value={f.received_date} onChange={e => up("received_date", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Submission deadline" half><input type="date" value={f.submission_deadline} onChange={e => up("submission_deadline", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Priority" half><select value={f.priority} onChange={e => up("priority", e.target.value)} style={ES.input}>{["LOW", "MEDIUM", "HIGH"].map(p => <option key={p}>{p}</option>)}</select></EngField>
        <EngField label="Scope summary"><textarea value={f.scope_summary} onChange={e => up("scope_summary", e.target.value)} rows={2} style={{ ...ES.input, resize: "vertical" }} /></EngField>
        <EngField label="Remarks"><textarea value={f.remarks} onChange={e => up("remarks", e.target.value)} rows={2} style={{ ...ES.input, resize: "vertical" }} /></EngField>
      </div>
      {err && <div style={{ color: "#EF4444", fontSize: 13, margin: "4px 0 10px" }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
        <Btn outline color="#6B7280" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Btn>
      </div>
    </EngModal>
  );
}

// ── Inquiry detail (fields + status + convert + edit + history) ───────────────
function InquiryDetail({ id, onBack }) {
  const [inq, setInq] = useState(null);
  const [events, setEvents] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => {
    authFetch(`${API}/api/eng/inquiries/${id}`).then(r => r.json()).then(setInq).catch(() => { });
    authFetch(`${API}/api/eng/inquiries/${id}/events`).then(r => r.json()).then(d => setEvents(Array.isArray(d) ? d : [])).catch(() => { });
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_inquiries", "eng_customers", "eng_projects"], load);
  const setStatus = async (s) => { await authFetch(`${API}/api/eng/inquiries/${id}/status`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: s }) }); load(); };
  const [msgErr, setMsgErr] = useState(false);
  const say = (text, bad) => { setMsg(text); setMsgErr(!!bad); };
  const convert = async () => {
    try {
      const r = await authFetch(`${API}/api/eng/inquiries/${id}/convert`, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (r.ok) say(`Created project ${d.project_no} — open it from the Projects tab.`);
      else say(typeof d.detail === "string" ? d.detail : `Convert failed (HTTP ${r.status}).`, true);
    } catch (e) { say(`Convert failed: ${e.message || e}`, true); }
    load();
  };
  const restore = async () => {
    const r = await authFetch(`${API}/api/eng/projects/${inq.archived_project_id}/restore`, { method: "POST" });
    const d = await r.json().catch(() => ({}));
    if (r.ok) say(`${inq.archived_project_no} restored — open it from the Projects tab.`);
    else say(r.status === 403 ? "Only a Super Admin can restore an archived project."
                              : (typeof d.detail === "string" ? d.detail : `Restore failed (HTTP ${r.status}).`), true);
    load();
  };

  if (!inq) return <div style={{ color: "#9CA3AF" }}>Loading…</div>;
  const dt = (v) => fmtIST(v, "date");
  const F = (label, val) => (<div style={{ marginBottom: 10 }}><div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280" }}>{label}</div><div style={{ fontSize: 14, color: "#374151" }}>{val || "—"}</div></div>);

  return (
    <div>
      <button onClick={onBack} style={{ border: "none", background: "transparent", color: "#3B82F6", fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 12 }}>← All inquiries</button>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#1F4E79" }}>{inq.inquiry_no}</div>
            <div style={{ fontSize: 14, color: "#374151" }}>{inq.customer_name || `Customer #${inq.customer_id}`}</div>
          </div>
          <Badge text={nice(inq.status)} color={INQ_STATUS_COLORS[inq.status] || "#6B7280"} />
          <div style={{ flex: 1 }} />
          <select value={inq.status} onChange={e => setStatus(e.target.value)} style={{ ...ES.input, width: 160 }}>
            {ENG_STATUSES.map(s => <option key={s} value={s}>{nice(s)}</option>)}
          </select>
          <Btn small outline onClick={() => setShowEdit(true)}>Edit</Btn>
          {inq.has_project ? <Badge text="✓ Converted" color="#10B981" />
            : inq.archived_project_no ? <>
                <Badge text={`${inq.archived_project_no} archived`} color="#6B7280" />
                <Btn small outline onClick={restore}>Restore project</Btn>
              </>
            : <Btn small onClick={convert}>Convert to project →</Btn>}
        </div>
        {inq.archived_project_no && !inq.has_project && !msg && (
          <div style={{ fontSize: 12.5, color: "#6B7280", marginBottom: 12 }}>
            This inquiry was converted to {inq.archived_project_no}, which has been archived. An inquiry has one project,
            so it cannot be converted again — restore that project, or raise a new inquiry for a fresh one.
          </div>)}
        {msg && <div style={{ background: msgErr ? "#FEF2F2" : "#ECFDF5", border: `1px solid ${msgErr ? "#FECACA" : "#A7F3D0"}`,
                              color: msgErr ? "#991B1B" : "#065F46", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{msg}</div>}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }} className="kz-registers">
          {F("Site", [inq.site_name, inq.site_city].filter(Boolean).join(", "))}
          {F("Category", nice(inq.project_category))}
          {F("Execution type", nice(inq.execution_type))}
          {F("Salesperson", inq.salesperson)}
          {F("Responsible engineer", inq.responsible_engineer)}
          {F("Priority", inq.priority)}
          {F("Received", dt(inq.received_date))}
          {F("Submission deadline", dt(inq.submission_deadline))}
          {F("Quotation no", inq.quotation_no)}
        </div>
        {inq.scope_summary && F("Scope summary", inq.scope_summary)}
        {inq.remarks && F("Remarks", inq.remarks)}
      </Card>

      <Card>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#1F4E79", marginBottom: 10 }}>History</div>
        {events.length === 0 && <div style={{ color: "#9CA3AF", fontSize: 13 }}>No recorded events yet (imported records carry no prior history).</div>}
        {events.map((e, i) => (
          <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "1px solid #F3F4F6", fontSize: 13, flexWrap: "wrap" }}>
            <Badge text={nice(e.action)} color="#6366F1" />
            <div style={{ flex: 1, minWidth: 120 }}>{e.from_value ? `${e.from_value} → ${e.to_value}` : e.to_value}{e.notes ? ` · ${e.notes}` : ""}</div>
            <div style={{ color: "#9CA3AF" }}>{fmtIST(e.timestamp)} · {e.performed_by}</div>
          </div>
        ))}
      </Card>

      {showEdit && <EditInquiryModal inq={inq} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
    </div>
  );
}

// ── Inquiries tracker ─────────────────────────────────────────────────────────
function InquiriesView({ me }) {
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState({ status: "", category: "", search: "" });
  const [showNew, setShowNew] = useState(false);
  const [msg, setMsg] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchDraft, setSearchDraft] = useState("");
  const [sel, setSel] = useState(null);
  const perPage = 25;

  const syncZoho = async () => {
    setSyncing(true); setMsg("Syncing from Zoho CRM…");
    try {
      const r = await authFetch(`${API}/api/eng/zoho/sync`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Sync failed");
      setMsg(`Zoho sync complete — ${d.customers_new} new customers, ${d.inquiries_new} new inquiries (${d.inquiries_updated} updated).`);
      load();
    } catch (e) { setMsg("Zoho sync failed: " + (e.message || e)); }
    setSyncing(false);
    setTimeout(() => setMsg(""), 8000);
  };

  const load = useCallback(async () => {
    const qs = new URLSearchParams();
    if (filter.status) qs.set("status", filter.status);
    if (filter.category) qs.set("category", filter.category);
    if (filter.search) qs.set("search", filter.search);
    qs.set("page", page); qs.set("per_page", perPage);
    const [ir, sr] = await Promise.all([
      authFetch(`${API}/api/eng/inquiries?${qs}`).then(r => r.json()),
      authFetch(`${API}/api/eng/dashboard/stats`).then(r => r.json()),
    ]);
    setRows(ir.items || []); setTotal(ir.total || 0); setStats(sr);
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);
  useLive(["eng_inquiries", "eng_customers", "eng_projects"], load);
  // Debounce the search box → filter, and reset to page 1 on any new search.
  useEffect(() => {
    const t = setTimeout(() => { setFilter(s => ({ ...s, search: searchDraft })); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchDraft]);
  useEffect(() => { authFetch(`${API}/api/eng/customers`).then(r => r.json()).then(d => setCustomers(Array.isArray(d) ? d : [])).catch(() => { }); }, [showNew]);

  const setStatus = async (id, status) => {
    await authFetch(`${API}/api/eng/inquiries/${id}/status`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }) });
    load();
  };
  const convert = async (id) => {
    const r = await authFetch(`${API}/api/eng/inquiries/${id}/convert`, { method: "POST" });
    const d = await r.json();
    setMsg(r.ok ? `Created project ${d.project_no}. Open the Projects tab to build its rooms.` : (d.detail || "Convert failed"));
    setTimeout(() => setMsg(""), 5000); load();
  };

  const overdue = (r) => r.submission_deadline && new Date(r.submission_deadline) < new Date() && !["WON", "LOST", "CANCELLED", "PROPOSAL_SENT"].includes(r.status);

  if (sel) return <InquiryDetail id={sel} onBack={() => { setSel(null); load(); }} />;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 20, color: "#1F4E79" }}>Inquiries</div>
        <div style={{ fontSize: 13, color: "#9CA3AF" }}>— Engineering & Estimation</div>
        <div style={{ flex: 1 }} />
        {HAS(me, "zoho.sync") && <Btn outline onClick={syncZoho} disabled={syncing}>{syncing ? "Syncing…" : "⟳ Sync from Zoho"}</Btn>}
        <Btn onClick={() => setShowNew(true)}>+ New inquiry</Btn>
      </div>

      {stats && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          <StatTile label="Inquiries" value={stats.total_inquiries} />
          <StatTile label="Overdue" value={stats.overdue} color={stats.overdue ? "#EF4444" : "#1F4E79"} />
          <StatTile label="Projects" value={stats.projects} color="#10B981" />
          {Object.entries(stats.by_status || {}).map(([s, n]) => <StatTile key={s} label={nice(s)} value={n} color={INQ_STATUS_COLORS[s]} />)}
        </div>
      )}

      {msg && <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", color: "#065F46", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{msg}</div>}

      <Card style={{ padding: 0 }}>
        <div style={{ display: "flex", gap: 8, padding: 12, borderBottom: "1px solid #F3F4F6", flexWrap: "wrap" }}>
          <input placeholder="Search inquiry no / site…" value={searchDraft} onChange={e => setSearchDraft(e.target.value)} style={{ ...ES.input, maxWidth: 240 }} />
          <select value={filter.status} onChange={e => { setFilter(s => ({ ...s, status: e.target.value })); setPage(1); }} style={{ ...ES.input, maxWidth: 180 }}>
            <option value="">All statuses</option>
            {ENG_STATUSES.map(s => <option key={s} value={s}>{nice(s)}</option>)}
          </select>
          <select value={filter.category} onChange={e => { setFilter(s => ({ ...s, category: e.target.value })); setPage(1); }} style={{ ...ES.input, maxWidth: 180 }}>
            <option value="">All categories</option>
            {ENG_CATEGORIES.map(s => <option key={s} value={s}>{nice(s)}</option>)}
          </select>
        </div>
        <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["Inquiry No", "Customer", "Site", "Category", "Deadline", "Salesperson", "Status", ""].map(h => <th key={h} style={ES.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows === null && <tr><td colSpan={8} style={{ ...ES.td, color: "#9CA3AF" }}>Loading…</td></tr>}
              {rows && rows.length === 0 && <tr><td colSpan={8} style={{ ...ES.td, color: "#9CA3AF" }}>No inquiries yet — create the first one.</td></tr>}
              {rows && rows.map(r => (
                <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => setSel(r.id)} title="Open inquiry">
                  <td style={{ ...ES.td, fontWeight: 700, color: "#1F4E79" }}>{r.inquiry_no}</td>
                  <td style={ES.td}>{r.customer_name || `#${r.customer_id}`}</td>
                  <td style={ES.td}>{r.site_name || "—"}</td>
                  <td style={ES.td}>{nice(r.project_category)}</td>
                  <td style={{ ...ES.td, color: overdue(r) ? "#EF4444" : "#374151", fontWeight: overdue(r) ? 700 : 400 }}>
                    {r.submission_deadline ? r.submission_deadline.slice(0, 10) : "—"}{overdue(r) ? " ⚠" : ""}
                  </td>
                  <td style={ES.td}>{r.salesperson || "—"}</td>
                  <td style={ES.td}>
                    <select value={r.status} onClick={e => e.stopPropagation()} onChange={e => { e.stopPropagation(); setStatus(r.id, e.target.value); }}
                      style={{ border: "none", background: (INQ_STATUS_COLORS[r.status] || "#6B7280") + "20", color: INQ_STATUS_COLORS[r.status] || "#6B7280", borderRadius: 6, padding: "3px 6px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                      {ENG_STATUSES.map(s => <option key={s} value={s} style={{ background: "#fff", color: "#111" }}>{nice(s)}</option>)}
                    </select>
                  </td>
                  <td style={ES.td}>
                    {r.has_project
                      ? <span style={{ fontSize: 11, color: "#10B981", fontWeight: 700 }}>✓ project</span>
                      : <Btn small outline onClick={(e) => { e.stopPropagation(); convert(r.id); }}>Convert →</Btn>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderTop: "1px solid #F3F4F6", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#6B7280" }}>
            {total ? `${(page - 1) * perPage + 1}–${(page - 1) * perPage + (rows ? rows.length : 0)} of ${total}` : "0 results"}
          </span>
          <div style={{ flex: 1 }} />
          <Btn small outline disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</Btn>
          <span style={{ fontSize: 12, color: "#374151" }}>Page {page} / {Math.max(1, Math.ceil(total / perPage))}</span>
          <Btn small outline disabled={page >= Math.ceil(total / perPage)} onClick={() => setPage(p => p + 1)}>Next ›</Btn>
        </div>
      </Card>

      {showNew && <NewInquiryModal customers={customers} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

// ── New-room modal ────────────────────────────────────────────────────────────
function NewRoomModal({ projectId, onClose, onCreated, sc }) {
  // Which room fields this project's system asks for (input matrix); a
  // project with none (cleanroom, unset) sees every field as before.
  const S = (k) => !sc || sc.show("room", k);
  const [f, setF] = useState({ building: "Main Building", floor: "Ground Floor", zone: "General", room_no: "", name: "", length_m: "", width_m: "", height_m: "", cleanroom_class: "", temperature_c: "", rh_percent: "", pressure_pa: "", occupancy: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const up = (k, v) => setF(s => ({ ...s, [k]: v }));
  const num = (v) => (v === "" || v === null ? null : Number(v));

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      if (!f.name.trim()) throw new Error("Room name is required.");
      const payload = {
        building: f.building || "Main Building", floor: f.floor || "Ground Floor", zone: f.zone || "General",
        room_no: f.room_no || null, name: f.name,
        length_m: num(f.length_m), width_m: num(f.width_m), height_m: num(f.height_m),
        cleanroom_class: f.cleanroom_class || null, temperature_c: num(f.temperature_c),
        rh_percent: num(f.rh_percent), pressure_pa: num(f.pressure_pa), occupancy: num(f.occupancy),
      };
      const r = await authFetch(`${API}/api/eng/projects/${projectId}/rooms`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const d = await jsonOrThrow(r, "Add room failed");
      onCreated(d);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  return (
    <EngModal title="Add room" onClose={onClose}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0 14px" }}>
        <EngField label="Building" half><input value={f.building} onChange={e => up("building", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Floor" half><input value={f.floor} onChange={e => up("floor", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Zone" half><input value={f.zone} onChange={e => up("zone", e.target.value)} style={ES.input} /></EngField>
        {S("cleanroom_class") && <EngField label={<>Cleanroom class<FieldHelp field="cleanroom_class" /></>} half><input value={f.cleanroom_class} onChange={e => up("cleanroom_class", e.target.value)} placeholder="ISO 7 / Grade C" style={ES.input} /></EngField>}
        <EngField label="Room no." half><input value={f.room_no} onChange={e => up("room_no", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Room name" half><input value={f.name} onChange={e => up("name", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Length (m)" half><input type="number" value={f.length_m} onChange={e => up("length_m", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Width (m)" half><input type="number" value={f.width_m} onChange={e => up("width_m", e.target.value)} style={ES.input} /></EngField>
        <EngField label="Height (m)" half><input type="number" value={f.height_m} onChange={e => up("height_m", e.target.value)} style={ES.input} /></EngField>
        {S("occupancy") && <EngField label="Occupancy" half><input type="number" value={f.occupancy} onChange={e => up("occupancy", e.target.value)} style={ES.input} /></EngField>}
        <EngField label="Temp (°C)" half><input type="number" value={f.temperature_c} onChange={e => up("temperature_c", e.target.value)} style={ES.input} /></EngField>
        <EngField label="RH (%)" half><input type="number" value={f.rh_percent} onChange={e => up("rh_percent", e.target.value)} style={ES.input} /></EngField>
        {S("pressure_pa") && <EngField label="Pressure (Pa)" half><input type="number" value={f.pressure_pa} onChange={e => up("pressure_pa", e.target.value)} style={ES.input} /></EngField>}
      </div>
      {err && <div style={{ color: "#EF4444", fontSize: 13, margin: "4px 0 10px" }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
        <Btn outline color="#6B7280" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={busy}>{busy ? "Saving…" : "Add room"}</Btn>
      </div>
    </EngModal>
  );
}

// ── URS / room design-inputs editor (Module 4.2) ──────────────────────────────
function RoomDesignModal({ room, onClose, onSaved, canEdit = true, sc }) {
  const d = room.design || {};
  // The project's input matrix (KAPL ENG VRF IN 001 for a VRV / VRF project)
  // decides which of these the room is asked for; a hidden field keeps its
  // stored value (it is resent unchanged below). No matrix: all, as before.
  const S = (k) => !sc || sc.show("room", k);
  const vrf = !!(sc && sc.applies);
  const L = (k, text) => <>{text}{vrf && sc.required("room", k) ? " *" : ""}<ScopeMark sc={sc} rec="room" k={k} /></>;
  const areaLocked = vrf && !sc.editable("room", "area_m2");
  const [clearGrade, setClearGrade] = useState(false);
  const [f, setF] = useState({
    ach: d.ach ?? "", supply_air_cmh: d.supply_air_cmh ?? "", fresh_air_cmh: d.fresh_air_cmh ?? "",
    exhaust_cmh: d.exhaust_cmh ?? "", pressure_polarity: d.pressure_polarity || "",
    process_load_w: d.process_load_w ?? "", equipment_load_w: d.equipment_load_w ?? "",
    lighting_load_w: d.lighting_load_w ?? "", working_hours: d.working_hours ?? "",
    applicable_standards: d.applicable_standards || "", customer_spec: d.customer_spec || "", remarks: d.remarks || "",
    // The room's own geometry and conditions, which were readable everywhere
    // and editable nowhere. A room master extracted from a customer document
    // carries whatever that document gave — KAPL's IIT-BHU workbook gives area
    // for all seven rooms and length/width for two — and the BOQ then blocks
    // on "Room length, width or height missing" with no box to supply them.
    length_m: room.length_m ?? "", width_m: room.width_m ?? "",
    height_m: room.height_m ?? "", area_m2: room.area_m2 ?? "",
    cleanroom_class: room.cleanroom_class || "",
    filter_coverage_pct: room.filter_coverage_pct ?? "",
    hepa_face_velocity_ms: room.hepa_face_velocity_ms ?? "",
    fresh_air_ach: room.fresh_air_ach ?? "",
    pressure_makeup_cmh: room.pressure_makeup_cmh ?? "",
    equipment_diversity: room.equipment_diversity ?? "",
    envelope_w_per_m2: room.envelope_w_per_m2 ?? "",
    temperature_c: room.temperature_c ?? "", rh_percent: room.rh_percent ?? "",
    pressure_pa: room.pressure_pa ?? "", occupancy: room.occupancy ?? "",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const up = (k, v) => setF(s => ({ ...s, [k]: v }));
  const num = (v) => (v === "" || v === null ? null : Number(v));

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = {
        ach: num(f.ach), supply_air_cmh: num(f.supply_air_cmh), fresh_air_cmh: num(f.fresh_air_cmh),
        exhaust_cmh: num(f.exhaust_cmh), pressure_polarity: f.pressure_polarity || null,
        process_load_w: num(f.process_load_w), equipment_load_w: num(f.equipment_load_w),
        lighting_load_w: num(f.lighting_load_w), working_hours: num(f.working_hours),
        applicable_standards: f.applicable_standards || null, customer_spec: f.customer_spec || null, remarks: f.remarks || null,
      };
      const r = await authFetch(`${API}/api/eng/rooms/${room.id}/design`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      await jsonOrThrow(r, "Save failed");   // throws with the real reason if it failed

      // The room's own fields go to a different endpoint, and only the ones
      // actually changed are sent: PATCH distinguishes "left alone" from
      // "cleared", and sending every box every time would turn an untouched
      // blank into a deliberate erasure.
      const roomFields = {};
      for (const k of ["length_m", "width_m", "height_m", "area_m2",
                       "temperature_c", "rh_percent", "pressure_pa", "occupancy",
                       "filter_coverage_pct", "hepa_face_velocity_ms",
                       "fresh_air_ach", "pressure_makeup_cmh",
                       "equipment_diversity", "envelope_w_per_m2"]) {
        const was = room[k] ?? "";
        if (String(f[k]) !== String(was)) roomFields[k] = num(f[k]);
      }
      if (String(f.cleanroom_class) !== String(room.cleanroom_class || "")) {
        roomFields.cleanroom_class = f.cleanroom_class || null;
      }
      if (clearGrade && room.gmp_grade) roomFields.gmp_grade = null;
      if (Object.keys(roomFields).length) {
        const r2 = await authFetch(`${API}/api/eng/rooms/${room.id}`, {
          method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(roomFields) });
        await jsonOrThrow(r2, "The URS inputs saved, but the room fields did not");
      }
      onSaved();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  // Clearing the class is part of this form's save (not a save of its own),
  // so nothing else typed here is lost.
  const clearClass = () => { up("cleanroom_class", ""); setClearGrade(true); };
  const ursShown = ["filter_coverage_pct", "hepa_face_velocity_ms", "fresh_air_ach", "pressure_makeup_cmh",
                    "equipment_diversity", "envelope_w_per_m2", "ach", "pressure_polarity", "fresh_air_cmh",
                    "exhaust_cmh", "supply_air_cmh", "working_hours", "process_load_w", "equipment_load_w",
                    "lighting_load_w", "applicable_standards"].some(S);

  return (
    <EngModal title={`${vrf ? "Room inputs" : "URS inputs"} — ${room.room_no || ""} ${room.name}`} onClose={onClose}>
      {vrf && <ScopeNote sc={sc}>The VRF room inputs — application, hours, RH control, people, lighting, equipment,
        motors, envelope and air streams — are entered on Engineering Flow → stage 4 (Calculation → Inputs).</ScopeNote>}
      {vrf && (room.cleanroom_class || room.gmp_grade) && clearGrade && (
        <div style={{ fontSize: 12.5, color: "#92400E", background: "#FFFBEB", border: "1px solid #FDE68A",
                      borderRadius: 8, padding: "7px 11px", marginBottom: 10 }}>
          The class will be cleared when you save.</div>)}
      {vrf && (room.cleanroom_class || room.gmp_grade) && !clearGrade && (
        <div style={{ fontSize: 12.5, color: "#991B1B", background: "#FEF2F2", border: "1px solid #FECACA",
                      borderRadius: 8, padding: "7px 11px", marginBottom: 10 }}>
          This room still carries {room.cleanroom_class ? `class ${room.cleanroom_class}` : ""}
          {room.cleanroom_class && room.gmp_grade ? " and " : ""}{room.gmp_grade ? `GMP grade ${room.gmp_grade}` : ""} —
          a cleanroom parameter outside the VRV / VRF input matrix; the VRF engine blocks a classified room.
          {canEdit && <> <button onClick={clearClass} disabled={busy}
            style={{ marginLeft: 6, fontSize: 12, border: "1px solid #FCA5A5", background: "#fff", color: "#991B1B",
                     borderRadius: 6, padding: "2px 8px", cursor: "pointer" }}>Clear it on save — this is an ordinary room</button></>}
        </div>)}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0 14px" }}>
        <div style={{ width: "100%", fontSize: 10.5, fontWeight: 700,
                      letterSpacing: 0.4, color: "#9CA3AF", margin: "2px 0 6px" }}>
          THE ROOM ITSELF
        </div>
        {S("cleanroom_class") && <EngField label={<>Cleanroom class<FieldHelp field="cleanroom_class" /></>} half>
          <input disabled={!canEdit} value={f.cleanroom_class} onChange={e => up("cleanroom_class", e.target.value)}
                 placeholder="ISO-6" style={ES.input} />
        </EngField>}
        {S("area_m2") && <EngField label={<>{L("area_m2", "Area (m²)")}<FieldHelp field="area_m2" /></>} half>
          <input disabled={!canEdit || areaLocked} type="number" step="any" value={f.area_m2}
                 title={areaLocked ? "Auto-calculated from length × width (input matrix: A)" : undefined}
                 onChange={e => up("area_m2", e.target.value)} style={ES.input} />
        </EngField>}
        <EngField label={L("length_m", "Length (m)")} half>
          <input disabled={!canEdit} type="number" step="any" value={f.length_m}
                 onChange={e => up("length_m", e.target.value)} style={ES.input} />
        </EngField>
        <EngField label={L("width_m", "Width (m)")} half>
          <input disabled={!canEdit} type="number" step="any" value={f.width_m}
                 onChange={e => up("width_m", e.target.value)} style={ES.input} />
        </EngField>
        <EngField label={<>{L("height_m", "Ceiling height (m)")}<FieldHelp field="height_m" /></>} half>
          <input disabled={!canEdit} type="number" step="any" value={f.height_m}
                 onChange={e => up("height_m", e.target.value)} style={ES.input} />
        </EngField>
        {S("occupancy") && <EngField label="Occupancy (people)" half>
          <input disabled={!canEdit} type="number" value={f.occupancy}
                 onChange={e => up("occupancy", e.target.value)} style={ES.input} />
        </EngField>}
        <EngField label={L("temperature_c", "Temperature (°C)")} half>
          <input disabled={!canEdit} type="number" step="any" value={f.temperature_c}
                 onChange={e => up("temperature_c", e.target.value)} style={ES.input} />
        </EngField>
        <EngField label={L("rh_percent", "Relative humidity (%)")} half>
          <input disabled={!canEdit} type="number" step="any" value={f.rh_percent}
                 onChange={e => up("rh_percent", e.target.value)} style={ES.input} />
        </EngField>
        {S("pressure_pa") && <EngField label="Pressure (Pa)" half>
          <input disabled={!canEdit} type="number" step="any" value={f.pressure_pa}
                 onChange={e => up("pressure_pa", e.target.value)} style={ES.input} />
        </EngField>}
        {/* Said rather than left to be discovered by a BOQ that blocks: area
            follows length x width whenever both are given, because the two
            must not be allowed to disagree. */}
        {(f.length_m && f.width_m) ? (
          <div style={{ width: "100%", fontSize: 11, color: "#6B7280",
                        margin: "-4px 0 8px" }}>
            Area will be set to {(Number(f.length_m) * Number(f.width_m)).toFixed(2)} m²
            from length × width.
          </div>
        ) : null}
        <div style={{ width: "100%", fontSize: 10.5, fontWeight: 700,
                      letterSpacing: 0.4, color: "#9CA3AF", margin: "8px 0 6px" }}>
          {ursShown ? "URS DESIGN INPUTS" : "NOTES"}
        </div>
        {S("filter_coverage_pct") && <EngField label={<>HEPA coverage (0–1)<FieldHelp field="filter_coverage_pct" /></>} half>
          <input disabled={!canEdit} type="number" step="any" value={f.filter_coverage_pct}
                 onChange={e => up("filter_coverage_pct", e.target.value)}
                 placeholder="e.g. 0.5" style={ES.input} />
        </EngField>}
        {S("hepa_face_velocity_ms") && <EngField label={<>Face velocity (m/s)<FieldHelp field="hepa_face_velocity_ms" /></>} half>
          <input disabled={!canEdit} type="number" step="any" value={f.hepa_face_velocity_ms}
                 onChange={e => up("hepa_face_velocity_ms", e.target.value)}
                 placeholder="0.45" style={ES.input} />
        </EngField>}
        {/* CFM-007/008. Outdoor air, which is NOT a share of supply — a
            cleanroom recirculates nearly all its supply through the ceiling
            HEPAs. Leaving these blank keeps the old 10%-of-supply estimate. */}
        {S("fresh_air_ach") && <EngField label={<>Fresh-air ACH<FieldHelp field="fresh_air_ach" /></>} half>
          <input disabled={!canEdit} type="number" step="any" value={f.fresh_air_ach}
                 onChange={e => up("fresh_air_ach", e.target.value)}
                 placeholder="e.g. 2" style={ES.input} />
        </EngField>}
        {S("pressure_makeup_cmh") && <EngField label={<>Pressure makeup (m³/h)<FieldHelp field="pressure_makeup_cmh" /></>} half>
          <input disabled={!canEdit} type="number" step="any" value={f.pressure_makeup_cmh}
                 onChange={e => up("pressure_makeup_cmh", e.target.value)}
                 placeholder="e.g. 85" style={ES.input} />
        </EngField>}
        {S("equipment_diversity") && <EngField label={<>Equipment diversity (0–1)<FieldHelp field="equipment_diversity" /></>} half>
          <input disabled={!canEdit} type="number" step="any" value={f.equipment_diversity}
                 onChange={e => up("equipment_diversity", e.target.value)}
                 placeholder="1 = all at once" style={ES.input} />
        </EngField>}
        {S("envelope_w_per_m2") && <EngField label={<>Envelope allowance (W/m²)<FieldHelp field="envelope_w_per_m2" /></>} half>
          <input disabled={!canEdit} type="number" step="any" value={f.envelope_w_per_m2}
                 onChange={e => up("envelope_w_per_m2", e.target.value)}
                 placeholder="blank = 15" style={ES.input} />
        </EngField>}
        {S("ach") && <EngField label={<>ACH (changes/hr)<FieldHelp field="ach" /></>} half><input disabled={!canEdit} type="number" value={f.ach} onChange={e => up("ach", e.target.value)} style={ES.input} /></EngField>}
        {S("pressure_polarity") && <EngField label="Pressure polarity" half>
          <select disabled={!canEdit} value={f.pressure_polarity} onChange={e => up("pressure_polarity", e.target.value)} style={ES.input}>
            <option value="">—</option>{["POSITIVE", "NEGATIVE", "NEUTRAL"].map(p => <option key={p}>{p}</option>)}
          </select>
        </EngField>}
        {S("fresh_air_cmh") && <EngField label="Fresh air (m³/h)" half><input disabled={!canEdit} type="number" value={f.fresh_air_cmh} onChange={e => up("fresh_air_cmh", e.target.value)} style={ES.input} /></EngField>}
        {S("exhaust_cmh") && <EngField label="Exhaust (m³/h)" half><input disabled={!canEdit} type="number" value={f.exhaust_cmh} onChange={e => up("exhaust_cmh", e.target.value)} style={ES.input} /></EngField>}
        {S("supply_air_cmh") && <EngField label="Supply air (m³/h)" half><input disabled={!canEdit} type="number" value={f.supply_air_cmh} onChange={e => up("supply_air_cmh", e.target.value)} style={ES.input} /></EngField>}
        {S("working_hours") && <EngField label="Working hours/day" half><input disabled={!canEdit} type="number" value={f.working_hours} onChange={e => up("working_hours", e.target.value)} style={ES.input} /></EngField>}
        {S("process_load_w") && <EngField label="Process load (W)" half><input disabled={!canEdit} type="number" value={f.process_load_w} onChange={e => up("process_load_w", e.target.value)} style={ES.input} /></EngField>}
        {S("equipment_load_w") && <EngField label="Equipment load (W)" half><input disabled={!canEdit} type="number" value={f.equipment_load_w} onChange={e => up("equipment_load_w", e.target.value)} style={ES.input} /></EngField>}
        {S("lighting_load_w") && <EngField label="Lighting load (W)" half><input disabled={!canEdit} type="number" value={f.lighting_load_w} onChange={e => up("lighting_load_w", e.target.value)} style={ES.input} /></EngField>}
        {S("applicable_standards") && <EngField label="Applicable standards"><input disabled={!canEdit} value={f.applicable_standards} onChange={e => up("applicable_standards", e.target.value)} placeholder="ISO 14644-1; EU GMP Annex 1" style={ES.input} /></EngField>}
        {S("customer_spec") && <EngField label={L("customer_spec", "Customer specification")}><textarea disabled={!canEdit} value={f.customer_spec} onChange={e => up("customer_spec", e.target.value)} rows={2} style={{ ...ES.input, resize: "vertical" }} /></EngField>}
        <EngField label="Remarks"><textarea disabled={!canEdit} value={f.remarks} onChange={e => up("remarks", e.target.value)} rows={2} style={{ ...ES.input, resize: "vertical" }} /></EngField>
      </div>
      {err && <div style={{ color: "#EF4444", fontSize: 13, margin: "4px 0 10px" }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
        <Btn outline color="#6B7280" onClick={onClose}>{canEdit ? "Cancel" : "Close"}</Btn>
        {canEdit
          ? <Btn onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save URS inputs"}</Btn>
          : <span style={{ fontSize: 12, color: "#6B7280", alignSelf: "center" }}>
              Read-only for your role
            </span>}
      </div>
    </EngModal>
  );
}

function DesignBasisBanner({ basis }) {
  const ok = basis.ready;
  return (
    <div style={{ background: ok ? "#ECFDF5" : "#FFFBEB", border: `1px solid ${ok ? "#A7F3D0" : "#FDE68A"}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
      <b style={{ color: ok ? "#065F46" : "#92400E" }}>{ok ? "✓ Design basis ready" : "⚠ Design inputs incomplete"}</b>
      {" "}— {basis.room_count} room(s).
      {basis.rooms_with_gaps?.length > 0 && (
        <div style={{ marginTop: 4, color: "#92400E" }}>
          Missing: {basis.rooms_with_gaps.map(g => `${g.room} (${g.missing.join(", ")})`).join("; ")}
        </div>
      )}
    </div>
  );
}

// ── Project detail (tree + rooms + stage gate) ────────────────────────────────
function ProjectDetail({ id, me, onBack, onGoTo }) {
  // Whether this reader may change the schedule at all. Every editable cell
  // asks the same question in the same place — an SCM approver could open a
  // cell, type a width, and be told by the server afterwards that their role
  // does not allow it.
  const canEditProject = HAS(me, "project.edit");
  const [p, setP] = useState(null);
  const [showRoom, setShowRoom] = useState(false);
  const [designRoom, setDesignRoom] = useState(null);
  const [basis, setBasis] = useState(null);
  const loadBasis = useCallback(() => authFetch(`${API}/api/eng/projects/${id}/design-basis`).then(r => r.json()).then(setBasis).catch(() => { }), [id]);
  const load = useCallback(() => { authFetch(`${API}/api/eng/projects/${id}`).then(r => r.json()).then(setP).catch(() => { }); loadBasis(); }, [id, loadBasis]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_projects", "eng_project_design_basis", "eng_project_events", "eng_inquiries"], load);
  // The project's input matrix (KAPL ENG VRF IN 001 on a VRV / VRF project):
  // which room columns and fields apply. Re-read when the system changes.
  const sc = useInputScope(API, authFetch, id, `${p?.system_category || ""}:${p?.room_count || 0}`);
  // Room schedule columns: [header, room field] — each shown only where the
  // project's input matrix applies it (a project without one sees all).
  const ROOM_COLS = [["Room No", "room_no"], ["Name", "name"], ["L (m)", "length_m"], ["W (m)", "width_m"],
                     ["H (m)", "height_m"], ["Area m²", "area_m2"], ["Class", "cleanroom_class"], ["ACH", "ach"],
                     ["T °C", "temperature_c"], ["RH %", "rh_percent"], ["Pa", "pressure_pa"], ["Occ", "occupancy"],
                     ["HEPA cov", "filter_coverage_pct"]].filter(([, k]) => sc.show("room", k));

  const advance = async (gate) => { await authFetch(`${API}/api/eng/projects/${id}/advance`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ stage_gate: gate }) }); load(); };

  if (!p) return <div style={{ color: "#9CA3AF" }}>Loading…</div>;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
        <button onClick={onBack} style={{ border: "none", background: "transparent", color: "#3B82F6", fontSize: 13, cursor: "pointer", padding: 0 }}>← All projects</button>
        <div style={{ flex: 1 }} />
        {HAS(me, "project.delete") && <DeleteButton
          what={`project ${p.project_no}`}
          onDelete={() => authFetch(`${API}/api/eng/projects/${id}`, { method: "DELETE" })}
          onDone={onBack} />}
      </div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#1F4E79" }}>{p.project_no}</div>
            <div style={{ fontSize: 14, color: "#374151" }}>{p.name}</div>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>Inquiry {p.inquiry_no} · {p.room_count} room(s) · rev {p.revision}</div>
          </div>
          <div style={{ flex: 1 }} />
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", marginBottom: 4 }}>STAGE GATE</div>
            <select value={p.stage_gate} onChange={e => advance(e.target.value)} style={{ ...ES.input, minWidth: 200 }}>
              {ENG_GATES.map((g, i) => <option key={g} value={g}>{i}. {gateLabel(g)}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #F3F4F6" }}>
          <TeamEditor api={API} authFetch={authFetch} project={p} canEdit={canEditProject}
            onSaved={d => setP(prev => ({ ...prev, ...d, buildings: prev.buildings }))} />
        </div>
      </Card>

      {/* KAPL Engg process flow: requirements → room master → constants →
          calculation → BOQ, each step's state read from its records. */}
      <ProjectSetup api={API} authFetch={authFetch} projectId={id} onGoTo={onGoTo} />

      {basis && <DesignBasisBanner basis={basis} />}

      {/* Directly under the banner that says the inputs are incomplete, because
          these files are the inputs — the layout drawing, the URS, the
          equipment list. The same register as the engineering flow's stage 1,
          the same rows: attach it in either place and it is one document with
          one revision history, not two copies drifting apart. */}
      <Card style={{ marginBottom: 16 }}>
        <Suspense fallback={<div style={{ color: "#9CA3AF", fontSize: 13 }}>Loading documents…</div>}>
          <DocumentRegister api={API} authFetch={authFetch} projectId={id}
            canEdit={HAS(me, "project.edit")} onChanged={load} />
        </Suspense>
      </Card>

      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#1F4E79" }}>Room schedule</div>
        <div style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8 }}>— click any value to edit it; click the row for the {sc.applies ? "room's inputs" : "full URS inputs"}</div>
        <div style={{ flex: 1 }} />
        {canEditProject && <Btn small onClick={() => setShowRoom(true)}>+ Add room</Btn>}
      </div>

      <ScopeNote sc={sc}>Area is calculated from L × W. The VRF room inputs (people, lighting, equipment,
        envelope, air streams) are on Engineering Flow → stage 4.</ScopeNote>
      <HiddenValues sc={sc} />

      {(!p.buildings || p.buildings.length === 0) && <Card><div style={{ color: "#9CA3AF", fontSize: 13 }}>No rooms yet. Add the first room — its building / floor / zone are created automatically.</div></Card>}

      {p.buildings && p.buildings.map(b => (
        <Card key={b.id} style={{ marginBottom: 12, padding: 0 }}>
          <div style={{ padding: "10px 14px", fontWeight: 700, color: "#1F4E79", borderBottom: "1px solid #F3F4F6" }}>🏢 {b.name}</div>
          {b.floors.map(fl => (
            <div key={fl.id}>
              <div style={{ padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#6B7280", background: "#FAFAFA" }}>▸ {fl.name}</div>
              {fl.zones.map(z => (
                <div key={z.id}>
                  <div style={{ padding: "4px 20px", fontSize: 12, color: "#9CA3AF" }}>Zone: {z.name}{z.cleanroom_class && sc.show("room", "cleanroom_class") ? <> · <Badge text={z.cleanroom_class} color="#06B6D4" /></> : null}</div>
                  <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      {/* L, W and H are three columns now, not one cell of
                          "– × – × –": you cannot type into a joined string,
                          and the height is the one the BOQ blocks on. */}
                      <thead><tr>{ROOM_COLS.map(([h, k]) => (
                        <th key={h} className={ac(h)} style={ES.th} title={sc.help("room", k) || undefined}>
                          {h}{sc.required("room", k) ? " *" : ""}<ScopeMark sc={sc} rec="room" k={k} /></th>))}
                        <th className="ctr" style={ES.th}>{sc.applies ? "Inputs" : "URS"}</th></tr></thead>
                      <tbody>
                        {z.rooms.map(rm => (
                          <tr key={rm.id} style={{ cursor: "pointer" }}
                              onClick={() => setDesignRoom(rm)}
                              title={canEditProject ? "Set URS / design inputs"
                                                    : "View the URS / design inputs"}>
                            {ROOM_COLS.map(([h, k]) => {
                              const ed = canEditProject && sc.editable("room", k);
                              if (k === "room_no") return <td key={k} className={ac(h)} style={{ ...ES.td, fontWeight: 600 }}>{rm.room_no || "—"}</td>;
                              if (k === "name") return <td key={k} className={ac(h)} style={ES.td}>{rm.name}</td>;
                              if (k === "area_m2") return <RoomCell key={k} canEdit={ed} cls={ac(h)} room={rm} field="area_m2" value={rm.area_m2} onSaved={load} format={m2} />;
                              if (k === "cleanroom_class") return <RoomCell key={k} canEdit={ed} room={rm} field="cleanroom_class" value={rm.cleanroom_class} onSaved={load} width={78} cls={ac(h)} />;
                              if (k === "ach") return <RoomCell key={k} canEdit={ed} cls={ac(h)} room={rm} field="ach" value={rm.design?.ach} onSaved={load} />;
                              if (k === "filter_coverage_pct") return (
                                /* Coverage sets the supply airflow on a cleanroom
                                   and usually governs it — 2.4x the air-change
                                   figure on KAPL's IIT-BHU rooms. It belongs in
                                   the schedule beside the ACH it competes with. */
                                <RoomCell key={k} canEdit={ed} cls={ac(h)} room={rm} field="filter_coverage_pct"
                                  value={rm.filter_coverage_pct} onSaved={load} width={64} />);
                              return <RoomCell key={k} canEdit={ed} cls={ac(h)} room={rm} field={k} value={rm[k]} onSaved={load}
                                width={["length_m", "width_m", "height_m"].includes(k) ? 60 : undefined} />;
                            })}
                            <td className="ctr" style={{ ...ES.td, color: rm.design ? "#10B981" : "#3B82F6", fontWeight: 600 }}>{rm.design ? "✓" : "Set →"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </Card>
      ))}

      <div id="project-boq" style={{ marginTop: 22 }}>
        <SystemPanel projectId={id} />
        <BoqPanel projectId={id} />
      </div>

      <div style={{ marginTop: 22 }}>
        <DrawingsPanel projectId={id} project={p} />
      </div>

      {HAS(me, "costing.view") && (
        <>
          <div style={{ marginTop: 22 }}>
            <CostingPanel projectId={id} />
          </div>
          <div style={{ marginTop: 22 }}>
            <ProposalPanel projectId={id} />
          </div>
        </>
      )}

      {showRoom && <NewRoomModal projectId={id} sc={sc} onClose={() => setShowRoom(false)} onCreated={() => { setShowRoom(false); load(); }} />}
      {/* The row still opens for a reader who may not edit — seeing the URS
          inputs is not the same act as changing them — but it opens read-only
          rather than offering a form that will be refused on save. */}
      {designRoom && <RoomDesignModal room={designRoom} canEdit={canEditProject} sc={sc}
                        onClose={() => setDesignRoom(null)}
                        onSaved={() => { setDesignRoom(null); load(); }} />}
    </div>
  );
}

// ── Cleanroom Auto-BOQ (Module 4.4) ───────────────────────────────────────────
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// The BOQ element master: what is taken off, in what unit, linked to which SKU.
// Its prices are rate sources (KAPL B1) — kept on the Rates & Policy page.
function ElementMasterModal({ onClose, onSaved }) {
  const [els, setEls] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [onlyUnpriced, setOnlyUnpriced] = useState(false);
  const [dirty, setDirty] = useState({});
  const load = () => authFetch(`${API}/api/eng/cleanroom-elements`).then(r => r.json())
    .then(d => setEls(Array.isArray(d) ? d : []));
  useEffect(() => { load(); }, []);
  const up = (id, k, v) => {
    setEls(s => s.map(e => e.id === id ? { ...e, [k]: v } : e));
    setDirty(d => ({ ...d, [id]: true }));
  };
  const saveAll = async () => {
    setBusy(true);
    for (const e of els.filter(x => dirty[x.id])) {
      await authFetch(`${API}/api/eng/cleanroom-elements/${e.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          default_wastage_pct: Number(e.default_wastage_pct) || 0,
          per_room_qty: e.per_room_qty === "" || e.per_room_qty === null ? null : Number(e.per_room_qty),
          item_code: (e.item_code || "").trim() || null,
        }),
      });
    }
    setDirty({}); await load(); setBusy(false); onSaved();
  };
  const shown = els.filter(e => {
    if (onlyUnpriced && e.sources_current) return false;
    const t = q.trim().toLowerCase();
    return !t || e.name.toLowerCase().includes(t) || e.key.toLowerCase().includes(t)
        || (e.item_code || "").toLowerCase().includes(t);
  });
  const unpriced = els.filter(e => !e.sources_current).length;

  return (
    <EngModal title="BOQ element master" onClose={onClose} maxWidth={980}>
      <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 8, lineHeight: 1.5 }}>
        What each element takes off, in which unit, linked to which item-master SKU. Its <b>base cost</b> is a
        rate source on the <b>Rates &amp; Policy</b> page (KAPL B1: project quotation → dealer / project price →
        Rate Master → validated Zoho cost → authorised allowance), approved and dated — never typed here.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search element or SKU…"
          style={{ ...ES.input, flex: 1, minWidth: 180 }} />
        <label style={{ fontSize: 12, color: "#374151", display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
          <input type="checkbox" checked={onlyUnpriced} onChange={e => setOnlyUnpriced(e.target.checked)} />
          Without a current general source only
        </label>
        <span style={{ fontSize: 12, fontWeight: 700, color: unpriced ? "#DC2626" : "#059669" }}>
          {unpriced ? `${unpriced} of ${els.length} without a current general source` : `all ${els.length} have one`}
        </span>
      </div>
      <div style={{ overflowX: "auto", maxHeight: "56vh", overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Element", "Unit", "Zoho SKU", "Rate sources", "Wastage %", "Per-room"].map(h =>
            <th key={h} className={ac(h)} style={ES.th}>{h}</th>)}</tr></thead>
          <tbody>
            {shown.map(e => (
              <tr key={e.id} style={{ background: e.sources_current ? undefined : "#FEF2F2" }}>
                <td style={ES.td}>
                  <div style={{ fontWeight: 600 }}>{e.name}</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF" }}>{e.key}</div>
                </td>
                <td style={ES.td}>{e.unit}</td>
                <td style={ES.td}>
                  <input value={e.item_code || ""} onChange={ev => up(e.id, "item_code", ev.target.value)}
                    placeholder="A06-002.001" style={{ ...ES.input, width: 118, fontSize: 11 }} />
                </td>
                <td style={ES.td}>
                  {e.sources_current ? `${e.sources_current} current` : "none current"}
                  {e.sources_pending > 0 && <div style={{ fontSize: 10, color: "#B45309" }}>{e.sources_pending} awaiting approval</div>}
                </td>
                <td style={ES.td}><input type="number" value={e.default_wastage_pct} onChange={ev => up(e.id, "default_wastage_pct", ev.target.value)} style={{ ...ES.input, width: 64 }} /></td>
                <td style={ES.td}><input type="number" value={e.per_room_qty ?? ""} onChange={ev => up(e.id, "per_room_qty", ev.target.value)} placeholder="—" style={{ ...ES.input, width: 64 }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12, alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "#9CA3AF", marginRight: "auto" }}>
          Re-run “Generate from rooms” to apply changed take-off settings to the BOQ.
        </span>
        <Btn outline color="#6B7280" onClick={onClose}>Close</Btn>
        <Btn onClick={saveAll} disabled={busy || !Object.keys(dirty).length}>
          {busy ? "Saving…" : `Save${Object.keys(dirty).length ? ` (${Object.keys(dirty).length})` : ""}`}
        </Btn>
      </div>
    </EngModal>
  );
}

// Top-level admin view: manage the global design constants without opening a project.
function DesignConstantsView() {
  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 4 }}>Design Constants</h2>
      <p style={{ fontSize: 13, color: "#6B7280", marginBottom: 16, maxWidth: 760 }}>
        The controlled register behind every calculation engine — KAPL Engineering Design Constants
        Master Rev 0.3. Each constant carries its ID, status, allowed stage and release severity, and is
        changed only by revision: maker, independent checker, approver. Every HVAC result records the
        constant IDs and revisions it was computed with.
      </p>
      {/* KAPL Design Constants Master Rev 0.3 — the controlled register, and
          the only place a design constant is changed (28-Sep-2026). */}
      <DesignConstantsRegister API={API} authFetch={authFetch} />
    </div>
  );
}

// Deterministic heat-load / airflow / equipment summary (hvac_engine output).
function HvacSummaryView({ hvac }) {
  if (!hvac || !hvac.totals || !hvac.totals.room_count) return null;
  const t = hvac.totals;
  const tile = (label, value, unit, alert) => (
    <div style={{ background: alert ? "#FEF2F2" : "#F0F9FF", border: `1px solid ${alert ? "#FECACA" : "#BAE6FD"}`, borderRadius: 8, padding: "8px 12px", minWidth: 120 }}>
      <div style={{ fontSize: 11, color: alert ? "#B91C1C" : "#0369A1", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: alert ? "#991B1B" : "#075985" }}>{value}<span style={{ fontSize: 11, fontWeight: 600, color: alert ? "#B91C1C" : "#0369A1" }}> {unit}</span></div>
    </div>
  );
  const vsel = t.vrf_selection;
  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#1F4E79" }}>HVAC design summary</div>
        {hvac.system_category && <Badge text={hvac.system_category.replace(/_/g, " ")} color="#8B5CF6" />}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {tile(hvac.engine === "VRF" ? "Cooling load (design, coincident)" : "Cooling load",
              t.cooling_tr == null ? "—" : (+t.cooling_tr).toFixed(2), "TR")}
        {/* VRF (KAPL brief 19-Oct-2026): the calculated load and the safety
            margin are separate figures, never folded into one. */}
        {hvac.engine === "VRF" && t.calculated_kw != null && tile("Calculated (before margin)", (+t.calculated_kw).toFixed(2), "kW")}
        {hvac.engine === "VRF" && t.margin_kw != null && tile(`Safety margin (SF ${t.safety_factor ?? "—"})`, (+t.margin_kw).toFixed(2), "kW")}
        {/* KAPL, 1 Sep 2026: "Add TR to HP conversion 1 TR = 1.25 HP". Their
            ratio, fixed for estimating, so it sits beside the tonnage and never
            replaces it — the real figure moves with the compressor. */}
        {t.cooling_hp != null && tile("Cooling load", t.cooling_hp, "HP")}
        {/* The VRF engine sizes no supply airflow: it is the approved IDU
            selection's OEM airflow, and until then it is not shown as 0. */}
        {hvac.engine === "VRF"
          ? (t.total_supply_cfm != null
              ? tile(`Total supply (approved IDUs${t.selected_idu_airflow && t.selected_idu_airflow.of > t.selected_idu_airflow.systems
                  ? `, ${t.selected_idu_airflow.systems} of ${t.selected_idu_airflow.of} systems` : ""})`, inr0(t.total_supply_cfm), "CFM")
              : tile("Total supply", t.selected_idu_airflow ? "IDU airflow awaiting OEM data" : "after unit selection", ""))
          : tile("Total supply", inr0(t.total_supply_cfm), "CFM")}
        {/* VRF selection (KAPL 19-Oct-2026): a negative selection margin is
            an undersized unit and BLOCKS the selection. */}
        {hvac.engine === "VRF" && vsel && tile("VRF systems (approved / proposed)",
          `${vsel.approved} / ${vsel.systems}${vsel.BLOCK ? ` · ${vsel.BLOCK} BLOCK` : ""}${vsel.HOLD ? ` · ${vsel.HOLD} HOLD` : ""}${vsel.FAIL ? ` · ${vsel.FAIL} FAIL` : ""}`,
          "", vsel.BLOCK > 0)}
        {hvac.engine === "VRF" && vsel && vsel.lowest_margin && tile(
          vsel.lowest_margin.margin_pct < 0 ? "Lowest selection margin — BLOCK (undersized)" : "Lowest selection margin",
          (vsel.lowest_margin.margin_pct * 100).toFixed(2), `% (${vsel.lowest_margin.system_id})`, vsel.lowest_margin.margin_pct < 0)}
        {tile("Fresh air", inr0(t.total_fresh_cmh), "m³/h")}
        {tile("Rooms", t.room_count, "")}
        {t.zone_count != null && tile("Zones", t.zone_count, "")}
        {tile("Total area", inr0(t.total_area_m2), "m²")}
      </div>
      <div style={{ fontSize: 12.5, color: "#374151", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
        {hvac.engine === "VRF"
          ? <>Indoor and outdoor units are selected on <b>Engineering Flow → Calculation</b> from released A06/002 items with
              OEM performance data; the BOQ lists them only once that selection is approved.</>
          : <>Equipment and materials are sized per zone on <b>Engineering Flow → Calculation</b> — step 4 decides AHU or a terminal
              system for each zone, step 5 sizes it, step 6 allocates it — and the BOQ below is that allocation.</>}
      </div>
      {/* The air handler selection table — KAPL's own sheet 05 handoff.
          "AHU 3 × 25000 CFM" above answers how many; this answers how big each
          one is, which rooms it carries, and how much spare it actually buys.
          The reserve asked for and the reserve delivered are not the same
          number once the airflow is taken up to the next standard size. */}
      {Array.isArray(hvac.zones) && hvac.zones.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", marginBottom: 4 }}>
            Air handler selection, by zone
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 560 }}>
              <thead>
                <tr style={{ color: "#6B7280", fontSize: 11, textTransform: "uppercase" }}>
                  <th style={{ padding: "4px 8px 4px 0", borderBottom: "1px solid #E5E7EB" }}>Zone</th>
                  <th style={{ padding: "4px 8px", borderBottom: "1px solid #E5E7EB" }}>Serves</th>
                  <th style={{ padding: "4px 8px", borderBottom: "1px solid #E5E7EB" }}>Computed</th>
                  <th style={{ padding: "4px 8px", borderBottom: "1px solid #E5E7EB" }}>Selected</th>
                  <th style={{ padding: "4px 8px", borderBottom: "1px solid #E5E7EB" }}>Spare</th>
                  <th style={{ padding: "4px 0 4px 8px", borderBottom: "1px solid #E5E7EB" }}>Cooling</th>
                  {/* KAPL ruling 15-Sep-2026 ask 07: the governing outdoor
                      condition must be identified at RAHU level, not only per
                      room. The engine named it; nothing displayed it. */}
                  <th style={{ padding: "4px 0 4px 12px", borderBottom: "1px solid #E5E7EB" }}>Governing</th>
                </tr>
              </thead>
              <tbody>
                {hvac.zones.map(z => (
                  <tr key={z.zone}>
                    <td style={{ padding: "5px 8px 5px 0", borderBottom: "1px solid #F3F4F6", fontWeight: 700, color: "#111827" }}>{z.zone}</td>
                    <td style={{ padding: "5px 8px", borderBottom: "1px solid #F3F4F6", color: "#6B7280" }}>{z.serves}</td>
                    <td style={{ padding: "5px 8px", borderBottom: "1px solid #F3F4F6", fontVariantNumeric: "tabular-nums", color: "#6B7280" }}>{inr0(z.base_supply_cfm)} CFM</td>
                    <td style={{ padding: "5px 8px", borderBottom: "1px solid #F3F4F6", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#075985" }}>{inr0(z.selected_cfm)} CFM</td>
                    <td style={{ padding: "5px 8px", borderBottom: "1px solid #F3F4F6", fontVariantNumeric: "tabular-nums", color: "#6B7280" }}>{z.spare_pct == null ? "—" : `${z.spare_pct}%`}</td>
                    <td style={{ padding: "5px 0 5px 8px", borderBottom: "1px solid #F3F4F6", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#075985" }}>{z.selected_tr} TR</td>
                    <td style={{ padding: "5px 0 5px 12px", borderBottom: "1px solid #F3F4F6", color: "#6B7280", whiteSpace: "nowrap" }}>
                      {z.governing_cooling_case || "—"}
                      {z.governing_dehumidification_case
                        && z.governing_dehumidification_case !== z.governing_cooling_case
                        ? <span style={{ color: "#B45309" }}> · dehum: {z.governing_dehumidification_case}</span>
                        : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* KAPL ruling 15-Sep-2026 item 1. A departure from a KAPL guideline is a
          Project-Specific Override: it is permitted, it needs an Engineering
          signature, and it never blocks. Only a statutory minimum blocks. The
          engine works all this out; without showing it, an engineer signing the
          design cannot see what they are signing off. */}
      {hvac.precedence && (hvac.precedence.overrides?.length > 0
                           || hvac.precedence.blocking?.length > 0
                           || hvac.precedence.notes?.length > 0) && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", marginBottom: 6 }}>
            Design basis — departures and defaults
          </div>
          {hvac.precedence.blocking?.length > 0 && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#B91C1C",
                          borderRadius: 6, padding: "6px 9px", fontSize: 12, marginBottom: 6 }}>
              ⛔ {hvac.precedence.blocking.length} requirement(s) below a statutory minimum — the design is blocked.
            </div>
          )}
          {[["overrides", "#FFFBEB", "#FCD34D", "#92400E", "Project-specific override — permitted, needs Engineering approval"],
            ["notes", "#F9FAFB", "#E5E7EB", "#4B5563", "Budgetary default in use"]].map(([key, bg, br, fg, caption]) =>
            hvac.precedence[key]?.length > 0 ? (
              <div key={key} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: fg, marginBottom: 3 }}>{caption}</div>
                {hvac.precedence[key].map((f, i) => (
                  <div key={i} style={{ background: bg, border: `1px solid ${br}`, borderRadius: 6,
                                        padding: "5px 9px", fontSize: 12, marginBottom: 3, color: "#374151" }}>
                    <b>{f.subject}</b> — {String(f.value)} <span style={{ color: "#9CA3AF" }}>vs</span> {f.reference}
                    {f.detail ? <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{f.detail}</div> : null}
                  </div>
                ))}
              </div>
            ) : null)}
        </div>
      )}
      {hvac.note && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 10, fontStyle: "italic" }}>{hvac.note}</div>}
    </Card>
  );
}

// HVAC system category — the "First Step": route the project to the right engine.
function SystemPanel({ projectId }) {
  const [sys, setSys] = useState(null);
  const [cat, setCat] = useState(null);
  const [showMatrix, setShowMatrix] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(() => authFetch(`${API}/api/eng/projects/${projectId}/system`).then(r => r.json()).then(setSys).catch(() => { }), [projectId]);
  useEffect(() => { load(); authFetch(`${API}/api/eng/hvac/systems`).then(r => r.json()).then(setCat).catch(() => { }); }, [load]);
  if (!sys || !cat) return null;
  const nameOf = k => (cat.categories.find(c => c.key === k) || {}).name || k;
  const setSystem = async (k) => {
    setBusy(true); setErr("");
    const r = await authFetch(`${API}/api/eng/projects/${projectId}/system`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ system_category: k }) });
    if (!r.ok) setErr((await r.json().catch(() => ({}))).detail || "Could not set the system.");
    setBusy(false); load();
  };
  const current = sys.system_category;
  // KAPL 18-Oct-2026: only systems with an issued KAPL standard are offered
  // (VRV/VRF so far). A project already on another system keeps it as its
  // current choice; it just cannot be newly picked.
  const offered = cat.categories.filter(c => c.offered);
  const choices = offered.concat(cat.categories.filter(c => c.key === current && !c.offered));
  const rec = offered.some(c => c.key === (sys.recommendation || {}).recommended) ? sys.recommendation : {};
  const matrixCols = cat.matrix.columns.map((c, i) => [c, i]).filter(([c]) => offered.some(o => o.name.includes(c) || c.includes(o.name)));
  const curNote = (cat.categories.find(c => c.key === (current || "CLEANROOM")) || {}).note;

  return (
    <Card style={{ marginBottom: 12, borderColor: "#8B5CF6" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#1F4E79" }}>HVAC system</div>
                <div style={{ flex: 1 }} />
        <select value={current || ""} onChange={e => setSystem(e.target.value || null)} disabled={busy} style={{ ...ES.input, width: 240 }}>
          {!current && <option value="" disabled>— select —</option>}
          {choices.map(c => <option key={c.key} value={c.key}>{c.name}{c.offered ? "" : " (current — no longer offered)"}</option>)}
        </select>
      </div>

      {!current && rec.recommended && (
        <div style={{ background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
          <div style={{ fontSize: 13, color: "#5B21B6" }}>
            <b>Recommended:</b> {nameOf(rec.recommended)}
            <Btn small onClick={() => setSystem(rec.recommended)} color="#8B5CF6" >Apply</Btn>
          </div>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>{rec.reason}</div>
          {rec.alternatives && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 3 }}>Also consider: {rec.alternatives.map(nameOf).join(", ")}</div>}
          {rec.gates && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>{rec.gates.map((g, i) => <span key={i}>{g.gate.split("—")[0].trim()}: <b>{g.answer}</b>{i < rec.gates.length - 1 ? " · " : ""}</span>)}</div>}
        </div>
      )}
      {current && <div style={{ fontSize: 12, color: "#374151", marginBottom: 6 }}><b>{nameOf(current)}</b> — {curNote}</div>}
      {err && <div style={{ fontSize: 12, color: "#B91C1C", marginBottom: 6 }}>{err}</div>}

      <button onClick={() => setShowMatrix(m => !m)} style={{ border: "none", background: "transparent", color: "#8B5CF6", fontSize: 12, cursor: "pointer", padding: 0 }}>{showMatrix ? "▲ hide" : "▼ show"} system selection matrix</button>
      {showMatrix && (
        <div style={{ overflowX: "auto", marginTop: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead><tr><th style={ES.th}></th>{matrixCols.map(([c]) => <th key={c} style={ES.th}>{c}</th>)}</tr></thead>
            <tbody>
              {cat.matrix.rows.map((row, i) => (
                <tr key={i}><td style={{ ...ES.td, fontWeight: 600, color: "#374151" }}>{row[0]}</td>
                  {matrixCols.map(([c, j]) => <td key={c} style={{ ...ES.td, color: "#6B7280" }}>{row[j + 1]}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** What a BOQ line's site record says, in one cell.
 *
 *  Three numbers matter and they are not interchangeable: delivered is what
 *  arrived, installed is what went in, measured is what may be billed. The
 *  Thane take-off keeps all three plus in-store and pending, and the reason it
 *  needed 246 columns is that four cells cannot say who or when.
 *
 *  A line nobody has touched says "not started" rather than showing 0 / 0 / 0,
 *  which reads like a deliberate entry of nothing.
 */
/** The change log — "No change without a Change ID".
 *
 *  Stage 07 of the contractor's procedure, and the identity stages 08, 09 and
 *  10 hang off. It asks for six things at the moment a change appears: an ID,
 *  the trigger, what it lands on, the drawing impact, the quantity impact and
 *  the urgency.
 *
 *  Committed and proposed money are shown apart and never added together. A
 *  change that is only proposed is somebody's forecast; folding it into the
 *  committed figure is how a forecast quietly becomes a claim.
 */
function ChangeRegister({ projectId, onChanged }) {
  const [reg, setReg] = useState(null);
  const [open, setOpen] = useState(false);
  const [raising, setRaising] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ trigger: "SITE_INSTRUCTION", title: "",
                                     affects: "", drawing_impact: "",
                                     quantity_impact: "", urgency: "ROUTINE" });
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    authFetch(`${API}/api/eng/projects/${projectId}/changes`)
      .then(r => (r.ok ? r.json() : null)).then(setReg).catch(() => { });
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_project_changes", "eng_change_lines"], load);

  const raise = async () => {
    setErr("");
    try {
      const r = await authFetch(`${API}/api/eng/projects/${projectId}/changes`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "It could not be raised.");
      setForm(f => ({ ...f, title: "", affects: "", drawing_impact: "",
                      quantity_impact: "" }));
      setRaising(false); load(); setDetail(d);
    } catch (e) { setErr(String(e.message || e)); }
  };

  if (!reg) return null;
  const TONE = {
    OPEN: "#B45309", ASSESSED: "#1D4ED8", APPROVED: "#047857",
    REJECTED: "#6B7280", IMPLEMENTED: "#047857",
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12,
                    flexWrap: "wrap" }}>
        <div style={{ fontWeight: 700, color: "#1F4E79" }}>Change register</div>
        <span style={{ fontSize: 12, color: "#6B7280" }}>
          {reg.changes.length === 0
            ? "No change has been raised on this project."
            : `${reg.changes.length} raised · ${reg.committed.changes} approved`}
        </span>
        {reg.work_held.length > 0 && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: "#B91C1C",
                         background: "#FEF2F2", border: "1px solid #FECACA",
                         borderRadius: 999, padding: "2px 9px" }}>
            work held: {reg.work_held.join(", ")}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <Btn small outline onClick={() => setOpen(!open)}>
          {open ? "Hide" : "Show"}
        </Btn>
        <Btn small onClick={() => { setRaising(!raising); setOpen(true); }}>
          Raise a change
        </Btn>
      </div>

      {(reg.committed.excess > 0 || reg.committed.saving > 0
        || reg.proposed.excess > 0) && (
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap",
                      marginTop: 10, fontSize: 13 }}>
          <span><b style={{ color: "#047857" }}>Committed</b>{" "}
            excess {inr(reg.committed.excess)} · saving {inr(reg.committed.saving)}
            {" · net "}<b>{inr(reg.committed.net)}</b></span>
          {reg.proposed.changes > 0 && (
            <span style={{ color: "#B45309" }}>
              <b>Proposed</b> excess {inr(reg.proposed.excess)} — not committed
            </span>
          )}
        </div>
      )}

      {open && (
        <div style={{ marginTop: 12 }}>
          {raising && (
            <div style={{ border: "1px solid #E5E7EB", borderRadius: 8,
                          padding: 12, marginBottom: 12, background: "#F9FAFB" }}>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                <select value={form.trigger}
                  onChange={e => setForm(f => ({ ...f, trigger: e.target.value }))}
                  style={{ ...ES.input, width: "auto", minWidth: 190 }}>
                  {Object.entries(reg.triggers).map(([k, l]) =>
                    <option key={k} value={k}>{l}</option>)}
                </select>
                <select value={form.urgency}
                  onChange={e => setForm(f => ({ ...f, urgency: e.target.value }))}
                  style={{ ...ES.input, width: "auto", minWidth: 150 }}>
                  {Object.entries(reg.urgency).map(([k, l]) =>
                    <option key={k} value={k}>{l}</option>)}
                </select>
                <input placeholder="What changed" value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  style={{ ...ES.input, flex: "2 1 260px" }} />
                <input placeholder="Floors / systems affected" value={form.affects}
                  onChange={e => setForm(f => ({ ...f, affects: e.target.value }))}
                  style={{ ...ES.input, flex: "1 1 180px" }} />
              </div>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap",
                            marginTop: 7 }}>
                <input placeholder="Drawing impact — which drawing, which revision"
                  value={form.drawing_impact}
                  onChange={e => setForm(f => ({ ...f, drawing_impact: e.target.value }))}
                  style={{ ...ES.input, flex: "1 1 260px" }} />
                <input placeholder="Quantity impact — in words; the numbers go on the change"
                  value={form.quantity_impact}
                  onChange={e => setForm(f => ({ ...f, quantity_impact: e.target.value }))}
                  style={{ ...ES.input, flex: "1 1 260px" }} />
                <Btn small onClick={raise} disabled={!form.title.trim()}>Raise</Btn>
              </div>
              {err && <div style={{ color: "#B91C1C", fontSize: 12.5,
                                    marginTop: 7 }}>{err}</div>}
            </div>
          )}

          {reg.changes.map(c => (
            <button key={c.id} onClick={() => setDetail(c)}
              style={{ width: "100%", textAlign: "left", display: "grid",
                       gridTemplateColumns: "78px 1fr 150px 110px", gap: 10,
                       alignItems: "baseline", padding: "8px 4px",
                       borderBottom: "1px solid #F9FAFB", background: "transparent",
                       border: "none", cursor: "pointer", font: "inherit" }}>
              <span style={{ fontFamily: "monospace", fontSize: 12.5,
                             color: "#1F4E79", fontWeight: 700 }}>{c.change_no}</span>
              <span style={{ minWidth: 0 }}>
                <span style={{ fontSize: 13.5 }}>{c.title}</span>
                <span style={{ display: "block", fontSize: 11.5, color: "#9CA3AF" }}>
                  {[c.trigger_label, c.affects].filter(Boolean).join(" · ")}
                </span>
              </span>
              <span style={{ fontSize: 12.5, color: "#374151",
                             fontVariantNumeric: "tabular-nums" }}>
                {c.excess > 0 && <span style={{ color: "#B45309" }}>+{inr(c.excess)}</span>}
                {c.excess > 0 && c.saving > 0 && " / "}
                {c.saving > 0 && <span style={{ color: "#047857" }}>−{inr(c.saving)}</span>}
                {c.lines.length === 0 && <span style={{ color: "#9CA3AF" }}>no quantities yet</span>}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: .3,
                             textTransform: "uppercase",
                             color: TONE[c.status] || "#6B7280" }}>
                {c.status_label}
                {c.urgency === "HOLD_WORK" && c.status === "OPEN" && " · held"}
              </span>
            </button>
          ))}
        </div>
      )}

      {detail && <ChangeDetail change={detail} onClose={() => setDetail(null)}
                               onChanged={() => { load(); onChanged && onChanged(); }} />}
    </Card>
  );
}


/** One change: its impacts, the lines it moves, and where it can go next. */
function ChangeDetail({ change, onClose, onChanged }) {
  const [c, setC] = useState(change);
  const [ln, setLn] = useState({ description: "", unit: "", qty_before: "",
                                 qty_after: "", rate: "" });
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  const refresh = useCallback((d) => { setC(d); onChanged && onChanged(); },
                              [onChanged]);
  // Decided or edited elsewhere while this is open. The form fields being
  // typed (the new line, the note) are left alone.
  useLive(["eng_project_changes", "eng_change_lines"], () => {
    authFetch(`${API}/api/eng/changes/${change.id}`)
      .then(r => (r.ok ? r.json() : null)).then(d => d && setC(d)).catch(() => { });
  });

  const call = async (url, body, method = "POST") => {
    setErr("");
    try {
      const r = await authFetch(`${API}${url}`, {
        method,
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "It could not be done.");
      refresh(d);
      return true;
    } catch (e) { setErr(String(e.message || e)); return false; }
  };

  const addLine = async () => {
    const okd = await call(`/api/eng/changes/${c.id}/lines`, {
      description: ln.description, unit: ln.unit || null,
      qty_before: Number(ln.qty_before || 0), qty_after: Number(ln.qty_after || 0),
      rate: ln.rate === "" ? null : Number(ln.rate),
    });
    if (okd) setLn({ description: "", unit: "", qty_before: "", qty_after: "", rate: "" });
  };

  const editable = !["APPROVED", "REJECTED", "IMPLEMENTED"].includes(c.status);

  return (
    <EngModal title={`${c.change_no} — ${c.title}`} onClose={onClose} maxWidth={780}>
      <div style={{ fontSize: 12.5, color: "#6B7280", marginBottom: 10 }}>
        {[c.trigger_label, c.urgency_label, c.affects,
          `raised by ${c.raised_by || "—"}`].filter(Boolean).join(" · ")}
      </div>
      {["drawing_impact", "quantity_impact", "description"].map(k => c[k] && (
        <div key={k} style={{ fontSize: 13, marginBottom: 6 }}>
          <b style={{ color: "#374151" }}>
            {k === "drawing_impact" ? "Drawing impact"
              : k === "quantity_impact" ? "Quantity impact" : "Detail"}:
          </b>{" "}{c[k]}
        </div>
      ))}

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", margin: "12px 0",
                    paddingTop: 10, borderTop: "1px solid #F3F4F6", fontSize: 13 }}>
        <span>Excess <b style={{ color: "#B45309" }}>{inr(c.excess)}</b></span>
        <span>Saving <b style={{ color: "#047857" }}>{inr(c.saving)}</b></span>
        <span>Net <b>{inr(c.net)}</b></span>
        {c.unpriced_lines > 0 && (
          <span style={{ color: "#B91C1C" }}>
            {c.unpriced_lines} line(s) unpriced — approval is blocked until they are
          </span>
        )}
      </div>

      {c.lines.map(l => (
        <div key={l.id} style={{ display: "grid",
              gridTemplateColumns: "1fr 130px 110px 100px auto", gap: 10,
              alignItems: "baseline", padding: "6px 0", fontSize: 13,
              borderBottom: "1px solid #F9FAFB" }}>
          <span>{l.description}
            {l.extra_item && <Badge text="extra item" color="#B45309" />}</span>
          <span style={{ color: "#6B7280", fontVariantNumeric: "tabular-nums" }}>
            {l.qty_before} → {l.qty_after} {l.unit || ""}
          </span>
          <span style={{ color: "#6B7280" }}>{inr(l.rate)}</span>
          <span style={{ fontWeight: 600,
                         color: l.amount >= 0 ? "#B45309" : "#047857",
                         fontVariantNumeric: "tabular-nums" }}>{inr(l.amount)}</span>
          {editable && (
            <button onClick={() => call(`/api/eng/changes/lines/${l.id}`, null, "DELETE")}
              style={{ border: "none", background: "transparent", color: "#9CA3AF",
                       cursor: "pointer" }}>×</button>
          )}
        </div>
      ))}

      {editable && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
          <input placeholder="Line affected" value={ln.description}
            onChange={e => setLn(s => ({ ...s, description: e.target.value }))}
            style={{ ...ES.input, flex: "2 1 200px" }} />
          <input placeholder="Unit" value={ln.unit}
            onChange={e => setLn(s => ({ ...s, unit: e.target.value }))}
            style={{ ...ES.input, width: 70 }} />
          <input type="number" placeholder="Was" value={ln.qty_before}
            onChange={e => setLn(s => ({ ...s, qty_before: e.target.value }))}
            style={{ ...ES.input, width: 82 }} />
          <input type="number" placeholder="Becomes" value={ln.qty_after}
            onChange={e => setLn(s => ({ ...s, qty_after: e.target.value }))}
            style={{ ...ES.input, width: 92 }} />
          <input type="number" placeholder="Rate" value={ln.rate}
            onChange={e => setLn(s => ({ ...s, rate: e.target.value }))}
            style={{ ...ES.input, width: 96 }} />
          <Btn small onClick={addLine} disabled={!ln.description.trim()}>Add</Btn>
        </div>
      )}

      {c.next_states.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 12,
                      borderTop: "1px solid #F3F4F6" }}>
          <input placeholder="Note — required to reject" value={note}
            onChange={e => setNote(e.target.value)}
            style={{ ...ES.input, marginBottom: 7 }} />
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {c.next_states.map(st => (
              <Btn key={st} small outline={st === "REJECTED"}
                color={st === "REJECTED" ? "#DC2626" : KZ.blue}
                onClick={() => call(`/api/eng/changes/${c.id}/decide`,
                                    { to: st, note: note || null })}>
                {st === "ASSESSED" ? "Mark assessed"
                  : st === "APPROVED" ? "Approve"
                  : st === "REJECTED" ? "Reject" : "Mark implemented"}
              </Btn>
            ))}
          </div>
        </div>
      )}
      {err && <div style={{ color: "#B91C1C", fontSize: 12.5, marginTop: 9 }}>{err}</div>}
    </EngModal>
  );
}


function OnSite({ line, onOpen }) {
  const p = line.progress;
  const has = p && p.movements > 0;
  const bar = (v) => Math.max(0, Math.min(100,
    line.net_qty ? (v / line.net_qty) * 100 : 0));
  return (
    <button onClick={onOpen} title="Record or review what happened on site"
      style={{ border: "none", background: "transparent", padding: 0,
               cursor: "pointer", font: "inherit", textAlign: "left",
               minWidth: 116 }}>
      {!has ? (
        <span style={{ fontSize: 12, color: "#9CA3AF" }}>not started</span>
      ) : (
        <>
          <div style={{ display: "flex", height: 6, borderRadius: 3,
                        overflow: "hidden", background: "#F3F4F6",
                        marginBottom: 3 }}>
            <span style={{ width: `${bar(p.measured)}%`, background: "#047857" }} />
            <span style={{ width: `${Math.max(0, bar(p.installed) - bar(p.measured))}%`,
                           background: "#34D399" }} />
            <span style={{ width: `${Math.max(0, bar(p.delivered) - bar(p.installed))}%`,
                           background: "#BFDBFE" }} />
          </div>
          <div style={{ fontSize: 11, color: "#6B7280",
                        fontVariantNumeric: "tabular-nums" }}>
            {p.installed} in · {p.in_store} store
          </div>
          {p.over_delivered && (
            <div style={{ fontSize: 10.5, color: "#B45309", fontWeight: 600 }}>
              over-delivered
            </div>
          )}
        </>
      )}
    </button>
  );
}


/** Record a delivery, an installation or a measurement against one BOQ line.
 *
 *  The order is the contractor's, not a preference: nothing installs before it
 *  arrives and nothing bills before it is measured. The server refuses both,
 *  and this form says so before the refusal rather than after.
 */
function SiteProgressModal({ line, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ kind: "DELIVERED", qty: "",
                                     happened_on: "", vendor: "",
                                     reference: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    authFetch(`${API}/api/eng/boq/lines/${line.id}/movements`)
      .then(r => r.json()).then(setData).catch(() => { });
  }, [line.id]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_boq_lines", "eng_boq_movements"], load);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const r = await authFetch(`${API}/api/eng/boq/lines/${line.id}/movements`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, qty: Number(form.qty) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "It could not be recorded.");
      setData(d);
      setForm(f => ({ ...f, qty: "", reference: "", note: "" }));
      onChanged && onChanged();
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const withdraw = async (id) => {
    setBusy(true); setErr("");
    try {
      const r = await authFetch(`${API}/api/eng/boq/movements/${id}`,
        { method: "DELETE" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "It could not be withdrawn.");
      setData(d); onChanged && onChanged();
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const KINDS = [["DELIVERED", "Delivered to site"], ["INSTALLED", "Installed"],
                 ["MEASURED", "Measured and certified"],
                 ["RETURNED", "Returned to vendor"]];
  const isDelivery = form.kind === "DELIVERED" || form.kind === "RETURNED";
  const stat = (label, v, tone) => (
    <div style={{ flex: "1 1 92px" }}>
      <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: .4,
                    color: "#9CA3AF" }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: tone,
                    fontVariantNumeric: "tabular-nums" }}>{v}</div>
    </div>
  );

  return (
    <EngModal title={`On site — ${line.description}`} onClose={onClose} maxWidth={720}>
      {!data ? <div style={{ color: "#9CA3AF", fontSize: 13 }}>Loading…</div> : (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap",
                        paddingBottom: 12, marginBottom: 12,
                        borderBottom: "1px solid #F3F4F6" }}>
            {stat("BOQ", `${data.net_qty} ${data.unit}`, "#111827")}
            {stat("Delivered", data.delivered, "#1D4ED8")}
            {stat("Installed", data.installed, "#047857")}
            {stat("In store", data.in_store, "#6B7280")}
            {stat("Billable", data.measured, data.measured ? "#047857" : "#9CA3AF")}
            {stat("Pending", data.pending,
                  data.over_delivered ? "#B45309" : "#6B7280")}
          </div>
          {data.over_delivered && (
            <div style={{ fontSize: 12.5, color: "#B45309", marginBottom: 10 }}>
              More has been delivered than the BOQ allows for. That is recorded,
              not blocked — but it is worth knowing before the next order.
            </div>
          )}

          <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
            <select value={form.kind}
              onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}
              style={{ ...ES.input, width: "auto", minWidth: 168 }}>
              {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input type="number" placeholder={`Qty (${data.unit})`} value={form.qty}
              onChange={e => setForm(f => ({ ...f, qty: e.target.value }))}
              style={{ ...ES.input, width: 118 }} />
            <input type="date" value={form.happened_on}
              onChange={e => setForm(f => ({ ...f, happened_on: e.target.value }))}
              style={{ ...ES.input, width: 152 }} />
            {isDelivery && (
              <input placeholder="Vendor" value={form.vendor}
                onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))}
                style={{ ...ES.input, flex: "1 1 160px" }} />
            )}
            <input placeholder="PO / WO / DC / RA no." value={form.reference}
              onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
              style={{ ...ES.input, flex: "1 1 150px" }} />
            <Btn small onClick={save}
              disabled={busy || !form.qty || Number(form.qty) <= 0}>Record</Btn>
          </div>
          <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 6 }}>
            Nothing installs before it arrives and nothing is billable before it
            is measured.
          </div>
          {err && <div style={{ color: "#B91C1C", fontSize: 12.5,
                                marginTop: 8 }}>{err}</div>}

          <div style={{ marginTop: 16 }}>
            {data.history.length === 0 ? (
              <div style={{ fontSize: 12.5, color: "#9CA3AF" }}>
                Nothing recorded against this line yet.
              </div>
            ) : data.history.map(m => (
              <div key={m.id} style={{ display: "grid",
                    gridTemplateColumns: "150px 90px 1fr auto", gap: 10,
                    alignItems: "baseline", padding: "7px 0",
                    borderBottom: "1px solid #F9FAFB", fontSize: 13 }}>
                <span style={{ color: m.kind === "RETURNED" ? "#B45309" : "#374151",
                               fontWeight: 600 }}>{m.label}</span>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                  {m.qty} {data.unit}
                </span>
                <span style={{ color: "#6B7280", fontSize: 12 }}>
                  {[m.happened_on, m.vendor, m.reference, m.recorded_by]
                    .filter(Boolean).join(" · ")}
                </span>
                <button onClick={() => withdraw(m.id)} disabled={busy}
                  title="Withdraw this record"
                  style={{ border: "none", background: "transparent",
                           color: "#9CA3AF", cursor: "pointer" }}>×</button>
              </div>
            ))}
          </div>
        </>
      )}
    </EngModal>
  );
}


function BoqPanel({ projectId }) {
  const [boq, setBoq] = useState(null);
  const [hvac, setHvac] = useState(null);
  const [showElements, setShowElements] = useState(false);
  const [site, setSite] = useState(null);      // the line whose site record is open
  const [busy, setBusy] = useState(false);
  const [openLine, setOpenLine] = useState(null);    // the line whose pricing detail is open
  const [srcLine, setSrcLine] = useState(null);      // entering a rate source for this line
  const [ovr, setOvr] = useState(null);              // { line } or { line: null } = whole project
  const [nl, setNl] = useState({ description: "", unit: "NOS", gross_qty: "", wastage_pct: "0" });
  const load = useCallback(() => {
    authFetch(`${API}/api/eng/projects/${projectId}/boq`).then(r => r.json()).then(setBoq).catch(() => { });
    authFetch(`${API}/api/eng/projects/${projectId}/hvac`).then(r => r.json()).then(setHvac).catch(() => { });
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_boq_lines", "eng_boq_movements", "eng_project_changes", "eng_rate_sources",
           "eng_commercial_overrides", "eng_commercial_policies"], load);

  // Since 28-Sep-2026 the BOQ is generated from a recorded, current HVAC run
  // (Blueprint §10.3); the server says why when there is none.
  const generate = async () => {
    setBusy(true);
    const r = await authFetch(`${API}/api/eng/projects/${projectId}/boq/generate`, { method: "POST" });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      window.alert(typeof d.detail === "string" ? d.detail : "The BOQ could not be generated.");
    }
    await load(); setBusy(false);
  };
  const del = async (lid) => { await authFetch(`${API}/api/eng/boq/lines/${lid}`, { method: "DELETE" }); load(); };
  // KAPL B1: a base cost is never typed onto a line — it is a rate source
  // (approved, dated). Re-price picks up sources approved or lapsed since.
  const reprice = async () => {
    setBusy(true);
    const r = await authFetch(`${API}/api/eng/projects/${projectId}/boq/reprice`, { method: "POST" });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      window.alert(typeof d.detail === "string" ? d.detail : "The BOQ could not be re-priced.");
    }
    await load(); setBusy(false);
  };
  const addManual = async () => {
    if (!nl.description.trim()) return;
    await authFetch(`${API}/api/eng/projects/${projectId}/boq/lines`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ description: nl.description, unit: nl.unit, gross_qty: Number(nl.gross_qty) || 0, wastage_pct: Number(nl.wastage_pct) || 0 }),
    });
    setNl({ description: "", unit: "NOS", gross_qty: "", wastage_pct: "0" }); load();
  };
  const com = boq && boq.commercial;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#1F4E79" }}>{boq && boq.crp_in_scope === false ? "Project BOQ" : "Cleanroom BOQ"}</div>
        {boq && <div style={{ fontSize: 14, fontWeight: 800, color: "#10B981" }}>{inr(boq.total_amount)}</div>}
        {boq && boq.unpriced_count > 0 &&
          <span style={{ fontSize: 11, fontWeight: 700, color: "#DC2626", background: "#FEF2F2",
                         border: "1px solid #FECACA", borderRadius: 6, padding: "3px 8px" }}>
            {boq.unpriced_count} unpriced
          </span>}
        <div style={{ flex: 1 }} />
        <Btn small outline onClick={() => setShowElements(true)}>Elements</Btn>
        <Btn small outline onClick={reprice} disabled={busy || !boq || !boq.line_count}>Re-price</Btn>
        <Btn small onClick={generate} disabled={busy}>{busy ? "Working…" : "⟳ Generate from rooms"}</Btn>
      </div>

      {boq && boq.validation && boq.validation.length > 0 && (
        <Card style={{ marginBottom: 12, background: boq.validation_blocks.length ? "#FEF2F2" : "#FFFBEB",
                       border: `1px solid ${boq.validation_blocks.length ? "#FECACA" : "#FDE68A"}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: boq.validation_blocks.length ? "#991B1B" : "#92400E", marginBottom: 6 }}>
            Design checks — {boq.validation_blocks.length} blocking, {boq.validation.length - boq.validation_blocks.length} warning
          </div>
          {boq.validation.map((v, i) => (
            <div key={i} style={{ fontSize: 12, color: "#78350F", lineHeight: 1.5, marginBottom: 3 }}>
              <b>{v.severity}</b> · {v.check} — {v.message}
            </div>
          ))}
        </Card>
      )}

      {boq && boq.takeoff && boq.takeoff.length > 0 && (
        <Card style={{ marginBottom: 12, padding: 0 }}>
          <div style={{ padding: "8px 14px", borderBottom: "1px solid #F3F4F6", fontWeight: 700, color: "#1F4E79" }}>
            Wall take-off — gross, deductions, net
            <span style={{ fontWeight: 400, fontSize: 11, color: "#9CA3AF", marginLeft: 8 }}>
              KAPL-ENG-CRP-04 §4.1 — panel behind an opening is not billed
            </span>
          </div>
          <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Room", "Perimeter m", "Gross m²", "Openings m²", "Shared wall m²", "Net m²"].map(h =>
                <th key={h} className={ac(h)} style={ES.th}>{h}</th>)}</tr></thead>
              <tbody>
                {boq.takeoff.map(t => (
                  <tr key={t.room_id}>
                    <td style={ES.td}>{t.room}</td>
                    <td style={ES.td}>{t.perimeter_m}</td>
                    <td style={ES.td}>{t.gross_wall_area_m2}</td>
                    <td style={{ ...ES.td, color: t.opening_deduction_m2 ? "#B45309" : "#D1D5DB" }}>
                      {t.opening_deduction_m2 ? `− ${t.opening_deduction_m2}` : "—"}</td>
                    <td style={{ ...ES.td, color: t.shared_wall_deduction_m2 ? "#B45309" : "#D1D5DB" }}>
                      {t.shared_wall_deduction_m2 ? `− ${t.shared_wall_deduction_m2}` : "—"}</td>
                    <td style={{ ...ES.td, fontWeight: 700 }}>{t.net_wall_area_m2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {boq && boq.unpriced_count > 0 && (
        <Card style={{ marginBottom: 12, background: "#FEF2F2", border: "1px solid #FECACA" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#991B1B", marginBottom: 4 }}>
            This total is incomplete — {boq.unpriced_count} of {boq.line_count} lines have no price.
          </div>
          <div style={{ fontSize: 12, color: "#7F1D1D", lineHeight: 1.5 }}>
            {boq.pricing_basis} Open a line (▸) to see why it is unpriced and to enter a source for its SKU,
            its element or that line; once approved, <b>Re-price</b>. Unpriced lines still show their quantity and
            contribute ₹0 to the total.
          </div>
        </Card>
      )}

      {boq && boq.line_count > 0 && (
        <BoqCommercialCard commercial={com} costTotal={boq.total_amount} onOverride={() => setOvr({ line: null })} />
      )}

      <HvacSummaryView hvac={hvac} />

      {boq === null && <Card><div style={{ color: "#9CA3AF", fontSize: 13 }}>Loading…</div></Card>}
      {boq && boq.line_count === 0 && <Card><div style={{ color: "#9CA3AF", fontSize: 13 }}>No BOQ yet. Click <b>Generate from rooms</b> to compute the priced take-off. It is generated from the requirements approved on Engineering Flow stage 2 and, with HVAC in scope, from a calculation run recorded on stage 4 (Heat load).</div></Card>}

      <ChangeRegister projectId={projectId} onChanged={load} />

      {boq && boq.groups.map(g => (
        <Card key={g.room_id || "proj"} style={{ marginBottom: 12, padding: 0 }}>
          <div style={{ display: "flex", padding: "8px 14px", borderBottom: "1px solid #F3F4F6" }}>
            <div style={{ fontWeight: 700, color: "#1F4E79" }}>{g.room}</div>
            <div style={{ flex: 1 }} />
            <div style={{ fontWeight: 700, color: "#374151" }}>{inr(g.subtotal)}</div>
          </div>
          <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["", "Element", "Unit", "Gross", "Wastage", "Net", "On site", "Supply rate", "Instl. rate", "Cost amount", "Selling ₹", ""].map((h, i) => <th key={h || i} className={ac(h)} style={ES.th}>{h}</th>)}</tr></thead>
              <tbody>
                {g.lines.map(ln => (
                  <Fragment key={ln.id}>
                  <tr style={{ background: ln.unpriced ? "#FEF2F2" : undefined }}>
                    <td style={ES.td}>
                      <button onClick={() => setOpenLine(openLine === ln.id ? null : ln.id)} title="Pricing: source, validity, skipped, base → ÷ → selling"
                        style={{ border: "none", background: "transparent", cursor: "pointer", color: "#1F4E79", fontSize: 12 }}>
                        {openLine === ln.id ? "▾" : "▸"}</button>
                    </td>
                    <td style={ES.td}>{ln.description}{ln.source === "MANUAL" && <Badge text="manual" color="#6366F1" />}</td>
                    <td style={ES.td}>{ln.unit}</td>
                    <td style={ES.td}>{ln.gross_qty}</td>
                    <td style={ES.td}>{ln.wastage_pct}%</td>
                    <td style={ES.td}>{ln.net_qty}</td>
                    {/* Delivered, installed, measured — derived from recorded
                        movements, so a line nobody has touched reads "not
                        started" rather than showing a blank that looks like
                        zero progress deliberately entered. */}
                    <td style={ES.td}><OnSite line={ln} onOpen={() => setSite(ln)} /></td>
                    <td style={ES.td}>
                      {ln.rate ? inr(ln.rate) : "—"}
                      <div style={{ marginTop: 2 }}><TierBadge tier={ln.rate_source} /></div>
                    </td>
                    <td style={ES.td}>
                      {ln.install_rate ? inr(ln.install_rate) : "—"}
                      {ln.install_source !== "UNPRICED" && <div style={{ marginTop: 2 }}><TierBadge tier={ln.install_source} /></div>}
                    </td>
                    <td style={{ ...ES.td, fontWeight: 600, color: ln.unpriced ? "#DC2626" : undefined }}>{inr(ln.cost_amount)}</td>
                    <td style={{ ...ES.td, fontWeight: 600, color: "#047857" }}>{ln.commercial ? inr(ln.commercial.selling_total) : "—"}</td>
                    <td style={ES.td}>{ln.source === "MANUAL" && <button onClick={() => del(ln.id)} style={{ border: "none", background: "transparent", color: "#EF4444", cursor: "pointer", fontSize: 13 }}>✕</button>}</td>
                  </tr>
                  {openLine === ln.id && (
                    <tr><td colSpan={12} style={{ ...ES.td, background: "#FFFFFF" }}>
                      <LinePricing line={ln} commercial={com} />
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <Btn small outline onClick={() => setSrcLine(ln)}>+ Rate source for this line…</Btn>
                        {com && com.applied && <Btn small outline color="#6D28D9" onClick={() => setOvr({ line: ln })}>Request override for this line…</Btn>}
                      </div>
                    </td></tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {boq && boq.line_count > 0 && (
        <Card style={{ display: "flex", alignItems: "center" }}>
          <div style={{ fontWeight: 700, color: "#1F4E79" }}>Project BOQ total</div>
          {/* Blueprint §10: every AUTO line traces back to the recorded run it came from. */}
          <div style={{ fontSize: 11.5, color: "#6B7280", marginLeft: 10 }}>
            {(boq.calc_run_ids || []).length
              ? <>generated from calculation run {boq.calc_run_ids.join(", ")}</>
              : <>not generated from a recorded run — regenerate to trace it</>}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#10B981" }}>{inr(boq.total_amount)}
              <span style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}> base cost</span></div>
            {com && com.applied && com.totals && <div style={{ fontWeight: 800, fontSize: 14, color: "#047857" }}>
              {inr(com.totals.grand_total)} <span style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>selling (policy v{com.policy.version})</span></div>}
          </div>
        </Card>
      )}

      <Card style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", marginBottom: 8 }}>ADD MANUAL LINE (project-level)</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <input placeholder="Description" value={nl.description} onChange={e => setNl(s => ({ ...s, description: e.target.value }))} style={{ ...ES.input, flex: "2 1 200px" }} />
          <input placeholder="Unit" value={nl.unit} onChange={e => setNl(s => ({ ...s, unit: e.target.value }))} style={{ ...ES.input, width: 70 }} />
          <input type="number" placeholder="Qty" value={nl.gross_qty} onChange={e => setNl(s => ({ ...s, gross_qty: e.target.value }))} style={{ ...ES.input, width: 80 }} />
          <input type="number" placeholder="Waste %" value={nl.wastage_pct} onChange={e => setNl(s => ({ ...s, wastage_pct: e.target.value }))} style={{ ...ES.input, width: 80 }} />
          <Btn small onClick={addManual}>Add</Btn>
        </div>
      </Card>

      <div style={{ fontSize: 12, color: "#6B7280", marginTop: 12 }}>
        Quantities and factors come from the <b>Design Constants</b> register (KAPL Design Constants
        Master Rev 0.3) — change them there, by revision.
      </div>

      {/* Saving the element master does not regenerate: a regeneration also moves the BOQ onto the
          Commercial Policy version in force, which must be a deliberate act (Generate). */}
      {showElements && <ElementMasterModal onClose={() => setShowElements(false)} onSaved={() => { setShowElements(false); load(); }} />}
      {srcLine && <LineSourceModal api={API} authFetch={authFetch} projectId={projectId} line={srcLine}
                                   onClose={() => setSrcLine(null)} onSaved={load} />}
      {ovr && <OverrideModal api={API} authFetch={authFetch} projectId={projectId} line={ovr.line}
                             values={com && (com.project_values || com.policy)} onClose={() => setOvr(null)} onSaved={load} />}
      {site && <SiteProgressModal line={site} onClose={() => setSite(null)}
                                 onChanged={load} />}
    </div>
  );
}

// ── Costing & Estimation (Module 4.12, admin-only) ────────────────────────────
function CostingPanel({ projectId }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => authFetch(`${API}/api/eng/projects/${projectId}/costing`)
    .then(r => r.ok ? r.json() : null).then(d => { setData(d); if (d) setF(d.inputs); }).catch(() => { }), [projectId]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_project_costing", "eng_boq_lines", "eng_project_changes", "eng_commercial_overrides"], load);

  const up = (k, v) => setF(s => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    const num = (v) => (v === "" || v === null ? null : Number(v));
    const body = {};
    ["labour_cost", "transport_cost", "testing_cost", "subcontract_cost", "design_cost",
      "site_overhead_pct", "project_overhead_pct", "contingency_pct", "warranty_pct",
      "margin_pct", "discount_pct", "tax_pct", "actual_cost"].forEach(k => { body[k] = num(f[k]); });
    body.mode = f.mode; body.notes = f.notes || null;
    const r = await authFetch(`${API}/api/eng/projects/${projectId}/costing`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const d = await r.json(); if (r.ok) { setData(d); setF(d.inputs); }
    setBusy(false);
  };

  if (!data || !f) return null;   // hidden for non-admins (403) or still loading
  const s = data.sheet;
  const amt = [["Labour", "labour_cost"], ["Transport", "transport_cost"], ["Testing & commissioning", "testing_cost"], ["Subcontract", "subcontract_cost"], ["Design / engineering", "design_cost"]];
  const pct = [["Site overhead %", "site_overhead_pct"], ["Project overhead %", "project_overhead_pct"], ["Contingency %", "contingency_pct"], ["Warranty %", "warranty_pct"], ["Margin %", "margin_pct"], ["Discount %", "discount_pct"], ["Tax (GST) %", "tax_pct"]];
  const row = (label, val, opts = {}) => (
    <div style={{ display: "flex", padding: "5px 0", borderBottom: "1px solid #F3F4F6", fontSize: 13, ...(opts.strong ? { fontWeight: 700 } : {}) }}>
      <div style={{ color: opts.strong ? "#1F4E79" : "#374151" }}>{label}</div>
      <div style={{ flex: 1 }} />
      <div style={{ color: opts.color || (opts.strong ? "#1F4E79" : "#374151") }}>{inr(val)}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#1F4E79" }}>Costing & estimation</div>
        <Badge text="restricted" color="#EF4444" />
        <div style={{ flex: 1 }} />
        <select value={f.mode} onChange={e => up("mode", e.target.value)} style={{ ...ES.input, width: 140 }}>
          {["BUDGETARY", "FIRM"].map(m => <option key={m}>{m}</option>)}
        </select>
        <Btn small onClick={save} disabled={busy}>{busy ? "Saving…" : "Save & recompute"}</Btn>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }} className="kz-registers">
        <Card>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#6B7280", marginBottom: 10 }}>COST INPUTS</div>
          <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 8 }}>Material ({inr(data.boq_total)}) is the BOQ's base cost (KAPL B1 rate hierarchy), pulled live.</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0 12px" }}>
            {amt.map(([label, k]) => <EngField key={k} label={label} half><input type="number" value={f[k] ?? ""} onChange={e => up(k, e.target.value)} style={ES.input} /></EngField>)}
            {pct.map(([label, k]) => <EngField key={k} label={label} half><input type="number" value={f[k] ?? ""} onChange={e => up(k, e.target.value)} style={ES.input} /></EngField>)}
            <EngField label="Actual cost (estimate vs actual)"><input type="number" value={f.actual_cost ?? ""} onChange={e => up("actual_cost", e.target.value)} placeholder="from Zoho later" style={ES.input} /></EngField>
          </div>
        </Card>

        <Card>
          {data.quotation && data.quotation.basis === "STANDARD_BOQ_FORMULA" && (
            <div style={{ marginBottom: 14, padding: "8px 10px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#047857", marginBottom: 6 }}>
                QUOTATION PRICE — Standard BOQ formula (Commercial Policy v{data.quotation.policy_version})</div>
              {row("Supply (cost ÷ divisor, incl. P&F on cost)", data.quotation.supply)}
              {row("Packing & forwarding", data.quotation.pf)}
              {row("Installation (cost ÷ divisor)", data.quotation.installation)}
              {row("Price before tax", data.quotation.price_before_tax, { strong: true })}
              {row(`GST ${data.quotation.tax_pct}%`, data.quotation.tax)}
              {row("Quotation price", data.quotation.final_price, { strong: true, color: "#047857" })}
              <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 4 }}>
                KAPL B1: in Budgetary mode the proposal states this price. The margin build-up below is shown for
                reference and is not used for it.</div>
              {(data.quotation.inputs_not_used || []).length > 0 && (
                <div style={{ fontSize: 11.5, color: "#B45309", marginTop: 4 }}>
                  Not in this price: {data.quotation.inputs_not_used.map(k => k.replace(/_/g, " ")).join(", ")}.
                </div>)}
            </div>
          )}
          <div style={{ fontWeight: 700, fontSize: 13, color: "#6B7280", marginBottom: 10 }}>COST SHEET</div>
          {row("Material (BOQ base cost)", s.material_cost)}
          {row("Labour", s.labour_cost)}
          {row("Transport", s.transport_cost)}
          {row("Testing & commissioning", s.testing_cost)}
          {row("Subcontract", s.subcontract_cost)}
          {row("Design / engineering", s.design_cost)}
          {row("Direct cost", s.direct_cost, { strong: true })}
          {row("Overheads", s.overheads)}
          {row("Contingency", s.contingency)}
          {row("Warranty provision", s.warranty)}
          {row("Total cost", s.total_cost, { strong: true })}
          {row("Margin", s.margin, { color: "#10B981" })}
          {row("Discount", s.discount, { color: "#EF4444" })}
          {row("Price before tax", s.price_before_tax, { strong: true })}
          {row("Tax (GST)", s.tax)}
          <div style={{ display: "flex", padding: "8px 0", marginTop: 4, fontSize: 15, fontWeight: 800 }}>
            <div style={{ color: "#1F4E79" }}>Final selling price</div>
            <div style={{ flex: 1 }} />
            <div style={{ color: "#10B981" }}>{inr(s.final_price)}</div>
          </div>
          <div style={{ fontSize: 12, color: "#6B7280" }}>Gross profit {inr(s.gross_profit)} · achieved margin <b>{s.achieved_margin_pct}%</b></div>
          {("actual_cost" in s) && (
            <div style={{ marginTop: 8, padding: "8px 10px", background: "#F9FAFB", borderRadius: 8, fontSize: 12 }}>
              Actual cost {inr(s.actual_cost)} · variance <b style={{ color: s.cost_variance <= 0 ? "#10B981" : "#EF4444" }}>{inr(s.cost_variance)}</b> · actual margin <b>{s.actual_margin_pct}%</b>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ── Proposal generation (Module 4.13, admin-only) ─────────────────────────────
function ProposalPanel({ projectId }) {
  const [busy, setBusy] = useState(""); const [err, setErr] = useState("");
  const dl = async (kind, path, fallback) => {
    setBusy(kind); setErr("");
    try {
      const r = await authFetch(`${API}/api/eng/projects/${projectId}/${path}`);
      if (!r.ok) throw new Error("Generation failed (" + r.status + ")");
      const blob = await r.blob();
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : fallback;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };
  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 15, color: "#1F4E79", marginBottom: 10 }}>Proposal & documents</div>
      <Card>
        <div style={{ fontSize: 13, color: "#374151", marginBottom: 10 }}>
          Generates the Kaizen-format documents from this baseline — logo, customer block, scope by category, priced BOQ (Supply/Installation) and commercial terms.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn onClick={() => dl("prop", "proposal.pdf", "Proposal.pdf")} disabled={!!busy} color="#EF4444">{busy === "prop" ? "Generating…" : "⬇ Proposal (PDF)"}</Btn>
          <Btn onClick={() => dl("boq", "boq.pdf", "BOQ.pdf")} disabled={!!busy} color="#0EA5E9">{busy === "boq" ? "Generating…" : "⬇ BOQ (PDF)"}</Btn>
          <Btn outline color="#6B7280" onClick={() => dl("docx", "proposal.docx", "Proposal.docx")} disabled={!!busy}>{busy === "docx" ? "Generating…" : "Proposal (Word)"}</Btn>
        </div>
        {err && <div style={{ color: "#EF4444", fontSize: 13, marginTop: 8 }}>{err}</div>}
      </Card>
    </div>
  );
}

// ── Plan geometry & CAD drawings (Module 4.3) ─────────────────────────────────
// Two resources, kept apart on purpose: a PLAN is geometry and is cheap and
// deterministic; a DRAWING is a render of one and depends on FreeCAD being
// installed. The panel never blocks the geometry side on the CAD side.

const ART_LABEL = {
  sheet_pdf: "PDF sheet", sheet_svg: "SVG sheet", sheet_dxf: "DXF sheet",
  model_dxf: "DXF model", step: "3D model (STEP)", fcstd: "FreeCAD file",
  plan_geometry: "Projection (JSON)",
};
const ART_ORDER = ["sheet_pdf", "sheet_dxf", "sheet_svg", "step", "model_dxf", "fcstd", "plan_geometry"];
const JOB_COLOR = { DONE: "#10B981", PARTIAL: "#F59E0B", RUNNING: "#3B82F6", QUEUED: "#6B7280", FAILED: "#EF4444" };

function BoundaryLegend({ walls }) {
  const by = (walls || []).reduce((a, w) => { a[w.boundary_type] = (a[w.boundary_type] || 0) + 1; return a; }, {});
  const colors = { SHARED_PARTITION: "#8B5CF6", CORRIDOR: "#0EA5E9", EXTERNAL: "#6B7280", CIVIL_INTERFACE: "#F59E0B" };
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {Object.entries(by).map(([k, n]) => <Badge key={k} text={`${k.replace("_", " ").toLowerCase()} · ${n}`} color={colors[k] || "#6B7280"} />)}
    </div>
  );
}

function PlanSummary({ plan }) {
  if (!plan) return null;
  const rooms = plan.placements || [];
  const walls = plan.walls || [];
  const shared = walls.filter(w => w.room_a_id && w.room_b_id);
  const doors = walls.reduce((n, w) => n + (w.openings || []).length, 0);
  const warn = plan.warnings || [];
  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <StatTile label="Rooms placed" value={rooms.length} />
        <StatTile label="Wall segments" value={walls.length} />
        {/* A shared partition is one wall object naming two rooms — it is what
            stops the same partition being billed twice (KAPL-ENG-CRP-04 §4.1). */}
        <StatTile label="Shared partitions" value={shared.length} color="#8B5CF6" />
        <StatTile label="Doors" value={doors} />
        <StatTile label="Extent (m)" value={`${plan.extent_x_m} × ${plan.extent_y_m}`} />
        <div style={{ flex: 1 }} />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 700 }}>REVISION</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1F4E79" }}>R{plan.revision} · {plan.source}</div>
        </div>
      </div>
      <BoundaryLegend walls={walls} />
      {shared.length > 0 && (
        <div style={{ fontSize: 12, color: "#374151", marginTop: 10, background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 8, padding: "8px 10px" }}>
          <b>{shared.length} shared partition{shared.length > 1 ? "s" : ""}</b> detected from the geometry. Each is one wall object billed once, and the
          matching <code>shared_wall_length_m</code> has been written back to those rooms — so the §4.1 take-off stops double-counting them.
        </div>
      )}
      {warn.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#B45309", marginBottom: 4 }}>⚠ Open items ({warn.length}) — printed on the sheet</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#92400E" }}>
            {warn.map((w, i) => <li key={i} style={{ marginBottom: 2 }}>{w}</li>)}
          </ul>
        </div>
      )}
    </Card>
  );
}

function DrawingsPanel({ projectId, project }) {
  const floors = [];
  (project?.buildings || []).forEach(b => (b.floors || []).forEach(f =>
    floors.push({ id: f.id, label: `${b.name} · ${f.name}`, rooms: (f.zones || []).reduce((n, z) => n + (z.rooms || []).length, 0) })));

  const [floorId, setFloorId] = useState(floors[0]?.id || null);
  const [plan, setPlan] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [kind, setKind] = useState("MULTIVIEW");
  const [sheet, setSheet] = useState("");
  const [jobs, setJobs] = useState([]);
  const [cad, setCad] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);       // { jobId, url }
  const timer = useRef(null);

  useEffect(() => {
    authFetch(`${API}/api/eng/cad/status`).then(r => r.json()).then(setCad).catch(() => { });
    authFetch(`${API}/api/eng/cad/kinds`).then(r => r.json()).then(setKinds).catch(() => { });
  }, []);

  const loadJobs = useCallback(() => {
    authFetch(`${API}/api/eng/projects/${projectId}/drawings`).then(r => r.json()).then(j => setJobs(Array.isArray(j) ? j : [])).catch(() => { });
  }, [projectId]);

  const loadPlan = useCallback(() => {
    if (!floorId) return;
    authFetch(`${API}/api/eng/floors/${floorId}/plan`)
      .then(r => (r.ok ? r.json() : null)).then(setPlan).catch(() => setPlan(null));
  }, [floorId]);

  useEffect(() => { loadPlan(); loadJobs(); }, [loadPlan, loadJobs]);
  // A render finishing (QUEUED -> DONE) shows without pressing anything.
  useLive(["eng_drawing_jobs", "eng_layout_plans"], () => { loadJobs(); loadPlan(); });
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const genPlan = async () => {
    setBusy("plan"); setErr("");
    try {
      const r = await authFetch(`${API}/api/eng/floors/${floorId}/plan`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      if (!r.ok) throw new Error((await r.json()).detail || `Failed (${r.status})`);
      const d = await r.json();
      setPlan(d.plan || d);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };

  // The render runs as a background job, so the panel polls rather than holding
  // a request open for however long FreeCAD takes.
  const poll = useCallback((jobId, tries = 0) => {
    authFetch(`${API}/api/eng/drawings/${jobId}`).then(r => r.json()).then(j => {
      loadJobs();
      if (["QUEUED", "RUNNING"].includes(j.status) && tries < 90) {
        timer.current = setTimeout(() => poll(jobId, tries + 1), 2000);
      } else {
        setBusy("");
        if (j.status === "FAILED") setErr(j.error || "The render failed — see the job row.");
      }
    }).catch(() => setBusy(""));
  }, [loadJobs]);

  const render = async () => {
    if (!plan) return;
    setBusy("draw"); setErr("");
    try {
      const body = { kind };
      if (sheet) body.sheet = sheet;
      const r = await authFetch(`${API}/api/eng/plans/${plan.plan_id}/drawings`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json()).detail || `Failed (${r.status})`);
      const j = await r.json();
      loadJobs();
      poll(j.id);
    } catch (e) { setErr(String(e.message || e)); setBusy(""); }
  };

  const grab = async (jobId, artifact) => {
    const r = await authFetch(`${API}/api/eng/drawings/${jobId}/files/${artifact}`);
    if (!r.ok) throw new Error(`Not available (${r.status})`);
    const cd = r.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    return { blob: await r.blob(), name: m ? m[1] : artifact };
  };

  const download = async (jobId, artifact) => {
    setErr("");
    try {
      const { blob, name } = await grab(jobId, artifact);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setErr(String(e.message || e)); }
  };

  // The SVG sheet is shown inline rather than only offered as a download —
  // a drawing you have to save and open elsewhere is a drawing nobody checks.
  const showSheet = async (jobId) => {
    setErr("");
    if (preview?.jobId === jobId) { URL.revokeObjectURL(preview.url); setPreview(null); return; }
    try {
      const { blob } = await grab(jobId, "sheet_svg");
      if (preview) URL.revokeObjectURL(preview.url);
      setPreview({ jobId, url: URL.createObjectURL(blob) });
    } catch (e) { setErr(String(e.message || e)); }
  };

  const retry = async (jobId) => {
    setBusy("draw"); setErr("");
    await authFetch(`${API}/api/eng/drawings/${jobId}/retry`, { method: "POST" });
    poll(jobId);
  };

  const activeKind = kinds.find(k => k.kind === kind);
  const canRender = plan && cad?.available && !busy;

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 15, color: "#1F4E79", marginBottom: 10 }}>Drawings — plan geometry & CAD</div>

      {cad && !cad.available && (
        <Card style={{ marginBottom: 12, borderLeft: "4px solid #EF4444" }}>
          <div style={{ fontWeight: 700, color: "#B91C1C", fontSize: 13, marginBottom: 4 }}>CAD rendering unavailable on this server</div>
          <div style={{ fontSize: 12, color: "#6B7280" }}>{(cad.hints || []).join(" ")}</div>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 6 }}>Plan geometry still generates — only the drawing files need FreeCAD.</div>
        </Card>
      )}

      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <EngField label="Floor">
            <select value={floorId || ""} onChange={e => setFloorId(Number(e.target.value))} style={{ ...ES.input, minWidth: 200 }}>
              {floors.map(f => <option key={f.id} value={f.id}>{f.label} ({f.rooms} room{f.rooms === 1 ? "" : "s"})</option>)}
            </select>
          </EngField>
          <Btn onClick={genPlan} disabled={!floorId || !!busy} color="#8B5CF6">
            {busy === "plan" ? "Laying out…" : plan ? "↻ Regenerate plan" : "⚙ Generate plan"}
          </Btn>
          <div style={{ flex: 1 }} />
          {cad?.available && <div style={{ fontSize: 11, color: "#9CA3AF" }}>FreeCAD {cad.version}</div>}
        </div>
        <div style={{ fontSize: 12, color: "#6B7280", marginTop: 8 }}>
          Rooms carry sizes but no position, so the layout engine arranges them along a circulation spine and derives the physical walls.
          The result is a <b>schematic arrangement</b> — right about adjacency, quantity and wall identity; only plausible about shape.
          Regenerating supersedes the previous revision rather than editing it.
        </div>
      </Card>

      {plan ? <PlanSummary plan={plan} /> : (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ color: "#9CA3AF", fontSize: 13 }}>No plan for this floor yet — generate one to derive its walls and enable drawings.</div>
        </Card>
      )}

      {plan && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <EngField label="Drawing">
              <select value={kind} onChange={e => setKind(e.target.value)} style={{ ...ES.input, minWidth: 260 }}>
                {kinds.map(k => <option key={k.kind} value={k.kind}>{k.title} ({k.views.length} view{k.views.length === 1 ? "" : "s"})</option>)}
              </select>
            </EngField>
            <EngField label="Sheet">
              <select value={sheet} onChange={e => setSheet(e.target.value)} style={{ ...ES.input, minWidth: 130 }}>
                <option value="">Auto (recommended)</option>
                {["A4", "A3", "A2", "A1", "A0"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </EngField>
            <Btn onClick={render} disabled={!canRender} color="#EF4444">{busy === "draw" ? "Rendering…" : "🖨 Create drawing"}</Btn>
          </div>
          {activeKind && (
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 8 }}>
              Views: {activeKind.views.join(" · ")}. Scale is chosen from the standard ladder unless you fix the sheet size.
            </div>
          )}
        </Card>
      )}

      {err && <div style={{ color: "#EF4444", fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {jobs.length > 0 && (
        <Card style={{ padding: 0, marginBottom: 12 }}>
          <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Drawing", "Kind", "Sheet", "Status", "Files", ""].map(h => <th key={h} className={ac(h)} style={ES.th}>{h}</th>)}</tr></thead>
              <tbody>
                {jobs.map(j => (
                  <tr key={j.id}>
                    <td style={{ ...ES.td, fontWeight: 600 }}>{j.drawing_no}</td>
                    <td style={ES.td}>{j.kind}</td>
                    <td style={ES.td}>{j.sheet_size} · 1:{j.scale}</td>
                    <td style={ES.td}>
                      <Badge text={j.status} color={JOB_COLOR[j.status] || "#6B7280"} />
                      {j.error && <div style={{ fontSize: 11, color: "#EF4444", whiteSpace: "normal", maxWidth: 280, marginTop: 3 }}>{j.error}</div>}
                    </td>
                    <td style={{ ...ES.td, whiteSpace: "normal" }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {ART_ORDER.filter(a => (j.artifacts || []).some(x => x.artifact === a && x.ok)).map(a => (
                          <button key={a} onClick={() => download(j.id, a)} title={`Download ${ART_LABEL[a] || a}`}
                            style={{ border: "1px solid #E5E7EB", background: "#fff", borderRadius: 6, padding: "3px 8px", fontSize: 11, cursor: "pointer", color: "#374151", fontFamily: "inherit" }}>
                            ⬇ {ART_LABEL[a] || a}
                          </button>
                        ))}
                        {(j.artifacts || []).filter(x => !x.ok).map(x => (
                          <span key={x.artifact} title={x.error || ""} style={{ fontSize: 11, color: "#9CA3AF", padding: "3px 4px" }}>✕ {ART_LABEL[x.artifact] || x.artifact}</span>
                        ))}
                      </div>
                    </td>
                    <td style={ES.td}>
                      {(j.artifacts || []).some(a => a.artifact === "sheet_svg" && a.ok) &&
                        <Btn small outline onClick={() => showSheet(j.id)}>{preview?.jobId === j.id ? "Hide" : "View"}</Btn>}
                      {["FAILED", "PARTIAL"].includes(j.status) &&
                        <Btn small outline color="#F59E0B" onClick={() => retry(j.id)}>Retry</Btn>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {preview && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#1F4E79" }}>Sheet preview</div>
            <div style={{ flex: 1 }} />
            <Btn small outline onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}>Close</Btn>
          </div>
          <div style={{ overflowX: "auto", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: 8 }}>
            <img src={preview.url} alt="Drawing sheet" style={{ width: "100%", minWidth: 700, display: "block", background: "#fff" }} />
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Delete (Super Admin) ──────────────────────────────────────────────────────
// Two steps on purpose. Deleting is archiving here — reversible — but it still
// removes a record from everyone else's view, so it should not be one stray
// click away.
function DeleteButton({ what, onDelete, onDone }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async () => {
    setBusy(true); setErr("");
    try {
      await jsonOrThrow(await onDelete(), "Delete failed");
      onDone && onDone();
    } catch (e) { setErr(String(e.message || e)); setBusy(false); setArmed(false); }
  };
  if (!armed) return <Btn small outline color="#DC2626" onClick={() => setArmed(true)}>🗑 Delete</Btn>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span style={{ fontSize: 12, color: "#B91C1C" }}>Archive {what}?</span>
      <Btn small color="#DC2626" disabled={busy} onClick={go}>{busy ? "Deleting…" : "Yes, archive"}</Btn>
      <Btn small outline onClick={() => setArmed(false)}>Cancel</Btn>
      {err && <span style={{ fontSize: 11, color: "#EF4444" }}>{err}</span>}
    </div>
  );
}

// ── Trust chip ────────────────────────────────────────────────────────────────
// The label comes from the server (ItemOut.trust). The UI does not decide what
// "approved" means — that judgement lives with the workflow that enforces it,
// so a chip can never claim more than the data supports.
const TONE = {
  green: { bg: "#ECFDF5", bd: "#A7F3D0", fg: "#047857" },
  amber: { bg: "#FFFBEB", bd: "#FDE68A", fg: "#B45309" },
  blue:  { bg: "#EFF6FF", bd: "#BFDBFE", fg: "#1D4ED8" },
  grey:  { bg: "#F3F4F6", bd: "#E5E7EB", fg: "#4B5563" },
  red:   { bg: "#FEF2F2", bd: "#FECACA", fg: "#B91C1C" },
};

function TrustChip({ trust, small }) {
  if (!trust) return null;
  const t = TONE[trust.tone] || TONE.grey;
  return (
    <span title={trust.hint}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: t.bg, border: `1px solid ${t.bd}`, color: t.fg,
        borderRadius: 999, padding: small ? "1px 8px" : "3px 10px",
        fontSize: small ? 10 : 11, fontWeight: 700, whiteSpace: "nowrap",
      }}>
      {trust.key === "LOCKED" ? "🔒" : trust.key === "ARCHIVED" ? "🗄"
        : trust.key === "REJECTED" ? "⚠" : "•"} {trust.label}
    </span>
  );
}

// ── Approvals queue ───────────────────────────────────────────────────────────
// Two views over the same table: what is waiting for a decision, and everything
// in the catalogue. The second matters because most of the catalogue never
// entered the queue — it was bulk-loaded — and you cannot approve what you
// cannot see. Both carry the SOURCE of each row, so it is obvious at a glance
// whether a decision is being made about something raised here or something
// that arrived from a spreadsheet.

const SOURCE_TONE = {
  "Raised here": { bg: "#EFF6FF", fg: "#1D4ED8" },
  "Master workbook": { bg: "#FFFBEB", fg: "#B45309" },
  "Zoho Books": { bg: "#F0FDFA", fg: "#0F766E" },
  "Unknown": { bg: "#F3F4F6", fg: "#4B5563" },
};

function SourceTag({ label }) {
  const t = SOURCE_TONE[label] || SOURCE_TONE.Unknown;
  return (
    <span style={{ background: t.bg, color: t.fg, borderRadius: 6, padding: "2px 7px",
                   fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

const PER_PAGE = 25;

/** SOP-A12 §12 — the four sign-offs, on the screen that approves the item.
 *
 *  The rule was enforced before this existed: a composite parent could not be
 *  approved until Engineering, SCM, Finance and the Item Master Checker had
 *  each signed, and every one of them could only be signed through the API. So
 *  the approval dialog refused the item and offered no way to satisfy it, and
 *  no A12 composite could be approved at all. KAPL's response of 12-Sep-2026
 *  put this at P0, "proceed now".
 *
 *  A stage that cannot be signed says why rather than going quiet. The common
 *  case is the one §12 exists for — you raised the item, so you may not review
 *  it — and a disabled button with no explanation reads as a broken screen.
 */
function A12SignOffs({ code, me, st, load, onChanged, origin }) {
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [delegating, setDelegating] = useState(null); // stage being signed on behalf
  const [filling, setFilling] = useState(null);       // {stage, field}
  const [fillVal, setFillVal] = useState("");
  const [opts, setOpts] = useState(null);
  const [fwd, setFwd] = useState("");
  useEffect(() => {
    authFetch(`${API}/api/items/composite/options`)
      .then(r => (r.ok ? r.json() : null)).then(setOpts).catch(() => { });
  }, []);

  /** Send this one item to the stages that have to sign it.
   *
   *  The action Admin, Super Admin and the Item Master Approver actually want
   *  on an item they are looking at, and the only place it existed was the bulk
   *  bar behind a row checkbox. So opening an item offered four Sign off
   *  buttons and no way to ASK anybody — and signing one stage then greys out
   *  the other three. (That restriction is since withdrawn — KZ-AUTH-2026-09-16
   *  q4 — but the missing route is why it was added.) That reads as the
   *  screen having done something strange rather than as the four-eyes rule.
   */
  const sendForSignOff = async () => {
    setBusy("fwd"); setErr(""); setFwd("");
    try {
      const r = await authFetch(`${API}/api/items/routing/forward`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_codes: [code],
                               note: "Sent for sign-off from the item." }),
      });
      const d = await jsonOrThrow(r, "Could not send it for sign-off");
      const by = d.by_stage || {};
      const names = Object.keys(by).map(k => k === "SCM" ? "SCM"
        : k === "FINANCE" ? "Finance" : "Engineering");
      const kept = Object.keys(d.carried_forward || {}).map(k => k === "SCM" ? "SCM"
        : k === "FINANCE" ? "Finance" : "Engineering");
      const keptNote = kept.length
        ? ` ${kept.join(", ")} already signed and nothing they own has changed, `
          + `so ${kept.length > 1 ? "those sign-offs stand" : "that sign-off stands"}.`
        : "";
      setFwd(names.length
        ? `Sent to ${names.join(", ")}. It is in their queue now, and comes `
          + `back here to be approved once they have signed.` + keptNote
        : kept.length ? keptNote.trim()
        : "Every stage already has this item open.");
      load(); onChanged && onChanged();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };

  // The Zoho-side fields live inside `zoho_fields`; the rest are columns.
  const ZOHO_SIDE = ["intra_state_tax", "inter_state_tax", "tax_preference",
                     "sales_account_name", "purchase_account_name",
                     "inventory_account_name"];

  const saveField = async (field, value) => {
    setBusy("fill"); setErr("");
    try {
      const body = ZOHO_SIDE.includes(field)
        ? { zoho_fields: { [field]: value } }
        : { [field]: (field === "min_order_qty" || field === "lead_time_days")
                     ? Number(value) : value };
      const r = await authFetch(`${API}/api/items/${code}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      await jsonOrThrow(r, "Could not save it");
      setFilling(null); setFillVal(""); load(); onChanged && onChanged();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };

  // Real values from their own Books organisation, not free text and not a
  // history of what somebody typed before.
  const choicesFor = (field) => {
    if (!opts) return null;
    const tax = (opts.taxes || []);
    switch (field) {
      // 18% first in both lists, because it is the default KAPL states and a
      // dropdown's first entry is what gets picked when someone is in a hurry.
      case "intra_state_tax":
        return tax.filter(t => /^GST/i.test(t.name || ""))
                  .sort((a, b) => (b.percentage === 18) - (a.percentage === 18))
                  .map(t => t.name);
      case "inter_state_tax":
        return tax.filter(t => /^IGST/i.test(t.name || ""))
                  .sort((a, b) => (b.percentage === 18) - (a.percentage === 18))
                  .map(t => t.name);
      case "tax_preference":
        return (opts.tax_preferences || []).map(t => t.label || t.key);
      case "sales_account_name":
        return ((opts.accounts || {}).sales || []).map(a => a.name);
      case "purchase_account_name":
        return ((opts.accounts || {}).purchase || []).map(a => a.name);
      case "inventory_account_name":
        return ((opts.accounts || {}).inventory || []).map(a => a.name);
      case "uom":
        return (opts.units || []).map(u => u.value);
      default:
        return null;
    }
  };
  const [why, setWhy] = useState("");

  if (!st) return null;

  const sign = async (stage, decision, onBehalf = false) => {
    if (onBehalf && !why.trim()) {
      setErr("An on-behalf signature needs a reason. It is recorded against "
             + "your name, permanently."); return;
    }
    setBusy(stage); setErr("");
    try {
      const r = await authFetch(`${API}/api/items/${code}/a12-review`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage, decision,
                               notes: onBehalf ? null : (why.trim() || null),
                               on_behalf: onBehalf,
                               reason: onBehalf ? why.trim() : null }),
      });
      await jsonOrThrow(r, "Could not record the review");
      setDelegating(null); setWhy("");
      load(); onChanged && onChanged();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };

  const done = st.stages.filter(s => s.passed).length;
  // A reviewer signs one stage; Admin, Super Admin, the Checker and the
  // Approver read all four. The server decides which, from the matrix.
  const shown = st.sees_all_stages || !st.my_stage
    ? st.stages
    : st.stages.filter(x => x.stage === st.my_stage);
  // What THIS account may fill, whatever stage the field sits under. null
  // means unrestricted.
  const mayFill = (field) => st.may_edit === null || st.may_edit === undefined
    ? true
    : (st.may_edit || []).includes(field);
  const roleOf = (stage) => (st.stage_roles?.[stage] || [])
    .filter(r => r !== "SUPER_ADMIN")
    .map(r => r.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()))
    .join(" or ");
  const mine = (me?.role || "").toUpperCase();
  const canSign = (stage) => !st.stage_roles
    || (st.stage_roles[stage] || []).includes(mine);
  // Signing one stage no longer closes the others: "one user, one stage" was
  // Kaizen's rule and is in no KAPL document (KZ-AUTH-2026-09-16 q4). Kept as a
  // note on the row so a reviewer can see they have already signed here.
  const signedAlready = st.stages.find(s => s.passed && s.by === me?.username);
  const mayDelegate = ["ADMIN", "SUPER_ADMIN"].includes(mine);
  // The Checker signs last, because its job is to confirm the other three.
  // KAPL-IM-SOP-03 §5 and §13. Said here so the button explains itself rather
  // than being refused after the click.
  const priorsLeft = st.stages
    .filter(x => x.stage !== "CHECKER" && !x.passed)
    .map(x => x.label);
  const blockedStage = (stage) =>
    stage === "CHECKER" && priorsLeft.length
      ? `${priorsLeft.join(", ")} ${priorsLeft.length === 1 ? "has" : "have"} `
        + `not signed yet. The Item Master Approver confirms that they have, `
        + `so it signs last.`
      : null;
  // Why this account cannot sign, stated once rather than per row. The server
  // is the authority; this only predicts its commonest refusal so the reason is
  // visible before the button is pressed.
  // The raiser may review; KAPL bar them from APPROVING and releasing, which
  // the approve button enforces separately. KAPL-IM-USER-MATRIX-01 §2 and §4.
  const blockedAll = null;

  return (
    <Card style={{ marginBottom: 14, background: "#FAFAFA", border: "1px solid #E5E7EB" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                    marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>
          {st.composite ? "SOP-A12 §12 sign-offs" : "§12 sign-offs"}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700,
                       color: done === st.stages.length ? "#047857" : "#B45309" }}>
          {done} of {st.stages.length} complete
        </span>
        {st.sop_status && (
          <span style={{ fontSize: 11, color: "#6B7280" }}>· {st.sop_status}</span>
        )}
        <div style={{ marginLeft: "auto" }}>
          {/* Not offered while a stage is already holding it — the same rule
              the Approvals row follows, or the two screens disagree about the
              same item. */}
          {HAS(me, "item.assign") && done < st.stages.length
            && (st.awaiting || []).length === 0 && (
            <Btn small color="#0369A1" disabled={!!busy}
              onClick={sendForSignOff}>
              {busy === "fwd" ? "Sending…" : "Send for sign-off"}
            </Btn>
          )}
          {(st.awaiting || []).length > 0 && (
            <span style={{ fontSize: 11.5, color: "#6B7280" }}>
              Already with {(st.awaiting || [])
                .map(x => (st.stages.find(s => s.stage === x)?.label || x)
                  .split(" ")[0]).join(", ")} — waiting on their signature.
            </span>
          )}
        </div>
      </div>

      {HAS(me, "item.assign") && done < st.stages.length
        && (st.awaiting || []).length === 0 && (
        <div style={{ fontSize: 11.5, color: "#6B7280", marginBottom: 8 }}>
          Fill in anything you can above, then <b>Send for sign-off</b> — it goes
          to Engineering, SCM and Finance, each signs the part they own, and it
          comes back here to be approved and released. You can also sign a stage
          yourself where you hold that role — including on an item you raised.
          What you cannot do is approve or release your own item: that is the
          one separation KAPL keep.
        </div>
      )}
      {fwd && (
        <div style={{ fontSize: 12, color: "#065F46", background: "#F0FDF4",
                      border: "1px solid #BBF7D0", borderRadius: 6,
                      padding: "6px 9px", marginBottom: 8 }}>{fwd}</div>
      )}

      {blockedAll && (
        <div style={{ fontSize: 11.5, color: "#B45309", marginBottom: 8 }}>{blockedAll}</div>
      )}

      {/* Four signatures on a bolt nobody here typed in is not the intended
          route, and a disabled button with no alternative just looks broken.
          Bulk-loaded data goes through Baseline Migration Approval instead —
          KAPL's ruling of 13-Sep-2026, and the screen for it is on this page. */}
      {done === 0 && ["MASTER_IMPORT", "ZOHO_SYNC", "SHEET_IMPORT"].includes(origin) && (
        <div style={{ fontSize: 11.5, color: "#1F4E79", marginBottom: 8,
                      background: "#F1F5F9", border: "1px solid #E2E8F0",
                      borderRadius: 6, padding: "7px 9px" }}>
          This item was bulk-loaded, not raised here. Four individual sign-offs are
          not the route for it — use <b>Baseline Migration Approval</b> at the foot
          of the Approvals page, which checks a whole category at once and records
          the workbook it came from.
        </div>
      )}

      {!st.sees_all_stages && st.my_stage && (
        <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 6 }}>
          Showing your stage only. {done} of {st.stages.length} stage(s) signed on
          this item.
        </div>
      )}

      {shown.map(s => (
        <div key={s.stage} style={{ borderTop: "1px solid #EEF0F2", padding: "8px 0" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <b style={{ fontSize: 12.5, color: "#111827" }}>{s.label}</b>
            {s.decision === "PASSED" && (
              <span style={{ fontSize: 11.5, color: "#047857" }}>
                signed by {s.by}{s.at ? ` · ${fmtIST(s.at)}` : ""}
              </span>
            )}
            {s.decision === "RETURNED" && (
              <span style={{ fontSize: 11.5, color: "#B91C1C" }}>
                returned by {s.by} — {s.notes}
              </span>
            )}
            {/* The item changed after this stage signed (KAPL-IM-SOP-03 §17),
                so the signature was withdrawn and it is asked again. */}
            {s.decision === "SUPERSEDED" && (
              <span style={{ fontSize: 11.5, color: "#B45309" }}>
                sign again — {s.notes}
              </span>
            )}
            {!s.decision && (
              <span style={{ fontSize: 11.5, color: "#9CA3AF" }}>not signed</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>
            {s.scope}
            {roleOf(s.stage) && (
              <span style={{ color: "#9CA3AF" }}> · signed by {roleOf(s.stage)}</span>
            )}
          </div>

          {/* What this reviewer is signing for — KAPL-IM-USER-MATRIX-01 §4,
              against the item's live values. A stage signed without seeing
              these is a signature on a blank, which is what §4 exists to stop.
              Shown while the stage is open; a signed stage keeps it collapsed
              so the panel stays readable once the work is done. */}
          {(s.checklist || []).length > 0 && (
            <details open={!s.passed} style={{ marginTop: 5 }}>
              <summary style={{ cursor: "pointer", fontSize: 11,
                                color: "#1F4E79", fontWeight: 600 }}>
                {(s.checklist.filter(c => c.ok).length)} of {s.checklist.length} checks
                satisfied
                {s.checklist.some(c => !c.ok && mayFill(c.field)) && (
                  <span style={{ color: "#9CA3AF", fontWeight: 400 }}>
                    {" "}· {s.checklist.filter(c => !c.ok && mayFill(c.field)).length}
                    {" "}you can fill
                  </span>
                )}
              </summary>
              <div style={{ marginTop: 4 }}>
                {s.checklist.map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: 7, fontSize: 11,
                                        padding: "2px 0", alignItems: "baseline" }}>
                    <span style={{ width: 13, flex: "0 0 auto",
                                   color: c.ok ? "#047857" : "#B45309" }}>
                      {c.ok ? "✓" : "·"}
                    </span>
                    <span style={{ flex: "0 0 auto", minWidth: 190,
                                   color: c.ok ? "#374151" : "#92400E" }}>
                      {c.label}
                    </span>
                    <span style={{ color: "#6B7280", overflowWrap: "anywhere",
                                   display: "flex", alignItems: "center", gap: 4,
                                   flexWrap: "wrap" }}>
                      {c.value || (c.ok ? "" : "— not recorded")}
                      {/* A suggestion, kept out of the value column. The
                          master's candidate used to be appended to the value,
                          so an empty HSN read as a filled one. */}
                      {c.hint && (
                        <span style={{ color: "#92400E", background: "#FFFBEB",
                                       border: "1px solid #FDE68A", borderRadius: 3,
                                       padding: "0 4px", fontSize: 10 }}>
                          {c.hint}
                        </span>
                      )}
                      {/* On the row, not inside the fill control.
                          It was next to Save and Cancel, which meant you had
                          to press "fill this" before you could ask what the
                          field was — deciding to answer before finding out
                          what the question is. The approver looking at a gap
                          is the person least likely to know what belongs in
                          it: the stage is not theirs, which is why it is
                          empty. */}
                      {!c.ok && <FieldHelp field={c.field} itemCode={code}
                        label={c.label}
                        onPick={mayFill(c.field) ? (v => {
                          setFilling({ stage: s.stage, field: c.field });
                          setFillVal(v);
                        }) : null} />}
                      {!c.ok && mayFill(c.field)
                        && c.field !== "suppliers" && c.field !== "classification" && (
                        filling && filling.stage === s.stage && filling.field === c.field ? (
                          <span style={{ display: "inline-flex", gap: 5, marginLeft: 6,
                                         verticalAlign: "middle", flexWrap: "nowrap",
                                         alignItems: "center" }}>
                            {c.field === "hsn_code" ? (
                              <HsnPicker compact value={fillVal} onChange={setFillVal}
                                itemCode={code} style={{ ...ES.inputSm, marginBottom: 0 }} />
                            ) : choicesFor(c.field) ? (
                              <select value={fillVal} autoFocus
                                onChange={e => setFillVal(e.target.value)}
                                style={{ ...ES.inputSm, marginBottom: 0,
                                         minWidth: 170, flex: "1 1 auto" }}>
                                <option value="">— choose —</option>
                                {choicesFor(c.field).map(o =>
                                  <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : (
                              <input value={fillVal} autoFocus
                                onChange={e => setFillVal(e.target.value)}
                                style={{ ...ES.inputSm, marginBottom: 0,
                                         minWidth: 130, maxWidth: 240,
                                         flex: "1 1 auto" }} />
                            )}
                            <Btn small color="#10B981" disabled={busy === "fill" || !fillVal}
                              onClick={() => saveField(c.field, fillVal)}>Save</Btn>
                            <Btn small outline color="#6B7280"
                              onClick={() => { setFilling(null); setFillVal(""); }}>Cancel</Btn>
                          </span>
                        ) : (
                          <button
                            onClick={() => { setFilling({ stage: s.stage, field: c.field });
                                             setFillVal(""); setErr(""); }}
                            style={{ marginLeft: 6, border: "1px solid #BFDBFE",
                                     background: "#EFF6FF", color: "#1F4E79",
                                     borderRadius: 5, fontSize: 10.5, padding: "1px 7px",
                                     cursor: "pointer" }}>
                            fill this
                          </button>
                        )
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
          {/* Why a stage is closed, said once and said differently.
              "Signed", "not yours" and "you already signed another" were three
              identical grey buttons with the same red sentence repeated under
              each — which reads as though pressing one had pressed them all. */}
          {!s.passed && !blockedAll && !canSign(s.stage) && (
            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
              Waiting on {roleOf(s.stage)}.
            </div>
          )}
          {!s.passed && blockedStage(s.stage) && (
            <div style={{ fontSize: 11.5, color: "#B45309", marginTop: 3 }}>
              {blockedStage(s.stage)}
            </div>
          )}
          {!s.passed && !blockedStage(s.stage) && canSign(s.stage) && signedAlready
            && signedAlready.stage !== s.stage && (
            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
              You also signed {signedAlready.label} on this item.
            </div>
          )}

          {delegating === s.stage ? (
            <div style={{ marginTop: 6, background: "#FFFBEB",
                          border: "1px solid #FDE68A", borderRadius: 6, padding: 9 }}>
              <div style={{ fontSize: 11.5, color: "#92400E", marginBottom: 6 }}>
                Signing <b>{s.label}</b> on behalf of {roleOf(s.stage)}. Your name,
                the represented role and this reason are recorded permanently.
              </div>
              <textarea value={why} onChange={e => setWhy(e.target.value)} rows={2} autoFocus
                placeholder="Why is this being signed for them? e.g. Finance Manager on leave until 20-Sep"
                style={{ ...ES.input, marginBottom: 6, resize: "vertical" }} />
              <div style={{ display: "flex", gap: 6 }}>
                <Btn small color="#B45309" disabled={!!busy}
                  onClick={() => sign(s.stage, "PASSED", true)}>
                  Sign on behalf of {roleOf(s.stage)}
                </Btn>
                <Btn small outline color="#6B7280"
                  onClick={() => { setDelegating(null); setWhy(""); setErr(""); }}>Cancel</Btn>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center" }}>
              {s.passed ? (
                <span style={{ fontSize: 12, fontWeight: 600, color: "#047857" }}>
                  ✓ Signed
                </span>
              ) : (
                <Btn small color="#10B981"
                  disabled={!!busy || !!blockedAll || !canSign(s.stage)
                            || !!blockedStage(s.stage)}
                  onClick={() => sign(s.stage, "PASSED")}>
                  {busy === s.stage ? "Saving…" : "Sign off"}
                </Btn>
              )}
              {/* The way through when the role-holder is not available.
                  KAPL-IM-USER-MATRIX-01 §5: Admin may approve on behalf of
                  Engineering, SCM, Finance, Checker or the Final Approver,
                  with the represented role, a reason and an audit entry. */}
              {!s.passed && mayDelegate
                && (!canSign(s.stage)
                    || !!(signedAlready && signedAlready.stage !== s.stage)) && (
                <Btn small outline color="#B45309" disabled={!!busy}
                  onClick={() => { setDelegating(s.stage); setWhy(""); setErr(""); }}>
                  Sign on behalf
                </Btn>
              )}
            </div>
          )}
        </div>
      ))}

      {err && <div style={{ color: "#EF4444", fontSize: 12, marginTop: 8 }}>{err}</div>}
    </Card>
  );
}

/** Baseline Migration Approval — KAPL's route for data nobody here raised.
 *
 *  Their response of 13-Sep-2026: "Do not review one-by-one. Create a controlled
 *  Baseline Migration Approval by Category/Subcategory. Run automated validation
 *  first; approve clean batches with named approver; route exceptions
 *  separately. Mark accepted records LEGACY_BASELINE_VERIFIED."
 *
 *  Nothing here approves anything on its own. The screen validates, shows what
 *  failed and why, and waits — a batch is approved only when an administrator
 *  names the source workbook and presses the button. That restraint is the
 *  point: this is the one screen in the system that can change the status of
 *  six thousand items at once.
 */
function BaselineApproval({ me }) {
  const [sum, setSum] = useState(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [rep, setRep] = useState(null);
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const loadSummary = useCallback(() => {
    authFetch(`${API}/api/items/baseline/summary`)
      .then(r => (r.ok ? r.json() : null)).then(setSum).catch(() => { });
  }, []);
  useEffect(() => {
    loadSummary();
    authFetch(`${API}/api/categories/`).then(r => r.json())
      .then(d => setCats(Array.isArray(d) ? d : d.categories || [])).catch(() => { });
  }, [loadSummary]);
  // Stamps made from another tab, or approvals that move items out of scope.
  useLive(["items"], loadSummary, { delay: 1500 });
  useLive(["categories", "subcategories"], () => {
    authFetch(`${API}/api/categories/`).then(r => r.json())
      .then(d => setCats(Array.isArray(d) ? d : d.categories || [])).catch(() => { });
  });

  const exceptions = usePaged(rep?.exception_register || [], 12, cat);

  const validate = async (code) => {
    setCat(code); setRep(null); setDone(null); setErr(""); setBusy("validate");
    try {
      const r = await authFetch(
        `${API}/api/items/baseline/validate?category=${encodeURIComponent(code)}`);
      setRep(await jsonOrThrow(r, "Could not validate the batch"));
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };

  const approve = async () => {
    setBusy("approve"); setErr("");
    try {
      const r = await authFetch(`${API}/api/items/baseline/approve`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category: cat, source: source.trim() }),
      });
      setDone(await jsonOrThrow(r, "Could not approve the batch"));
      setRep(null); loadSummary();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };

  if (!HAS(me, "item.approve") || !sum) return null;

  return (
    <Card style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 700, color: "#1F4E79", marginBottom: 4 }}>
        Baseline Migration Approval
      </div>
      <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12, maxWidth: "70ch" }}>
        For master data that arrived in bulk and nobody here raised. It is checked
        mechanically — nomenclature, unit, HSN, duplicates, status — and what passes is
        stamped <code>LEGACY_BASELINE_VERIFIED</code> against your name and the workbook it
        came from. That is a weaker claim than an ordinary approval, and it stays
        distinguishable from one. {sum.still_eligible.toLocaleString()} item(s) eligible,
        {" "}{sum.baseline_verified.toLocaleString()} already stamped.
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {cats.filter(c => /^A\d\d$/.test(c.code)).map(c => (
          <Btn key={c.code} small outline={cat !== c.code} disabled={!!busy}
            onClick={() => validate(c.code)}>{c.code}</Btn>
        ))}
      </div>

      {busy === "validate" && (
        <div style={{ fontSize: 13, color: "#9CA3AF" }}>Checking {cat}…</div>
      )}

      {rep && (
        <>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 10,
                        fontSize: 13 }}>
            <span><b>{rep.eligible.toLocaleString()}</b> eligible</span>
            <span style={{ color: "#047857" }}><b>{rep.clean.toLocaleString()}</b> clean</span>
            <span style={{ color: "#B45309" }}>
              <b>{rep.exceptions.toLocaleString()}</b> exception(s)
            </span>
          </div>

          {rep.exceptions > 0 && (
            <>
              <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>
                These stay at PENDING_REVIEW. They are not approved by being in the batch.
              </div>
              <ScrollBox max={260}>
                {exceptions.slice.map(e => (
                  <div key={e.item_code} style={{ padding: "6px 0",
                        borderBottom: "1px solid #F3F4F6", fontSize: 12 }}>
                    <code style={{ fontWeight: 600 }}>{e.item_code}</code>{" "}
                    <span style={{ color: "#6B7280" }}>{e.short_name}</span>
                    <div style={{ color: "#B45309", marginTop: 2 }}>
                      {(e.reasons || []).join(" · ")}
                    </div>
                  </div>
                ))}
              </ScrollBox>
              <Pager p={exceptions} noun="exception" />
            </>
          )}

          {rep.clean > 0 ? (
            <div style={{ marginTop: 12, padding: 12, background: "#F8FAFC",
                          border: "1px solid #E2E8F0", borderRadius: 8 }}>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                <b>{rep.clean.toLocaleString()}</b> item(s) in {rep.scope} would be approved and
                stamped in your name ({me.username}). Name the workbook they came from — the
                stamp is a claim about provenance and is worthless without it.
              </div>
              <input value={source} onChange={e => setSource(e.target.value)}
                placeholder="e.g. KAPL_Item_Master_Final_Consolidated_2026_Updated-2.xlsx"
                style={{ ...ES.input, marginBottom: 8 }} />
              <Btn small color="#B45309" disabled={busy === "approve" || !source.trim()}
                onClick={approve}>
                {busy === "approve" ? "Approving…"
                  : `Approve ${rep.clean.toLocaleString()} item(s) in ${rep.scope}`}
              </Btn>
            </div>
          ) : (
            <div style={{ marginTop: 12, fontSize: 13, color: "#B45309" }}>
              Nothing in {rep.scope} passes the checks. Resolve the exceptions above first.
            </div>
          )}
        </>
      )}

      {done && (
        <div style={{ marginTop: 12, padding: 12, background: "#F0FDF4",
                      border: "1px solid #BBF7D0", borderRadius: 8, fontSize: 13 }}>
          <b>{done.approved.toLocaleString()}</b> item(s) in {done.scope} stamped
          {" "}<code>{done.stamp}</code> by {done.approver}, from {done.source}.
          {done.left_as_exceptions > 0 && (
            <> {done.left_as_exceptions.toLocaleString()} left in the exception register.</>
          )}
        </div>
      )}

      {err && <div style={{ color: "#EF4444", fontSize: 12, marginTop: 8 }}>{err}</div>}
    </Card>
  );
}

// ── Review an item (approve / reject with a note) ─────────────────────────────
// Replaces a window.prompt that showed nothing but an item code. Deciding
// whether a record is fit to be trusted means looking at the record: the
// description, the HSN, where it came from and who raised it. The reason is
// typed against the thing it refers to, not into a bare box.
function ReviewItemModal({ code, action, me, users, onClose, onDone }) {
  const [it, setIt] = useState(null);
  // Same reason as on the item page: a rejection reason or a self-approval
  // override is typed, refused for something else, and must not vanish when
  // the dialog is closed. Kept per item AND per decision — the words for a
  // rejection are not the words for an approval.
  const [note, setNote, clearNote] = useDraft(`review.${action}.${code}`);
  const [owner, setOwner] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState(null);
  const [spec] = useItemFields(code);
  const rejecting = action === "REJECTED";
  // Sent back to Draft: a hold, not a verdict (KAPL, 27-Sep-2026). Needs a
  // reason like a rejection, but no owner decision and no approval checks.
  const holding = action === "DRAFT";

  // The §12 sign-offs live here rather than inside the panel, because the
  // Approve button has to know about them too. With the panel owning the state
  // the button could only see the data-quality score: an A12 parent at 100%
  // offered a live Approve that the server then refused with "4 more sign-off(s)
  // required". The button's own rule is that it withholds itself when the
  // server will refuse — this is the other half of that rule.
  const [a12, setA12] = useState(null);
  const loadA12 = useCallback(() => {
    authFetch(`${API}/api/items/${code}/a12-review`)
      .then(r => (r.ok ? r.json() : null))
      // Stages, not composite-ness: the gate applies to every item now.
      .then(d => setA12(d && (d.stages || []).length ? d : null))
      .catch(() => { });
  }, [code]);
  useEffect(() => loadA12(), [loadA12]);

  const signOffsLeft = a12
    ? a12.stages.filter(x => !x.passed).length
    : 0;

  useEffect(() => {
    authFetch(`${API}/api/items/${code}`)
      .then(r => jsonOrThrow(r, "Could not load the item"))
      .then(d => { setIt(d); setOwner(d.owner || ""); })
      .catch(e => setErr(String(e.message || e)));
    // The same completeness check the server applies on Approve, shown before
    // the decision instead of arriving as a refusal after it.
    authFetch(`${API}/api/items/${code}/readiness`)
      .then(r => (r.ok ? r.json() : null)).then(setQ).catch(() => { });
  }, [code]);

  // A stage signing while this dialog is open updates the sign-offs, the
  // readiness and the item — never the owner or note being typed here.
  useLive(touchesItem(code), () => {
    loadA12();
    authFetch(`${API}/api/items/${code}`)
      .then(r => (r.ok ? r.json() : null)).then(d => d && setIt(d)).catch(() => { });
    authFetch(`${API}/api/items/${code}/readiness`)
      .then(r => (r.ok ? r.json() : null)).then(setQ).catch(() => { });
  });

  // Approving an item you raised yourself. Permitted, and recorded as the
  // documented override KAPL-IM-USER-MATRIX-01 §2 allows — so the reason is
  // asked for HERE rather than arriving as a refusal after the click.
  const selfApproval = !rejecting && !holding && it && me
    && it.requested_by && it.requested_by === me.username;

  const submit = async () => {
    if (rejecting && !note.trim()) { setErr("A rejection needs a reason — the creator has to act on it."); return; }
    if (holding && !note.trim()) { setErr("Say why it is going back to Draft — the creator has to know what to change."); return; }
    if (rejecting && !owner) { setErr("Choose who picks this up — an imported item has no creator to send it back to."); return; }
    if (selfApproval && !note.trim()) {
      setErr("You raised this item, so approving it yourself is a documented "
             + "override — say why, and it is recorded against your name.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const r = await authFetch(`${API}/api/items/${code}/status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        // performed_by is set from the token server-side; sent here only so the
        // payload is complete and readable in a network log.
        body: JSON.stringify({ status: action, performed_by: me.username,
                               notes: note.trim() || null,
                               override_reason: selfApproval
                                 ? (note.trim() || null) : undefined,
                               assign_owner: owner || null }),
      });
      await jsonOrThrow(r, "Could not change the status");
      clearNote();          // the decision is made; the draft has served
      onDone();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  const F = (label, value) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280" }}>{label}</div>
      <div style={{ fontSize: 13, color: "#111827", whiteSpace: "pre-wrap" }}>{value || "—"}</div>
    </div>
  );

  return (
    // Wider than the rest: this dialog now carries four checklists with their
    // values and inline editors beside them, and 680px wrapped every row.
    <EngModal title={!HAS(me, "item.approve") ? `Review ${code}`
                     : rejecting ? `Reject ${code}`
                     : holding ? `Move ${code} to Draft (hold)` : `Approve ${code}`}
      onClose={onClose} maxWidth={980}>
      {!it ? <div style={{ color: "#9CA3AF", fontSize: 13 }}>{err || "Loading…"}</div> : (
        <>
          <Card style={{ marginBottom: 14, background: "#F9FAFB" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
              <span style={{ fontWeight: 800, fontSize: 16, color: "#1F4E79" }}>{it.item_code}</span>
              <TrustChip trust={it.trust} small />
            </div>
            {F("Name", it.short_name)}
            {F("Description", it.full_description)}
            <div className="kz-registers" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {F("HSN / SAC", it.hsn_code)}
              {/* Mirrors _SOURCE_LABELS in app/services/item_service.py. A new
                  origin that is not listed here reads as "Unknown", which is
                  how SHEET_IMPORT first appeared on this screen. */}
              {F("Source", { IN_APP: "Raised here", MASTER_IMPORT: "Master workbook",
                             ZOHO_SYNC: "Zoho Books",
                             SHEET_IMPORT: "Uploaded sheet" }[it.origin] || "Unknown")}
              {F("Raised by", it.requested_by)}
              {F("Status", (it.status || "").replace("_", " "))}
              {/* Zoho Books marks Unit mandatory, and this screen showed no
                  unit at all — so an item that Books would refuse looked
                  complete here. */}
              {F("Unit (UOM)", it.uom)}
              {F("Make", it.make)}
              {F("Vendor", (it.suppliers || []).map(v => v.name).filter(Boolean).join(", "))}
            </div>
          </Card>

          {/* What the item actually IS, under the labels the master gives them.
              An approver was asked to sign off a name, a description and an HSN
              — none of which say whether the material, size or grade are right.
              The attributes are the specification; approving without them is
              initialling a summary. */}
          {(spec?.attributes || []).length > 0 && (
            <Card style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#374151",
                            marginBottom: 8 }}>
                What this item is
              </div>
              <div className="kz-registers" style={{ display: "grid",
                    gridTemplateColumns: "1fr 1fr", gap: "0 14px" }}>
                {spec.attributes.map(a => (
                  <div key={a.key}>{F(a.label, a.value)}</div>
                ))}
              </div>
            </Card>
          )}

          <A12SignOffs code={code} me={me} st={a12} load={loadA12} origin={it.origin} onChanged={() =>
            authFetch(`${API}/api/items/${code}/readiness`)
              .then(r => (r.ok ? r.json() : null)).then(setQ).catch(() => { })} />

          {/* Whether it is complete, before the decision rather than after.
              This is the same check the server applies when Approve is pressed,
              so a refusal is visible while there is still something to do about
              it instead of arriving as an error afterwards. */}
          {/* This reads eight core master-data fields and nothing else — not
              the stage checklists above it, not the signatures. It said
              "Data quality 100% · complete" on an item whose own checklist
              directly above read "14 of 23 checks satisfied", with five
              Finance fields unrecorded and two stages unsigned. The number was
              right for what it measured; the words "data quality" and
              "complete" claimed the whole item. It now says what it covers,
              and it never says complete while anything above it is
              outstanding. */}
          {q && (() => {
            const stages = a12?.stages || [];
            const chk = stages.flatMap(x => x.checklist || []);
            const openChecks = chk.filter(c => !c.ok).length;
            const unsigned = stages.filter(x => !x.passed).length;
            const settled = q.ready_to_approve && !openChecks && !unsigned;
            return (
            <Card style={{ marginBottom: 14,
                  background: settled ? "#F0FDF4" : "#FEF2F2",
                  border: `1px solid ${settled ? "#BBF7D0" : "#FECACA"}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8,
                            flexWrap: "wrap", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>
                  Core master data
                </span>
                <span style={{ fontSize: 12, fontWeight: 700,
                               color: q.ready_to_approve ? "#047857" : "#B91C1C" }}>
                  {q.checks - q.issues} of {q.checks}
                </span>
                <span style={{ fontSize: 11.5, color: "#6B7280" }}>
                  — HSN, description, classification, name, vendor, price, make, UOM.
                  {" "}The stage checklists above are counted separately.
                </span>
              </div>
              {(openChecks > 0 || unsigned > 0) && (
                <div style={{ fontSize: 12, color: "#B91C1C", marginBottom: 8,
                              fontWeight: 600 }}>
                  {chk.filter(c => c.ok).length} of {chk.length} stage checks satisfied
                  {openChecks > 0 && ` · ${openChecks} still to fill`}
                  {unsigned > 0 && ` · ${unsigned} stage${unsigned > 1 ? "s" : ""} unsigned`}
                </div>
              )}
              {!q.ready_to_approve && (
                <div style={{ fontSize: 11.5, color: "#B91C1C", marginBottom: 8 }}>
                  Approval will be refused until the blocking items below are resolved.
                </div>
              )}
              {q.findings.map((f, i) => (
                <div key={i} style={{ fontSize: 12, color: "#374151", marginTop: 4 }}>
                  <b style={{ color: f.level === "BLOCK" ? "#B91C1C" : "#B45309" }}>
                    {f.level === "BLOCK" ? "BLOCKS" : "NOTE"}
                  </b>{" "}
                  <b>{f.label}</b> — {f.message}
                </div>
              ))}
            </Card>
            );
          })()}

          {/* Assigning here is the natural moment: whoever is rejecting knows who
              should pick it up, and an imported item has no creator to fall back
              on — 7,211 rows were raised by 'master_import', which nobody can
              sign in as. The server refuses a rejection with no owner. */}
          <EngField label={it.owner ? "Owner (who is answerable for this item)"
                                    : "Assign an owner — this item has none"}>
            <select value={owner} onChange={e => setOwner(e.target.value)}
              style={{ ...ES.input, borderColor: (rejecting && !owner) ? "#FCA5A5" : undefined }}>
              <option value="">— nobody —</option>
              {(users || []).filter(u => u.is_active).map(u => (
                <option key={u.username} value={u.username}>
                  {u.username}{u.full_name ? ` — ${u.full_name}` : ""} ({u.role_label || u.role})
                </option>
              ))}
            </select>
            {!it.owner && (
              <div style={{ fontSize: 11, color: "#B45309", marginTop: 4 }}>
                Raised by <code>{it.requested_by || "an importer"}</code>, which is not an account
                anyone can sign in as. Without an owner a rejection reaches nobody.
              </div>
            )}
          </EngField>

          {/* A reviewer opens this to LOOK — at the description, the attributes
              and their own stage's checklist — before signing or marking the
              stage done. They hold no approve right, so the note box and the
              green Approve below it were asking them for a decision the server
              would refuse. */}
          {HAS(me, "item.approve") && (
          <>
          {selfApproval && (
            <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A",
                          borderRadius: 8, padding: "8px 10px", fontSize: 12,
                          color: "#92400E", marginBottom: 10 }}>
              You raised this item. Approving it yourself is permitted as a
              <b> documented override</b> — KAPL-IM-USER-MATRIX-01 §2 — so the
              reason below is required and is recorded against your name,
              permanently.
            </div>
          )}
          {holding && (
            <div style={{ fontSize: 12.5, color: "#1E40AF", background: "#EFF6FF",
                          border: "1px solid #BFDBFE", borderRadius: 8,
                          padding: "8px 11px", marginBottom: 10 }}>
              The item goes back to <b>Draft</b> and is held there — it is <b>not rejected</b>.
              Open review requests are withdrawn; sign-offs already given stay, and when it is
              submitted again only the stages whose fields changed are asked again.
            </div>
          )}
          <EngField label={rejecting ? "Reason for rejection (required — the creator sees this)"
                           : holding ? "Why it is on hold (required — the creator sees this)"
                           : selfApproval ? "Why you are approving your own item (required)"
                           : "Note (optional)"}>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} autoFocus
              placeholder={rejecting ? "e.g. HSN is wrong for a cassette AC — should be 84151090"
                           : holding ? "e.g. waiting for the vendor's drawing — resubmit with the corner radius confirmed"
                           : selfApproval ? "e.g. sole Finance approver; item raised and checked by me, Engineering and SCM have signed"
                           : "anything worth recording with the approval"}
              style={{ ...ES.input, resize: "vertical" }} />
          </EngField>
          </>
          )}
          {err && <div style={{ color: "#EF4444", fontSize: 12, margin: "6px 0" }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            {!HAS(me, "item.approve") && (
              <span style={{ fontSize: 11.5, color: "#6B7280", marginRight: "auto",
                             alignSelf: "center" }}>
                Approving is the Item Master Approver's step. Sign your stage above —
                that is your whole part, and it takes the item off your queue.
              </span>
            )}
            <Btn outline color="#6B7280" onClick={onClose}>Close</Btn>
            {/* Not offered when the server will refuse it. Pressing Approve on
                an item with a placeholder HSN produces an error and nothing
                else; the reason is already on screen above, so the button
                withholds itself and says which one it is. Reject stays
                available — an incomplete item is exactly what you reject. */}
            {HAS(me, "item.approve") && (
            <Btn color={rejecting ? "#EF4444" : holding ? "#B45309" : "#10B981"} onClick={submit}
              disabled={busy || (!rejecting && !holding && ((q && !q.ready_to_approve)
                                                || signOffsLeft > 0))}>
              {busy ? "Saving…"
                : rejecting ? "Reject item"
                : holding ? "Move to Draft"
                : (q && !q.ready_to_approve)
                  ? `${q.blocking} thing(s) to resolve first`
                  : signOffsLeft > 0
                    ? `${signOffsLeft} sign-off(s) outstanding`
                    : "Approve item"}
            </Btn>
            )}
          </div>
        </>
      )}
    </EngModal>
  );
}

// ── Quick edit from the approvals list ────────────────────────────────────────
// An approver reviewing an item should be able to correct a wrong HSN or a
// muddled description and then approve it, rather than reject it over a typo
// and send it back round the loop. Editing an item that is ALREADY approved
// withdraws the sign-off and returns it for review — the server enforces that,
// and the dialog says so before you start.
/** The Approvals queue's editor — the same form as the item page.
 *
 *  It had its own, with five fields and no classification, so the same item
 *  offered different things depending on which screen you reached it from.
 *  Worse, its "Note (recorded in the audit trail)" wrote to `remarks` and
 *  loaded blank, so saving replaced the price basis, GST and UOM the import
 *  had recorded there with nothing.
 */
function QuickEditItem({ code, me, onClose, onSaved }) {
  const [item, setItem] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [spec, reloadSpec] = useItemFields(code);
  const canEditLocked = HAS(me, "item.edit_locked");

  useEffect(() => {
    authFetch(`${API}/api/items/${code}`)
      .then(r => jsonOrThrow(r, "Could not load the item"))
      .then(setItem).catch(e => setErr(String(e.message || e)));
  }, [code]);

  // The form fills once BOTH the item and the field list are in — one without
  // the other produces a form with no values or no fields.
  useEffect(() => {
    if (item && spec) setForm(blankEdit(item, spec));
  }, [item, spec]);

  const locked = Boolean(item?.is_locked);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const r = await authFetch(`${API}/api/items/${code}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editBody(form, spec, me, blankEdit(item, spec))),
      });
      const saved = await jsonOrThrow(r, "Save failed");
      setItem(saved);
      reloadSpec();                       // or the next edit reverts this one
      onSaved();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  return (
    <EngModal title={`Edit ${code}`} onClose={onClose} maxWidth={720}>
      {!item ? <div style={{ color: "#9CA3AF", fontSize: 13 }}>Loading…</div> : (
        <>
          <div style={{ fontSize: 13, color: "#374151", marginBottom: 10 }}>
            {item.short_name}
          </div>
          {locked && (
            canEditLocked ? (
              <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E",
                            borderRadius: 8, padding: "8px 10px", fontSize: 12, marginBottom: 12 }}>
                This item is approved and locked. You can change it because you are a Super Admin.
                It stays approved, and the sign-off moves to your name — an approval should carry
                whoever vouches for the content as it stands.
              </div>
            ) : (
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C",
                            borderRadius: 8, padding: "8px 10px", fontSize: 12, marginBottom: 12 }}>
                This item is approved and locked, so it cannot be edited. Only a Super Admin can
                change an item that has been signed off.
              </div>
            )
          )}
          <ItemEditFields item={item} spec={spec} form={form} setForm={setForm} />
          {err && <div style={{ color: "#EF4444", fontSize: 12, margin: "6px 0" }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn outline color="#6B7280" onClick={onClose}>Cancel</Btn>
            <Btn onClick={save} disabled={busy || !spec || (locked && !canEditLocked)}>
              {busy ? "Saving…" : "Save changes"}
            </Btn>
          </div>
        </>
      )}
    </EngModal>
  );
}
/** What has been sent to me, and why.
 *
 *  The counterpart to forwarding. An SCM or Finance approver had no list of
 *  their own work — the Approvals page showed every pending item in the
 *  catalogue, 13,052 of them, with no way to tell which ones anybody wanted
 *  from THEM. This shows only what was actually asked for, and shows the
 *  reason as it was recorded at the moment of asking rather than re-deriving
 *  it, because the item has been edited since and that is the whole point.
 *
 *  Clearing a stage is not approving the item and this cannot approve it. The
 *  item goes back to the Item Master Approver, who finishes it.
 */
function MyQueue({ me, onChanged, onOpenItem, review, onReview, tick }) {
  const [q, setQ] = useState(null);
  // `busy` and the clear-a-stage handler went with the Done button: the row's
  // only action now opens a dialog, which owns its own busy state.
  const [err] = useState("");
  // What the modal is currently asking for, if anything. Declared here with
  // the other hooks and NOT beside the handler that uses it: this component
  // returns early when nothing is routed to the role, and a hook below that
  // return runs on some renders and not others. React refuses the build.

  const load = useCallback(() => {
    authFetch(`${API}/api/items/routing/queue`).then(r => r.json())
      .then(setQ).catch(() => setQ(null));
  }, []);
  useEffect(() => { load(); }, [load, tick]);

  // The §12 sign-offs waiting on the same person, folded into the same list.
  // They were a second card further down the page — same reader, same question
  // ("what needs me?"), two answers, two different verbs. An SCM approver had
  // to learn that "Done" and "Open" were different kinds of work before they
  // could tell which list to read.
  const signOffs = (review?.items || []).map(it => ({
    kind: "SIGN", key: `sign:${it.item_code}`, item_code: it.item_code,
    name: it.short_name, by: it.requested_by, composite: it.composite,
    stage_label: review.label,
  }));
  const fills = (q?.items || []).map(row => ({ ...row, kind: "FILL",
                                               key: `fill:${row.assignment_id}`,
                                               by: row.assigned_by }));
  const all = [...fills, ...signOffs];
  const total = (q?.total || 0) + (review?.total || 0);
  if ((!q || !q.stages?.length) && !signOffs.length) return null;

  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#1F4E79" }}>Waiting for you</div>
        <Badge text={`${total}`} color={total ? "#B45309" : "#9CA3AF"} />
        <span style={{ fontSize: 12, color: "#6B7280" }}>
          {(q?.stages || []).map(s => s === "SCM" ? "SCM" : s === "FINANCE" ? "Finance" : "Engineering").join(" · ")}
        </span>
      </div>
      {total === 0 ? (
        <div style={{ fontSize: 13, color: "#9CA3AF" }}>
          Nothing is waiting on you. Items arrive here when an administrator or the
          Item Master Approver forwards one that is missing something you own, and
          when an item raised here needs your stage signed.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 8 }}>
            Everything that needs you, in one list. Open a row, check it, and sign your
            stage. <b>Fields missing</b> means your stage owns something not yet
            recorded — supply it on the item first; <b>Signature only</b> means there is
            nothing outstanding. Signing is not approving: the item goes back to the
            Item Master Approver, who approves and releases it.
            {" "}A stage fills in its own fields rather than sending the item back: the
            fields asked of you here are yours to supply, and nobody else can answer them.
            If the item itself is wrong — a wrong classification, a duplicate, the wrong
            item entirely — say so in the note and raise it with the Item Master Approver.
          </div>
          {err && <div style={{ color: "#B91C1C", fontSize: 12, marginBottom: 6 }}>{err}</div>}
          <ScrollBox max="40vh">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Item code", "Name", "Needs", "Asked for", "By", ""].map(h =>
                <th key={h} style={ES.th} className={ac(h)}>{h}</th>)}</tr></thead>
              <tbody>
                {all.map(row => (
                  <tr key={row.key}>
                    {/* The queue says "fill in what is asked for on the item" and
                        gave no way to reach the item. Everything here is a field
                        somebody must open the item to supply. */}
                    <td style={{ ...ES.td, fontWeight: 600 }} className="ctr">
                      <button onClick={() => onOpenItem && onOpenItem(row.item_code)}
                        title="Open the item to fill these in"
                        style={{ border: "none", background: "transparent", padding: 0,
                                 font: "inherit", fontWeight: 700, color: "#1D4ED8",
                                 cursor: "pointer", textDecoration: "underline" }}>
                        {row.item_code}
                      </button>
                    </td>
                    <td style={ES.td} className="txt">{row.name || "—"}</td>
                    <td style={ES.td} className="ctr">
                      {/* What the row NEEDS, not what to press. It read
                            an instruction to fill in, next to a button reading
                            Open to sign — two instructions, one of them wrong.
                            The column
                            answers what the row needs, and the answer is a
                            count of the fields still missing, or nothing but
                            the signature. */}
                      {(() => {
                        const n = (row.reason || []).filter(x => x.field).length;
                        return row.kind === "SIGN" || !n
                          ? <Badge text="Signature only" color="#1F4E79" />
                          : <Badge text={`${n} field${n > 1 ? "s" : ""} missing`}
                              color="#B45309" />;
                      })()}
                      {row.composite && <>{" "}<Badge text="composite" color="#8B5CF6" /></>}
                    </td>
                    <td style={{ ...ES.td, whiteSpace: "normal" }} className="txt">
                      {row.kind === "SIGN"
                        ? <span style={{ color: "#6B7280", fontSize: 12 }}>
                            Nothing outstanding — it needs your {row.stage_label} signature.
                          </span>
                        : row.reason?.length
                        ? row.reason.map((x, i) => (
                            <div key={i} style={{ fontSize: 12 }}>
                              <b style={{ color: x.level === "BLOCK" ? "#B91C1C" : "#B45309" }}>
                                {x.label}
                              </b>
                              <span style={{ color: "#6B7280" }}> — {x.message}</span>
                            </div>))
                        : <span style={{ color: "#9CA3AF" }}>{row.note || "No reason recorded"}</span>}
                    </td>
                    <td style={ES.td} className="ctr">{row.by || "—"}</td>
                    <td style={ES.td} className="ctr">
                      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        {/* One verb on every row.
                            There were two — "Done" on a Fill-in row, "Open to
                            sign" on a Sign-off one — for the same event, and
                            Done recorded no signature, so a stage could clear
                            its own row and leave the item no closer to being
                            approvable. Signing is what finishing means; it
                            closes the request at the same time. */}
                        <Btn small onClick={() => onReview && onReview(row.item_code)}>
                          Open to sign
                        </Btn>
                        {/* §17 keeps a RETURN at every stage, and §§10-12 end each
                            stage's decision list with it — so the control stays.
                            What it must not be is the obvious button for a field
                            the stage itself owns: SCM returned an item for its
                            UOM and price, both SCM's, and it went away from the
                            only person who had the answer. Where everything asked
                            is the stage's own, it is not offered. */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollBox>
        </>
      )}
    </Card>
  );
}

/** Which queues this account has any business in.
 *
 *  Each entry is a whole queue — a status AND a flow — rather than one axis of
 *  a filter matrix. The page had five status tabs crossed with four flow
 *  buttons, which asked the reader to compose a query before they could see
 *  their work; KAPL: "these are lot more and the KAPL may get confused."
 *
 *  Gated on what the account can DO, so nobody is shown a queue whose rows
 *  carry no button for them: a stage reviewer has no use for "awaiting
 *  release", a maker none for the item pool.
 */
function TABS_FOR(me, myStage) {
  const can = (p) => HAS(me, p);
  const approver = can("item.approve");
  const reviewer = approver || Boolean(me?.my_stage);
  const t = [];
  if (myStage) {
    t.push({ key: "mine", view: "PENDING_REVIEW", flow: "MINE_TO_SIGN",
             label: `Needs my ${myStage} signature`,
             hint: "Items whose stage you sign, still unsigned" });
  }
  if (reviewer) {
    t.push({ key: "review", view: "PENDING_REVIEW", flow: "IN_REVIEW",
             label: "In review",
             hint: "Submitted, or forwarded to an approver" });
  }
  if (can("item.assign")) {
    t.push({ key: "pool", view: "PENDING_REVIEW", flow: "POOL",
             label: "Item pool",
             hint: "Imported items that never went through the workflow — "
                 + "pick one and send it for sign-off" });
  }
  if (can("item.release")) {
    t.push({ key: "approved", view: "APPROVED", flow: "",
             label: "Ready to release",
             hint: "Approved, waiting to be released" });
  }
  if (can("item.submit")) {
    t.push({ key: "draft", view: "DRAFT", flow: "",
             label: "Drafts", hint: "Not yet submitted for review" });
  }
  t.push({ key: "rejected", view: "REJECTED", flow: "",
           label: reviewer ? "Rejected" : "Rejected — needs your attention",
           hint: "Sent back with a reason" });
  t.push({ key: "all", view: "", flow: "",
           label: reviewer ? "All items" : "All my items",
           hint: "Every status" });
  return t;
}

function Approvals({ me }) {
  const approver = HAS(me, "item.approve");
  // Anyone who signs a §12 stage gets the same page as an approver.
  //
  // KAPL, 16-Sep-2026: "make the approval page same for all the places like
  // item master approver." Finance and SCM were getting a different screen —
  // a plain card of rows with no filters, no counts and no sight of what was
  // already signed — while the Item Master Approver got the full list. Same
  // job, two screens. `my_stage` comes with the account so this can be decided
  // before any items are fetched.
  const reviewer = approver || Boolean(me?.my_stage);
  // What is waiting on this account's own §12 stage.
  //
  // A reviewer holds no approve right and raises nothing, so this screen
  // offered them the maker's view — "items you raised", empty — which is what
  // SCM reported on 14-Sep-2026. They need the opposite list.
  const [queue, setQueue] = useState(null);
  // Bumped whenever something on this page changes an item. Both queues above
  // the list watch it: signing in the dialog emptied the stage's work but left
  // "Waiting for you 3" and the same three rows on screen, because the dialog
  // only reloaded the list underneath it.
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick(t => t + 1), []);
  useEffect(() => {
    authFetch(`${API}/api/items/review-queue`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setQueue(d && d.stage ? d : null))
      .catch(() => { });
  }, [tick]);
  const zohoStatus = useZohoStatus();
  // A creator lands on what needs their attention; anyone who signs a stage on
  // the queue.
  const [view, setView] = useState(reviewer ? "PENDING_REVIEW" : "REJECTED");
  const [mine, setMine] = useState(!reviewer);
  const [origin, setOrigin] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  // Multi-select. 13,693 items are awaiting review; a queue that can only be
  // worked one row at a time cannot be cleared however many people use it.

  /** Which half of "waiting for review" to show.
   *
   *  13,041 of the 13,052 items here came from the master import with the
   *  status already written on them — nobody submitted them and nobody was
   *  asked to look. Eleven actually travelled. Showing all of it in one list
   *  buried those eleven, which is why the queue read as hopeless.
   *
   *  So the queue holds what somebody is genuinely waiting on, and the pool —
   *  a real library of items, usable, just not work in progress — is one click
   *  away for the people who forward from it.
   */
  // A stage reviewer lands on their own work; an approver on everything in
  // review, because their job is the whole queue rather than one stage.
  const [flow, setFlow] = useState(
    !approver && me?.my_stage ? "MINE_TO_SIGN" : "IN_REVIEW");
  const [batchMsg, setBatchMsg] = useState(null);
  // Which of these codes Zoho Books had already used for something else.
  const [retired, setRetired] = useState({});
  const [rows, setRows] = useState(null);
  // The §12 stage this account signs. Taken from the ACCOUNT, not from the
  // rows: derived from rows it disappeared the moment the filter returned
  // none, which is exactly when someone is standing on the "Needs my
  // signature" tab having just cleared it — the tab they were on would vanish
  // underneath them.
  const myStage = me?.my_stage_label || null;
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [imported, setImported] = useState(null);
  const [adopting, setAdopting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [users, setUsers] = useState([]);
  const [owner, setOwner] = useState("");

  // `quiet` keeps the rows on screen while a live update refetches them;
  // blanking the table on every change elsewhere would make it flicker.
  const load = useCallback(async (quiet) => {
    if (quiet !== true) setRows(null);
    try {
      const qs = new URLSearchParams({ limit: String(PER_PAGE), skip: String(page * PER_PAGE) });
      if (view) qs.set("status", view);
      if (origin) qs.set("origin", origin);
      // Only the waiting-for-review tab splits this way; the others are small
      // enough to read whole, and DRAFT has no pool to speak of.
      if (view === "PENDING_REVIEW" && flow) qs.set("flow", flow);
      // Scoped to the tab, not across it: searching inside "Waiting for
      // review" should not return released items. The status filter stays on.
      if (q.trim()) qs.set("q", q.trim());
      // Scoping is enforced server-side from the token, not from this flag.
      if (mine || !reviewer) qs.set("mine", "true");
      const r = await authFetch(`${API}/api/items/list?${qs}`);
      const d = await jsonOrThrow(r, "Could not load the list");
      setRows(d.items || []);
      setTotal(typeof d.total === "number" ? d.total : (d.items || []).length);
    } catch (e) { setErr(String(e.message || e)); setRows([]); }
  }, [view, origin, page, mine, approver, q, flow]);
  useEffect(() => { load(); }, [load]);
  // Any sign-off, approval or status change anywhere: the list, the "waiting
  // for you" queue and its counts all move without a refresh.
  useLive(ITEM_TABLES, () => {
    load(true);
    refresh();
    authFetch(`${API}/api/items/imported/summary`)
      .then(r => r.ok ? r.json() : null).then(setImported).catch(() => { });
  });
  // A new filter starts at the first page. Landing on page 4 of a search that
  // returns two rows shows an empty table and looks like nothing matched.
  useEffect(() => { setPage(0); }, [view, origin, mine, q]);
  // A message about the last action does not belong over a different list.
  useEffect(() => { setBatchMsg(null); }, [view, origin, mine, q, page]);

  // Asked for the visible page only. A code with a history is worth saying so
  // on the row, but it is a fact about 266 items and should not cost anything
  // on the other 13,427.
  useEffect(() => {
    const codes = (rows || []).map(r => r.item_code).filter(Boolean);
    if (!codes.length) { setRetired({}); return; }
    let alive = true;
    authFetch(`${API}/api/items/retired-codes`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ item_codes: codes }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive) setRetired(d?.retired || {}); })
      .catch(() => { });
    return () => { alive = false; };
  }, [rows]);
  useEffect(() => {
    authFetch(`${API}/api/items/imported/summary`)
      .then(r => r.ok ? r.json() : null).then(setImported).catch(() => { });
    // The user list is still needed: the reject dialog asks who picks an
    // unowned item up, which is where ownership is now set.
    authFetch(`${API}/api/users/`)
      .then(r => r.ok ? r.json() : []).then(u => setUsers(Array.isArray(u) ? u : [])).catch(() => { });
  }, []);

  // Approve and Reject open the item so the decision is made while looking at
  // it. Reopen is not a judgement, so it goes straight through.
  const decide = async (code, status) => {
    if (status === "APPROVED" || status === "REJECTED" || status === "DRAFT") {
      setReviewing({ code, action: status });
      return;
    }
    setBusy(code); setErr("");
    try {
      const r = await authFetch(`${API}/api/items/${code}/status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, performed_by: me.username }),
      });
      await jsonOrThrow(r, "Could not change the status");
      await load();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };

  /** One workflow step, applied to every ticked row.
   *
   *  The server walks them one at a time through the same call a single
   *  transition uses, so four eyes and the completeness gate still hold per
   *  item. A batch is a convenience for the person, not a lighter rule for the
   *  items — and one that cannot move is reported rather than failing the rest.
   */
  /** Send the selected items to whoever owns what each one is missing.
   *
   *  The stage is not passed. The server computes it per item from the same
   *  readiness findings the item's own page shows, so a selection of two
   *  thousand items lands on three desks correctly rather than on one desk
   *  wrongly.
   */
  const doForward = async (only) => {
    const codes = only ? [only] : [];
    if (!codes.length) return;
    setBusy("fwd"); setErr(""); setBatchMsg(null);
    try {
      const r = await authFetch(`${API}/api/items/routing/forward`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_codes: codes }),
      });
      const d = await jsonOrThrow(r, "Could not forward");
      const parts = Object.entries(d.by_stage || {})
        .map(([st, n]) => `${n} to ${st === "SCM" ? "SCM" : st === "FINANCE" ? "Finance" : "Engineering"}`);
      setBatchMsg({
        ok: true,
        text: parts.length
          ? `Forwarded ${d.assigned} request(s) across ${d.items} item(s) — ${parts.join(", ")}.`
            + (d.skipped ? ` ${d.skipped} skipped (already with someone, or nothing missing).` : "")
          : `Nothing to forward — none of the ${d.items} selected item(s) is missing anything an approver owns.`,
      });
      load();
    } catch (e) { setErr(e.message || "Could not forward"); }
    setBusy("");
  };

  // doBatch is gone with the selection. Everything it did — forward, release,
  // submit — is a button on the row, and batch-status refuses APPROVED and
  // REJECTED outright.

  const adopt = async (code, confirm) => {
    setBusy("adopt"); setErr("");
    try {
      const r = await authFetch(`${API}/api/items/adopt`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category_code: code, confirm }),
      });
      const d = await jsonOrThrow(r, "Adoption failed");
      if (!confirm) setAdopting({ code, count: d.would_adopt, sample: d.sample });
      else {
        setAdopting(null);
        await load();
        authFetch(`${API}/api/items/imported/summary`).then(r2 => r2.json()).then(setImported).catch(() => { });
      }
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };

  const pages = Math.max(1, Math.ceil(total / PER_PAGE));
  const here = (t) => view === t.view && flow === t.flow;

  const tab = (val, label) => (
    <button key={val} onClick={() => setView(val)}
      style={{ border: "1px solid " + (view === val ? "#1F4E79" : "#E5E7EB"),
               background: view === val ? "#1F4E79" : "#fff",
               color: view === val ? "#fff" : "#374151",
               borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600,
               cursor: "pointer", fontFamily: "inherit" }}>{label}</button>
  );

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 3 }}>Approvals</h2>
      <p style={{ fontSize: 13, color: "#6B7280", marginBottom: 14, maxWidth: 900, lineHeight: 1.6 }}>
        {reviewer
          ? <>An item is only trustworthy once someone here has signed it off. Approving locks it —
              after that it can be pushed to Zoho and reused without further checking. Most of the
              catalogue was bulk-loaded and never passed through this queue, so its source is shown
              against every row.</>
          : <>What has happened to the items you raised. A rejected item comes back here with the
              reason attached — fix it and submit it again.</>}
      </p>

      {/* Above the catalogue, because it is the shorter and more urgent list:
          these are the items somebody is actually waiting on you for. Renders
          nothing at all for a role that no stage routes to. */}
      {/* Opens the same quick-edit form the list rows use, so a queue row can
          be acted on where it sits rather than hunted for in the catalogue. */}
      {/* Only for accounts that do not get the full list below.
          An approver saw every item twice — once here as "Signature only" and
          once below as "0 of 4 signed", both true and about different things.
          A reviewer who cannot approve has no list below, so this is theirs. */}
      {!reviewer && (
        <MyQueue me={me} onChanged={load} onOpenItem={setEditing}
          review={queue} tick={tick}
          onReview={code => setReviewing({ code, action: "APPROVED" })} />
      )}
      {/* §17 — where a returned item goes. It had nowhere to go before this. */}
      {err && <div style={{ color: "#EF4444", fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {/* One row of tabs, and only the ones this account can act on.
          There were two rows and nine controls — five statuses crossed with
          four flow filters — which is a matrix to be understood rather than a
          list to be worked. Each tab now sets the status AND the flow together,
          so choosing one is choosing a queue rather than composing a query. */}
      <div className="kz-scroll-x" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        {TABS_FOR(me, myStage).map(t => (
          <button key={t.key} title={t.hint}
            onClick={() => { setView(t.view); setFlow(t.flow); setPage(0); }}
            style={{ border: "1px solid " + (here(t) ? "#1F4E79" : "#E5E7EB"),
                     background: here(t) ? "#1F4E79" : "#fff",
                     color: here(t) ? "#fff" : "#374151",
                     borderRadius: 8, padding: "6px 12px", fontSize: 12,
                     fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            {t.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search this tab — code, name, HSN, vendor"
          style={{ ...ES.input, width: "auto", minWidth: 250, padding: "5px 10px",
                   fontSize: 12 }} />
        {q && (
          <button onClick={() => setQ("")}
            title="Clear the search"
            style={{ border: "none", background: "transparent", color: "#6B7280",
                     cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>
            × clear
          </button>
        )}
        {approver && (
          <label style={{ fontSize: 12, color: "#6B7280", display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={mine} onChange={e => setMine(e.target.checked)} />
            Only mine
          </label>
        )}
        <span style={{ fontSize: 12, color: "#6B7280" }}>Source</span>
        <select value={origin} onChange={e => setOrigin(e.target.value)}
          style={{ ...ES.input, width: "auto", minWidth: 170, padding: "5px 8px", fontSize: 12 }}>
          <option value="">All sources</option>
          <option value="IN_APP">Raised here</option>
          <option value="MASTER_IMPORT">Master workbook</option>
        </select>
      </div>

      <Card style={{ marginBottom: 16, padding: 0 }}>
        <div style={{ padding: "10px 14px", fontWeight: 700, color: "#1F4E79",
                      borderBottom: "1px solid #F3F4F6", display: "flex", flexWrap: "wrap", gap: 8 }}>
          <span>{view === "PENDING_REVIEW" ? "Waiting for review" : "All items"} ({total.toLocaleString()})</span>
          {total > 0 && (
            <span style={{ fontWeight: 500, color: "#6B7280", fontSize: 12 }}>
              page {page + 1} of {pages.toLocaleString()}
            </span>
          )}
        </div>
        {/* No selection, and no batch bar.
            Approve and reject went first — each carries per-item information a
            selection has nowhere to put. Forward, release and submit followed,
            because every one of them is on the row itself and a tick box that
            exists for three rarely-used shortcuts is a column of noise on every
            screen. KAPL, 16-Sep-2026: "the selection of items should also be
            removed … the flow should be now seamless." */}
        {batchMsg && (
          <div style={{ padding: "10px 14px", background: batchMsg.failed ? "#FFFBEB" : "#F0FDF4",
                        borderBottom: "1px solid #E5E7EB", fontSize: 12.5 }}>
            <b style={{ color: batchMsg.failed ? "#B45309" : "#047857" }}>{batchMsg.message}</b>
            {(batchMsg.failures || []).length > 0 && (
              <div style={{ marginTop: 6, color: "#6B7280", maxHeight: 150, overflowY: "auto" }}>
                {batchMsg.failures.map(f => (
                  <div key={f.item_code}><code>{f.item_code}</code> — {f.reason}</div>
                ))}
              </div>
            )}
          </div>
        )}
        {rows === null ? <div style={{ padding: 14, color: "#9CA3AF", fontSize: 13 }}>Loading…</div>
          : rows.length === 0 ? <div style={{ padding: 14, color: "#9CA3AF", fontSize: 13 }}>Nothing here.</div>
            : (
              <div className="kz-tablewrap">
                {/* Every item created in the app, which is the list that grows fastest of any here. */}
<ScrollBox max="56vh">
<table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    {["Item code", "Name", "Source", "Status", "Trust", "Owner", ""].map(h =>
                      <th key={h} style={ES.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {rows.map(it => {
                      // Named isMine, not mine — the outer `mine` is the
                      // "only my items" filter, and two different meanings
                      // under one name a few lines apart is a trap.
                      const isMine = it.requested_by === me.username;
                      const pending = it.status === "PENDING_REVIEW";
                      const rejected = it.status === "REJECTED";
                      // The two steps that had no control anywhere.
                      const draft = it.status === "DRAFT";
                      const approved = it.status === "APPROVED";
                      // The raiser may approve their own item. KAPL,
                      // 16-Sep-2026; it is the documented override
                      // KAPL-IM-USER-MATRIX-01 §2 already allowed, and the
                      // dialog collects the reason. This row was still hiding
                      // the button and telling the Item Master Approver that
                      // "an Admin approves it here", which stopped being true.
                      const canSignOff = HAS(me, "item.approve");
                      return (
                        <tr key={it.item_code} style={rejected ? { background: "#FEF2F2" } : undefined}>
                          <td style={{ ...ES.td, fontWeight: 600,
                                       borderLeft: rejected ? "3px solid #DC2626" : undefined }}>
                            {it.item_code}
                            {/* This number meant something else in Zoho until
                                that record was renamed. Worth saying on the row:
                                anyone reconciling against a purchase order will
                                meet the suffixed code and needs to know why. */}
                            {retired[it.item_code] && (
                              <div style={{ fontSize: 10, color: "#B45309",
                                            fontWeight: 600, marginTop: 2 }}
                                title={`Zoho Books used this code for "${retired[it.item_code].name}". That record was renamed ${retired[it.item_code].sku} to release the number for this item.`}>
                                was {retired[it.item_code].sku} in Zoho
                              </div>
                            )}
                          </td>
                          <td style={{ ...ES.td, whiteSpace: "normal", minWidth: 200 }}>
                            {it.short_name}
                            {rejected && it.review_note && (
                              <div style={{ fontSize: 11, color: "#B91C1C", marginTop: 3 }}>
                                ⚠ {it.review_note}
                              </div>
                            )}
                          </td>
                          <td style={ES.td}><SourceTag label={it.source_label} /></td>
                          <td style={ES.td}>{(it.status || "").replace("_", " ")}</td>
                          <td style={ES.td}><TrustChip trust={it.trust} small /></td>
                          <td style={ES.td}>
                            {it.owner
                              ? it.owner
                              : <span style={{ color: "#B45309", fontSize: 11 }}
                                  title={`Raised by ${it.requested_by || "an importer"}, which is not an account anyone can sign in as — a rejection on this item would reach nobody.`}>
                                  unassigned
                                </span>}
                          </td>
                          <td style={ES.td}>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {HAS(me, "item.edit_any")
                                && (!it.is_locked || HAS(me, "item.edit_locked")) && (
                                <Btn small outline onClick={() => setEditing(it.item_code)}>Edit</Btn>
                              )}
                              {/* Signing your own §12 stage is NOT approving,
                                  so it must not sit behind the approve right.
                                  It did, and Finance — who signs a stage and
                                  approves nothing — saw "waiting on an
                                  approver" on all eighteen items it was
                                  itself holding, with no way to sign any of
                                  them. */}
                              {pending && it.signoffs?.mine && (
                                <Btn small color="#1F4E79"
                                  title={`${it.signoffs.mine} is yours to sign on this item.`}
                                  onClick={() => setReviewing({ code: it.item_code,
                                                                action: "APPROVED" })}>
                                  Sign {it.signoffs.mine.split(" ")[0]}
                                </Btn>
                              )}
                              {pending && canSignOff && (
                                <>
                                  {/* Green means "this can be approved". It was
                                      green on every pending row, including ones
                                      with §12 stages unsigned — so Approve
                                      opened a dialog whose only message was
                                      that you cannot approve it yet. The row
                                      now says what is missing. */}
                                  {/* The route, on the row. Forwarding existed
                                      only in the bulk bar behind a checkbox,
                                      so the way to move one item was to open it
                                      and sign the stages yourself — which the
                                      four-eyes rule then blocks after the
                                      first one. */}
                                  {/* Not offered on an item that is already
                                      out. It was shown whenever a signature was
                                      outstanding, so A01-001.065 — sitting in
                                      Engineering, SCM and Finance's queues at
                                      that moment — invited you to send it to
                                      the same three people again. */}
                                  {HAS(me, "item.assign")
                                    && (it.signoffs?.outstanding || []).length > 0
                                    && (it.signoffs?.awaiting || []).length === 0 && (
                                    <Btn small outline color="#0369A1"
                                      disabled={busy === "fwd"}
                                      title="Send it to Engineering, SCM and Finance to sign"
                                      onClick={() => doForward(it.item_code)}>
                                      Send for sign-off
                                    </Btn>
                                  )}
                                  {(it.signoffs?.awaiting || []).length > 0 && (
                                    <span style={{ fontSize: 11, color: "#6B7280",
                                                   alignSelf: "center" }}
                                      title={"Already sent. These stages are holding it "
                                           + "and have not signed yet."}>
                                      with {it.signoffs.awaiting
                                        .map(a => a.split(" ")[0]).join(", ")}
                                    </span>
                                  )}
                                  {(it.signoffs?.outstanding || []).length > 0 ? (
                                    <Btn small outline color="#B45309"
                                      disabled={busy === it.item_code}
                                      title={`Waiting on ${it.signoffs.outstanding.join(", ")}. `
                                             + `Opening it shows what is left.`}
                                      onClick={() => decide(it.item_code, "APPROVED")}>
                                      {it.signoffs.done}/{it.signoffs.total} signed
                                    </Btn>
                                  ) : (
                                  <Btn small color="#10B981" disabled={busy === it.item_code}
                                    onClick={() => decide(it.item_code, "APPROVED")}>Approve</Btn>
                                  )}
                                  <Btn small outline color="#B45309" disabled={busy === it.item_code}
                                    title="Put it back to Draft and hold it — not a rejection"
                                    onClick={() => decide(it.item_code, "DRAFT")}>Move to Draft</Btn>
                                  <Btn small outline color="#EF4444" disabled={busy === it.item_code}
                                    onClick={() => decide(it.item_code, "REJECTED")}>Reject</Btn>

                                </>
                              )}
                              {draft && HAS(me, "item.submit") && (
                                <Btn small color="#1D4ED8" disabled={busy === it.item_code}
                                  onClick={() => decide(it.item_code, "PENDING_REVIEW")}>
                                  Submit for review
                                </Btn>
                              )}
                              {approved && HAS(me, "item.release") && (
                                <Btn small color="#7C3AED" disabled={busy === it.item_code}
                                  onClick={() => decide(it.item_code, "RELEASED")}>
                                  Release
                                </Btn>
                              )}
                              {/* The last step of the journey, in the queue
                                  that works the earlier ones. It was only on
                                  the item page, so finishing a release meant
                                  leaving the list and opening the item to press
                                  one more button. The chip decides for itself
                                  whether the item is eligible. */}
                              <ZohoChip item={it} configured={zohoStatus.configured}
                                onSynced={() => load()} />
                              {rejected && it.requested_by === me.username && (
                                <Btn small color="#3B82F6" disabled={busy === it.item_code}
                                  onClick={() => decide(it.item_code, "DRAFT")}>Reopen</Btn>
                              )}
                              {pending && !canSignOff && !it.signoffs?.mine && (
                                <span style={{ fontSize: 11, color: "#9CA3AF", alignSelf: "center" }}
                                  title="Approving needs the item.approve right. Your own stage, where you have one, is signed from the button beside this.">
                                  waiting on an approver
                                </span>
                              )}
                              {pending && canSignOff && isMine && (
                                <span style={{ fontSize: 11, color: "#B45309", alignSelf: "center" }}
                                  title={"You raised this item. Approving it yourself is permitted "
                                       + "as a documented override — KAPL-IM-USER-MATRIX-01 §2 — "
                                       + "and the dialog will ask you for the reason."}>
                                  yours — approving needs a reason
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
</ScrollBox>
              </div>
            )}
        {pages > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                        borderTop: "1px solid #F3F4F6", flexWrap: "wrap" }}>
            <Btn small outline disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>‹ Prev</Btn>
            <span style={{ fontSize: 12, color: "#6B7280" }}>
              {(page * PER_PAGE + 1).toLocaleString()}–{Math.min((page + 1) * PER_PAGE, total).toLocaleString()} of {total.toLocaleString()}
            </span>
            <Btn small outline disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)}>Next ›</Btn>
          </div>
        )}
      </Card>

      {editing && <QuickEditItem code={editing} me={me} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }} />}

      {reviewing && <ReviewItemModal code={reviewing.code} action={reviewing.action} me={me} users={users}
        /* Reload on close too, not only on a decision. Signing a stage inside
           the dialog changes the row behind it, and closing without approving
           left the old count on screen: A01-001.008 read "0/4 signed" in the
           list while the dialog it had just been signed in read "1 of 4". */
        onClose={() => { setReviewing(null); load(); refresh(); }}
        onDone={() => { setReviewing(null); load(); refresh(); }} />}

      {/* "Nobody is answerable for 953 items" is gone.
          It assigned a whole category's ownership to one person in a click, to
          solve a problem that is already solved where it actually bites: a
          rejection on an unowned item is refused, and the dialog makes the
          rejecter choose who picks it up — at the moment somebody knows who
          that should be, one item at a time. Assigning 425 MA items to a name
          in advance is ownership by fiat, and it is the last bulk action on a
          page whose whole flow is now per item. */}

      {HAS(me, "item.delete") && imported && imported.total_unadopted > 0 && (
        <Card>
          <div style={{ fontWeight: 700, color: "#B45309", marginBottom: 4 }}>
            Imported stock awaiting adoption ({imported.total_unadopted.toLocaleString()})
          </div>
          <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12 }}>
            These were bulk-loaded and carry no approver, so they cannot be pushed to Zoho.
            Adopting a category records your sign-off against every item in it — review the list
            before you confirm.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {imported.by_category.map(c => (
              <Btn key={c.code} small outline disabled={busy === "adopt"}
                onClick={() => adopt(c.code, false)}>
                {c.code} · {c.name} ({c.count})
              </Btn>
            ))}
          </div>
          {adopting && (
            <div style={{ marginTop: 12, padding: 12, background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8 }}>
              <div style={{ fontSize: 13, color: "#92400E", marginBottom: 8 }}>
                <b>{adopting.count}</b> item(s) in {adopting.code} will be marked approved and locked,
                signed off in your name. Sample: {(adopting.sample || []).join(", ")}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn small color="#B45309" disabled={busy === "adopt"}
                  onClick={() => adopt(adopting.code, true)}>Yes, adopt {adopting.count}</Btn>
                <Btn small outline onClick={() => setAdopting(null)}>Cancel</Btn>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* KAPL's route for bulk-loaded master data — their ruling of
          13-Sep-2026 — and it stays, because 630 items have already gone
          through it and four individual signatures on a bolt nobody here typed
          in is not the intended path for the other 13,359.

          But it was rendered to EVERYONE. A Finance or SCM reviewer, and a
          maker, saw a panel of A01–A12 buttons that the server refuses them:
          baseline_approve requires ITEM_APPROVE. Shown now only to the accounts
          that can actually use it, which is the difference between a control
          and a puzzle. */}
      {HAS(me, "item.approve") && <BaselineApproval me={me} />}
    </div>
  );
}

// ── Archive (Super Admin) ─────────────────────────────────────────────────────
function Archive() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    authFetch(`${API}/api/items/deleted/list`)
      .then(r => jsonOrThrow(r, "Could not load the archive"))
      .then(setRows).catch(e => { setErr(String(e.message || e)); setRows([]); });
  }, []);
  useEffect(() => { load(); }, [load]);
  useLive(["items"], load);

  const restore = async (code) => {
    setErr("");
    try {
      const r = await authFetch(`${API}/api/items/${code}/restore`, { method: "POST" });
      await jsonOrThrow(r, "Restore failed");
      load();
    } catch (e) { setErr(String(e.message || e)); }
  };

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 3 }}>Archive</h2>
      <p style={{ fontSize: 13, color: "#6B7280", marginBottom: 16 }}>
        Deleted items are archived rather than destroyed, so their audit history keeps its
        subject and anything already in Zoho is not orphaned. Restoring puts one back.
      </p>
      {err && <div style={{ color: "#EF4444", fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <Card style={{ padding: 0 }}>
        {rows === null ? <div style={{ padding: 14, color: "#9CA3AF", fontSize: 13 }}>Loading…</div>
          : rows.length === 0 ? <div style={{ padding: 14, color: "#9CA3AF", fontSize: 13 }}>Nothing archived.</div>
            : (
              <div className="kz-tablewrap">
                {/* The archive only ever grows — nothing is pruned from it. */}
<ScrollBox max="56vh">
<table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>{["Item code", "Name", "Status", "Deleted", "By", ""].map(h => <th key={h} className={ac(h)} style={ES.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.item_code}>
                        <td style={{ ...ES.td, fontWeight: 600 }}>{r.item_code}</td>
                        <td style={{ ...ES.td, whiteSpace: "normal" }}>{r.short_name}</td>
                        <td style={ES.td}>{r.status}</td>
                        <td style={ES.td}>{fmtIST(r.deleted_at)}</td>
                        <td style={ES.td}>{r.deleted_by}</td>
                        <td style={ES.td}><Btn small outline onClick={() => restore(r.item_code)}>Restore</Btn></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
</ScrollBox>
              </div>
            )}
      </Card>
    </div>
  );
}

// ── Projects list ─────────────────────────────────────────────────────────────
function ProjectsView({ me, onGoTo }) {
  const [rows, setRows] = useState(null);
  // Arriving from another screen's "Go to Projects" opens that project.
  const [sel, setSelRaw] = useState(() => takeOpenProject());
  const setSel = (id) => { setSelRaw(id); if (id) setActiveProject(id); };
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 25;
  // An error is not an empty list.
  //
  // This was `.catch(() => setRows([]))`, so a 500 rendered "No projects yet —
  // convert an inquiry from the Inquiries tab" over eighteen existing
  // projects. The screen told the reader to go and create what they already
  // had, and gave no hint that anything had failed.
  const [loadErr, setLoadErr] = useState("");
  const [unreadable, setUnreadable] = useState([]);
  const load = useCallback(() => {
    setLoadErr("");
    return authFetch(`${API}/api/eng/projects?page=${page}&per_page=${perPage}`)
      .then(async r => {
        if (!r.ok) throw new Error(`The server could not list projects (${r.status}).`);
        return r.json();
      })
      .then(d => {
        setRows(d.items || []);
        setTotal(d.total || 0);
        setUnreadable(d.unreadable || []);
      })
      .catch(e => { setRows(null); setLoadErr(String(e.message || e)); });
  }, [page]);
  useEffect(() => { if (!sel) load(); }, [sel, load]);
  useLive(["eng_projects", "eng_inquiries"], () => { if (!sel) load(); });

  if (sel) return <ProjectDetail id={sel} me={me} onBack={() => setSel(null)} onGoTo={onGoTo} />;

  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 20, color: "#1F4E79", marginBottom: 14 }}>Projects</div>
      <Card style={{ padding: 0 }}>
        <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Project No", "Name", "Inquiry", "Stage", "Rooms", "Engineer", ""].map(h => <th key={h} style={ES.th}>{h}</th>)}</tr></thead>
            <tbody>
              {rows === null && <tr><td colSpan={7} style={{ ...ES.td, color: "#9CA3AF" }}>Loading…</td></tr>}
              {loadErr && (
                <tr><td colSpan={7} style={{ ...ES.td, color: "#B45309" }}>
                  {loadErr} Nothing has been lost — reload, and if it persists
                  the list is failing on a project it cannot read rather than
                  on there being none.
                </td></tr>
              )}
              {!loadErr && rows === null && <tr><td colSpan={7} style={{ ...ES.td, color: "#9CA3AF" }}>Loading…</td></tr>}
              {!loadErr && rows && rows.length === 0 && <tr><td colSpan={7} style={{ ...ES.td, color: "#9CA3AF" }}>No projects yet — convert an inquiry from the Inquiries tab.</td></tr>}
              {/* A project the API could not serialise. Named rather than
                  dropped: a project that quietly vanishes from this list is
                  worse than one that appears saying it cannot be read. */}
              {(unreadable || []).map(u => (
                <tr key={`bad-${u.id}`}>
                  <td colSpan={7} style={{ ...ES.td, color: "#B45309" }}>
                    {u.project_no || `Project ${u.id}`} — {u.name || "unnamed"} could not be
                    read: {u.error}
                  </td>
                </tr>
              ))}
              {rows && rows.map(p => (
                <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => setSel(p.id)}>
                  <td style={{ ...ES.td, fontWeight: 700, color: "#1F4E79" }}>{p.project_no}</td>
                  <td style={ES.td}>{p.name}</td>
                  <td style={ES.td}>{p.inquiry_no}</td>
                  <td style={ES.td}><Badge text={gateLabel(p.stage_gate)} color="#8B5CF6" /></td>
                  <td style={ES.td}>{p.room_count}</td>
                  <td style={ES.td}>{p.responsible_engineer || "—"}</td>
                  <td style={ES.td}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}
                      onClick={e => e.stopPropagation()}>
                      <span style={{ color: "#3B82F6", cursor: "pointer" }}
                        onClick={() => setSel(p.id)}>Open →</span>
                      {/* Delete lives on the row as well as the detail page.
                          Putting it only inside the record meant you had to open
                          something to find out you could remove it. */}
                      {HAS(me, "project.delete") && (
                        <DeleteButton what={`project ${p.project_no}`}
                          onDelete={() => authFetch(`${API}/api/eng/projects/${p.id}`, { method: "DELETE" })}
                          onDone={load} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > perPage && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderTop: "1px solid #F3F4F6" }}>
            <span style={{ fontSize: 12, color: "#6B7280" }}>{(page - 1) * perPage + 1}–{(page - 1) * perPage + (rows ? rows.length : 0)} of {total}</span>
            <div style={{ flex: 1 }} />
            <Btn small outline disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</Btn>
            <span style={{ fontSize: 12, color: "#374151" }}>Page {page} / {Math.max(1, Math.ceil(total / perPage))}</span>
            <Btn small outline disabled={page >= Math.ceil(total / perPage)} onClick={() => setPage(p => p + 1)}>Next ›</Btn>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── Price List (live Zoho Books prices) ───────────────────────────────────────
// ── Zoho purchase panel (shared) ──────────────────────────────────────────────
// One rendering of what Zoho knows about a SKU's buying history, used both by
// the card the price list opens and by the item page reached from the
// dashboard. They previously drew the same item_intel payload two different
// ways, so the same item looked like two different records depending on how you
// arrived at it.
function ZohoPurchasePanel({ data }) {
  const [tab, setTab] = useState("history");
  const st = data?.stats;

  const Tile = ({ label, value, sub, color }) => (
    <div style={{ background: "#F9FAFB", border: "1px solid #F3F4F6", borderRadius: 8,
                  padding: "8px 12px", minWidth: 112 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase",
                    letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: color || "#111827" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#9CA3AF" }}>{sub}</div>}
    </div>
  );
  const tabBtn = (k, label) => (
    <button key={k} onClick={() => setTab(k)}
      style={{ border: "1px solid " + (tab === k ? "#1F4E79" : "#E5E7EB"),
               background: tab === k ? "#1F4E79" : "#fff", color: tab === k ? "#fff" : "#374151",
               borderRadius: 8, padding: "5px 11px", fontSize: 12, fontWeight: 600,
               cursor: "pointer", fontFamily: "inherit" }}>{label}</button>
  );

  if (!st) {
    return (
      <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8,
                    padding: 12, fontSize: 13, color: "#6B7280" }}>
        This item has never appeared on a purchase order, so there is no price history to show.
      </div>
    );
  }

  return (
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <Tile label="Purchased" value={`${st.po_count}×`}
          sub={`${st.first_purchase} → ${st.last_purchase}`} />
        <Tile label="Vendors" value={st.vendor_count} />
        <Tile label="Total qty" value={st.total_qty} />
        <Tile label="Total spend" value={inr(st.total_spend)} />
        <Tile label="Last rate" value={inr(st.last_price)} color="#047857" sub="per unit" />
        <Tile label="Range" value={`${inr(st.min_price)} – ${inr(st.max_price)}`} sub="per unit" />
        {/* Two averages on purpose: the plain mean of line rates, and a mean
            weighted by quantity. They part company where one unit was bought at
            a one-off price beside orders of hundreds, and the weighted one is
            what the stock actually cost. */}
        <Tile label="Weighted avg" value={inr(st.weighted_avg)} sub="by quantity" />
      </div>

      {/* Purchase Summary — the graph beside the numbers. KAPL, 27-Sep-2026,
          on A02-001.002: "still the graph is not there". The Sales Order
          Summary is on Overview; this tab had tiles and a table only. Spend
          per month for the last twelve, from the same Books purchase lines as
          the table, each bar saying what it is made of. */}
      {(() => {
        const now = new Date();
        const months = Array.from({ length: 12 }, (_, i) => {
          const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        });
        const by = Object.fromEntries(months.map(m => [m, { amount: 0, qty: 0, orders: 0 }]));
        let older = 0;
        (data.purchases || []).forEach(p => {
          const m = String(p.date || "").slice(0, 7);
          if (!by[m]) { if (m) older += 1; return; }
          const amt = Number(p.amount) || (Number(p.rate) || 0) * (Number(p.quantity) || 0);
          by[m].amount += amt; by[m].qty += Number(p.quantity) || 0; by[m].orders += 1;
        });
        const peak = Math.max(1, ...months.map(m => by[m].amount));
        return (
          <div style={{ border: "1px solid #F3F4F6", borderRadius: 8, padding: "12px 12px 8px",
                        marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5, color: "#111827" }}>
                Purchase Summary (In INR)
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ fontSize: 12, color: "#6B7280" }}>Last 12 months</div>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 110,
                          borderBottom: "1px solid #E5E7EB", paddingBottom: 2 }}>
              {months.map(m => {
                const v = by[m];
                const avg = v.qty ? v.amount / v.qty : 0;
                return (
                  <div key={m} style={{ flex: 1, display: "flex", alignItems: "flex-end", height: "100%" }}
                    title={v.orders
                      ? `${m} — ${inr(v.amount)} on ${v.orders} line(s), qty ${v.qty}, avg ${inr(avg)} / unit`
                      : `${m} — no purchase`}>
                    <div style={{ flex: 1, height: `${v.amount ? Math.max(3, (v.amount / peak) * 100) : 0}%`,
                                  background: "#1F4E79", borderRadius: "3px 3px 0 0" }} />
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
              {months.map(m => (
                <div key={m} style={{ flex: 1, textAlign: "center", fontSize: 9.5, color: "#9CA3AF" }}>
                  {m.slice(5)}
                </div>
              ))}
            </div>
            {older > 0 && (
              <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 6 }}>
                {older} older purchase line(s) are in the history below, before this window.
              </div>
            )}
          </div>
        );
      })()}

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        {tabBtn("history", `Purchase history (${(data.purchases || []).length})`)}
        {tabBtn("vendors", `Vendors (${(data.by_vendor || []).length})`)}
      </div>

      {tab === "history" && (
        <div className="kz-tablewrap" style={{ maxHeight: 340 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["PO", "Date", "Vendor", "Qty", "Rate / unit", "Amount"].map(h =>
              <th key={h} className={ac(h)} style={ES.th}>{h}</th>)}</tr></thead>
            <tbody>
              {(data.purchases || []).map((p, i) => (
                <tr key={(p.po_number || "") + i}>
                  <td style={{ ...ES.td, fontWeight: 600 }}>{p.po_number || "—"}</td>
                  <td style={ES.td}>{p.date || "—"}</td>
                  <td style={{ ...ES.td, whiteSpace: "normal" }}>{p.vendor || "—"}</td>
                  <td style={ES.td}>{p.quantity ?? "—"}</td>
                  <td style={{ ...ES.td, fontWeight: 700, color: "#047857" }}>
                    {p.rate ? inr(p.rate) : "—"}</td>
                  <td style={{ ...ES.td, color: "#6B7280" }}>{p.amount ? inr(p.amount) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "vendors" && (
        <div className="kz-tablewrap" style={{ maxHeight: 340 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Vendor", "Orders", "Qty", "Last rate", "Last order", "Rate range"].map(h =>
              <th key={h} className={ac(h)} style={ES.th}>{h}</th>)}</tr></thead>
            <tbody>
              {(data.by_vendor || []).map((v, i) => (
                <tr key={v.vendor + i}>
                  <td style={{ ...ES.td, whiteSpace: "normal" }}>{v.vendor}</td>
                  <td style={ES.td}>{v.orders}</td>
                  <td style={ES.td}>{v.qty}</td>
                  <td style={{ ...ES.td, fontWeight: 700, color: "#047857" }}>
                    {v.last_rate ? inr(v.last_rate) : "—"}</td>
                  <td style={ES.td}>{v.last_date || "—"}</td>
                  <td style={{ ...ES.td, color: "#6B7280" }}>
                    {v.min_rate ? `${inr(v.min_rate)} – ${inr(v.max_rate)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ color: "#9CA3AF", fontSize: 11, marginTop: 8 }}>
        Read-only from Zoho Books purchase orders.
      </div>
    </>
  );
}

// ── Zoho item card (opened from the price list) ───────────────────────────────
// ── Item Creation Logic ───────────────────────────────────────
// A read-only window on the controlled Q&A specification
// (KAPL-INV-AI-LOGIC-03). It is deliberately not editable: the document is
// loaded from a file whose SHA-256 is recorded, so what the flow asks and what
// KAPL issued can always be compared. Editing a question here would break that.

function QAScriptModal({ code, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    authFetch(`${API}/api/qa-spec/script/${encodeURIComponent(code)}`)
      .then(r => jsonOrThrow(r, "Could not load this script"))
      .then(setD).catch(e => setErr(String(e.message || e)));
  }, [code]);

  const sub = d?.subcategory;
  const cell = { padding: "8px 10px", fontSize: 12.5, borderBottom: "1px solid #F3F4F6", verticalAlign: "top" };
  const head = { padding: "8px 10px", fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: .5, borderBottom: "1px solid #E5E7EB" };

  return (
    <EngModal title={code} onClose={onClose} maxWidth={1080}>
      {err && <div style={{ color: "#EF4444", fontSize: 13 }}>{err}</div>}
      {!d && !err && <div style={{ color: "#9CA3AF", fontSize: 13 }}>Loading…</div>}
      {sub && (
        <>
          <Card style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#1F4E79" }}>{sub.subcategory_name}</div>
              <Badge text={sub.status} color={sub.status === "ACTIVE" ? "#10B981" : "#9CA3AF"} />
              <div style={{ flex: 1 }} />
              <div style={{ fontSize: 12, color: "#6B7280" }}>
                {sub.category_code} {sub.category_name} · UOM {sub.default_uom || "—"}
              </div>
            </div>
            {sub.initial_description_example && (
              <div style={{ marginTop: 8, fontSize: 12.5, color: "#6B7280" }}>
                <span style={{ fontWeight: 600 }}>Example description:</span> {sub.initial_description_example}
              </div>
            )}
            {sub.required_evidence && (
              <div style={{ marginTop: 4, fontSize: 12.5, color: "#6B7280" }}>
                <span style={{ fontWeight: 600 }}>Evidence:</span> {sub.required_evidence}
              </div>
            )}
          </Card>

          {sub.reserved ? (
            <Card>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#B45309", marginBottom: 8 }}>
                {sub.reserved.behaviour || "BLOCKED"} — this code loads no questions
              </div>
              {sub.reserved.prompt && (
                <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>{sub.reserved.prompt}</div>
              )}
              {sub.reserved.system_action && (
                <div style={{ fontSize: 12.5, color: "#6B7280", marginBottom: 6 }}>
                  <b>System action:</b> {sub.reserved.system_action}
                </div>
              )}
              {sub.reserved.activation_requirement && (
                <div style={{ fontSize: 12.5, color: "#6B7280" }}>
                  <b>To activate:</b> {sub.reserved.activation_requirement}
                </div>
              )}
            </Card>
          ) : (
            <Card>
              <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
                  <thead><tr>
                    {["", "Field", "Asked when missing", "Confirmed when extracted", "Stored", "Name"].map((h, i) =>
                      <th key={i} style={head}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {d.stages.map(st => (
                      <tr key={st.script_id}>
                        <td style={{ ...cell, fontWeight: 700, color: "#1F4E79", whiteSpace: "nowrap" }}>{st.stage}</td>
                        <td style={cell}>
                          <div style={{ fontWeight: 600 }}>{st.dynamic_field || "—"}</div>
                          <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                            {st.required || "—"}{st.input_control ? ` · ${st.input_control}` : ""}
                          </div>
                          {st.option_source && st.option_source !== "N/A" && (
                            <div style={{ fontSize: 11, color: "#9CA3AF" }}>from {st.option_source}</div>
                          )}
                        </td>
                        <td style={cell}>{st.ask_question || "—"}</td>
                        <td style={cell}>
                          {st.confirmation_prompt || "—"}
                          {st.sample_user_answer && (
                            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 3 }}>
                              e.g. {st.sample_user_answer}
                            </div>
                          )}
                        </td>
                        <td style={{ ...cell, whiteSpace: "nowrap" }}>
                          <code style={{ fontSize: 11.5 }}>{st.attribute_key || (st.store_path || "").split(".").pop()}</code>
                          {st.is_identity && (
                            <div style={{ fontSize: 10, color: "#10B981", fontWeight: 700 }}>IDENTITY</div>
                          )}
                        </td>
                        <td style={{ ...cell}}>
                          {st.nomenclature_order ? st.nomenclature_order : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 10, lineHeight: 1.6 }}>
                Confidence thresholds are the document's: ≥ 0.90 prefill, 0.70–0.89 confirm, below 0.70 ask.
                “Identity” marks the answers the duplicate check is made on. “Name” is the position the value
                takes in the generated nomenclature.
              </div>
            </Card>
          )}
        </>
      )}
    </EngModal>
  );
}

function ItemCreationLogic() {
  const [v, setV] = useState(null);
  const [subs, setSubs] = useState([]);
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    authFetch(`${API}/api/qa-spec/subcategories`)
      .then(r => jsonOrThrow(r, "Could not load the specification"))
      .then(d => { setV(d.version); setSubs(d.subcategories || []); })
      .catch(e => setErr(String(e.message || e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  useLive(["categories", "subcategories", "eng_qa_subcategory"], load);

  const cats = [...new Set(subs.map(s => s.category_code))].sort();
  const shown = subs.filter(s =>
    (!cat || s.category_code === cat) &&
    (!status || s.status === status) &&
    (!q || `${s.subcategory_code} ${s.subcategory_name}`.toLowerCase().includes(q.toLowerCase())));

  const chip = (label, on, onClick) => (
    <button key={label} onClick={onClick} style={{
      padding: "5px 11px", fontSize: 12, borderRadius: 999, cursor: "pointer",
      border: `1px solid ${on ? "#1F4E79" : "#E5E7EB"}`,
      background: on ? "#EAF0F6" : "#fff", color: on ? "#1F4E79" : "#6B7280",
      fontWeight: on ? 600 : 500,
    }}>{label}</button>
  );

  if (err) return <Card><div style={{ color: "#EF4444", fontSize: 13 }}>{err}</div></Card>;

  if (v === null && !subs.length) {
    return <div style={{ color: "#9CA3AF", padding: 20 }}>Loading specification…</div>;
  }

  if (!v) {
    return (
      <Card>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>No specification loaded</div>
        <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.7 }}>
          The item-creation questions come from a controlled workbook, not from code. Load one with:
          <div style={{ marginTop: 8, padding: "10px 12px", background: "#F9FAFB", borderRadius: 6,
                        fontFamily: "ui-monospace, monospace", fontSize: 12, overflowX: "auto" }}>
            python load_qa_spec.py --xlsx "&lt;workbook&gt;.xlsx" --version 4.0 --commit
          </div>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1F4E79" }}>
              {v.document_ref || "Item creation specification"} · v{v.document_version || "—"}
            </div>
            <div style={{ fontSize: 12.5, color: "#6B7280", marginTop: 3 }}>{v.source_file}</div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2, fontFamily: "ui-monospace, monospace" }}>
              sha256 {(v.sha256 || "").slice(0, 16)}…
            </div>
          </div>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            {[["Subcategories", v.subcategories_total],
              ["Active", v.subcategories_active],
              ["Reserved", v.subcategories_reserved],
              ["Script rows", v.script_rows]].map(([l, n]) => (
              <div key={l}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>{l}</div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{n}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 10 }}>
          Loaded {fmtIST(v.loaded_at, "date")}
          {v.loaded_by ? ` by ${v.loaded_by}` : ""}. Read-only — replace it by loading a new issue.
        </div>
      </Card>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        {chip("All categories", !cat, () => setCat(""))}
        {cats.map(c => chip(c, cat === c, () => setCat(cat === c ? "" : c)))}
        <div style={{ width: 12 }} />
        {chip("Active", status === "ACTIVE", () => setStatus(status === "ACTIVE" ? "" : "ACTIVE"))}
        {chip("Reserved", status === "RESERVED", () => setStatus(status === "RESERVED" ? "" : "RESERVED"))}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search code or name…"
               style={{ marginLeft: "auto", padding: "7px 11px", fontSize: 13, border: "1px solid #E5E7EB",
                        borderRadius: 6, minWidth: 190 }} />
      </div>

      <Card>
        <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
          {/* One row per SOP clause; a long procedure fills the page on its own. */}
<ScrollBox max="56vh">
<table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead><tr>
              {["Code", "Subcategory", "Status", "Dynamic labels (P3 → P7C)", "UOM"].map(h => (
                <th key={h} className={ac(h)} style={{ padding: "8px 12px", fontSize: 10, fontWeight: 700,
                                     color: "#6B7280", textTransform: "uppercase", letterSpacing: .5,
                                     borderBottom: "1px solid #E5E7EB" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {shown.map(s => (
                <tr key={s.subcategory_code} onClick={() => setOpen(s.subcategory_code)}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 600, color: "#3B82F6",
                               borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" }}>{s.subcategory_code}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, borderBottom: "1px solid #F3F4F6" }}>
                    {s.subcategory_name}
                  </td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #F3F4F6" }}>
                    <Badge text={s.status} color={s.status === "ACTIVE" ? "#10B981" : "#9CA3AF"} />
                  </td>
                  <td style={{ padding: "9px 12px", fontSize: 12, color: "#6B7280",
                               borderBottom: "1px solid #F3F4F6" }}>
                    {s.status === "ACTIVE"
                      ? ["P3", "P4", "P5A", "P5B", "P6", "P7A", "P7B", "P7C"]
                          .map(k => s.labels[k]).filter(x => x && x !== "N/A").join(" · ")
                      : "—"}
                  </td>
                  <td style={{ padding: "9px 12px", fontSize: 12.5, color: "#6B7280",
                               borderBottom: "1px solid #F3F4F6" }}>{s.default_uom || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
</ScrollBox>
        </div>
        {!shown.length && (
          <div style={{ color: "#9CA3AF", fontSize: 13, padding: "10px 2px" }}>
            Nothing matches those filters.
          </div>
        )}
        <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 10 }}>
          Showing {shown.length} of {subs.length}. Open a row for its full question script.
        </div>
      </Card>

      {open && <QAScriptModal code={open} onClose={() => setOpen(null)} />}
    </>
  );
}

/** "Saved.", and what happened to the name in Zoho Books after a rename. */
function zohoSyncNote(saved) {
  const z = saved && saved.zoho_name_sync;
  if (!z || z.state === "NOT_LINKED") return "Saved.";
  return `Saved. ${z.message || ""}`.trim();
}

function ZohoItemBody({ data, masterName }) {
  const px = data?.price;
  const st = data?.stats;
  const retired = !!data?.renamed_old;
  // The record that held this code before it was released. Its purchase
  // history is real and is not this item's — shown apart, and never mixed
  // into the figures above.
  const prev = data?.previous_holder;
  const F = (label, value) => (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280" }}>{label}</div>
      <div style={{ fontSize: 13, color: "#111827" }}>{value ?? "—"}</div>
    </div>
  );
  // KAPL B1 (20-Oct-2026): no Books figure prices a BOQ by itself. A purchase
  // / landed cost does only once Purchase validates it as a rate source (tier
  // 4, Rates & Policy); the selling rate is shown for information.
  const sellRate = px && px.rate ? `₹${inr0(px.rate)}` : null;
  // A PO line rate is per-unit too, but priced against whatever unit that PO
  // used — a roll rather than a metre — so on 800 of the 807 items that have
  // both, the two disagree, some by more than tenfold.
  // Boolean(): `st.last_price && px.purchase_rate` returns the number 0 when a
  // Zoho item carries no rate, and JSX renders a bare 0 onto the card.
  const poDiffers = !!(st && px && st.last_price && px.purchase_rate
    && Math.abs(st.last_price - px.purchase_rate) > 0.01);

  return (
    <>
      {prev && (
        <Card style={{ marginBottom: 12, background: "#FFFBEB",
                       border: "1px solid #FDE68A" }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#92400E",
                        marginBottom: 6 }}>
            Earlier purchases under this code belong to a different item
          </div>
          <div style={{ fontSize: 13, color: "#374151", marginBottom: 8 }}>
            <b>{prev.name}</b>
            <span style={{ color: "#9CA3AF" }}> · {prev.sku}</span>
          </div>
          <div style={{ display: "grid",
                        gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))",
                        gap: 10, marginBottom: 8 }}>
            {F("Its orders", prev.purchase_orders)}
            {F("Last purchased", prev.last_purchased)}
            {F("Last rate", prev.last_rate != null ? `₹${prev.last_rate}` : null)}
            {F("Range", prev.rate_low != null
                ? `₹${prev.rate_low} – ₹${prev.rate_high}` : null)}
            {F("Total spend", prev.total_spend != null
                ? `₹${prev.total_spend.toLocaleString("en-IN")}` : null)}
          </div>
          <div style={{ fontSize: 11.5, color: "#78350F", lineHeight: 1.5 }}>
            {prev.note} Nothing above or below counts it as this item's price.
          </div>
        </Card>
      )}
      {!px && !prev && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: "#6B7280" }}>
            Nothing purchased against this item yet.
          </div>
        </Card>
      )}
      {px && (
        <Card style={{ marginBottom: 12 }}>
          {retired && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8,
                          padding: "8px 11px", fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>
              <div style={{ fontWeight: 700, color: "#B91C1C", marginBottom: 2 }}>
                Superseded record
              </div>
              <div style={{ color: "#6B7280" }}>
                This Zoho code was retired under the <b>-OLD</b> suffix to release the
                number. It is not selectable and cannot price a BOQ. Its purchase history
                is kept here for reference only.
              </div>
            </div>
          )}
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1F4E79", marginBottom: masterName && px.zoho_name && masterName !== px.zoho_name ? 2 : 8 }}>
            {masterName || px.zoho_name}
          </div>
          {masterName && px.zoho_name && masterName !== px.zoho_name && (
            <div style={{ fontSize: 12, color: "#B45309", marginBottom: 8 }}>
              Zoho Books still names it “{px.zoho_name}”. Saving the item sends its name to Books; if
              this stays, the last attempt was refused — see the item's Audit tab.
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
            {F("Unit", px.unit)}
            {F("Brand / make", px.brand)}
            {F("HSN / SAC", px.hsn)}
            {F("GST", px.gst_rate ? `${px.gst_rate}%` : null)}
            {F("Stock on hand", px.stock_on_hand)}
            {F("Selling rate (information)", sellRate)}
            {F("Purchase (cost) rate", px.purchase_rate ? `₹${inr0(px.purchase_rate)}` : null)}
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 10, lineHeight: 1.6 }}>
            Books figures are evidence only: a BOQ is priced from a purchase / landed cost only after Purchase
            validates it on Rates &amp; Policy (KAPL B1 tier 4).
          </div>
          {poDiffers && (
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4, lineHeight: 1.6 }}>
              {poDiffers && <div>The last PO rate (₹{inr0(st.last_price)}) differs from this item's purchase rate. A PO line is priced against whatever unit that PO used, so check the unit before trusting it.</div>}
            </div>
          )}
        </Card>
      )}
      <ZohoPurchasePanel data={data} />
    </>
  );
}

function ZohoItemCard({ sku, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    authFetch(`${API}/api/items/${encodeURIComponent(sku)}/zoho`)
      .then(r => jsonOrThrow(r, "Could not load this item from Zoho"))
      .then(setD).catch(e => setErr(String(e.message || e)));
  }, [sku]);

  return (
    <EngModal title={sku} onClose={onClose} maxWidth={900}>
      {err && <div style={{ color: "#EF4444", fontSize: 13 }}>{err}</div>}
      {!d && !err && <div style={{ color: "#9CA3AF", fontSize: 13 }}>Loading…</div>}
      {d && !d.found && <div style={{ color: "#9CA3AF", fontSize: 13 }}>Zoho holds nothing for this SKU.</div>}
      {d && d.found && <ZohoItemBody data={d} />}
    </EngModal>
  );
}

function PriceList() {
  const [openSku, setOpenSku] = useState(null);
  const [data, setData] = useState(null);
  const [qDraft, setQDraft] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [onlyPriced, setOnlyPriced] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState("");
  const per = 50;

  useEffect(() => { const t = setTimeout(() => { setQ(qDraft.trim()); setPage(0); }, 350); return () => clearTimeout(t); }, [qDraft]);
  const load = useCallback(() => {
    const p = new URLSearchParams({ skip: String(page * per), limit: String(per) });
    if (q) p.set("q", q);
    if (onlyPriced) p.set("only_priced", "true");
    authFetch(`${API}/api/eng/zoho/price-list?${p}`).then(r => r.json()).then(setData).catch(() => setData({ items: [], total: 0 }));
  }, [q, page, onlyPriced]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_zoho_items", "items"], load, { delay: 1500 });

  const sync = async () => {
    setSyncing(true); setMsg("");
    try {
      const r = await authFetch(`${API}/api/eng/zoho/sync-books`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Sync failed");
      setMsg("✅ Prices refreshed from Zoho Books.");
      load();
    } catch (e) { setMsg(`❌ ${e.message}`); }
    setSyncing(false);
  };

  const rows = data?.items || [];
  const pages = Math.max(1, Math.ceil((data?.total || 0) / per));
  const lastSync = data?.last_sync ? fmtIST(data.last_sync) : "never";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 10, marginBottom: 6 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>Price List</h2>
        <span style={{ fontSize: 12, color: "#6B7280" }}>Master items with their Zoho Books prices — last sync {lastSync}</span>
        <div style={{ flex: 1 }} />
        <Btn small outline onClick={sync} disabled={syncing}>{syncing ? "Syncing…" : "⟳ Sync from Zoho"}</Btn>
      </div>
      <p style={{ fontSize: 12, color: "#6B7280", marginTop: 0, marginBottom: 12 }}>
        Read-only. These are the prices a BOQ uses when an element is linked to the matching SKU.
        Only items in this master are listed; records that exist only in Zoho Books are not shown.
      </p>
      {msg && <div style={{ fontSize: 12, marginBottom: 10, color: msg.startsWith("✅") ? "#166534" : "#DC2626" }}>{msg}</div>}

      <Card style={{ padding: 0 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", padding: "10px 12px", borderBottom: "1px solid #F3F4F6" }}>
          <input value={qDraft} onChange={e => setQDraft(e.target.value)} placeholder="Search code, name, brand or HSN…"
            style={{ flex: 1, minWidth: 200, padding: "8px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 13 }} />
          <label style={{ fontSize: 12, color: "#374151", display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={onlyPriced} onChange={e => { setOnlyPriced(e.target.checked); setPage(0); }} /> Purchased only
          </label>
          <span style={{ fontSize: 12, color: "#6B7280", whiteSpace: "nowrap" }}>{(data?.total || 0).toLocaleString("en-IN")} items</span>
        </div>
        <div className="kz-tablewrap" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            {/* Selling and Purchase came off the Zoho ITEM record — a list price
                nobody may have paid. What is shown now is what a unit actually
                cost on the last purchase order, and when. */}
            <thead><tr>{["SKU", "Item", "Unit", "Rate ₹ / unit", "PO date", "Vendor", "GST %", "HSN/SAC", "Stock"].map(h =>
              <th key={h} style={ES.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data === null && <tr><td colSpan={9} style={{ ...ES.td, color: "#9CA3AF" }}>Loading…</td></tr>}
              {data && rows.length === 0 && <tr><td colSpan={9} style={{ ...ES.td, color: "#9CA3AF" }}>No matching items.</td></tr>}
              {rows.map((it, i) => (
                <tr key={(it.sku || "") + i}
                    onClick={() => it.sku && setOpenSku(it.sku)}
                    style={{ cursor: it.sku ? "pointer" : "default" }}
                    title={it.sku ? "Open everything Zoho holds for this item" : ""}>
                  <td style={{ ...ES.td, fontFamily: "monospace", fontSize: 11, whiteSpace: "nowrap",
                               color: "#1F4E79", fontWeight: 700 }}>
                    {it.sku || "—"}
                  </td>
                  <td style={ES.td}>
                    <div>{it.name}</div>
                    {it.zoho_name && <div style={{ fontSize: 11, color: "#B45309" }}>In Zoho Books as “{it.zoho_name}”</div>}
                    {it.brand && <div style={{ fontSize: 10, color: "#9CA3AF" }}>{it.brand}</div>}
                  </td>
                  <td style={ES.td}>{it.unit || "—"}</td>
                  <td style={{ ...ES.td, fontWeight: 700, color: it.po_rate > 0 ? "#059669" : "#D1D5DB" }}
                      title={it.po_rate > 0
                        ? `Unit rate on ${it.po_number || "the last PO"}${it.po_qty ? ` (qty ${it.po_qty})` : ""}`
                        : "This item has never appeared on a purchase order."}>
                    {it.po_rate > 0 ? inr(it.po_rate) : "—"}
                  </td>
                  <td style={{ ...ES.td, color: "#6B7280", whiteSpace: "nowrap" }}>{it.po_date || "—"}</td>
                  <td style={{ ...ES.td, color: "#6B7280", maxWidth: 180, overflow: "hidden",
                               textOverflow: "ellipsis" }} title={it.po_vendor || ""}>
                    {it.po_vendor || "—"}</td>
                  <td style={ES.td}>{it.gst_rate ? `${it.gst_rate}%` : "—"}</td>
                  <td style={{ ...ES.td, fontSize: 11 }}>{it.hsn_or_sac || "—"}</td>
                  <td style={ES.td}>{it.stock_on_hand ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderTop: "1px solid #F3F4F6" }}>
            <span style={{ fontSize: 12, color: "#6B7280" }}>{page * per + 1}–{page * per + rows.length} of {(data?.total || 0).toLocaleString("en-IN")}</span>
            <div style={{ flex: 1 }} />
            <Btn small outline disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>‹ Prev</Btn>
            <span style={{ fontSize: 12, color: "#374151" }}>Page {page + 1} / {pages}</span>
            <Btn small outline disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)}>Next ›</Btn>
          </div>
        )}
      </Card>
      {openSku && <ZohoItemCard sku={openSku} onClose={() => setOpenSku(null)} />}
    </div>
  );
}

export default function App() {
  useTableAligner();                           // one alignment rule for every table
  const [me, setMe] = useState(null);          // null = not signed in
  const [booting, setBooting] = useState(true);
  const [tab, setTab] = useState(null);
  const [showPwChange, setShowPwChange] = useState(false);

  const logout = useCallback(() => {
    setToken(null); setMe(null); setTab(null);
  }, []);

  // Any 401 from anywhere drops straight back to the login screen.
  useEffect(() => { _onUnauthorized = logout; return () => { _onUnauthorized = null; }; }, [logout]);

  // One live connection per signed-in tab; screens subscribe with useLive.
  useEffect(() => {
    if (!me) return undefined;
    startLive(`${API}/api/live`, () => _token);
    return () => stopLive();
  }, [me]);

  // Resume an existing session on reload.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!_token) { if (alive) setBooting(false); return; }
      try {
        const r = await authFetch(`${API}/api/auth/me`);
        if (r.ok && alive) { const u = await r.json(); setMe(u); setTab(tabsFor(u)[0]); }
      } catch { /* fall through to login */ }
      if (alive) setBooting(false);
    })();
    return () => { alive = false; };
  }, []);

  const onLoggedIn = (user) => { setMe(user); setTab(tabsFor(user)[0]); };

  // A reset link lands on "/?reset=<token>". Handled before the login gate,
  // because someone following it has no session by definition.
  const resetToken = new URLSearchParams(window.location.search).get("reset");
  if (resetToken && !me) {
    return (
      <div style={{ minHeight: "100vh", background: "#F8FAFC", display: "flex", alignItems: "center",
                    justifyContent: "center", padding: 20, fontFamily: "system-ui, sans-serif" }}>
        <div style={{ width: "100%", maxWidth: 380 }}>
          <div style={{ textAlign: "center", marginBottom: 22 }}>
            <img src="/kaizen-logo.png" alt="Kaizen Airtreat Pvt. Ltd."
              style={{ width: 190, height: "auto", display: "block", margin: "0 auto 10px" }} />
          </div>
          <ResetPassword token={resetToken}
            onDone={() => { window.history.replaceState({}, "", "/"); window.location.reload(); }} />
        </div>
      </div>
    );
  }

  if (booting) {
    return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF", fontFamily: "system-ui, sans-serif" }}>Loading…</div>;
  }
  if (!me) return <Login onLoggedIn={onLoggedIn} />;
  if (me.must_change_password) {
    return <ChangePassword forced onDone={() => setMe({ ...me, must_change_password: false })} />;
  }

  const tabs = tabsFor(me);
  const current = tabs.includes(tab) ? tab : tabs[0];

  return (
    <div className="kz-shell" style={{ background: "#F8FAFC", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {/* Vertical navigation rail — brand blue, its own scroll container so a
          growing tab list never pushes the page. Collapses to a horizontal
          strip under 860px (see index.css). */}
      <nav className="kz-rail">
        <div style={{ padding: "14px 12px 12px", borderBottom: "1px solid rgba(255,255,255,.12)" }}>
          {/* No chip: the logo sits straight on the rail. The wordmark in
              kaizen-logo.png is mid-grey, which reads at only 2.2:1 against this
              navy — hence the white box that used to be here. kaizen-logo-light
              .png is the same artwork with the greyscale wordmark knocked out to
              white (8.7:1) and the arrow mark's brand colours untouched, so the
              background can be transparent without losing the company name. */}
          <div style={{ padding: "6px 12px", textAlign: "center" }}>
            <img src="/kaizen-logo-light.png" alt="Kaizen Airtreat Pvt. Ltd."
              style={{ width: "100%", maxWidth: 150, height: "auto", display: "block", margin: "0 auto" }} />
          </div>
          <div style={{ fontSize: 11, color: "#93B4D1", marginTop: 8, textAlign: "center" }}>Item Master System</div>
        </div>

        <div className="kz-rail-nav">
          {tabs.map(t => {
            const on = current === t;
            return (
              <button key={t} onClick={() => setTab(t)} title={t}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "9px 12px", marginTop: 2, borderRadius: 8, cursor: "pointer",
                  border: "none", textAlign: "left", whiteSpace: "nowrap",
                  background: on ? "rgba(255,255,255,.16)" : "transparent",
                  color: on ? "#fff" : "#C7D9E8",
                  fontWeight: on ? 700 : 500, fontSize: 13,
                  boxShadow: on ? "inset 3px 0 0 #7DD3FC" : "none",
                }}>
                <span style={{ width: 18, textAlign: "center", fontSize: 14, flexShrink: 0 }}>{TAB_ICONS[t] || "•"}</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{t}</span>
              </button>
            );
          })}
        </div>

        <div className="kz-rail-foot" style={{ padding: "10px 12px", borderTop: "1px solid rgba(255,255,255,.12)" }}>
          <div style={{ fontSize: 12, color: "#E2E8F0", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {me.full_name || me.username}
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, color: HAS(me, "item.delete") ? "#FCA5A5" : HAS(me, "user.manage") ? "#C4B5FD" : "#7DD3FC", marginBottom: 8 }}>{me.role_label || me.role}</div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setShowPwChange(true)} title="Change password"
              style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: "1px solid rgba(255,255,255,.22)", background: "transparent", color: "#C7D9E8", fontSize: 11, cursor: "pointer" }}>🔑 Password</button>
            <button onClick={logout} title="Sign out"
              style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: "1px solid rgba(255,255,255,.22)", background: "transparent", color: "#C7D9E8", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Sign out</button>
          </div>
        </div>
      </nav>

      <div className="kz-main">
        {/* No section bar here. Every page already opens with its own heading —
            Dashboard, Inquiries, Projects, "Classify & Create Item", AI
            Assistant, SOP Documents, Design Constants, Zoho Sync, Price List,
            Users — so a strip repeating the tab name printed the same word twice
            on every screen and spent 48px of vertical space doing it. Which tab
            is active is already shown, highlighted, in the rail. */}
      {showPwChange && (
        <div onClick={() => setShowPwChange(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 400 }}>
            <ChangePassword onDone={() => setShowPwChange(false)} onCancel={() => setShowPwChange(false)} />
          </div>
        </div>
      )}

        <div className="kz-content">
        {/* Full width — see .kz-page in index.css for why the 1280px cap went. */}
        <div className="kz-page">
        {current === "Dashboard" && <Dashboard me={me} />}
        {current === "Inquiries" && <InquiriesView me={me} />}
        {current === "Projects" && <ProjectsView me={me} onGoTo={(t) => setTab(t)} />}
        {current === "Create Items" && <CreateItems me={me} />}
        {current === "AI Chat" && <AIChat />}
        {current === "SOPs" && <SOPs me={me} />}
        {current === "Design Constants" && <DesignConstantsView />}
        {current === "Item Creation Logic" && <ItemCreationLogic />}
        {current === "Engineering Flow" && (
          <Suspense fallback={<div style={{ color: "#9CA3AF", padding: 20 }}>Loading the flow…</div>}>
            <EngineeringFlow api={API} authFetch={authFetch} me={me?.username}
              can={(perm) => HAS(me, perm)} onGoTo={(t) => setTab(t)} />
          </Suspense>
        )}
        {current === "Room Builder" && (
          <Suspense fallback={<div style={{ color: "#9CA3AF", padding: 20 }}>Loading the room builder…</div>}>
            <RoomBuilder api={API} authFetch={authFetch} can={(perm) => HAS(me, perm)} />
          </Suspense>
        )}
        {current === "Zoho Sync" && <ZohoSync />}
        {current === "Price List" && <PriceList />}
        {current === "Rates & Policy" && <RatesPolicy api={API} authFetch={authFetch}
                                           can={(perm) => HAS(me, perm)} me={me?.username} />}
        {current === "Users" && <Users me={me} />}
        {current === "Approvals" && <Approvals me={me} />}
        {current === "Archive" && <Archive />}
        </div>
        </div>
      </div>
    </div>
  );
}
